import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  InputBase,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import MenuIcon from '@mui/icons-material/Menu';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PrintIcon from '@mui/icons-material/Print';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import RoomServiceOutlinedIcon from '@mui/icons-material/RoomServiceOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import EditLocationAltOutlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CountdownTimer from '../components/CountdownTimer';
import RouteElapsedTimer from '../components/RouteElapsedTimer';
import IfoodCustomerTrackingLink from '../components/IfoodCustomerTrackingLink';
import NinetyNineFoodOrderActions, { getNinetyNineFoodPickupInfo } from '../components/NinetyNineFoodOrderActions';
import { address99, fallback99Items, flatten99Options, operational99Rows, option99Total } from '../utils/ninetyNineFoodPresentation';
import CourierQuoteOptions from '../components/CourierQuoteOptions';
import OrderEditDialog from '../components/OrderEditDialog';
import { createAmendmentPrinter } from '../utils/amendmentPrinter';
import { getCommercialTotal, getCommercialPayments, getCommercialOrderSource, getAmendmentCollectionMethod, getAmendmentPrintIdentity, hasCommercialAmendment } from '../utils/orderCommercial';
import BeeDeliveryStatusPanel from '../components/BeeDeliveryStatusPanel';
import BeeDeliveryOperationsPanel from '../components/BeeDeliveryOperationsPanel';
import NinetyNineFoodCustomerDelivery from '../components/NinetyNineFoodCustomerDelivery';
import NinetyNineFoodEntregaPanel from '../components/NinetyNineFoodEntregaPanel';
import { createCourierIntentId, outsideShippingEndpoint } from '../utils/courierIntent';
import { courierCardLabel, courierStatusBatches } from '../utils/courierCardStatus';
import { getOrderCountdownMinutes } from '../utils/orderCountdown';
import pixOnlineContract from '../utils/pixOnlinePaymentContract';
import './Pedidos.css';
import './Pedidos.board.css';
import { usePedidosBoardLayout } from '../utils/usePedidosBoardLayout';
import {
  READY_ORDER_ATTENTION_DURATION_MS,
  detectVerifiedReadyOrderTransitions,
  getReadyOrderAttentionKey,
} from '../utils/readyOrderAttention';
import { useAuth } from '../auth/AuthContext';
import LocationConfirmation from '../components/LocationConfirmation';
import { getApiUrl, getDefaultBridgeUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { useFiscalAvailability } from '../utils/useFiscalAvailability';
import {
  fetchOrderDisplayCatalog,
  projectIfoodPrintItems,
  projectOrderDisplayItem,
  resolveCatalogImageUrl,
} from '../utils/catalog';
import { createPedidosDataLoader, readPedidosSnapshot } from '../utils/pedidosDataLoader';
import {
  prefetchOperationalCatalog,
  scheduleOperationalCatalogPrefetch,
} from '../utils/operationalCatalogLoader';
import {
  buildOrderAddressSuggestionRequest,
  mergeOrderAddressSuggestion,
  normalizeMapSuggestionAddress,
  normalizeOptionalOrderPostalCode,
} from '../utils/orderAddressAutocomplete';
import {
  AUTH_SESSION_CHANGING_EVENT,
  getAuthSessionMarker,
  getPageAuthSession,
  isAdminLike,
  isProgrammer,
  isStoredSessionTokenCurrent,
  isStoredSessionCurrent,
} from '../utils/auth';
import {
  IFOOD_WIDGET_PROFILE_TENANT_STORAGE_KEY,
  bindWidgetProfileToTenant,
  buildIfoodWidgetSignature,
  isWidgetRequestCurrentForTenant,
  isWidgetProfileCompatibleWithTenant,
  isWidgetRuntimeCompatibleWithTenant,
  loadTenantScopedWidgetConfig,
  normalizeWidgetTenantKey,
  resetTenantScopedWidgetConfig,
  runWithWidgetProfileLock,
  shouldInvalidateWidgetForProfileStorageChange,
} from '../utils/ifoodWidgetIsolation';
import { getActivePrintPoints, getAutoPrintPointsForOrder, normalizePrintPoints } from '../utils/printPoints';
import { shouldShowKdsRoute } from '../utils/kdsOrders';
import { getLocalOrderResponsibility } from '../utils/localOrderResponsibility';
import { hasActiveIfoodShippingForCustomerTracking } from '../utils/ifoodCustomerTracking';
import { loadJsPdf } from '../utils/jsPdf';
import {
  AUTO_ACCEPT_KEY, AUTO_PRINT_KEY, GENERAL_SETTINGS_KEY, PRINTER_SETTINGS_KEY,
  applyCibdStationBindings, getTenantSettingsSession, getTenantStorageKey, isCibdStationConnectionCurrent,
  readTenantLocalJson, resolveCibdStationConnection, writeTenantLocalJson,
} from '../utils/tenantSettingsStorage';
import { checkCibdStatus, sendCibdPrint } from '../utils/cibdClient';
import { buildLocalOrderSessionMarker, LOCAL_ORDER_CREATED_EVENT } from '../utils/localOrderEvents';
import {
  flushOperationalAlertQueue,
  isOperationalAlertAuthFailure,
  reportOperationalAlert,
} from '../utils/operationalAlerts';

const API_URL = getApiUrl();
const CIBD_URL = getDefaultBridgeUrl();
const SYSTEM_PRINT_BASE_URL = CIBD_URL;
const IFOOD_WIDGET_SCRIPT_SRC = 'https://widgets.ifood.com.br/widget.js';
const IFOOD_WIDGET_MAX_MERCHANTS = 10;
const IFOOD_WIDGET_HOST_ID = 'ifood-widget-host';
const IFOOD_WIDGET_RECONNECT_COOLDOWN_MS = 30000;
const IFOOD_WIDGET_PROFILE_CONFLICT_MESSAGE = 'Este perfil do navegador já está vinculado ao widget de outra loja. Use um perfil separado do Chrome para cada tenant.';
const AUTO_PRINT_REGISTRY_KEY = 'brutos-auto-print-registry-v1';
const AUTO_PRINT_REGISTRY_TTL_MS = 18 * 60 * 60 * 1000;
const AUTO_PRINT_RETRY_DELAY_MS = 45 * 1000;
const AUTO_PRINT_STALE_QUEUE_MS = 2 * 60 * 1000;
const AUTO_PRINT_MAX_ATTEMPTS = 5;
const AUTO_PRINT_STARTUP_RECOVERY_MS = 10 * 60 * 1000;
const AUTO_PRINT_ORDERS_TIMEOUT_MS = 12 * 1000;
const AUTO_PRINT_TELEMETRY_TIMEOUT_MS = 2 * 1000;
const AUTO_PRINT_REGISTRY_CHANGED_EVENT = 'brutos-auto-print-registry-changed';
const AUTO_PRINT_ACTIVE_SINCE_KEY = 'brutos-auto-print-active-since';
const IFOOD_LOCAL_REFRESH_INTERVAL_MS = 5 * 1000;
const PRINT_CATALOG_TIMEOUT_MS = 15 * 1000;
const PRINT_CATALOG_REFRESH_INTERVAL_MS = 30 * 1000;

const ReadyOrderAttentionOverlay = ({ startedAt }) => {
  const [delayMs] = useState(() => -Math.max(
    0,
    Math.min(READY_ORDER_ATTENTION_DURATION_MS, Date.now() - startedAt)
  ));
  return (
    <Box
      aria-hidden="true"
      className="pedidos-local-ready-attention"
      style={{ '--pedidos-ready-attention-delay': `${delayMs}ms` }}
    />
  );
};
const AUTO_PRINT_ALL_CHANNELS_REFRESH_INTERVAL_MS = 30 * 1000;
export const AUTO_PRINT_SETTINGS_CHANGED_EVENT = 'brutos-auto-print-settings-changed';
const ORDER_DETAILS_CACHE_TTL_MS = 45 * 1000;
const NEW_ORDER_SOUND_PATTERN = [
  { delayMs: 0, durationMs: 220, frequencies: [1046.5, 1318.5], volume: 0.18 },
  { delayMs: 320, durationMs: 240, frequencies: [783.99, 987.77], volume: 0.17 },
  { delayMs: 860, durationMs: 220, frequencies: [1046.5, 1318.5], volume: 0.18 },
  { delayMs: 1180, durationMs: 240, frequencies: [783.99, 987.77], volume: 0.17 },
];
const DEFAULT_COMANDA_SETTINGS = {
  printerId: '',
  cibdToken: '',
  printPoints: [{ id: 'caixa', name: 'Caixa', printerId: '', enabled: true, isDefault: true }],
  comandaTitle: 'COMANDA',
  comandaSubtitle: 'Pedido',
  comandaShowOrderNumber: true,
  comandaOrderNumberBanner: false,
  comandaShowCustomer: true,
  comandaShowCustomerDocument: false,
  comandaShowCustomerPhone: true,
  comandaShowContact: true,
  comandaShowAddress: true,
  comandaShowItems: true,
  comandaShowNotes: true,
  comandaShowTotals: true,
  comandaShowDiscounts: false,
  comandaShowAdditionalFees: false,
  comandaShowPayment: true,
  comandaShowPaymentDetails: false,
  comandaShowDeliveryTime: true,
  comandaShowSalesChannel: true,
  comandaShowCreatedAt: true,
  comandaShowMerchant: false,
  comandaShowDeliveryMode: true,
  comandaShowPickupCode: false,
  comandaBoldTitle: true,
  comandaBoldOrderNumber: true,
  comandaBoldCustomer: false,
  comandaBoldAddress: false,
  comandaBoldPhone: false,
  comandaBoldOptions: false,
  comandaBoldTotal: true,
  comandaStrongPrint: false,
  comandaFontFamily: 'courier',
  comandaSizeTitle: 18,
  comandaSizeOrderNumberBanner: 16,
  comandaSizeOrderNumber: 14,
  comandaSizeItem: 12,
  comandaSizeTotal: 14,
};

const normalizeBearerToken = (token = '') => {
  const trimmed = String(token || '').trim();
  if (!trimmed) return '';
  if (/^bearer\s+/i.test(trimmed)) return trimmed;
  return `Bearer ${trimmed}`;
};

const buildCibdHeaders = (settings = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  if (settings.cibdToken) {
    headers.Authorization = normalizeBearerToken(settings.cibdToken);
  }
  return headers;
};

const fileNameFromContentDisposition = (disposition, fallback) => {
  const match = String(disposition || '').match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

const getFiscalResponseMessage = (payload = {}, fallback) => {
  const details = payload.details || payload.errors || null;
  const missingFields = details?.missingFields || details?.missing_fields || [];
  if (Array.isArray(missingFields) && missingFields.length) {
    return `${payload.error || fallback} Campos faltantes: ${missingFields.join(', ')}.`;
  }
  return payload.error || payload.message || fallback;
};

const getOrderEventCode = (event = {}) => (
  String(event.fullCode || event.code || event.eventType || '').trim().toUpperCase()
);

const isOrderPlacedEvent = (event) => (
  ['PLC', 'PLACED', 'ORDER_PLACED'].includes(getOrderEventCode(event))
);

const isOrderConfirmedEvent = (event) => (
  ['CFM', 'CONFIRMED', 'ORDER_CONFIRMED'].includes(getOrderEventCode(event))
);

const isNewOrderSoundEnabled = (session) => {
  if (!getTenantStorageKey(GENERAL_SETTINGS_KEY, session)) return false;
  try {
    const settings = readTenantLocalJson(GENERAL_SETTINGS_KEY, {}, session);
    return settings.notificacoes !== false && settings.somNovoPedido !== false;
  } catch {
    return true;
  }
};

const IFOOD_ON_DEMAND_SHIPPING_MODES = new Set(['IFOOD_PLATFORM', 'IFOOD_SHIPPING', 'IFOOD_ON_DEMAND', 'OUTSIDE_IFOOD', 'SOB_DEMANDA']);
const IFOOD_SHIPPING_CANCEL_DRIVER_STATUSES = new Set([
  'requested',
  'searching',
  'assigned',
  'goingToOrigin',
  'arrivedAtOrigin',
]);
const IFOOD_SHIPPING_HIDDEN_AFTER_CANCEL_STATUSES = new Set([
  'cancelled',
  'cancellationRequested',
  'cancellationAccepted',
]);
const IFOOD_SHIPPING_REQUEST_EVENT_CODES = new Set([
  'REQUEST_DRIVER',
  'REQUEST_DRIVER_SUCCESS',
  'REQUEST_DRIVER_SUCCES',
  'RDS',
  'REQUEST_DRIVER_FAILED',
  'RDF',
]);
const IFOOD_PICKUP_CODE_EVENT_CODES = new Set([
  'DELIVERY_PICKUP_CODE_REQUESTED',
  'DELIVERY_PICKUP_CODE_VALIDATION_SUCCESS',
  'DPCR',
  'DPCS',
]);

const STORE_DRIVER_EVENT_CODES = new Set([
  'ASSIGN_DRIVER',
  'ADR',
  'GOING_TO_ORIGIN',
  'GTO',
  'ARRIVED_AT_ORIGIN',
  'AAO',
  'DISPATCHED',
  'DSP',
  'COLLECTED',
  'CLT',
]);

const firstNonEmptyText = (...values) => (
  values
    .map((value) => String(value || '').trim())
    .find(Boolean) || ''
);

const isShippingRequestEventCode = (code = '') => (
  IFOOD_SHIPPING_REQUEST_EVENT_CODES.has(String(code || '').trim().toUpperCase())
);

const isCancelledShippingForDisplay = (shipping = null) => {
  if (!shipping || typeof shipping !== 'object') return false;
  const status = String(shipping.status || '').trim();
  const cancelStatus = String(shipping.cancelRequest?.status || '').trim();
  return (
    IFOOD_SHIPPING_HIDDEN_AFTER_CANCEL_STATUSES.has(status) ||
    IFOOD_SHIPPING_HIDDEN_AFTER_CANCEL_STATUSES.has(cancelStatus) ||
    (shipping.active === false && shipping.cancelRequest?.ok === true)
  );
};

const hasIfoodOnDemandShippingEvidence = (shipping = null) => {
  if (!shipping || typeof shipping !== 'object') return false;

  const provider = String(shipping.provider || '').trim().toLowerCase();
  const mode = String(shipping.mode || '').trim().toUpperCase();
  if (provider && provider !== 'ifood') return false;
  if (mode && !IFOOD_ON_DEMAND_SHIPPING_MODES.has(mode)) return false;

  const request = shipping.request && typeof shipping.request === 'object' ? shipping.request : {};
  const tracking = shipping.tracking && typeof shipping.tracking === 'object' ? shipping.tracking : {};
  const addressChange = shipping.addressChange && typeof shipping.addressChange === 'object' ? shipping.addressChange : {};
  const eventCodes = [
    shipping.lastEventCode,
    ...(Array.isArray(shipping.events) ? shipping.events.map((event) => event?.code) : []),
  ].map((code) => String(code || '').trim().toUpperCase()).filter(Boolean);

  return Boolean(
    request.idempotencyKey ||
    request.accepted ||
    request.response ||
    request.quoteId ||
    request.requestedAt ||
    tracking.data ||
    tracking.pickupCode ||
    tracking.deliveryConfirmationCode ||
    tracking.returnCode ||
    tracking.checkedAt ||
    addressChange.pending ||
    addressChange.status ||
    eventCodes.some(isShippingRequestEventCode)
  );
};

const normalizeStoreDriver = (source = {}) => {
  if (!source || typeof source !== 'object') return null;
  const driver = (
    source.driver ||
    source.motoboy ||
    source.courier ||
    source.worker ||
    source.deliveryman ||
    source.deliveryMan ||
    {}
  );
  const name = firstNonEmptyText(
    driver.workerName,
    driver.name,
    driver.fullName,
    source.workerName,
    source.driverName,
    source.courierName,
    source.motoboyName
  );
  const phone = firstNonEmptyText(
    driver.workerPhone,
    driver.phone,
    driver.phoneNumber,
    driver.whatsapp,
    source.workerPhone,
    source.driverPhone,
    source.courierPhone,
    source.motoboyPhone
  );
  const vehicleType = firstNonEmptyText(driver.workerVehicleType, driver.vehicleType, source.workerVehicleType, source.vehicleType);

  if (!name && !phone && !vehicleType) return null;
  return { name, phone, vehicleType };
};

const getStoreDeliveryDriver = (order = {}) => {
  const entregasZap = order?.additionalInfo?.entregasZap && typeof order.additionalInfo.entregasZap === 'object'
    ? order.additionalInfo.entregasZap
    : {};
  const logisticsActions = Array.isArray(entregasZap.logisticsActions) ? entregasZap.logisticsActions : [];
  const lastInboundExecuted = Array.isArray(entregasZap.lastInboundLogistics?.executed)
    ? entregasZap.lastInboundLogistics.executed
    : [];
  const history = Array.isArray(order?.history) ? order.history : [];

  const candidates = [
    order.ninetyNineFood?.consumerCourier,
    entregasZap.driver,
    ...lastInboundExecuted.slice().reverse().map((entry) => entry?.driver),
    ...logisticsActions.slice().reverse().map((entry) => entry?.driver),
    ...history.slice().reverse().flatMap((event) => {
      const code = getOrderEventCode(event);
      const integrationStatus = String(event?.integrationStatus || '').trim();
      if (!event?.driver && !event?.metadata && !event?.payload && !STORE_DRIVER_EVENT_CODES.has(code) && integrationStatus !== 'assigned') {
        return [];
      }
      return [
        event?.driver,
        event?.metadata,
        event?.payload,
        event,
      ];
    }),
  ];

  for (const candidate of candidates) {
    const driver = normalizeStoreDriver(candidate);
    if (driver) return driver;
  }
  return null;
};

const ORDER_SOURCE_CONFIG = {
  ifood: {
    key: 'ifood',
    label: 'iFood',
    color: '#ea1d2c',
    backgroundColor: '#fff1f2',
    borderColor: '#ea1d2c',
  },
  site: {
    key: 'site',
    label: 'Site',
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    borderColor: '#2563eb',
  },
  balcao: {
    key: 'balcao',
    label: 'Balcão',
    color: '#6558f5',
    backgroundColor: '#f4f2ff',
    borderColor: '#6558f5',
  },
  salao: {
    key: 'salao',
    label: 'Salão',
    color: '#b91c1c',
    backgroundColor: '#fff1f2',
    borderColor: '#dc2626',
  },
  pedidos10: {
    key: 'pedidos10',
    label: 'Pedidos10',
    color: '#16a34a',
    backgroundColor: '#ecfdf5',
    borderColor: '#16a34a',
  },
  '99food': {
    key: '99food',
    label: '99Food',
    color: '#111827',
    backgroundColor: '#fff4b8',
    borderColor: '#f6c600',
  },
  whatsapp: {
    key: 'whatsapp',
    label: 'WhatsApp',
    color: '#128c7e',
    backgroundColor: '#ecfdf3',
    borderColor: '#25d366',
  },
};

const LOCAL_AUTO_PRINT_SOURCE_KEYS = new Set([
  'site',
  'balcao',
  'salao',
  'pedidos10',
  '99food',
  'whatsapp',
]);

const normalizeOrderSourceText = (value = '') => (
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
);

const getOrderSourceKey = (order = {}) => {
  const orderId = String(order.orderId || order.id || '').trim().toUpperCase();
  if (orderId.startsWith('BALCAO-')) return 'balcao';
  if (orderId.startsWith('SALAO-')) return 'salao';
  if (orderId.startsWith('SITE-')) return 'site';
  if (orderId.startsWith('PEDIDOS10-')) return 'pedidos10';
  if (orderId.startsWith('99FOOD-')) return '99food';
  if (orderId.startsWith('WHATSAPP-') || orderId.startsWith('ZAP-') || orderId.startsWith('BZ-')) return 'whatsapp';

  const sourceCandidates = [
    order.source,
    order.origin,
    order.channel,
    order.salesChannel,
    order.integration,
    order.provider,
    order.fullOrderData?.source,
    order.fullOrderData?.origin,
    order.fullOrderData?.channel,
    order.fullOrderData?.salesChannel,
    order.fullOrderData?.merchant?.source,
  ].map(normalizeOrderSourceText).filter(Boolean);

  if (sourceCandidates.some((source) => source.includes('whatsapp') || source === 'wpp' || source.includes('zap'))) {
    return 'whatsapp';
  }

  if (sourceCandidates.some((source) => source === '99' || source.includes('99food') || source.includes('food99'))) {
    return '99food';
  }

  if (sourceCandidates.some((source) => source.includes('pedidos10') || source.includes('pedido10'))) {
    return 'pedidos10';
  }

  if (sourceCandidates.some((source) => source.includes('salao') || source.includes('mesa') || source.includes('dinein'))) {
    return 'salao';
  }

  if (sourceCandidates.some((source) => source.includes('balcao'))) {
    return 'balcao';
  }

  if (sourceCandidates.some((source) => source.includes('site') || source.includes('cardapio'))) {
    return 'site';
  }

  if (sourceCandidates.some((source) => source.includes('ifood'))) {
    return 'ifood';
  }

  return 'ifood';
};

export const isNinetyNineFoodTotalUnknown = (order = {}) => getOrderSourceKey(order) === '99food'
  && (order.ninetyNineFood?.totalUnknown === true || order.totalKnown === false
    || order.total?.orderAmount === null || order.total?.orderAmount === undefined);
const NINETY_NINE_FOOD_TOTAL_UNKNOWN = 'Total não informado pela 99Food';

const getNinetyNineFoodOfficialId = (order = {}) => (
  getOrderSourceKey(order) === '99food' ? String(order.externalOrderId || '').trim() : ''
);
const getOrderGeneralNotes = (order = {}) => (
  getOrderSourceKey(order) === '99food' && typeof order.notes === 'string'
    ? order.notes : order.observations || ''
);
const getComandaOrderNotes = (order = {}) => (
  getOrderGeneralNotes(order) || (getOrderSourceKey(order) === '99food' ? '' : order.additionalInfo?.metadata?.customerEmail || '')
);

const getOrderSourceConfig = (order = {}) => (
  ORDER_SOURCE_CONFIG[getOrderSourceKey(order)] || ORDER_SOURCE_CONFIG.ifood
);

const isCompactLocalOrder = (order = {}) => (
  ['preparing', 'ready', 'finished', 'cancelled'].includes(String(order.status || '').trim().toLowerCase())
  && ['balcao', 'salao'].includes(getOrderSourceKey(order))
);

const isLocalReadyOrder = (order = {}) => (
  String(order.status || '').trim().toLowerCase() === 'ready' && isCompactLocalOrder(order)
);

export const getAutoPrintOrderKey = (order = {}, tenantKey = '') => {
  const orderId = String(order.orderId || order.id || '').trim();
  if (!orderId) return '';

  const source = getOrderSourceKey(order);
  const createdAt = order.createdAt ? new Date(order.createdAt) : null;
  const createdDate = createdAt && Number.isFinite(createdAt.getTime())
    ? createdAt.toISOString().slice(0, 10)
    : '';

  const tenant = String(tenantKey || '').trim();
  const revision = Number(order._printRevision);
  return [tenant, source, orderId, createdDate, Number.isSafeInteger(revision) && revision > 0 ? `edit-${revision}` : ''].filter(Boolean).join(':');
};

const readAutoPrintRegistry = () => {
  try {
    return JSON.parse(localStorage.getItem(AUTO_PRINT_REGISTRY_KEY) || '{}') || {};
  } catch {
    return {};
  }
};

const getAutoPrintRegistryEntry = (order, tenantKey = '') => (
  readAutoPrintRegistry()[getAutoPrintOrderKey(order, tenantKey)] || null
);

const pruneAutoPrintRegistry = (registry = {}, now = Date.now()) => {
  const entries = Object.entries(registry).filter(([, value]) => {
    const savedAt = Number(value?.at || 0);
    return savedAt > 0 && now - savedAt <= AUTO_PRINT_REGISTRY_TTL_MS;
  });
  return Object.fromEntries(entries);
};

const writeAutoPrintRegistry = (registry = {}) => {
  localStorage.setItem(AUTO_PRINT_REGISTRY_KEY, JSON.stringify(registry));
  window.dispatchEvent(new Event(AUTO_PRINT_REGISTRY_CHANGED_EVENT));
};

const buildAutoPrintRegistryEntry = (order, status, now = Date.now(), patch = {}) => ({
  at: now,
  updatedAt: now,
  status,
  orderId: String(order.orderId || ''),
  displayId: String(order.displayId || order.orderId || '').slice(0, 24),
  source: getOrderSourceKey(order),
  attempts: Number(patch.attempts || 0),
  ...patch,
});

const rememberAutoPrintOrder = (order, status = 'sent', patch = {}, tenantKey = '') => {
  const key = getAutoPrintOrderKey(order, tenantKey);
  if (!key) return false;

  const now = Date.now();
  const registry = pruneAutoPrintRegistry(readAutoPrintRegistry(), now);
  if (registry[key]) {
    writeAutoPrintRegistry(registry);
    return false;
  }

  const token = `${now}-${Math.random().toString(36).slice(2)}`;
  registry[key] = buildAutoPrintRegistryEntry(order, status, now, {
    token,
    ...patch,
  });
  writeAutoPrintRegistry(registry);

  const saved = readAutoPrintRegistry();
  return saved[key]?.token === token;
};

const setAutoPrintRegistryEntry = (order, status, patch = {}, tenantKey = '') => {
  const key = getAutoPrintOrderKey(order, tenantKey);
  if (!key) return null;
  const now = Date.now();
  const registry = pruneAutoPrintRegistry(readAutoPrintRegistry(), now);
  const previous = registry[key] || {};
  registry[key] = buildAutoPrintRegistryEntry(order, status, now, {
    ...previous,
    status,
    updatedAt: now,
    ...patch,
  });
  writeAutoPrintRegistry(registry);
  return registry[key];
};

const canRetryAutoPrintEntry = (entry = {}, now = Date.now()) => {
  const status = String(entry.status || '');
  const attempts = Number(entry.attempts || 0);
  if (['sent', 'seen-on-enable', 'needs-review', 'unknown'].includes(status)) return false;
  if (attempts >= AUTO_PRINT_MAX_ATTEMPTS) return false;
  if (status === 'failed') {
    return Number(entry.nextRetryAt || 0) <= now;
  }
  if (status === 'queued') {
    if (entry.phase !== 'pre-send') return false;
    return now - Number(entry.updatedAt || entry.at || 0) >= AUTO_PRINT_STALE_QUEUE_MS;
  }
  return true;
};

const getPendingAutoPrintPointIds = (entry = {}, points = []) => {
  const pointStates = entry?.pointStates;
  if (!pointStates || typeof pointStates !== 'object' || !Object.keys(pointStates).length) {
    return points.map((point) => point.id);
  }
  return points
    .filter((point) => !['sent', 'unknown', 'dispatching'].includes(pointStates[point.id]?.status))
    .map((point) => point.id);
};

const getRemotePrintCompletion = (order, points) => {
  const expected = getAutoPrintPointsForOrder(order, points, isSalonOrder(order));
  const sentPointIds = new Set((order.additionalInfo?.printing?.history || [])
    .filter((event) => event.status === 'sent')
    .map((event) => String(event.printPointId || '')));
  const complete = (expected.length > 0 && expected.every((point) => sentPointIds.has(point.id)))
    || (expected.length === 1 && sentPointIds.size === 0);
  return { expected, sentPointIds, complete };
};

const mergeAutoPrintPointStates = (previous = {}, pointResults = [], now = Date.now()) => {
  const states = { ...(previous || {}) };
  pointResults.forEach((result) => {
    const pointId = String(result?.pointId || '').trim();
    if (!pointId) return;
    const ok = Boolean(result.ok);
    states[pointId] = {
      ...(states[pointId] || {}),
      pointId,
      pointName: result.pointName || states[pointId]?.pointName || pointId,
      printerId: result.printerId || states[pointId]?.printerId || '',
      status: ok ? 'sent' : result.dispatchStarted ? 'unknown' : 'failed',
      ...(ok ? { sentAt: now, lastError: '' } : { failedAt: now, lastError: result.error || 'Falha ao enviar para o ponto.' }),
    };
  });
  return states;
};

const claimAutoPrintOrder = (order, points = [], tenantKey = '') => {
  const key = getAutoPrintOrderKey(order, tenantKey);
  if (!key) return null;
  const now = Date.now();
  const registry = pruneAutoPrintRegistry(readAutoPrintRegistry(), now);
  const previous = registry[key];
  if (previous && !canRetryAutoPrintEntry(previous, now)) {
    writeAutoPrintRegistry(registry);
    return null;
  }

  const token = `${now}-${Math.random().toString(36).slice(2)}`;
  const attempts = Number(previous?.attempts || 0) + 1;
  const pendingPointIds = getPendingAutoPrintPointIds(previous, points);
  if (!pendingPointIds.length) {
    writeAutoPrintRegistry(registry);
    return null;
  }
  registry[key] = buildAutoPrintRegistryEntry(order, 'queued', now, {
    ...(previous || {}),
    token,
    attempts,
    queuedAt: now,
    phase: 'pre-send',
    nextRetryAt: null,
    lastError: '',
  });
  writeAutoPrintRegistry(registry);

  const saved = readAutoPrintRegistry();
  return saved[key]?.token === token ? {
    key,
    token,
    attempts,
    pendingPointIds,
    pointStates: saved[key]?.pointStates || {},
  } : null;
};

const markAutoPrintOrder = (order, status, patch = {}, tenantKey = '') => (
  setAutoPrintRegistryEntry(order, status, patch, tenantKey)
);

const reconcileAutoPrintEntry = (order, tenantKey = '') => {
  const entry = getAutoPrintRegistryEntry(order, tenantKey);
  if (!entry) return null;
  if (entry.status === 'queued' && entry.phase !== 'pre-send'
    && Date.now() - Number(entry.updatedAt || entry.at || 0) >= AUTO_PRINT_STALE_QUEUE_MS) {
    const pointStates = Object.fromEntries(Object.entries(entry.pointStates || {}).map(([pointId, point]) => [
      pointId, point?.status === 'dispatching' ? { ...point, status: 'unknown' } : point,
    ]));
    return markAutoPrintOrder(order, Object.keys(pointStates).length ? 'failed' : 'unknown', {
      pointStates,
      phase: Object.keys(pointStates).length ? 'pre-send' : entry.phase,
      nextRetryAt: Date.now(),
      lastError: 'Envio anterior sem confirmação do CIBD.',
    }, tenantKey);
  }
  if (entry.status === 'failed' && !entry.phase) {
    return markAutoPrintOrder(order, 'unknown', { lastError: 'Tentativa anterior sem confirmação do CIBD.' }, tenantKey);
  }
  return entry;
};

const loadIfoodWidgetScript = () => {
  if (window.iFoodWidget && typeof window.iFoodWidget.init === 'function') {
    return Promise.resolve();
  }

  if (window.__ifoodWidgetScriptPromise) {
    return window.__ifoodWidgetScriptPromise;
  }

  window.__ifoodWidgetScriptPromise = new Promise((resolve, reject) => {
    let scriptNode = document.querySelector('script[data-ifood-widget="true"]');

    const handleLoad = () => resolve();
    const handleError = () => {
      window.__ifoodWidgetScriptPromise = null;
      reject(new Error('Falha ao carregar script do widget do iFood.'));
    };

    if (!scriptNode) {
      scriptNode = document.createElement('script');
      scriptNode.src = IFOOD_WIDGET_SCRIPT_SRC;
      scriptNode.async = true;
      scriptNode.dataset.ifoodWidget = 'true';
      document.body.appendChild(scriptNode);
    }

    scriptNode.addEventListener('load', handleLoad, { once: true });
    scriptNode.addEventListener('error', handleError, { once: true });

    if (window.iFoodWidget && typeof window.iFoodWidget.init === 'function') {
      resolve();
    }
  });

  return window.__ifoodWidgetScriptPromise;
};

const loadIfoodWidgetConfig = async (tenantKey) => (
  loadTenantScopedWidgetConfig(window, tenantKey, () => (
    apiFetch(`${API_URL}/api/settings/ifood`, { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Falha ao carregar configuração do widget do iFood.');
        }

        const data = await response.json();
        return {
          integrationEnabled: data.integrationEnabled !== false,
          widgetId: String(data.widgetId || '').trim(),
          merchantIds: Array.isArray(data.merchantIds)
            ? data.merchantIds.map((item) => String(item || '').trim()).filter(Boolean)
            : [],
        };
      })
  ))
);

const resetIfoodWidgetConfigCache = (tenantKey) => (
  resetTenantScopedWidgetConfig(window, tenantKey)
);

const normalizeWidgetMerchantId = (value) => String(value || '').trim().toLowerCase();

const normalizeWidgetMerchantIds = (merchantIds = []) => (
  Array.from(new Set(
    merchantIds.map(normalizeWidgetMerchantId).filter(Boolean)
  )).slice(0, IFOOD_WIDGET_MAX_MERCHANTS)
);

const getIfoodWidgetHostSelector = () => {
  let host = document.getElementById(IFOOD_WIDGET_HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = IFOOD_WIDGET_HOST_ID;
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);
  }
  return `#${IFOOD_WIDGET_HOST_ID}`;
};

const initializeIfoodWidget = async ({
  tenantKey,
  widgetId,
  widgetMerchantIds,
  shouldShow,
  forceReconnect = false,
}) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (!normalizedTenantKey) {
    throw new Error('Tenant ausente para inicializar o widget iFood.');
  }
  if (!isWidgetRequestCurrentForTenant(window, normalizedTenantKey)) {
    hideIfoodWidget();
    throw new Error('Inicialização do widget iFood cancelada por mudança de sessão.');
  }

  const widgetSignature = buildIfoodWidgetSignature({
    tenantKey: normalizedTenantKey,
    widgetId,
    merchantIds: widgetMerchantIds,
  });

  const activeInit = window.__ifoodWidgetInitEntry;
  if (activeInit?.promise) {
    if (
      activeInit.tenantKey !== normalizedTenantKey
      || activeInit.signature !== widgetSignature
    ) {
      hideIfoodWidget();
      throw new Error('Já existe uma inicialização do widget iFood para outro contexto.');
    }

    const result = await activeInit.promise;
    if (!isWidgetRequestCurrentForTenant(window, normalizedTenantKey)) {
      hideIfoodWidget();
      throw new Error('Inicialização do widget iFood cancelada por mudança de sessão.');
    }
    if (shouldShow && typeof window.iFoodWidget.show === 'function') {
      window.iFoodWidget.show();
    }
    return result;
  }

  if (forceReconnect) {
    const now = Date.now();
    const lastReconnectAt = Number(window.__ifoodWidgetLastReconnectAt || 0);
    const elapsed = now - lastReconnectAt;

    if (lastReconnectAt && elapsed < IFOOD_WIDGET_RECONNECT_COOLDOWN_MS) {
      const seconds = Math.ceil((IFOOD_WIDGET_RECONNECT_COOLDOWN_MS - elapsed) / 1000);
      throw new Error(`Aguarde ${seconds}s para reconectar o widget iFood novamente.`);
    }

    window.__ifoodWidgetLastReconnectAt = now;
  }

  const initEntry = {
    tenantKey: normalizedTenantKey,
    signature: widgetSignature,
    promise: null,
  };
  initEntry.promise = (async () => {
    await runWithWidgetProfileLock(window, async () => {
      if (
        !isWidgetRequestCurrentForTenant(window, normalizedTenantKey)
        || !isWidgetProfileCompatibleWithTenant(window, normalizedTenantKey)
      ) {
        hideIfoodWidget();
        throw new Error(IFOOD_WIDGET_PROFILE_CONFLICT_MESSAGE);
      }

      await window.iFoodWidget.init({
        widgetId,
        merchantIds: widgetMerchantIds,
        autoShow: false,
        containerSelector: getIfoodWidgetHostSelector(),
      });

      if (!isWidgetRequestCurrentForTenant(window, normalizedTenantKey)) {
        hideIfoodWidget();
        throw new Error('Inicialização do widget iFood cancelada por mudança de sessão.');
      }
      if (!bindWidgetProfileToTenant(window, normalizedTenantKey)) {
        hideIfoodWidget();
        throw new Error(IFOOD_WIDGET_PROFILE_CONFLICT_MESSAGE);
      }

      window.__ifoodWidgetInitialized = true;
      window.__ifoodWidgetTenantKey = normalizedTenantKey;
      window.__ifoodWidgetSignature = widgetSignature;
      window.__ifoodWidgetLastInitAt = new Date().toISOString();
    });

    return {
      merchantCount: widgetMerchantIds.length,
      signature: widgetSignature,
    };
  })();
  window.__ifoodWidgetInitEntry = initEntry;

  try {
    const result = await initEntry.promise;
    if (!isWidgetRequestCurrentForTenant(window, normalizedTenantKey)) {
      hideIfoodWidget();
      throw new Error('Inicialização do widget iFood cancelada por mudança de sessão.');
    }
    if (shouldShow && typeof window.iFoodWidget.show === 'function') {
      window.iFoodWidget.show();
    }
    return result;
  } catch (error) {
    hideIfoodWidget();
    throw error;
  } finally {
    if (window.__ifoodWidgetInitEntry === initEntry) {
      window.__ifoodWidgetInitEntry = null;
    }
  }
};

const showIfoodWidget = async (options = {}) => {
  if (window.__ifoodWidgetSessionChanging || window.__brutosExternalAuthChanged) {
    hideIfoodWidget();
    throw new Error('O widget iFood foi bloqueado durante a troca de sessão.');
  }

  const tenantKey = normalizeWidgetTenantKey(options.tenantKey);
  if (!tenantKey) {
    throw new Error('Tenant ausente para carregar o widget iFood.');
  }
  if (!isWidgetProfileCompatibleWithTenant(window, tenantKey)) {
    hideIfoodWidget();
    throw new Error(IFOOD_WIDGET_PROFILE_CONFLICT_MESSAGE);
  }

  window.__ifoodWidgetRequestedTenantKey = tenantKey;

  if (options.refreshConfig) {
    resetIfoodWidgetConfigCache(tenantKey);
  }

  const { integrationEnabled, widgetId, merchantIds } = await loadIfoodWidgetConfig(tenantKey);
  if (!isWidgetRequestCurrentForTenant(window, tenantKey)) {
    hideIfoodWidget();
    throw new Error('Carregamento do widget iFood cancelado por mudança de sessão.');
  }
  if (!integrationEnabled) {
    throw new Error('Integração iFood desativada nas configurações.');
  }

  await loadIfoodWidgetScript();
  if (!isWidgetRequestCurrentForTenant(window, tenantKey)) {
    hideIfoodWidget();
    throw new Error('Carregamento do widget iFood cancelado por mudança de sessão.');
  }
  const widgetMerchantIds = normalizeWidgetMerchantIds(merchantIds);
  const shouldShow = options.autoShow !== false;

  if (!widgetId) {
    throw new Error('Widget iFood sem widgetId configurado.');
  }

  if (!widgetMerchantIds.length) {
    throw new Error('Widget iFood sem lojas configuradas para este login/CNPJ.');
  }

  if (!window.iFoodWidget) {
    throw new Error('Widget iFood indisponível no navegador.');
  }

  const widgetSignature = buildIfoodWidgetSignature({
    tenantKey,
    widgetId,
    merchantIds: widgetMerchantIds,
  });
  if (!isWidgetRuntimeCompatibleWithTenant(window, tenantKey)) {
    hideIfoodWidget();
    throw new Error('O widget iFood aberto pertence a outro login. Recarregue a página antes de continuar.');
  }

  if (!window.__ifoodWidgetInitialized || options.forceReconnect) {
    return initializeIfoodWidget({
      tenantKey,
      widgetId,
      widgetMerchantIds,
      shouldShow,
      forceReconnect: Boolean(options.forceReconnect),
    });
  }

  if (window.__ifoodWidgetSignature !== widgetSignature) {
    hideIfoodWidget();
    throw new Error('A configuração de lojas do widget mudou. Use reconectar antes de continuar.');
  }
  if (!bindWidgetProfileToTenant(window, tenantKey)) {
    hideIfoodWidget();
    throw new Error(IFOOD_WIDGET_PROFILE_CONFLICT_MESSAGE);
  }

  if (shouldShow && typeof window.iFoodWidget.show === 'function') {
    window.iFoodWidget.show();
  }

  return {
    merchantCount: widgetMerchantIds.length,
    signature: widgetSignature,
  };
};

const hideIfoodWidget = () => {
  if (window.iFoodWidget && typeof window.iFoodWidget.hide === 'function') {
    window.iFoodWidget.hide();
  }
};

const releaseIfoodWidgetForTenant = (tenantKey) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (
    normalizedTenantKey
    && window.__ifoodWidgetRequestedTenantKey !== normalizedTenantKey
  ) {
    return;
  }

  window.__ifoodWidgetRequestedTenantKey = '';
  hideIfoodWidget();
};

const invalidateIfoodWidgetPresentation = () => {
  window.__ifoodWidgetSessionChanging = true;
  window.__ifoodWidgetRequestedTenantKey = '';
  hideIfoodWidget();
};

const normalizeCancellationReasons = (payload, options = {}) => {
  const rawReasons = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.cancellationReasons)
      ? payload.cancellationReasons
      : Array.isArray(payload?.reasons)
        ? payload.reasons
        : [];

  return rawReasons
    .map((reason, index) => {
      const cancellationCode = String(
        reason?.cancelCodeId ||
        reason?.cancellationCode ||
        reason?.cancelCode ||
        ''
      ).trim();
      const code = String(
        cancellationCode ||
        reason?.reasonCode ||
        reason?.code ||
        reason?.reason ||
        reason?.id ||
        reason?.cancellationCode ||
        ''
      ).trim();
      const description = String(
        reason?.description ||
        reason?.label ||
        reason?.title ||
        reason?.name ||
        code ||
        `Motivo ${index + 1}`
      ).trim();
      const details = String(
        reason?.details ||
        reason?.message ||
        reason?.justificationHint ||
        ''
      ).trim();

      if (!code) return null;
      if (options.requireCancellationCode && !cancellationCode) return null;

      return {
        code,
        cancelCodeId: cancellationCode || undefined,
        cancellationCode: cancellationCode || undefined,
        apiReason: String(reason?.reason || '').trim() || undefined,
        description,
        details,
        requiresJustification: Boolean(
          reason?.requiresJustification ||
          reason?.requireJustification ||
          reason?.requiresDescription ||
          reason?.additionalDescriptionRequired
        ),
      };
    })
    .filter(Boolean);
};

const LOCAL_ORDER_CANCELLATION_REASONS = [
  {
    code: 'CUSTOMER_REQUESTED',
    description: 'Cliente solicitou cancelamento',
  },
  {
    code: 'ITEM_UNAVAILABLE',
    description: 'Item indisponível',
  },
  {
    code: 'DUPLICATE_ORDER',
    description: 'Pedido duplicado',
  },
  {
    code: 'STORE_OPERATION',
    description: 'Loja não consegue preparar este pedido',
    requiresJustification: true,
  },
  {
    code: 'OTHER',
    description: 'Outro motivo',
    requiresJustification: true,
  },
];

const createOutsideShippingItem = () => ({
  id: '',
  name: '',
  quantity: '1',
  unitPrice: '',
  optionsPrice: '0',
});

const createOutsideShippingForm = (merchantId = '') => ({
  draftId: '',
  merchantId,
  displayId: '',
  customerName: '',
  phoneType: 'CUSTOMER',
  countryCode: '55',
  areaCode: '',
  phoneNumber: '',
  merchantFee: '',
  preparationTime: '',
  postalCode: '',
  streetNumber: '',
  streetName: '',
  complement: '',
  reference: '',
  neighborhood: '',
  city: '',
  state: '',
  country: 'BR',
  latitude: '',
  longitude: '',
  items: [createOutsideShippingItem()],
  paymentMode: 'OFFLINE',
  paymentMethod: 'CREDIT',
  paymentValue: '',
  cardBrand: '',
  cashChangeFor: '',
});

const createShippingAddressChangeForm = () => ({
  postalCode: '',
  streetNumber: '',
  streetName: '',
  complement: '',
  reference: '',
  neighborhood: '',
  city: '',
  state: '',
  country: 'BR',
  latitude: '',
  longitude: '',
});

const createPickupCodeConfirmationState = () => ({
  code: '',
  submitting: false,
  message: '',
  severity: 'success',
});

const OUTSIDE_SHIPPING_ADDRESS_FIELDS = new Set([
  'postalCode',
  'streetNumber',
  'streetName',
  'complement',
  'reference',
  'neighborhood',
  'city',
  'state',
  'country',
]);

const normalizeMoneyInput = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : null;
};

const parseCoordinateInput = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const numeric = Number(raw.replace(',', '.'));
  if (!Number.isFinite(numeric) || numeric === 0) return null;
  return numeric;
};

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const splitBrazilianPhone = (value) => {
  let digits = digitsOnly(value);
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }
  return {
    countryCode: '55',
    areaCode: digits.length >= 10 ? digits.slice(0, 2) : '',
    number: digits.length >= 10 ? digits.slice(2, 11) : digits,
  };
};

const getExternalOrderSource = (order = {}) => {
  const source = getOrderSourceKey(order);
  if(source==='99food') return Number(order.deliveryType)===2 ? '99food' : '';
  if (['balcao', 'site', 'pedidos10'].includes(source)) return source;
  const orderId = String(order?.orderId || '').trim().toUpperCase();
  if (orderId.startsWith('BALCAO-')) return 'balcao';
  if (orderId.startsWith('SITE-')) return 'site';
  if (orderId.startsWith('PEDIDOS10-')) return 'pedidos10';
  return '';
};

const isNinetyNineFoodCourierEligible = order => getOrderSourceKey(order)==='99food' && Number(order.deliveryType)===2
  && order.providerAcceptance==='accepted' && Number(order.officialStatus)===200 && order.fulfillment==='delivery'
  && order.ninetyNineFood?.consumerRouteStarted!==true && !order.ninetyNineFood?.entrega?.operationId
  && !order.ninetyNineFood?.externalCourier?.operationId && order.ninetyNineFood?.privacyProtected!==true;

const getCourierOrderKey = (order) => {
  const source = getExternalOrderSource(order);
  const orderId = String(order?.orderId || '').trim();
  return source && orderId ? `${source}:${orderId}` : '';
};

const firstNonEmpty = (...values) => values
  .map((value) => String(value || '').trim())
  .find(Boolean) || '';

const getExternalOrderCustomerName = (order = {}) => firstNonEmpty(
  order?.customer?.name,
  order?.customer?.fullName,
  order?.customer?.customerName,
  order?.customerName,
  order?.user?.name,
  order?.clientName,
  order?.name
);

const getExternalOrderCustomerPhone = (order = {}) => firstNonEmpty(
  order?.customer?.whatsapp,
  order?.customer?.whatsApp,
  order?.customer?.phoneNumber,
  order?.customer?.phone?.number,
  order?.customer?.phone,
  order?.user?.whatsapp,
  order?.user?.whatsApp,
  order?.user?.phone,
  order?.customerPhone,
  order?.clientPhone,
  order?.phone,
  order?.whatsapp,
  order?.whatsApp
);

const getExternalOrderDeliveryAddress = (order = {}) => (
  order?.additionalInfo?.deliveryAddressOverride?.address ||
  order?.delivery?.deliveryAddress ||
  order?.delivery?.address ||
  order?.delivery?.addressText ||
  order?.deliveryAddress ||
  order?.deliveryAddressText ||
  order?.shippingAddress ||
  order?.customerAddress ||
  order?.customer?.address ||
  order?.customer?.deliveryAddress ||
  order?.customer?.addressText ||
  order?.user?.address ||
  order?.address ||
  order?.addressText ||
  {}
);

const parseAddressForShippingForm = (address) => {
  if (!address) return {};
  if (typeof address === 'string') {
    const parts = address
      .split(/\s*[•|]\s*/g)
      .map((part) => String(part || '').trim())
      .filter(Boolean);
    const streetPart = parts[0] || address;
    const streetPieces = streetPart.split(',').map((part) => part.trim()).filter(Boolean);
    const streetName = streetPieces.length >= 2 ? streetPieces[0] : streetPart;
    const streetNumber = streetPieces.length >= 2 ? streetPieces[1] : '';
    return {
      streetName: firstNonEmpty(streetName),
      streetNumber: firstNonEmpty(streetNumber),
      neighborhood: firstNonEmpty(parts[1], streetPieces[2]),
      city: firstNonEmpty(parts[2], streetPieces[3]),
      reference: parts.slice(3).join(' • '),
    };
  }

  const formattedAddress = firstNonEmpty(address.formattedAddress, address.address);
  const parsedFormatted = formattedAddress ? parseAddressForShippingForm(formattedAddress) : {};
  return {
    ...parsedFormatted,
    postalCode: firstNonEmpty(address.postalCode, address.cep, address.zip),
    streetName: firstNonEmpty(address.streetName, address.street, address.logradouro, parsedFormatted.streetName),
    streetNumber: firstNonEmpty(address.streetNumber, address.number, address.numero, parsedFormatted.streetNumber),
    complement: firstNonEmpty(address.complement, address.complemento),
    reference: firstNonEmpty(address.reference, address.observation, address.observations, parsedFormatted.reference),
    neighborhood: firstNonEmpty(address.neighborhood, address.bairro, parsedFormatted.neighborhood),
    city: firstNonEmpty(address.city, address.cidade, parsedFormatted.city),
    state: firstNonEmpty(address.state, address.uf),
    country: firstNonEmpty(address.country, 'BR'),
    latitude: firstNonEmpty(address.coordinates?.latitude, address.latitude, address.lat),
    longitude: firstNonEmpty(address.coordinates?.longitude, address.longitude, address.lng, address.lon),
  };
};

const getExternalOrderTotalAmount = (order = {}) => normalizeMoneyInput(
  order?.total?.orderAmount ??
  order?.total?.total ??
  order?.totalAmount ??
  order?.total
) || 0;

const getExternalOrderPaymentInfo = (order = {}) => {
  const total = getExternalOrderTotalAmount(order);
  const payments = order?.payments || {};
  const methods = Array.isArray(payments) ? payments : Array.isArray(payments.methods) ? payments.methods : [];
  const firstMethod = methods[0] || {};
  const paid = normalizeMoneyInput(
    (hasCommercialAmendment(order) ? payments.totalPaid : undefined) ??
    payments.prepaid ??
    payments.paid ??
    payments.paidAmount ??
    payments.amountPaid ??
    order?.paymentPrepaid ??
    order?.paidAmount ??
    order?.amountPaid ??
    order?.payment?.paid ??
    order?.payment?.paidAmount
  ) || 0;
  const pending = normalizeMoneyInput(
    (hasCommercialAmendment(order) ? payments.totalDue : undefined) ??
    payments.pending ??
    payments.amountDue ??
    payments.due ??
    order?.paymentPending ??
    order?.pendingAmount ??
    order?.amountDue ??
    order?.payment?.pending ??
    order?.payment?.amountDue
  ) || Math.max(total - paid, 0);
  const methodLabel = String(firstMethod.method || firstMethod.type || order?.paymentMethod || '').trim().toLowerCase();
  const isPaid = total > 0 && pending <= 0;
  let paymentMethod = 'CREDIT';
  if (methodLabel.includes('debit') || methodLabel.includes('débito')) paymentMethod = 'DEBIT';
  if (methodLabel.includes('cash') || methodLabel.includes('dinheiro')) paymentMethod = 'CASH';

  return {
    paymentMode: isPaid ? 'ONLINE' : 'OFFLINE',
    paymentMethod,
    paymentValue: pending > 0 ? String(pending) : '',
    cardBrand: firstMethod.card?.brand || '',
    cashChangeFor: firstMethod.cash?.changeFor || '',
  };
};

const getExternalOrderItemsForShipping = (order = {}) => {
  const items = Array.isArray(order.items) ? order.items : [];
  return items.length
    ? items.map((item, index) => {
        const quantity = Number(item.quantity || 1) || 1;
        const unitPrice = normalizeMoneyInput(
          item.unitPrice ??
          item.unitValue ??
          item.price ??
          item.value ??
          item.totalPrice / quantity
        );
        const name = firstNonEmpty(item.name, item.description, item.productName);
        return {
          id: firstNonEmpty(
            item.externalCode,
            item.id,
            item.sku,
            item.code,
            item.productId,
            item.itemId,
            item.catalogItemId,
            name ? `item-${index + 1}` : ''
          ),
          name,
          quantity: String(quantity),
          unitPrice: unitPrice !== null ? String(unitPrice) : '',
          optionsPrice: String(normalizeMoneyInput(item.optionsPrice ?? item.complementsPrice ?? item.additionalPrice) || 0),
        };
      })
    : [createOutsideShippingItem()];
};

const buildOutsideShippingGeocodePayload = (form = {}) => ({
  postalCode: String(form.postalCode || '').trim(),
  streetNumber: String(form.streetNumber || '').trim(),
  streetName: String(form.streetName || '').trim(),
  complement: String(form.complement || '').trim(),
  reference: String(form.reference || '').trim(),
  neighborhood: String(form.neighborhood || '').trim(),
  city: String(form.city || '').trim(),
  state: String(form.state || '').trim().toUpperCase(),
  country: String(form.country || 'BR').trim().toUpperCase(),
});

const buildShippingAddressChangePayload = (form = {}, coordinates = {}) => {
  const latitude = parseCoordinateInput(coordinates.latitude ?? form.latitude);
  const longitude = parseCoordinateInput(coordinates.longitude ?? form.longitude);
  return {
    streetNumber: String(form.streetNumber || '').trim(),
    streetName: String(form.streetName || '').trim(),
    ...(String(form.complement || '').trim() ? { complement: String(form.complement || '').trim() } : null),
    ...(String(form.reference || '').trim() ? { reference: String(form.reference || '').trim() } : null),
    neighborhood: String(form.neighborhood || '').trim(),
    city: String(form.city || '').trim(),
    state: String(form.state || '').trim().toUpperCase(),
    country: String(form.country || 'BR').trim().toUpperCase(),
    coordinates: {
      latitude,
      longitude,
    },
  };
};

const validateShippingAddressChangePayload = (payload = {}) => {
  if (!payload.streetName || !payload.neighborhood || !payload.city || !payload.state || !payload.country) {
    return 'Preencha rua, bairro, cidade, UF e país do novo endereço.';
  }
  if (payload.state.length !== 2 || payload.country.length !== 2) {
    return 'UF e país precisam ter 2 letras.';
  }
  if (!Number.isFinite(payload.coordinates?.latitude) || !Number.isFinite(payload.coordinates?.longitude)) {
    return 'Localize o novo endereço para obter coordenadas reais antes de solicitar a alteração.';
  }
  return '';
};

const createOutsideShippingFormFromOrder = (order = {}, merchantId = '') => {
  const phone = splitBrazilianPhone(getExternalOrderCustomerPhone(order));
  const address = parseAddressForShippingForm(getExternalOrderDeliveryAddress(order));
  const payment = getExternalOrderPaymentInfo(order);
  const deliveryFee = normalizeMoneyInput(order?.total?.deliveryFee ?? order?.delivery?.deliveryFee ?? order?.deliveryFee);

  return {
    ...createOutsideShippingForm(merchantId),
    displayId: String(order?.displayId || '').replace(/\D/g, '').slice(0, 4),
    customerName: getExternalOrderCustomerName(order),
    countryCode: phone.countryCode,
    areaCode: phone.areaCode,
    phoneNumber: phone.number,
    merchantFee: deliveryFee !== null ? String(deliveryFee) : '',
    postalCode: digitsOnly(address.postalCode).slice(0, 8),
    streetNumber: address.streetNumber || '',
    streetName: address.streetName || '',
    complement: address.complement || '',
    reference: address.reference || '',
    neighborhood: address.neighborhood || '',
    city: address.city || '',
    state: String(address.state || '').toUpperCase().slice(0, 2),
    country: String(address.country || 'BR').toUpperCase().slice(0, 2),
    latitude: address.latitude || '',
    longitude: address.longitude || '',
    items: getExternalOrderItemsForShipping(order),
    ...payment,
    ...(getOrderSourceKey(order)==='99food' ? {phoneType:'STORE',areaCode:'',phoneNumber:'',paymentMode:'ONLINE'} : {}),
  };
};

const getShippingAddressChangeSourceAddress = (order = {}) => (
  order?.shipping?.addressChange?.address ||
  order?.delivery?.deliveryAddress ||
  order?.fullOrderData?.delivery?.deliveryAddress ||
  order?.deliveryAddress ||
  getExternalOrderDeliveryAddress(order)
);

const createShippingAddressChangeFormFromOrder = (order = {}) => {
  const address = parseAddressForShippingForm(getShippingAddressChangeSourceAddress(order));
  return {
    ...createShippingAddressChangeForm(),
    postalCode: digitsOnly(address.postalCode).slice(0, 8),
    streetNumber: address.streetNumber || '',
    streetName: address.streetName || '',
    complement: address.complement || '',
    reference: address.reference || '',
    neighborhood: address.neighborhood || '',
    city: address.city || '',
    state: String(address.state || '').toUpperCase().slice(0, 2),
    country: String(address.country || 'BR').toUpperCase().slice(0, 2),
    latitude: address.latitude || '',
    longitude: address.longitude || '',
  };
};

const createOrderAddressEditFormFromOrder = (order = {}) => {
  const address = parseAddressForShippingForm(getExternalOrderDeliveryAddress(order));
  return {
    ...createShippingAddressChangeForm(),
    postalCode: digitsOnly(address.postalCode).slice(0, 8),
    streetNumber: address.streetNumber || '',
    streetName: address.streetName || '',
    complement: address.complement || '',
    reference: address.reference || '',
    neighborhood: address.neighborhood || '',
    city: address.city || '',
    state: String(address.state || '').toUpperCase().slice(0, 2),
    country: String(address.country || 'BR').toUpperCase().slice(0, 2),
  };
};

const initialCounters = {
  preparing: 0,
  ready: 0,
  enRoute: 0,
  finished: 0,
  cancelled: 0,
};

const statusBlocks = [
  {
    key: 'preparing',
    title: 'Em preparo',
    description: 'Aqui aparecerão os pedidos em preparo e aguardando rota',
    icon: RoomServiceOutlinedIcon,
  },
  {
    key: 'ready',
    title: 'Pronto',
    description: 'Aqui ficarão seus pedidos prontos para coleta',
    icon: ShoppingBagOutlinedIcon,
  },
  {
    key: 'enRoute',
    title: 'Em rota',
    description: 'Aqui ficarão seus pedidos a caminho dos seus clientes',
    icon: DeliveryDiningOutlinedIcon,
  },
];

const statusLabels = {
  preparing: 'Em preparo',
  ready: 'Pronto',
  enRoute: 'Em rota',
  finished: 'Concluído',
  cancelled: 'Cancelado',
};

const statusFilterOptions = [
  { key: 'preparing', label: 'Em preparo' },
  { key: 'ready', label: 'Pronto' },
  { key: 'enRoute', label: 'Em rota' },
  { key: 'finished', label: 'Concluído' },
  { key: 'cancelled', label: 'Cancelado' },
];

const normalizeSearchText = (value) => (
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim()
);

const normalizeSearchCompact = (value) => normalizeSearchText(value).replace(/[^a-z0-9]/g, '');

const normalizeOrderStatusKey = (status) => {
  const compact = normalizeSearchCompact(status);
  const map = {
    preparing: 'preparing',
    preparo: 'preparing',
    empreparo: 'preparing',
    preparacao: 'preparing',
    pronto: 'ready',
    ready: 'ready',
    enroute: 'enRoute',
    emrota: 'enRoute',
    finished: 'finished',
    concluido: 'finished',
    finalizado: 'finished',
    completed: 'finished',
    delivered: 'finished',
    entregue: 'finished',
    cancelled: 'cancelled',
    canceled: 'cancelled',
    cancelado: 'cancelled',
  };
  return map[compact] || String(status || '').trim();
};

const getSearchCustomerText = (customer) => {
  if (!customer) return '';
  if (typeof customer === 'string') return customer;
  return [
    customer.name,
    customer.fullName,
    customer.customerName,
    customer.firstName,
    customer.lastName,
    customer.phone?.number,
    customer.phone?.localizer,
    customer.phone,
    customer.whatsapp,
    customer.whatsApp,
  ].filter(Boolean).join(' ');
};

const getOrderSearchText = (order = {}) => [
  order.displayId,
  order.orderId,
  order.id,
  order.shortNumber,
  order.source,
  order.salesChannel,
  order.status,
  statusLabels[order.status],
  order.orderType,
  order.orderTiming,
  order.fulfillment,
  order.customerName,
  order.clientName,
  order.customerPhone,
  order.phone,
  order.whatsapp,
  order.tableId,
  order.tableNumber,
  order.tableSessionId,
  order.waiterUserId,
  order.waiterName,
  getSearchCustomerText(order.customer),
  getSearchCustomerText(order.fullOrderData?.customer),
  getSearchCustomerText(order.lastPayload?.order?.customer),
  order.fullOrderData?.displayId,
  order.lastPayload?.order?.displayId,
  order.lastPayload?.order?.shortNumber,
].filter(Boolean).join(' ');

const negotiationStatusLabels = {
  PENDING: 'Pendente',
  ACCEPTED: 'Aceita',
  REJECTED: 'Rejeitada',
  PROPOSED_ALTERNATIVE: 'Contraoferta enviada',
  EXPIRED: 'Expirada',
};

const isTerminalStatus = (status) => ['finished', 'cancelled'].includes(normalizeOrderStatusKey(status));

const buildDashboardState = (orders = [], lastSync = null, now = Date.now()) => {
  const finishedTtlMs = 6 * 60 * 60 * 1000;
  const cleanedOrders = [...(orders || [])]
    .sort((a, b) => {
      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return bDate - aDate;
    })
    .filter((order) => {
      if (order.status === 'ready' && String(order.orderId || '').startsWith('TEST-')) {
        return false;
      }
      if (!isTerminalStatus(order.status)) {
        return true;
      }
      const finishedAtValue = order.finishedAt || order.updatedAt || order.createdAt;
      const finishedAtMs = finishedAtValue ? new Date(finishedAtValue).getTime() : NaN;
      if (!Number.isFinite(finishedAtMs)) return true;
      return now - finishedAtMs <= finishedTtlMs;
    });

  const counters = cleanedOrders.reduce(
    (acc, order) => {
      const status = normalizeOrderStatusKey(order.status);
      if (status === 'cancelled') {
        acc.cancelled += 1;
        acc.finished += 1;
        return acc;
      }
      if (acc[status] !== undefined) {
        acc[status] += 1;
      }
      return acc;
    },
    { ...initialCounters }
  );

  return {
    counters,
    orders: cleanedOrders,
    lastSync,
  };
};

const isSalonOrder = (order = {}) => {
  const orderId = String(order.orderId || order.id || '').trim().toUpperCase();
  const source = normalizeOrderSourceText(order.source || order.salesChannel || order.channel || order.origin || '');
  return orderId.startsWith('SALAO-') || source.includes('salao');
};

const isIfoodOrder = (order = {}) => {
  const orderId = String(order.orderId || '');
  const source = String(order.source || order.salesChannel || '').trim().toLowerCase();
  if (orderId.startsWith('BALCAO-') || orderId.startsWith('SALAO-') || orderId.startsWith('SITE-') || orderId.startsWith('PEDIDOS10-') || orderId.startsWith('BZ-')) {
    return false;
  }
  if (source.includes('zap') || source.includes('whatsapp')) return false;
  return source === 'ifood' || source === '' || source === 'loja ifood';
};

const canCancelLocalOrder = (order = {}) => {
  if (!order || isTerminalStatus(order.status)) return false;
  const orderId = String(order.orderId || order.id || '').trim().toUpperCase();
  const sourceKey = getOrderSourceKey(order);
  return (
    sourceKey === 'site' ||
    sourceKey === 'whatsapp' ||
    orderId.startsWith('SITE-') ||
    orderId.startsWith('BZ-') ||
    orderId.startsWith('ZAP-') ||
    orderId.startsWith('WHATSAPP-')
  );
};

const hasActiveNegotiation = (order) => Boolean(order?.negotiation?.active);
const getNegotiationStatusLabel = (status) => negotiationStatusLabels[String(status || '').toUpperCase()] || status || '-';

const parseNegotiationAmount = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') {
    return value > 999 ? value / 100 : value;
  }
  const normalized = String(value).trim();
  if (!normalized) return 0;
  if (/^-?\d+$/.test(normalized)) {
    return Number(normalized) / 100;
  }
  return parseAmount(normalized);
};

const formatNegotiationAmount = (value) => formatCurrency(parseNegotiationAmount(value));

const getStatusChipProps = (status) => {
  if (status === 'cancelled') {
    return {
      label: statusLabels.cancelled,
      color: 'error',
      variant: 'outlined',
    };
  }

  if (status === 'finished') {
    return {
      label: statusLabels.finished,
      color: 'success',
      variant: 'outlined',
    };
  }

  return {
    label: statusLabels[status] || status,
    color: 'default',
    variant: 'filled',
  };
};

const getMerchantDisplayName = (merchant = {}) => (
  merchant?.name ||
  merchant?.tradingName ||
  merchant?.displayName ||
  merchant?.corporateName ||
  merchant?.shortName ||
  merchant?.id ||
  'Loja iFood'
);

const parseInterruptionDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const normalized = /([zZ]|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isInterruptionActive = (interruption = {}, referenceDate = new Date()) => {
  const start = parseInterruptionDate(interruption?.start);
  const end = parseInterruptionDate(interruption?.end);
  const now = referenceDate.getTime();
  const startTime = start && !Number.isNaN(start.getTime()) ? start.getTime() : NaN;
  const endTime = end && !Number.isNaN(end.getTime()) ? end.getTime() : NaN;
  return Number.isFinite(startTime) && Number.isFinite(endTime) && startTime <= now && endTime >= now;
};

const deriveMerchantBadge = ({ merchantStatus, interruptions }) => {
  const activeInterruptions = (Array.isArray(interruptions) ? interruptions : []).filter((item) => isInterruptionActive(item));
  if (activeInterruptions.length) {
    return { label: 'Pausada', color: 'warning' };
  }

  const entries = Array.isArray(merchantStatus)
    ? merchantStatus
    : Array.isArray(merchantStatus?.operations)
      ? merchantStatus.operations
      : merchantStatus
        ? [merchantStatus]
        : [];

  const hasOpenOperation = entries.some((entry) => {
    const state = String(entry?.state || '').toUpperCase();
    const message = String(entry?.message?.status || entry?.message?.title || '').toLowerCase();
    return entry?.available === true || state === 'OK' || message.includes('aberta');
  });
  if (hasOpenOperation) {
    return { label: 'Aberta', color: 'success' };
  }

  const hasClosedState = entries.some((entry) => {
    const state = String(entry?.state || '').toUpperCase();
    const message = String(entry?.message?.status || entry?.message?.title || '').toLowerCase();
    return (
      entry?.available === false ||
      state === 'CLOSED' ||
      state === 'ERROR' ||
      state === 'WARNING' ||
      message.includes('fechada')
    );
  });

  if (hasClosedState) {
    return { label: 'Fechada', color: 'error' };
  }

  return { label: 'Sem status', color: 'default' };
};

const extractMerchantStatusPayload = (payload) => {
  if (Array.isArray(payload?.merchantStatus)) return payload.merchantStatus;
  if (payload?.merchantStatus && typeof payload.merchantStatus === 'object') return payload.merchantStatus;
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && Array.isArray(payload.operations)) return payload;
  return [];
};

const extractInterruptionsPayload = (payload) => (
  Array.isArray(payload) ? payload : payload?.interruptions || []
);

const normalizeMerchantListPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.merchants)) return payload.merchants;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getMerchantIdentifier = (merchant = {}) => (
  String(merchant?.merchantId || merchant?.id || merchant?.storeId || merchant?.uuid || '').trim()
);

const isMerchantIdLike = (value) => (
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || '').trim())
);

const mergeMerchantStoreSummary = (previous = {}, next = {}) => {
  const nextName = String(next.name || '').trim();
  const previousName = String(previous.name || '').trim();
  const name = nextName && !isMerchantIdLike(nextName)
    ? nextName
    : previousName || nextName;
  const previousStatusLabel = previous.statusLabel || '';
  const nextStatusLabel = next.statusLabel || '';
  const shouldKeepPreviousStatus = (
    previousStatusLabel &&
    previousStatusLabel !== 'Sem status' &&
    (!nextStatusLabel || nextStatusLabel === 'Sem status')
  );

  return {
    ...previous,
    ...next,
    name,
    stale: shouldKeepPreviousStatus || next.stale === true,
    statusLabel: shouldKeepPreviousStatus ? previousStatusLabel : nextStatusLabel || previousStatusLabel || 'Sem status',
    statusColor: shouldKeepPreviousStatus ? previous.statusColor || 'default' : next.statusColor || previous.statusColor || 'default',
    interruptions: shouldKeepPreviousStatus
      ? previous.interruptions || []
      : Array.isArray(next.interruptions) ? next.interruptions : previous.interruptions || [],
  };
};

const buildMerchantStoreSummary = ({
  merchantId,
  merchant = {},
  merchantStatus = [],
  interruptions = [],
}) => {
  const normalizedId = String(merchantId || getMerchantIdentifier(merchant) || '').trim();
  const badge = deriveMerchantBadge({ merchantStatus, interruptions });

  return {
    merchantId: normalizedId,
    name: getMerchantDisplayName({ ...merchant, id: normalizedId }),
    statusLabel: badge.label,
    statusColor: badge.color,
    interruptions,
    stale: merchant.stale === true || merchant.cache?.refreshDue === true || badge.label === 'Sem status',
    cache: merchant.cache || null,
  };
};

const formatCurrency = (value) => {
  const normalizedValue =
    value && typeof value === 'object'
      ? value.orderAmount ?? value.total ?? value.amount ?? value.value ?? value.subTotal ?? 0
      : value;
  const numeric = Number(normalizedValue || 0);
  if (!Number.isFinite(numeric)) {
    return (0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const parseAmount = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
  if (typeof value === 'string') {
    const cleaned = value
      .replace(/[^\d,.-]/g, '')
      .replace(/\.(?=\d{3}(?:[.,]|$))/g, '')
      .replace(',', '.');
    const parsed = Number.parseFloat(cleaned);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const formatMoney = (value) => formatCurrency(parseAmount(value));

const getCashChangeAmount = (method = {}, fallbackValue = 0) => {
  const changeFor = parseAmount(method?.cash?.changeFor ?? 0);
  const baseValue = parseAmount(method?.value ?? fallbackValue ?? 0);
  if (changeFor <= 0) return 0;
  return Math.max(changeFor - baseValue, 0);
};

const COUPON_KEYWORDS = ['coupon', 'cupom', 'discount', 'desconto', 'benefit', 'benefits', 'voucher', 'promotion', 'promo'];

const pathLooksLikeCouponData = (path) => {
  const normalized = String(path || '').toLowerCase();
  return COUPON_KEYWORDS.some((keyword) => normalized.includes(keyword));
};

const normalizeCouponSponsor = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized.includes('ifood')) return 'ifood';
  if (
    normalized.includes('merchant') ||
    normalized.includes('store') ||
    normalized.includes('shop') ||
    normalized.includes('restaurant') ||
    normalized.includes('restaurante') ||
    normalized.includes('partner') ||
    normalized.includes('loja')
  ) {
    return 'merchant';
  }
  return '';
};

const extractCouponBreakdown = (order) => {
  const fallbackTotal = parseAmount(
    order?.fullOrderData?.total?.benefits ||
    order?.total?.benefits ||
    order?.total?.discounts ||
    order?.coupon?.amount ||
    0
  );

  const result = {
    code: String(
      order?.coupon?.code ||
      order?.fullOrderData?.coupon?.code ||
      order?.fullOrderData?.discount?.code ||
      ''
    ).trim(),
    totalAmount: fallbackTotal,
    ifoodAmount: 0,
    merchantAmount: 0,
    unknownAmount: 0,
  };

  const visited = new WeakSet();

  const addAmount = (sponsor, amount) => {
    const numericAmount = parseAmount(amount);
    if (numericAmount <= 0) return;
    if (sponsor === 'ifood') {
      result.ifoodAmount += numericAmount;
      return;
    }
    if (sponsor === 'merchant') {
      result.merchantAmount += numericAmount;
      return;
    }
    result.unknownAmount += numericAmount;
  };

  const inspectCandidate = (node, path) => {
    if (!node || typeof node !== 'object') return;

    const directIfoodAmount = parseAmount(
      node.ifoodAmount ||
      node.issuerAmount ||
      node.platformAmount ||
      node.marketplaceAmount
    );
    const directMerchantAmount = parseAmount(
      node.merchantAmount ||
      node.storeAmount ||
      node.restaurantAmount ||
      node.partnerAmount ||
      node.shopAmount
    );

    if (directIfoodAmount > 0 || directMerchantAmount > 0) {
      addAmount('ifood', directIfoodAmount);
      addAmount('merchant', directMerchantAmount);
      return;
    }

    const ownerSponsor = normalizeCouponSponsor(
      node.ownerName ||
      node.sponsor ||
      node.sponsoredBy ||
      node.fundedBy ||
      node.provider ||
      node.liability
    );
    const baseAmount = parseAmount(
      node.amount ||
      node.value ||
      node.discountAmount ||
      node.benefitAmount ||
      node.totalDiscount ||
      node.totalBenefit
    );

    if (Array.isArray(node.liabilities) && node.liabilities.length && baseAmount > 0) {
      let allocated = 0;
      node.liabilities.forEach((entry) => {
        const sponsor = normalizeCouponSponsor(entry?.name || entry?.type || entry?.ownerName);
        const amount = parseAmount(entry?.amount);
        if (amount > 0) {
          addAmount(sponsor, amount);
          allocated += amount;
          return;
        }
        const percentage = parseAmount(entry?.percentage);
        if (percentage > 0) {
          const proportionalAmount = (baseAmount * percentage) / 100;
          addAmount(sponsor, proportionalAmount);
          allocated += proportionalAmount;
        }
      });

      if (allocated < baseAmount) {
        addAmount(ownerSponsor, baseAmount - allocated);
      }
      return;
    }

    if (Array.isArray(node.sponsorshipValues) && node.sponsorshipValues.length) {
      node.sponsorshipValues.forEach((entry) => {
        addAmount(
          normalizeCouponSponsor(entry?.name || entry?.type || entry?.ownerName || entry?.sponsor),
          entry?.amount || entry?.value
        );
      });
      return;
    }

    if (pathLooksLikeCouponData(path) && ownerSponsor && baseAmount > 0) {
      addAmount(ownerSponsor, baseAmount);
    }
  };

  const walk = (node, path = '', depth = 0) => {
    if (!node || depth > 8) return;
    if (Array.isArray(node)) {
      node.forEach((item, index) => walk(item, `${path}[${index}]`, depth + 1));
      return;
    }
    if (typeof node !== 'object') return;
    if (visited.has(node)) return;
    visited.add(node);

    inspectCandidate(node, path);

    Object.entries(node).forEach(([key, value]) => {
      const nextPath = path ? `${path}.${key}` : key;
      walk(value, nextPath, depth + 1);
    });
  };

  walk(order?.fullOrderData, 'fullOrderData');
  walk(order?.lastPayload, 'lastPayload');
  walk(order?.coupon, 'coupon');
  walk(order?.total, 'total');

  const explicitTotal = result.ifoodAmount + result.merchantAmount + result.unknownAmount;
  if (result.totalAmount <= 0 && explicitTotal > 0) {
    result.totalAmount = explicitTotal;
  } else if (result.totalAmount > 0 && explicitTotal > 0 && explicitTotal < result.totalAmount) {
    result.unknownAmount += result.totalAmount - explicitTotal;
  } else if (result.totalAmount > 0 && explicitTotal > result.totalAmount + 0.01) {
    result.ifoodAmount = 0;
    result.merchantAmount = 0;
    result.unknownAmount = result.totalAmount;
  }

  result.hasDiscount = result.totalAmount > 0;
  result.hasSponsorBreakdown = result.ifoodAmount > 0 || result.merchantAmount > 0;
  return result;
};

const extractCouponBreakdownFromBenefits = (benefits = [], fallback = null) => {
  const summary = {
    totalAmount: 0,
    ifoodAmount: 0,
    merchantAmount: 0,
  };

  if (!Array.isArray(benefits) || benefits.length === 0) {
    return fallback || summary;
  }

  benefits.forEach((benefit) => {
    summary.totalAmount += parseAmount(benefit?.value);
    (benefit?.sponsorshipValues || []).forEach((entry) => {
      const sponsor = normalizeCouponSponsor(entry?.name || entry?.type);
      const amount = parseAmount(entry?.value || entry?.amount);
      if (sponsor === 'ifood') summary.ifoodAmount += amount;
      if (sponsor === 'merchant') summary.merchantAmount += amount;
    });
  });

  if (summary.totalAmount <= 0 && fallback) {
    return fallback;
  }

  return {
    ...(fallback || {}),
    ...summary,
    hasDiscount: summary.totalAmount > 0,
    hasSponsorBreakdown: summary.ifoodAmount > 0 || summary.merchantAmount > 0,
  };
};

const normalizeAddressDisplayText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const addressTextIncludes = (text, value) => {
  const needle = normalizeAddressDisplayText(value);
  if (!needle) return false;
  return normalizeAddressDisplayText(text).includes(needle);
};

const formatAddressForDisplay = (value) => {
  if (!value) return '';
  if (typeof value !== 'object') {
    return String(value || '')
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean)
      .join('\n');
  }

  const parsed = parseAddressForShippingForm(value);
  const lines = [];
  const primaryLine =
    [parsed.streetName, parsed.streetNumber].filter(Boolean).join(', ') ||
    firstNonEmpty(value.formattedAddress, value.address);

  if (primaryLine) lines.push(primaryLine);
  if (parsed.complement && !addressTextIncludes(lines.join(' '), parsed.complement)) lines.push(parsed.complement);
  if (parsed.neighborhood && !addressTextIncludes(lines.join(' '), parsed.neighborhood)) lines.push(parsed.neighborhood);

  const cityLine = [parsed.city, parsed.state].filter(Boolean).join(' - ');
  if (cityLine && !addressTextIncludes(lines.join(' '), parsed.city || cityLine)) lines.push(cityLine);
  if (parsed.reference && !addressTextIncludes(lines.join(' '), parsed.reference)) lines.push(`Ref: ${parsed.reference}`);

  return lines.filter(Boolean).join('\n');
};

const PAYMENT_METHOD_LABELS = {
  unknown: 'Não informado',
  cash: 'Dinheiro',
  credit: 'Cartão de crédito',
  debit: 'Cartão de débito',
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  pix: 'Pix',
  voucher: 'Vale',
  vr: 'Vale-refeição',
  va: 'Vale-alimentação',
  meal_voucher: 'Vale-refeição',
  food_voucher: 'Vale-alimentação',
  card: 'Cartão',
};

const PAYMENT_TIMING_LABELS = {
  prepaid: 'Online',
  on_delivery: 'Na entrega',
  delivery: 'Na entrega',
  pay_on_delivery: 'Na entrega',
  online: 'Online',
};

const formatPaymentMethodLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const normalized = raw.toLowerCase();
  return PAYMENT_METHOD_LABELS[normalized] || raw;
};

const formatPaymentTimingLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const normalized = raw.toLowerCase();
  return PAYMENT_TIMING_LABELS[normalized] || raw;
};

const ORDER_TIMING_LABELS = {
  immediate: 'Imediato',
  scheduled: 'Agendado',
};

const formatOrderTimingLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const normalized = raw.toLowerCase();
  return ORDER_TIMING_LABELS[normalized] || raw;
};

const formatPaymentStatusLabel = (paymentMethod = {}) => {
  if (paymentMethod.amendmentRevision && Number(paymentMethod.amountPaid) > 0 && Number(paymentMethod.amountDue || 0) === 0) return 'Recebido na loja';
  const type = String(paymentMethod?.type || '').trim().toLowerCase();
  if (type === 'offline') return 'Na Entrega';
  if (type === 'online') return 'Pago Online';
  return formatPaymentTimingLabel(paymentMethod?.type || '') || '-';
};

const getOptionalAmount = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') continue;
    const amount = parseAmount(value);
    if (Number.isFinite(amount)) return amount;
  }
  return null;
};

const getPaymentMethodAmount = (method = {}) =>
  parseAmount(method.value ?? method.amount ?? method.total ?? method.price ?? 0);

const isPrepaidMethod = (method = {}) => {
  const type = String(method.type || '').trim().toUpperCase();
  if (method.prepaid === true) return true;
  return type === 'ONLINE' || type === 'PREPAID';
};

const isPendingMethod = (method = {}) => {
  const type = String(method.type || '').trim().toUpperCase();
  if (method.prepaid === false) return true;
  return type === 'OFFLINE' || type === 'ON_DELIVERY' || type === 'PAY_ON_DELIVERY';
};

const resolvePaymentAmounts = (payments = {}, orderTotal = 0) => {
  const methods = Array.isArray(payments?.methods) ? payments.methods : [];
  const explicitPaid = getOptionalAmount(
    payments.prepaid,
    payments.totalPaid,
    payments.paid,
    payments.paidAmount,
    payments.amountPaid
  );
  const explicitPending = getOptionalAmount(
    payments.pending,
    payments.totalDue,
    payments.due,
    payments.pendingAmount,
    payments.amountDue
  );
  const methodPaid = methods
    .filter((method) => isPrepaidMethod(method) && !isPendingMethod(method))
    .reduce((sum, method) => sum + getPaymentMethodAmount(method), 0);
  const methodPending = methods
    .filter((method) => isPendingMethod(method))
    .reduce((sum, method) => sum + getPaymentMethodAmount(method), 0);

  const total = parseAmount(orderTotal);
  let paid = explicitPaid ?? methodPaid;
  let pending = explicitPending ?? methodPending;

  if (explicitPaid === null && explicitPending !== null && total > 0) {
    paid = Math.max(total - pending, 0);
  }
  if (explicitPending === null && total > 0) {
    pending = Math.max(total - paid, 0);
  }

  return {
    paid: Number.isFinite(paid) ? paid : 0,
    pending: Number.isFinite(pending) ? pending : 0,
  };
};

const normalizeComandaPaymentLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (['online', 'prepaid', 'paid_online'].includes(normalized)) return 'Online';
  if (['offline', 'on_delivery', 'pay_on_delivery'].includes(normalized)) return '';
  if (['cash', 'money', 'dinheiro'].includes(normalized)) return 'Dinheiro';
  if (['pix'].includes(normalized)) return 'Pix';
  if (
    normalized.includes('card') ||
    normalized.includes('credit') ||
    normalized.includes('debit') ||
    normalized.includes('cartao') ||
    normalized.includes('cartão')
  ) return 'Cartão';
  return formatPaymentMethodLabel(value);
};

const isLocalPixOnlinePending = (order, payments = order?.payments) => pixOnlineContract.isLocalPixSource(order?.source || order?.salesChannel || order?.originSystem)
  && [order?.source, order?.salesChannel, order?.originSystem].filter(Boolean).every(value => pixOnlineContract.isLocalPixSource(value) || ['BRUTOS', 'BRUTOS_DELIVERY'].includes(String(value).trim().toUpperCase()))
  && pixOnlineContract.isPixOnlineManualPending(payments, { allowLegacy: true, orderAmount: order?.total?.orderAmount ?? order?.fullOrderData?.total?.orderAmount ?? null });

const resolveComandaPaymentLabel = (payments = {}, order = {}) => {
  const methods = Array.isArray(payments?.methods) ? payments.methods : [];
  const collectionMethod = getAmendmentCollectionMethod(order);
  if (collectionMethod) return normalizeComandaPaymentLabel(collectionMethod);
  if (hasCommercialAmendment(order)) {
    const labels = methods.map((method) => normalizeComandaPaymentLabel(method.method)).filter(Boolean);
    if (labels.length) return [...new Set(labels)].join(' + ');
  }
  if (isLocalPixOnlinePending(order, payments)) return pixOnlineContract.PIX_ONLINE_CONFIRMATION_LABEL;
  const primaryMethod = methods[0] || {};

  if (isPrepaidMethod(primaryMethod) || String(payments?.paymentTiming || '').trim().toLowerCase() === 'prepaid') {
    return 'Online';
  }

  return (
    normalizeComandaPaymentLabel(primaryMethod.method) ||
    normalizeComandaPaymentLabel(primaryMethod.type) ||
    normalizeComandaPaymentLabel(payments.paymentMethod) ||
    normalizeComandaPaymentLabel(payments.method) ||
    ''
  );
};

const amendmentPaymentLines = (order = {}) => {
  if (!hasCommercialAmendment(order) || order.commercialConflict) return [];
  const methods = getCommercialPayments(order).methods || [];
  return methods.map((method) => ({
    method: method.method,
    label: formatPaymentMethodLabel(method.method || method.type || ''),
    amountPaid: parseAmount(method.amountPaid ?? (method.prepaid === true ? method.value ?? method.amount : 0)),
    amountDue: parseAmount(method.amountDue ?? (method.prepaid === false ? method.value ?? method.amount : 0)),
    pendingLabel: method.collectionResponsibility === 'merchant' ? 'Conferir na loja' : 'Cobrar na entrega',
  }));
};

const amendmentPaymentTexts = (order) => amendmentPaymentLines(order).flatMap((method) => [
  method.amountPaid > 0 ? `${method.label} recebido: ${formatCurrency(method.amountPaid)}` : '',
  method.amountDue > 0 ? `${method.label} - ${method.pendingLabel}: ${formatCurrency(method.amountDue)}` : '',
].filter(Boolean));

const getCustomerPhoneNumber = (value) => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value !== 'object') return '';
  return String(value.number || value.phoneNumber || value.value || value.fullNumber || '').trim();
};

const getCustomerPhoneLocalizer = (value) => {
  if (!value || typeof value !== 'object') return '';
  return String(value.localizer || value.localizador || value.code || '').trim();
};

const buildComandaPdfBase64 = ({ JsPdf, printerConfig, data }) => {
  if (typeof JsPdf !== 'function') throw new Error('Gerador de PDF indisponível.');
  const fontMap = {
    courier: 'courier',
    helvetica: 'helvetica',
    times: 'times',
  };
  const fontFamily = fontMap[printerConfig.comandaFontFamily] || fontMap.courier;
  const forceBold = !!printerConfig.comandaStrongPrint;
  const sizeBoost = forceBold ? 1 : 0;
  const {
    orderNumber,
    officialOrderId,
    createdAt,
    salesChannel,
    customerName,
    customerDocument,
    customerPhone,
    merchantName,
    localizer,
    address,
    deliveryMode,
    pickupCode,
    pickupCodeLabel = 'Retirada',
    deliveryTime,
    items,
    notes,
    total,
    totalUnknown,
    deliveryFee,
    discounts,
    discountIfood,
    discountMerchant,
    additionalFees,
    paymentMethod,
    paymentPrepaid,
    paymentPending,
    paymentToCollect,
    paymentMerchantPending,
    paymentMethods,
    amendmentRevision,
    amendmentReason,
    refundPendingAmount,
    totalRefunded,
  } = data;

  const width = Number(printerConfig.paperWidthMm) === 58 ? 58 : 80;
  // `noscale` keeps the receipt anchored at the top of continuous paper.
  // Four millimetres protect content from common thermal non-printable edges.
  const margin = width === 58 ? 5 : 4;
  const contentWidth = width - margin * 2;
  const entries = [];

  const addText = (text, options = {}) => {
    if (!text) return;
    entries.push({ type: 'text', text, ...options });
  };

  const addMoneyRow = (label, value, options = {}) => {
    if (!label) return;
    entries.push({
      type: 'money-row',
      left: label,
      right: formatMoney(value),
      ...options,
    });
  };

  const addDivider = () => entries.push({ type: 'space', height: 2 });

  if (printerConfig.comandaOrderNumberBanner && orderNumber) {
    const bannerSize = Math.round((printerConfig.comandaSizeOrderNumberBanner || 16) * 1.35);
    entries.push({
      type: 'banner',
      text: orderNumber,
      size: bannerSize,
      gapAfter: 4,
    });
  }

  const subtitleParts = [];
  if (printerConfig.comandaSubtitle) subtitleParts.push(printerConfig.comandaSubtitle);
  if (printerConfig.comandaShowCreatedAt && createdAt) subtitleParts.push(createdAt);
  addText(subtitleParts.join(' - '), { size: 10, align: 'center', gapAfter: 1 });

  addDivider();
  if (Number(amendmentRevision) > 0) {
    addText(`PEDIDO ALTERADO - Revisão ${amendmentRevision}`, { bold: true, align: 'center' });
    addText('Esta comanda substitui a versão anterior.', { size: 10, align: 'center' });
    if (amendmentReason) addText(`Alteração: ${amendmentReason}`, { size: 10 });
    addDivider();
  }

  if (printerConfig.comandaShowOrderNumber && orderNumber) {
    addText(`Pedido ${orderNumber}`, {
      size: (printerConfig.comandaSizeOrderNumber || 14) + sizeBoost,
      bold: forceBold || printerConfig.comandaBoldOrderNumber,
      gapAfter: 1,
    });
  }
  if (printerConfig.comandaShowOrderNumber && officialOrderId) {
    addText(`ID 99Food: ${officialOrderId}`, { size: 10, gapAfter: 1 });
  }

  if (printerConfig.comandaShowSalesChannel && salesChannel) addText(`Canal: ${salesChannel}`);
  if (printerConfig.comandaShowMerchant && merchantName) addText(`Restaurante: ${merchantName}`);
  if (printerConfig.comandaShowCustomer && customerName) {
    addText(`Cliente: ${customerName}`, { bold: forceBold || printerConfig.comandaBoldCustomer });
  }
  if (printerConfig.comandaShowCustomerDocument && customerDocument) addText(`CPF: ${customerDocument}`);
  if (printerConfig.comandaShowCustomerPhone && customerPhone) {
    addText(`Telefone: ${customerPhone}`, { bold: forceBold || printerConfig.comandaBoldPhone });
  }
  if (printerConfig.comandaShowContact && localizer) addText(`Localizador: ${localizer}`);
  if (printerConfig.comandaShowAddress && address) {
    addText(`Endereço: ${formatAddressForDisplay(address)}`, { bold: forceBold || printerConfig.comandaBoldAddress });
  }
  if (printerConfig.comandaShowDeliveryMode && deliveryMode) addText(`Tipo: ${deliveryMode}`);
  if (printerConfig.comandaShowPickupCode && pickupCode) addText(`${pickupCodeLabel}: ${pickupCode}`);
  if (printerConfig.comandaShowDeliveryTime && deliveryTime) addText(`Prazo: ${deliveryTime}`);

  if (printerConfig.comandaShowItems && items.length) {
    addDivider();
    addText('Itens', { bold: true, gapAfter: 1 });
    items.forEach((item) => {
      const qty = Number(item.quantity || 1);
      const name = item.name || '';
      const price = salesChannel === '99Food' ? option99Total(item) : item.totalPrice || parseAmount(item.unitPrice || 0) * qty;
      addMoneyRow(`${qty}x ${name}`, price, { size: (printerConfig.comandaSizeItem || 12) + sizeBoost });
      if (item.observations) addText(`Obs: ${item.observations}`, { size: 10 + sizeBoost });
      (salesChannel === '99Food' ? flatten99Options(item.options) : item.options || []).forEach((opt) => {
        const optQty = Number(opt.quantity || 1);
        const indent = salesChannel === '99Food' ? '  '.repeat((opt.depth || 0) + 1) : '  ';
        addMoneyRow(`${indent}+ ${optQty}x ${opt.name}`, salesChannel === '99Food' ? option99Total(opt) : opt.price || 0, {
          size: 10 + sizeBoost,
          bold: forceBold || printerConfig.comandaBoldOptions,
        });
        if (salesChannel === '99Food' && opt.observations) addText(`${indent}Obs: ${opt.observations}`, { size: 10 + sizeBoost });
      });
    });
  }

  if (printerConfig.comandaShowNotes && notes) {
    addDivider();
    addText('Observacoes', { bold: true, gapAfter: 1 });
    addText(notes, { size: 10 });
  }
  if (salesChannel === '99Food' && printerConfig.comandaShowNotes) (data.ninetyNineFoodInstructions || []).forEach(line => addText(line, { size: 10 }));
  if (salesChannel === '99Food' && printerConfig.comandaShowTotals) (data.ninetyNineFoodPrices || []).forEach(line => addText(line, { size: 10 }));
  if (salesChannel === '99Food' && printerConfig.comandaShowPayment) (data.ninetyNineFoodChange || []).forEach(line => addText(line, { bold: true }));

  if (printerConfig.comandaShowTotals) {
    addDivider();
    if (deliveryFee) addMoneyRow('Entrega:', deliveryFee);
    if (printerConfig.comandaShowDiscounts && discounts) addMoneyRow('Descontos:', discounts);
    if (printerConfig.comandaShowDiscounts && discountIfood) addMoneyRow('Subsidiado iFood:', discountIfood);
    if (printerConfig.comandaShowDiscounts && discountMerchant) addMoneyRow('Subsidiado loja:', discountMerchant);
    if (printerConfig.comandaShowAdditionalFees && additionalFees) addMoneyRow('Taxas adicionais:', additionalFees);
    if (totalUnknown) addText(NINETY_NINE_FOOD_TOTAL_UNKNOWN, { bold: true });
    else addMoneyRow('Total:', total, {
      size: (printerConfig.comandaSizeTotal || 14) + sizeBoost,
      bold: forceBold || printerConfig.comandaBoldTotal,
    });
  }

  if (printerConfig.comandaShowPayment) {
    addDivider();
    if (paymentMethod) addText(`Pagamento: ${paymentMethod}`);
    (paymentMethods || []).forEach((method) => {
      if (method.amountPaid > 0) addMoneyRow(`${method.label} recebido:`, method.amountPaid);
      if (method.amountDue > 0) addMoneyRow(`${method.label} - ${method.pendingLabel}:`, method.amountDue);
    });
    if (totalUnknown) addText('Valores de pagamento não informados pela 99Food.');
    else { addMoneyRow('Pago:', paymentPrepaid); addMoneyRow('Pendente:', paymentPending); }
    if (Number(amendmentRevision) > 0 && Number.isFinite(paymentToCollect)) addMoneyRow('Cobrar na entrega:', paymentToCollect);
    if (Number(paymentMerchantPending) > 0) addMoneyRow('Conferir na loja:', paymentMerchantPending);
    if (Number(refundPendingAmount) > 0) addMoneyRow('A devolver ao cliente:', refundPendingAmount);
    if (Number(totalRefunded) > 0) addMoneyRow('Devolvido:', totalRefunded);
  }

  const tempDoc = new JsPdf({ unit: 'mm', format: [width, 200] });
  const bottomPadding = 6;
  const lineHeight = (size) => (Number(size || 12) * 0.3527) * 1.2;
  let estimatedHeight = margin;

  entries.forEach((entry) => {
    if (entry.type === 'space') {
      estimatedHeight += entry.height || 2;
      return;
    }
    const size = entry.size || 12;
    const lh = lineHeight(size);
    if (entry.type === 'banner') {
      estimatedHeight += lh + 2;
      return;
    }
    if (entry.type === 'money-row') {
      tempDoc.setFont(fontFamily, (entry.bold || forceBold) ? 'bold' : 'normal');
      tempDoc.setFontSize(size);
      const rightWidth = Math.min(contentWidth * 0.45, tempDoc.getTextWidth(String(entry.right)) + 1);
      const leftWidth = Math.max(18, contentWidth - rightWidth - 2);
      const wrapped = tempDoc.splitTextToSize(String(entry.left), leftWidth);
      estimatedHeight += Math.max(1, wrapped.length) * lh + (entry.gapAfter || 0);
      return;
    }
    const wrapped = tempDoc.splitTextToSize(String(entry.text), contentWidth);
    estimatedHeight += wrapped.length * lh + (entry.gapAfter || 0);
  });

  estimatedHeight = Math.max(estimatedHeight + margin + bottomPadding, 60);
  const doc = new JsPdf({
    orientation: estimatedHeight >= width ? 'portrait' : 'landscape',
    unit: 'mm',
    format: [width, estimatedHeight],
  });
  let y = margin;

  const renderText = (entry) => {
    const size = entry.size || 12;
    const lh = lineHeight(size);
    doc.setFont(fontFamily, (entry.bold || forceBold) ? 'bold' : 'normal');
    doc.setFontSize(size);
    const wrapped = doc.splitTextToSize(String(entry.text), contentWidth);
    wrapped.forEach((line) => {
      const x = entry.align === 'center' ? width / 2 : margin;
      doc.text(line, x, y, { align: entry.align || 'left' });
      y += lh;
    });
    y += entry.gapAfter || 0;
  };

  const renderMoneyRow = (entry) => {
    const size = entry.size || 12;
    const lh = lineHeight(size);
    doc.setFont(fontFamily, (entry.bold || forceBold) ? 'bold' : 'normal');
    doc.setFontSize(size);
    const right = String(entry.right || '');
    const rightWidth = Math.min(contentWidth * 0.45, doc.getTextWidth(right) + 1);
    const leftWidth = Math.max(18, contentWidth - rightWidth - 2);
    const wrapped = doc.splitTextToSize(String(entry.left || ''), leftWidth);
    wrapped.forEach((line, index) => {
      doc.text(line, margin, y);
      if (index === 0 && right) {
        doc.text(right, width - margin, y, { align: 'right' });
      }
      y += lh;
    });
    y += entry.gapAfter || 0;
  };

  entries.forEach((entry) => {
    if (entry.type === 'space') {
      y += entry.height || 2;
      return;
    }
    if (entry.type === 'banner') {
      const size = entry.size || 12;
      const lh = lineHeight(size);
      const bannerHeight = lh * 1.4;
      doc.setFillColor(0);
      doc.rect(margin, y, contentWidth, bannerHeight, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(size);
      doc.text(String(entry.text), width / 2, y + bannerHeight / 2 + lh / 3, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y += bannerHeight + (entry.gapAfter || 0);
      return;
    }
    if (entry.type === 'money-row') {
      renderMoneyRow(entry);
      return;
    }
    renderText(entry);
  });

  const dataUri = doc.output('datauristring');
  return {
    pdfBase64: dataUri.split(',')[1] || '',
    pdfPaper: {
      widthMm: width,
      heightMm: Number(estimatedHeight.toFixed(2)),
    },
  };
};

export const loadAutomaticPrinterSettings = (session) => {
  try {
    const saved = readTenantLocalJson(PRINTER_SETTINGS_KEY, {}, session);
    const connection = resolveCibdStationConnection({ baseUrl: SYSTEM_PRINT_BASE_URL, session, tenantSettings: saved });
    return applyCibdStationBindings({
      ...DEFAULT_COMANDA_SETTINGS,
      ...saved,
      printerId: saved.printerId || saved.printerName || DEFAULT_COMANDA_SETTINGS.printerId,
      cibdToken: connection ? connection.token : saved.cibdToken || saved.bridgeToken || DEFAULT_COMANDA_SETTINGS.cibdToken,
      cibdBaseUrl: connection?.baseUrl || SYSTEM_PRINT_BASE_URL,
      cibdConnection: connection,
      printPoints: normalizePrintPoints(saved),
    }, connection);
  } catch {
    return { ...DEFAULT_COMANDA_SETTINGS };
  }
};

const printTelemetryQueues = new Map();
const recordAutomaticIfoodPrintEvent = (order, payload = {}, session = getTenantSettingsSession()) => {
  if (!order?.orderId || getOrderSourceKey(order) !== 'ifood' || !session?.token
    || !getTenantStorageKey(PRINTER_SETTINGS_KEY, session)) return;
  const tenant = String(session.user?.companyId || '');
  const key = `${tenant}:${order.orderId}:${payload.printPointId || 'all'}`;
  const occurredAt = new Date().toISOString();
  const previous = printTelemetryQueues.get(key) || Promise.resolve();
  const next = previous.catch(() => {}).then(async () => {
    if (!getTenantStorageKey(PRINTER_SETTINGS_KEY, session)) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), AUTO_PRINT_TELEMETRY_TIMEOUT_MS);
    let deadlineTimer;
    try {
      await Promise.race([
        apiFetch(`${API_URL}/api/ifood/orders/${encodeURIComponent(order.orderId)}/print-events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` },
          body: JSON.stringify({ ...payload, at: occurredAt }),
          signal: controller.signal,
        }),
        new Promise((resolve) => { deadlineTimer = window.setTimeout(resolve, AUTO_PRINT_TELEMETRY_TIMEOUT_MS); }),
      ]);
    } catch {
      // A telemetria é observacional; o resultado do CIBD é guardado localmente.
    } finally {
      window.clearTimeout(timer);
      window.clearTimeout(deadlineTimer);
    }
  });
  printTelemetryQueues.set(key, next);
  next.finally(() => { if (printTelemetryQueues.get(key) === next) printTelemetryQueues.delete(key); });
};

const isAutoPrintableOrder = (order = {}) => {
  const orderId = String(order.orderId || '');
  if (!orderId || orderId.startsWith('TEST-')) return false;
  const sourceKey = getOrderSourceKey(order);
  const status = normalizeOrderStatusKey(order.status);
  if (sourceKey === '99food') return status === 'preparing' && order.requiresAcceptance !== true
    && Number(order.officialStatus) >= 200 && Number(order.officialStatus) < 900;
  if (LOCAL_AUTO_PRINT_SOURCE_KEYS.has(sourceKey)) return status === 'preparing';
  const hasConfirmed = order.history?.some(isOrderConfirmedEvent);
  const hasPaid = Number(order.payments?.prepaid || 0) > 0;
  return status === 'preparing' && (hasConfirmed || hasPaid);
};

const fetchPrintCatalog = async ({ signal } = {}) => {
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', abortFromCaller, { once: true });
  const timeoutId = window.setTimeout(() => controller.abort(), PRINT_CATALOG_TIMEOUT_MS);
  try {
    return await fetchOrderDisplayCatalog(API_URL, { signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromCaller);
  }
};

export const printAutomaticComanda = async (order, options = {}) => {
  const printTrigger = 'auto';
  const printAttempt = options.attempt || null;
  const session = options.session || getTenantSettingsSession();
  const sourceKey = getOrderSourceKey(order);
  const isIfood = sourceKey === 'ifood';
  if (isIfood && (!options.catalogVerified || !options.catalog)) {
    return {
      ok: false,
      catalogUnavailable: true,
      error: 'Não foi possível validar o cardápio local. A comanda não foi enviada. Tente novamente.',
    };
  }
  const itemsToPrint = isIfood
    ? projectIfoodPrintItems(order, options.catalog)
    : (order.items || []);
  let connection = null;
  const canContinue = () => Boolean(getTenantStorageKey(PRINTER_SETTINGS_KEY, session))
    && (!connection || isCibdStationConnectionCurrent(connection, { baseUrl: SYSTEM_PRINT_BASE_URL, session }))
    && (options.canContinue ? options.canContinue() : true);
  if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
  const printerConfig = loadAutomaticPrinterSettings(session);
  connection = printerConfig.cibdConnection;
  if (!canContinue()) return { ok: false, cancelled: true, error: 'Conexão do CIBD alterada antes da impressão.' };
  const availablePoints = getActivePrintPoints(printerConfig);
  const printPoints = availablePoints.filter((point) => options.printPointIds?.includes(point.id));
  if (!printPoints.length) {
    const error = 'Nenhum ponto de impressão ativado possui impressora selecionada.';
    if (isIfood && canContinue()) recordAutomaticIfoodPrintEvent(order, { status: 'failed', trigger: printTrigger, attempt: printAttempt, printerId: '', bridge: SYSTEM_PRINT_BASE_URL, error }, session);
    return { ok: false, error };
  }

  const totalInfo = getCommercialTotal(order);
  const payments = getCommercialPayments(order);
  const customer = order.customer || {};
  const fullCustomer = order.fullOrderData?.customer || {};
  const orderTotal = parseAmount(totalInfo.orderAmount || totalInfo.total || 0);
  const paymentAmounts = resolvePaymentAmounts(payments, orderTotal);
  const hasConfirmed = order.history?.some(isOrderConfirmedEvent);
  if (isIfood && normalizeOrderStatusKey(order.status) === 'preparing' && !hasConfirmed && !paymentAmounts.paid) {
    return { ok: false, error: 'Pedido ainda não confirmado ou pago para impressão.' };
  }

  const customerName = typeof customer === 'object'
    ? customer.name || fullCustomer.name || 'Cliente'
    : String(customer || fullCustomer.name || 'Cliente');
  const customerPhone = [
    ...(sourceKey === '99food' ? [customer.providerVirtualPhone] : []),
    getCustomerPhoneNumber(customer.phone), customer.phoneNumber, customer.whatsapp,
    getCustomerPhoneNumber(fullCustomer.phone), fullCustomer.phoneNumber, fullCustomer.whatsapp,
  ].map((value) => String(value || '').trim()).find(Boolean) || '';
  const address = sourceKey === '99food' ? address99(order) : customer.address || customer.addressInfo || customer.deliveryAddress
    || fullCustomer.address || fullCustomer.addressInfo || fullCustomer.deliveryAddress
    || order.delivery?.deliveryAddress || order.fullOrderData?.delivery?.deliveryAddress || order.deliveryAddress || '';
  const sourceLabels = { balcao: 'Balcão', salao: 'Salão', whatsapp: 'WhatsApp', site: 'Site', pedidos10: 'Pedidos10' };
  const salesChannel = sourceKey === '99food' ? '99Food' : sourceLabels[sourceKey] || order.salesChannel || 'iFood';

  let cibdStatus = null;
  try {
    if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
    cibdStatus = await checkCibdStatus({ baseUrl: printerConfig.cibdBaseUrl });
  } catch (preflightError) {
    const error = preflightError?.message || 'Não foi possível acessar o CIBD. Verifique se o CIBD está aberto e permita o acesso à rede/dispositivos locais no navegador.';
    const pointResults = printPoints.map((point) => ({ ok: false, pointId: point.id, pointName: point.name, printerId: point.printerId, error }));
    if (isIfood && canContinue()) pointResults.forEach((result) => recordAutomaticIfoodPrintEvent(order, {
      status: 'failed', trigger: printTrigger, attempt: printAttempt, printerId: result.printerId,
      printPointId: result.pointId, printPointName: result.pointName, bridge: SYSTEM_PRINT_BASE_URL, error,
    }, session));
    return { ok: false, error, pointResults };
  }

  try {
    if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
    const JsPdf = await loadJsPdf();
    if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
    const couponBreakdown = extractCouponBreakdown(order);
    const pdfDocument = buildComandaPdfBase64({
      JsPdf,
      printerConfig: { ...printerConfig, paperWidthMm: cibdStatus?.paperWidthMm },
      data: {
        orderNumber: String(order.displayId || order.orderId || ''),
        officialOrderId: getNinetyNineFoodOfficialId(order),
        createdAt: order.createdAt ? new Date(order.createdAt).toLocaleString('pt-BR') : new Date().toLocaleString('pt-BR'),
        salesChannel,
        customerName,
        customerDocument: customer.documentNumber || customer.cpf || fullCustomer.documentNumber || fullCustomer.cpf || '',
        customerPhone,
        merchantName: order.merchant?.name || '',
        localizer: (sourceKey === '99food' ? customer.providerLocator : '') || getCustomerPhoneLocalizer(customer.phone) || getCustomerPhoneLocalizer(fullCustomer.phone),
        address,
        deliveryMode: order.fulfillment === 'delivery' ? 'Entrega' : order.fulfillment === 'pickup' ? 'Retirada' : order.delivery?.mode || '',
        pickupCode: sourceKey === '99food' ? getNinetyNineFoodPickupInfo(order).code : order.delivery?.pickupCode || '',
        ...(sourceKey === '99food' ? { pickupCodeLabel: getNinetyNineFoodPickupInfo(order).label } : {}),
        deliveryTime: order.delivery?.deliveryDateTime ? new Date(order.delivery.deliveryDateTime).toLocaleString('pt-BR') : '',
        items: itemsToPrint, notes: printerConfig.comandaShowNotes ? getComandaOrderNotes(order) : '',
        ninetyNineFoodInstructions: operational99Rows(order, formatCurrency, ['instructions']),
        ninetyNineFoodPrices: operational99Rows(order, formatCurrency, ['price']),
        ninetyNineFoodChange: operational99Rows(order, formatCurrency, ['payment']),
        total: orderTotal, totalUnknown: isNinetyNineFoodTotalUnknown(order), deliveryFee: parseAmount(totalInfo.deliveryFee || 0), discounts: couponBreakdown.totalAmount,
        discountIfood: couponBreakdown.ifoodAmount, discountMerchant: couponBreakdown.merchantAmount,
        additionalFees: parseAmount(totalInfo.additionalFees || 0), paymentMethod: resolveComandaPaymentLabel(payments, order),
        paymentPrepaid: paymentAmounts.paid, paymentPending: paymentAmounts.pending,
        paymentMethods: amendmentPaymentLines(order),
        paymentToCollect: hasCommercialAmendment(order) ? Number(payments.deliveryAmountDue || 0) : undefined,
        paymentMerchantPending: hasCommercialAmendment(order) ? Math.max(0, paymentAmounts.pending - Number(payments.deliveryAmountDue || 0)) : undefined,
        amendmentRevision: order.amendmentRevision, amendmentReason: order.amendment?.reason,
        refundPendingAmount: payments.refundPendingAmount, totalRefunded: payments.totalRefunded,
      },
    });
    if (!pdfDocument?.pdfBase64) return { ok: false, error: 'PDF vazio.' };
    const { pdfBase64, pdfPaper } = pdfDocument;
    const orderFallback = isNinetyNineFoodTotalUnknown(order) ? undefined : {
      id: String(order.displayId || order.orderId || ''), salesChannel, createdAt: Number(order.createdAt || Date.now()),
      customer: { name: customerName, phone: customerPhone, ...(formatAddressForDisplay(address) ? { address: formatAddressForDisplay(address) } : {}) },
      delivery: { fee: parseAmount(totalInfo.deliveryFee || 0), pickupCode: sourceKey === '99food' ? getNinetyNineFoodPickupInfo(order).code : order.delivery?.pickupCode || '' },
      items: sourceKey === '99food' ? fallback99Items(itemsToPrint) : itemsToPrint.map((item) => ({ name: item.name || '', quantity: Number(item.quantity || 1), unitPrice: parseAmount(item.unitPrice || 0), totalPrice: parseAmount(item.totalPrice || item.unitPrice || 0), observations: item.observations || '', options: (item.options || []).map((option) => ({ name: option.name || '', quantity: Number(option.quantity || 1), price: parseAmount(option.price || 0) })) })),
      observations: [Number(order.amendmentRevision) > 0 ? `PEDIDO ALTERADO - Revisão ${order.amendmentRevision}. Esta comanda substitui a versão anterior. ${order.amendment?.reason || ''}` : '',
        sourceKey === '99food' && printerConfig.comandaShowOrderNumber && getNinetyNineFoodOfficialId(order) ? `ID 99Food: ${getNinetyNineFoodOfficialId(order)}` : '',
        sourceKey === '99food' ? (printerConfig.comandaShowNotes ? getOrderGeneralNotes(order) : '') : order.observations || '',
        ...(printerConfig.comandaShowPayment ? amendmentPaymentTexts(order) : [])].filter(Boolean).join('\n'), total: orderTotal,
      amendmentRevision: order.amendmentRevision,
      payment: { method: resolveComandaPaymentLabel(payments, order), prepaid: paymentAmounts.paid, pending: paymentAmounts.pending,
        methods: hasCommercialAmendment(order) ? amendmentPaymentLines(order) : undefined,
        amountToCollect: payments.deliveryAmountDue, merchantPending: hasCommercialAmendment(order) ? Math.max(0, paymentAmounts.pending - Number(payments.deliveryAmountDue || 0)) : undefined,
        refundPendingAmount: payments.refundPendingAmount, totalRefunded: payments.totalRefunded },
    };
    if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
    if (isIfood) printPoints.forEach((point) => recordAutomaticIfoodPrintEvent(order, {
      status: 'queued', trigger: printTrigger, attempt: printAttempt, printerId: point.printerId,
      printPointId: point.id, printPointName: point.name, bridge: SYSTEM_PRINT_BASE_URL,
    }, session));
    const settled = await Promise.allSettled(printPoints.map(async (point) => {
      if (!canContinue()) throw new Error('Sessão alterada antes da impressão.');
      await options.onPointDispatchStart?.(point);
      let result;
      try {
        await sendCibdPrint({
          baseUrl: printerConfig.cibdBaseUrl,
          headers: buildCibdHeaders(printerConfig),
          body: JSON.stringify({ printerId: point.printerId, pdfBase64, pdfPaper, order: orderFallback,
            ...(sourceKey === '99food' ? { forceExternalPdf: true } : {}) }),
          fetchImpl: (...args) => {
            if (!canContinue()) throw new Error('Sessão alterada antes da impressão.');
            return fetch(...args);
          },
        });
        result = { ok: true, dispatchStarted: true, pointId: point.id, pointName: point.name, printerId: point.printerId };
      } catch (error) {
        const rejectedBeforeQueue = error?.kind === 'http' && Number(error.status) >= 400
          && Number(error.status) < 500 && ![408, 429].includes(Number(error.status));
        result = { ok: false, dispatchStarted: !rejectedBeforeQueue, pointId: point.id, pointName: point.name, printerId: point.printerId,
          error: error?.message || 'Falha ao enviar para a impressora.' };
      }
      try { await options.onPointResult?.(result); } catch { result.ackFailed = true; }
      return result;
    }));
    const pointResults = settled.map((result, index) => result.status === 'fulfilled' ? result.value : ({
      ok: false, pointId: printPoints[index].id, pointName: printPoints[index].name, printerId: printPoints[index].printerId,
      error: result.reason?.message || 'Falha ao enviar para a impressora.',
    }));
    if (isIfood && canContinue()) pointResults.forEach((result) => recordAutomaticIfoodPrintEvent(order, {
      status: result.ok ? 'sent' : 'failed', trigger: printTrigger, attempt: printAttempt, printerId: result.printerId,
      printPointId: result.pointId, printPointName: result.pointName, bridge: SYSTEM_PRINT_BASE_URL,
      ...(result.ok ? {} : { error: result.error }),
    }, session));
    const failures = pointResults.filter((result) => !result.ok);
    return failures.length ? { ok: false, error: failures.map((result) => `${result.pointName}: ${result.error}`).join(' | '), pointResults } : { ok: true, pointResults };
  } catch (err) {
    const error = err?.message || 'Não foi possível imprimir a comanda. Verifique o CIBD e tente novamente.';
    if (canContinue()) recordAutomaticIfoodPrintEvent(order, { status: 'failed', trigger: printTrigger, attempt: printAttempt, bridge: SYSTEM_PRINT_BASE_URL, error }, session);
    return { ok: false, cancelled: !canContinue(), error };
  }
};

const claimAutoPrintOrderExclusively = async (order, points, tenantKey) => {
  if (navigator?.locks?.request) {
    return navigator.locks.request(`brutos-auto-print:${tenantKey}`, () => claimAutoPrintOrder(order, points, tenantKey));
  }
  return claimAutoPrintOrder(order, points, tenantKey);
};

export const reserveManualPrintExclusively = async (order, tenantKey, startedAt, reviewConfirmed = false) => {
  const reserve = () => {
    const current = getAutoPrintRegistryEntry(order, tenantKey);
    const recent = Date.now() - Number(current?.updatedAt || 0) < AUTO_PRINT_STALE_QUEUE_MS;
    const needsReview = current?.status === 'needs-review' || current?.status === 'unknown'
      || (current?.status === 'queued' && current.phase === 'dispatching')
      || Object.values(current?.pointStates || {}).some((point) => point?.status === 'unknown');
    if ((current?.status === 'queued' && current.phase === 'dispatching' && recent)
      || (current?.status === 'unknown' && current.phase === 'manual-in-progress' && recent)
      || (current?.status === 'sent' && Number(current.updatedAt || 0) >= startedAt)
      || (needsReview && !reviewConfirmed)) return null;
    const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const saved = markAutoPrintOrder(order, 'unknown', {
      token,
      phase: 'manual-in-progress',
      pointStates: current?.pointStates || {},
      lastError: '',
    }, tenantKey);
    return saved?.token === token ? token : null;
  };
  if (navigator?.locks?.request) {
    return navigator.locks.request(`brutos-auto-print:${tenantKey}`, reserve);
  }
  return reserve();
};

export const AutoPrintRuntime = ({ tenantKey = '', sessionToken = '' }) => {
  const session = useMemo(() => ({ token: sessionToken, user: { companyId: tenantKey } }), [sessionToken, tenantKey]);
  const [enabled, setEnabled] = useState(() => readTenantLocalJson(AUTO_PRINT_KEY, false, session) === true);
  const [orders, setOrders] = useState([]);
  const [localCreatedOrders, setLocalCreatedOrders] = useState([]);
  const [printCatalogState, setPrintCatalogState] = useState({ status: 'pending', catalog: null });
  const ordersSessionRef = useRef(null);
  const printedRef = useRef(new Set());
  const sessionValidRef = useRef(true);

  useEffect(() => {
    const refreshSetting = (event) => {
      const key = getTenantStorageKey(AUTO_PRINT_KEY, session);
      if (event?.type === 'storage' && key && event.key && event.key !== key
        && event.key !== 'brutos-auth-session') return;
      setEnabled(readTenantLocalJson(AUTO_PRINT_KEY, false, session) === true);
    };
    refreshSetting();
    window.addEventListener('storage', refreshSetting);
    window.addEventListener(AUTO_PRINT_SETTINGS_CHANGED_EVENT, refreshSetting);
    return () => {
      window.removeEventListener('storage', refreshSetting);
      window.removeEventListener(AUTO_PRINT_SETTINGS_CHANGED_EVENT, refreshSetting);
    };
  }, [session]);

  useEffect(() => {
    const key = getTenantStorageKey(AUTO_PRINT_ACTIVE_SINCE_KEY, session);
    if (!key) return;
    if (enabled) {
      if (!Number(localStorage.getItem(key))) localStorage.setItem(key, String(Date.now()));
    } else {
      localStorage.removeItem(key);
    }
  }, [enabled, session]);

  useEffect(() => {
    ordersSessionRef.current = null;
    printedRef.current.clear();
    setOrders([]);
    setLocalCreatedOrders([]);
    setPrintCatalogState({ status: 'pending', catalog: null });
    sessionValidRef.current = true;
  }, [tenantKey, sessionToken]);

  useEffect(() => {
    const invalidatePendingWork = () => {
      sessionValidRef.current = false;
    };
    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidatePendingWork);
    return () => window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, invalidatePendingWork);
  }, []);

  useEffect(() => {
    if (!enabled || !getTenantStorageKey(AUTO_PRINT_KEY, session)) return undefined;
    const receiveCreatedOrder = (event) => {
      const order = event?.detail?.order;
      if (!order?.orderId || !LOCAL_AUTO_PRINT_SOURCE_KEYS.has(getOrderSourceKey(order))) return;
      if (!sessionValidRef.current || !getTenantStorageKey(AUTO_PRINT_KEY, session)) return;
      const activeSession = getPageAuthSession();
      if (
        String(event?.detail?.tenantKey || '') !== String(tenantKey)
        || String(order.companyId || '') !== String(tenantKey)
        || String(activeSession?.token || '') !== String(sessionToken)
        || event?.detail?.sessionMarker !== buildLocalOrderSessionMarker(activeSession)
        || !isStoredSessionCurrent(activeSession)
      ) return;
      setLocalCreatedOrders((current) => [
        order,
        ...(current || []).filter((candidate) => (
          getOrderSourceKey(candidate) !== getOrderSourceKey(order)
          || String(candidate.orderId || '') !== String(order.orderId)
        )),
      ]);
    };
    window.addEventListener(LOCAL_ORDER_CREATED_EVENT, receiveCreatedOrder);
    return () => window.removeEventListener(LOCAL_ORDER_CREATED_EVENT, receiveCreatedOrder);
  }, [enabled, session, sessionToken, tenantKey]);

  useEffect(() => {
    if (!enabled || !getTenantStorageKey(AUTO_PRINT_KEY, session)) return undefined;
    let active = true;
    let retryTimer = null;
    let controller = null;

    const loadCatalog = async () => {
      controller?.abort();
      controller = new AbortController();
      setPrintCatalogState({ status: 'pending', catalog: null });
      try {
        const catalog = await fetchPrintCatalog({ signal: controller.signal });
        if (!active || !sessionValidRef.current || !getTenantStorageKey(AUTO_PRINT_KEY, session)) return;
        setPrintCatalogState({ status: 'ready', catalog });
      } catch (error) {
        if (!active || error?.name === 'AbortError') return;
        setPrintCatalogState({ status: 'failed', catalog: null });
      } finally {
        if (active) retryTimer = window.setTimeout(loadCatalog, PRINT_CATALOG_REFRESH_INTERVAL_MS);
      }
    };

    loadCatalog();
    return () => {
      active = false;
      controller?.abort();
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [enabled, sessionToken, tenantKey, session]);

  useEffect(() => {
    if (!enabled || !getTenantStorageKey(AUTO_PRINT_KEY, session)) return undefined;
    let active = true;
    const endpoints = [
      ['ifood', `${API_URL}/api/ifood/orders`, IFOOD_LOCAL_REFRESH_INTERVAL_MS],
      ['site', `${API_URL}/api/webhooks/site/orders`, AUTO_PRINT_ALL_CHANNELS_REFRESH_INTERVAL_MS],
      ['balcao', `${API_URL}/api/balcao/orders`, AUTO_PRINT_ALL_CHANNELS_REFRESH_INTERVAL_MS],
      ['pedidos10', `${API_URL}/api/integrations/pedidos10/orders`, AUTO_PRINT_ALL_CHANNELS_REFRESH_INTERVAL_MS],
      ['salao', `${API_URL}/api/salon/orders`, AUTO_PRINT_ALL_CHANNELS_REFRESH_INTERVAL_MS],
    ];
    const snapshots = new Map();
    const inFlight = new Set();
    const controllers = new Map();
    const publish = () => {
      if (!active || !sessionValidRef.current || !getTenantStorageKey(AUTO_PRINT_KEY, session)) return;
      ordersSessionRef.current = session;
      setOrders([...snapshots.entries()].flatMap(([, snapshot]) => snapshot.orders.map((order) => ({
        ...order,
        __autoPrintSnapshotFresh: snapshot.fresh,
      }))));
    };
    const loadEndpoint = async (key, url) => {
      if (inFlight.has(key) || !active || !sessionValidRef.current || !getTenantStorageKey(AUTO_PRINT_KEY, session)) return;
      inFlight.add(key);
      const controller = new AbortController();
      controllers.set(key, controller);
      let timeoutId;
      try {
        const loaded = await Promise.race([
          (async () => {
          const response = await apiFetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error(`Falha ao consultar ${url}`);
          const payload = await response.json().catch(() => ({}));
          return Array.isArray(payload.orders) ? payload.orders : [];
          })(),
          new Promise((resolve, reject) => {
            timeoutId = window.setTimeout(() => {
              controller.abort();
              reject(new Error('Consulta de pedidos excedeu o prazo.'));
            }, AUTO_PRINT_ORDERS_TIMEOUT_MS);
          }),
        ]);
        if (!active || !sessionValidRef.current) return;
        snapshots.set(key, { orders: loaded, fresh: true });
        publish();
      } catch {
        if (!active || !sessionValidRef.current) return;
        const previous = snapshots.get(key);
        if (previous) snapshots.set(key, { ...previous, fresh: false });
        publish();
      } finally {
        window.clearTimeout(timeoutId);
        if (controllers.get(key) === controller) controllers.delete(key);
        inFlight.delete(key);
      }
    };
    endpoints.forEach(([key, url]) => loadEndpoint(key, url));
    // Same startup sync used by /pedidos; subsequent reads remain local snapshots.
    const syncController = new AbortController();
    apiFetch(`${API_URL}/api/ifood/orders?sync=1`, { signal: syncController.signal }).catch(() => {});
    const timers = endpoints.map(([key, url, interval]) => window.setInterval(() => loadEndpoint(key, url), interval));
    return () => {
      active = false;
      syncController.abort();
      controllers.forEach((controller) => controller.abort());
      timers.forEach((timer) => window.clearInterval(timer));
    };
  }, [enabled, sessionToken, tenantKey, session]);

  useEffect(() => {
    const canContinue = () => (
      sessionValidRef.current
      && Boolean(tenantKey)
      && Boolean(getTenantStorageKey(AUTO_PRINT_KEY, session))
      && readTenantLocalJson(AUTO_PRINT_KEY, false, session) === true
    );
    if (!enabled || !canContinue()) return;
    const filterCandidates = (collection, fetched = false) => (collection || []).filter((order) => {
      const key = getAutoPrintOrderKey(order, tenantKey);
      return key && !printedRef.current.has(key) && isAutoPrintableOrder(order)
        && (!fetched || order.__autoPrintSnapshotFresh === true);
    });
    const points = getActivePrintPoints(loadAutomaticPrinterSettings(session));
    if (ordersSessionRef.current === session) {
      const activeSince = Number(localStorage.getItem(getTenantStorageKey(AUTO_PRINT_ACTIVE_SINCE_KEY, session)));
      const now = Date.now();
      orders.filter((order) => order.__autoPrintSnapshotFresh === true).forEach((order) => {
        const existing = reconcileAutoPrintEntry(order, tenantKey);
        if (existing && !isAutoPrintableOrder(order)
          && (existing.status === 'failed' || (existing.status === 'queued' && existing.phase === 'pre-send'))) {
          markAutoPrintOrder(order, 'needs-review', {
            phase: 'manual-review',
            lastError: 'Pedido mudou de status antes da retomada; confira a impressão.',
          }, tenantKey);
          return;
        }
        const createdAt = Date.parse(String(order.createdAt || ''));
        if (!Number.isFinite(createdAt) || createdAt < Math.max(activeSince || now, now - AUTO_PRINT_REGISTRY_TTL_MS)
          || now - createdAt <= AUTO_PRINT_STARTUP_RECOVERY_MS
          || existing) return;
        const remoteSent = String(order.additionalInfo?.printing?.lastStatus || '').toLowerCase() === 'sent';
        const historyHasSent = (order.additionalInfo?.printing?.history || [])
          .some((event) => event.status === 'sent');
        if (remoteSent || historyHasSent) {
          const { expected, sentPointIds, complete } = getRemotePrintCompletion(order, points);
          if (complete) return;
          rememberAutoPrintOrder(order, 'needs-review', {
            pointStates: Object.fromEntries(expected.map((point) => [point.id, {
              pointId: point.id, pointName: point.name,
              status: sentPointIds.has(point.id) ? 'sent' : 'unknown',
            }])),
            lastError: 'Telemetria parcial; confira os pontos antes de reenviar.',
          }, tenantKey);
          return;
        }
        rememberAutoPrintOrder(order, 'needs-review', {
          lastError: 'Pedido sem confirmação após interrupção da autoimpressão; confira o papel.',
        }, tenantKey);
      });
    }
    const immediateCandidates = filterCandidates(localCreatedOrders);
    let fetchedCandidates = ordersSessionRef.current === session ? filterCandidates(orders, true) : [];
    if (ordersSessionRef.current === session) {
      const now = Date.now();
      fetchedCandidates = fetchedCandidates.filter((order) => {
        const createdAt = new Date(order.createdAt || 0).getTime();
        const wasSent = String(order.additionalInfo?.printing?.lastStatus || '').toLowerCase() === 'sent'
          || (order.additionalInfo?.printing?.history || []).some((event) => event.status === 'sent');
        const entry = reconcileAutoPrintEntry(order, tenantKey);
        if (wasSent && !entry) {
          const { expected, sentPointIds, complete } = getRemotePrintCompletion(order, points);
          if (!complete) {
            rememberAutoPrintOrder(order, 'needs-review', {
              pointStates: Object.fromEntries(expected.map((point) => [point.id, {
                pointId: point.id, pointName: point.name,
                status: sentPointIds.has(point.id) ? 'sent' : 'unknown',
              }])),
              lastError: 'Telemetria parcial; confira os pontos antes de reenviar.',
            }, tenantKey);
          } else {
            rememberAutoPrintOrder(order, 'seen-on-enable', {}, tenantKey);
          }
          printedRef.current.add(getAutoPrintOrderKey(order, tenantKey));
          return false;
        }
        if (entry?.status === 'sent' || entry?.status === 'seen-on-enable') {
          rememberAutoPrintOrder(order, 'seen-on-enable', {}, tenantKey);
          printedRef.current.add(getAutoPrintOrderKey(order, tenantKey));
          return false;
        }
        if (entry?.status === 'unknown' || entry?.status === 'needs-review') {
          printedRef.current.add(getAutoPrintOrderKey(order, tenantKey));
          return false;
        }
        if (!entry && (!Number.isFinite(createdAt) || now - createdAt > AUTO_PRINT_STARTUP_RECOVERY_MS)) {
          rememberAutoPrintOrder(order, 'needs-review', {
            lastError: 'Pedido anterior à janela de recuperação; conferir impressão antes de reenviar.',
          }, tenantKey);
          printedRef.current.add(getAutoPrintOrderKey(order, tenantKey));
          return false;
        }
        return true;
      });
    }
    const eligibleByKey = new Map();
    [...immediateCandidates, ...fetchedCandidates].forEach((order) => {
      const key = getAutoPrintOrderKey(order, tenantKey);
      if (key && !eligibleByKey.has(key)) eligibleByKey.set(key, order);
    });
    let eligible = [...eligibleByKey.values()];
    eligible = eligible.filter((order) => (
      getOrderSourceKey(order) !== 'ifood'
      || (printCatalogState.status === 'ready' && Boolean(printCatalogState.catalog))
    ));
    eligible.forEach(async (order) => {
      const selectedPoints = getAutoPrintPointsForOrder(order, points, isSalonOrder(order));
      const claim = await claimAutoPrintOrderExclusively(order, selectedPoints, tenantKey);
      if (!claim || !canContinue()) return;
      const key = getAutoPrintOrderKey(order, tenantKey);
      printedRef.current.add(key);
      const attemptCanContinue = () => canContinue()
        && getAutoPrintRegistryEntry(order, tenantKey)?.token === claim.token;
      const updatePoint = (point, status) => {
        if (!canContinue()) return;
        const current = getAutoPrintRegistryEntry(order, tenantKey);
        if (!current || current.token !== claim.token) return;
        const pointStates = {
          ...(current.pointStates || {}),
          [point.pointId || point.id]: {
            ...(current.pointStates?.[point.pointId || point.id] || {}),
            pointId: point.pointId || point.id,
            pointName: point.pointName || point.name,
            printerId: point.printerId,
            status,
            updatedAt: Date.now(),
          },
        };
        markAutoPrintOrder(order, 'queued', { phase: 'dispatching', pointStates }, tenantKey);
      };
      const result = await printAutomaticComanda(order, {
        attempt: claim.attempts,
        printPointIds: claim.pendingPointIds,
        canContinue: attemptCanContinue,
        session,
        onPointDispatchStart: (point) => updatePoint(point, 'dispatching'),
        onPointResult: (point) => updatePoint(point, point.ok ? 'sent' : point.dispatchStarted ? 'unknown' : 'failed'),
        ...(getOrderSourceKey(order) === 'ifood' ? {
          catalogVerified: true,
          catalog: printCatalogState.catalog,
        } : {}),
      });
      const pointStates = mergeAutoPrintPointStates(claim.pointStates, result?.pointResults || []);
      // The CIBD can have accepted one or more points immediately before a
      // logout/session switch. Persist those local acknowledgements first so a
      // stale claim retries only the remaining points in the next session.
      if (!attemptCanContinue() || result?.cancelled) {
        if (!canContinue()) return;
        if (getAutoPrintRegistryEntry(order, tenantKey)?.token !== claim.token) return;
        printedRef.current.delete(key);
        markAutoPrintOrder(order, 'failed', {
          failedAt: Date.now(),
          attempts: claim.attempts,
          nextRetryAt: Date.now() + AUTO_PRINT_RETRY_DELAY_MS,
          lastError: 'Sessão alterada durante a impressão automática.',
          pointStates,
          phase: 'pre-send',
        }, tenantKey);
        return;
      }
      if (result?.ok) {
        markAutoPrintOrder(order, 'sent', { sentAt: Date.now(), attempts: claim.attempts, lastError: '', pointStates, phase: 'complete' }, tenantKey);
      } else {
        printedRef.current.delete(key);
        markAutoPrintOrder(order, 'failed', { failedAt: Date.now(), attempts: claim.attempts, nextRetryAt: Date.now() + AUTO_PRINT_RETRY_DELAY_MS, lastError: result?.error || 'Falha ao imprimir automaticamente.', pointStates, phase: 'pre-send' }, tenantKey);
      }
    });
  }, [enabled, localCreatedOrders, orders, printCatalogState, sessionToken, tenantKey, session]);

  return null;
};

const PedidosScreen = ({ sidebarOpen, onToggleSidebar }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const currentUser = auth.user;
  const settingsSession = useMemo(() => ({ token: auth.token, user: currentUser }), [auth.token, currentUser]);
  const currentTenantKey = normalizeWidgetTenantKey(currentUser?.companyId);
  const currentOperationalAlertSessionKey = useMemo(() => {
    const pageSession = getPageAuthSession();
    return getAuthSessionMarker(
      pageSession?.token === auth.token
        ? pageSession
        : { token: auth.token, user: currentUser }
    );
  }, [auth.token, currentUser]);
  const isOperationalAlertTenantCurrent = useCallback((tenantKey) => (
    normalizeWidgetTenantKey(tenantKey) === currentTenantKey
    && isStoredSessionTokenCurrent(auth.token)
  ), [auth.token, currentTenantKey]);
  const canUseIfoodShipping = Boolean(currentUser);
  const canUseFiscalTools = isProgrammer(currentUser);
  const dashboardSession = useMemo(() => {
    const pageSession = getPageAuthSession();
    return pageSession?.token === auth.token ? pageSession : settingsSession;
  }, [auth.token, settingsSession]);
  const isDashboardSessionCurrent = useCallback(() => {
    const pageSession = getPageAuthSession();
    return Boolean(auth.token) && pageSession?.token === auth.token
      && String(pageSession?.user?.companyId || '') === currentTenantKey
      && pageSession?.browserSessionId === dashboardSession.browserSessionId
      && isStoredSessionCurrent(dashboardSession);
  }, [auth.token, currentTenantKey, dashboardSession]);
  const prefetchBalcaoCatalog = useCallback(() => {
    if (!isDashboardSessionCurrent()) return Promise.resolve(null);
    return prefetchOperationalCatalog({
      session: dashboardSession,
      fetchCatalog: (signal, metadata) => apiFetch(`${API_URL}/api/salon/catalog`, {
        ...(signal ? { signal } : {}),
        ...(Object.keys(metadata.headers || {}).length ? { headers: metadata.headers } : {}),
      }),
    });
  }, [dashboardSession, isDashboardSessionCurrent]);
  const restoredSnapshot = useMemo(() => (
    isDashboardSessionCurrent() ? readPedidosSnapshot(dashboardSession) : null
  ), [dashboardSession, isDashboardSessionCurrent]);
  const [dashboard, setDashboard] = useState(() => buildDashboardState(
    Object.values(restoredSnapshot?.channels || {}).flatMap((channel) => channel.orders || []),
    restoredSnapshot?.channels?.ifood?.lastSync || null
  ));
  const [merchantCard, setMerchantCard] = useState(() => restoredSnapshot?.merchant?.card || {
    merchantId: '', name: '', statusLabel: 'Sem status', statusColor: 'default',
    interruptions: [], stores: [],
  });
  const [dataFreshness, setDataFreshness] = useState({ pending: ['iFood', 'Balcão', 'Salão', 'Site', 'Pedidos10', '99Food'], failed: [], merchantVerified: false, merchantReadVerified: false, merchantError: null });
  const dataLoaderRef = useRef(null);
  const verifiedOrderIdsRef = useRef(new Set());
  const verifiedOrderVersionsRef = useRef(new Map());
  const autoAcceptAttemptedVersionsRef = useRef(new Map());
  const isDashboardOrderCurrent = useCallback((order) => (
    isDashboardSessionCurrent() && verifiedOrderIdsRef.current.has(order?.orderId)
  ), [isDashboardSessionCurrent]);
  const [ifoodShippingEnabled, setIfoodShippingEnabled] = useState(false);
  const [ifoodShippingMerchantIds, setIfoodShippingMerchantIds] = useState([]);
  const [merchantActionLoading, setMerchantActionLoading] = useState(false);
  const [merchantListAnchorEl, setMerchantListAnchorEl] = useState(null);
  const [merchantListLoading, setMerchantListLoading] = useState(false);
  const [ifoodWidgetLoading, setIfoodWidgetLoading] = useState(false);
  const [ifoodWidgetStatus, setIfoodWidgetStatus] = useState(() => ({
    state: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'idle',
    message: '',
  }));
  const [loading, setLoading] = useState(!restoredSnapshot);
  const [error, setError] = useState(null);
  const [autoAccept, setAutoAccept] = useState(() => readTenantLocalJson(AUTO_ACCEPT_KEY, false, settingsSession) === true);
  const [autoPrint, setAutoPrint] = useState(() => readTenantLocalJson(AUTO_PRINT_KEY, false, settingsSession) === true);
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const [selectedPreparingOrderIds, setSelectedPreparingOrderIds] = useState([]);
  const [sendingSelectedToKds, setSendingSelectedToKds] = useState(false);
  const [activeBatchDrag, setActiveBatchDrag] = useState(null);
  const [isBoardDragging, setIsBoardDragging] = useState(false);
  const [readyAttentionStartedAtByKey, setReadyAttentionStartedAtByKey] = useState(() => new Map());
  const boardRef = usePedidosBoardLayout(isBoardDragging);
  const [orderModal, setOrderModal] = useState({ open: false, order: null });
  const [orderEditTarget, setOrderEditTarget] = useState(null);
  const [amendmentPrintNotice, setAmendmentPrintNotice] = useState('');
  const amendmentPrintInFlightRef = useRef(new Set());
  const [printPointDialog, setPrintPointDialog] = useState({ open: false, order: null, points: [], printing: false });
  const [manualPrintLoading, setManualPrintLoading] = useState(false);
  const manualPrintInFlightRef = useRef(false);
  const fiscalFeature = useFiscalAvailability({
    enabled: canUseFiscalTools,
    sessionKey: currentOperationalAlertSessionKey,
    isSessionCurrent: isDashboardSessionCurrent,
  });
  const [fiscalPanelOpen, setFiscalPanelOpen] = useState(false);
  const [fiscalPreview, setFiscalPreview] = useState(null);
  const [fiscalIssueResult, setFiscalIssueResult] = useState(null);
  const [fiscalLoadingPreview, setFiscalLoadingPreview] = useState(false);
  const [fiscalIssuing, setFiscalIssuing] = useState(false);
  const [fiscalError, setFiscalError] = useState('');
  const [fiscalSuccess, setFiscalSuccess] = useState('');

  useEffect(() => scheduleOperationalCatalogPrefetch(() => {
    void prefetchBalcaoCatalog();
  }), [prefetchBalcaoCatalog]);
  const [cancellationModal, setCancellationModal] = useState({
    open: false,
    order: null,
    mode: 'ifood',
    reasons: [],
    loading: false,
    submitLoading: false,
    selectedReason: '',
    justification: '',
    error: '',
  });
  const [shippingModal, setShippingModal] = useState({
    open: false,
    order: null,
    loading: false,
    requesting: false,
    error: '',
    availabilities: [],
    selectedQuoteId: '',
  });
  const [outsideShippingModal, setOutsideShippingModal] = useState({
    open: false,
    sourceOrder: null,
    form: createOutsideShippingForm(),
    loadingQuote: false,
    geocoding: false,
    creating: false,
    error: '',
    availabilities: [],
    selectedQuoteId: '',
    geocodeResult: null,
    addressSuggestions: [],
    loadingAddressSuggestions: false,
  });
  const [courierQuoteModal, setCourierQuoteModal] = useState({
    open: false,
    order: null,
    merchantId: '',
    loading: false,
    quotes: [],
    errors: [],
    selectedQuoteId: '',
    error: '',
    capabilities: {},
    hiringBee: false,
    confirmBee: false,
    hireAttempted: false,
  });
  const courierQuoteRequestRef = useRef(0);
  const beeHireAttemptedKeysRef = useRef(new Set());
  const beeStatusRequestRef = useRef(0);
  const [beeDeliveryStatus, setBeeDeliveryStatus] = useState({ orderKey: '', loading: false, advancing: false, data: null, error: '' });
  const [courierBoardStatuses, setCourierBoardStatuses] = useState(() => new Map());
  const courierBoardRefreshRef = useRef(0);
  const courierBoardOrderKeys = useMemo(() => JSON.stringify(
    [...new Set((dashboard.orders || []).filter(isDashboardOrderCurrent).map(getCourierOrderKey).filter(Boolean))]
      .sort().map((key) => {
        const separator = key.indexOf(':');
        return { source: key.slice(0, separator), orderId: key.slice(separator + 1) };
      })
  ), [dashboard.orders, isDashboardOrderCurrent]);
  useEffect(() => {
    const orders = JSON.parse(courierBoardOrderKeys);
    const requestedKeys = new Set(orders.map(({ source, orderId }) => `${source}:${orderId}`));
    const controller = new AbortController();
    const generation = ++courierBoardRefreshRef.current;
    let latestRefresh = 0;
    setCourierBoardStatuses(new Map());
    if (!orders.length || !isDashboardSessionCurrent()) return () => controller.abort();
    const refresh = async () => {
      if (!isDashboardSessionCurrent()) return;
      const refreshId = ++latestRefresh;
      try {
        const batches = courierStatusBatches(orders);
        const responses = await Promise.all(batches.map(async (batch) => {
          const response = await apiFetch(`${API_URL}/api/courier/orders/statuses`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orders: batch }), signal: controller.signal,
          });
          if (!response.ok) throw new Error('Status logistico indisponivel');
          return response.json();
        }));
        if (controller.signal.aborted || generation !== courierBoardRefreshRef.current || refreshId !== latestRefresh || !isDashboardSessionCurrent()) return;
        const next = new Map();
        responses.forEach((data) => {
          (Array.isArray(data.statuses) ? data.statuses : []).forEach((entry) => {
            const key = `${entry.source}:${entry.orderId}`;
            if (entry.provider === 'bee' && courierCardLabel('bee', entry.status) && requestedKeys.has(key)) next.set(key, entry);
          });
        });
        setCourierBoardStatuses((previous) => {
          next.forEach((entry, key) => {
            const old = previous.get(key);
            if (old && Date.parse(old.observedAt || '') > Date.parse(entry.observedAt || '')) next.set(key, old);
          });
          return next;
        });
      } catch (_) {
        if (!controller.signal.aborted && generation === courierBoardRefreshRef.current && refreshId === latestRefresh && isDashboardSessionCurrent()) {
          setCourierBoardStatuses(new Map());
        }
      }
    };
    void refresh();
    const timer = setInterval(refresh, 30000);
    return () => { clearInterval(timer); controller.abort(); courierBoardRefreshRef.current += 1; };
  }, [courierBoardOrderKeys, dashboardSession.browserSessionId, isDashboardSessionCurrent]);
  const [shippingAddressChangeModal, setShippingAddressChangeModal] = useState({
    open: false,
    order: null,
    form: createShippingAddressChangeForm(),
    geocoding: false,
    submitting: false,
    error: '',
    geocodeResult: null,
  });
  const [orderAddressEditModal, setOrderAddressEditModal] = useState({
    open: false,
    order: null,
    form: createShippingAddressChangeForm(),
    submitting: false,
    error: '',
    addressSuggestions: [],
    loadingAddressSuggestions: false,
    addressSuggestionError: '',
    selectedStreetName: '',
  });
  const [orderAddressEditFeedback, setOrderAddressEditFeedback] = useState(null);
  const [shippingActionState, setShippingActionState] = useState({
    loading: false,
    error: '',
    message: '',
  });
  const [pickupCodeConfirmation, setPickupCodeConfirmation] = useState(createPickupCodeConfirmationState);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState('');
  const catalogRef = useRef(restoredSnapshot?.catalog || { products: {}, options: {} });
  const dashboardRef = useRef(dashboard);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const alertIntervalRef = useRef(null);
  const previousOrderIdsRef = useRef(new Set());
  const seenNegotiationKeysRef = useRef(new Set());
  const autoAcceptingOrderIdsRef = useRef(new Set());
  const trackingRefreshInFlightRef = useRef(new Set());
  const orderDetailsCacheRef = useRef(new Map());
  const orderDetailsInFlightRef = useRef(new Map());
  const orderDetailsAbortRef = useRef(null);
  const readyAttentionTimersRef = useRef(new Map());
  const canUseFiscalOrderModal = Boolean(
    canUseFiscalTools &&
    fiscalFeature.active &&
    fiscalFeature.provider === 'sefaz_direct' &&
    fiscalFeature.environment === 'homologation'
  );

  const updateDashboard = useCallback((updater) => {
    const nextDashboard = updater(dashboardRef.current);
    dashboardRef.current = nextDashboard;
    setDashboard(nextDashboard);
  }, []);

  const startReadyOrderAttention = useCallback((orderKeys = []) => {
    const keys = orderKeys.filter(Boolean);
    if (!keys.length) return;

    const startedAt = Date.now();
    setReadyAttentionStartedAtByKey((current) => {
      const next = new Map(current);
      keys.forEach((key) => {
        if (!next.has(key)) next.set(key, startedAt);
      });
      return next;
    });

    keys.forEach((key) => {
      const previousTimer = readyAttentionTimersRef.current.get(key);
      if (previousTimer) return;
      const timer = setTimeout(() => {
        readyAttentionTimersRef.current.delete(key);
        setReadyAttentionStartedAtByKey((current) => {
          if (!current.has(key)) return current;
          const next = new Map(current);
          next.delete(key);
          return next;
        });
      }, READY_ORDER_ATTENTION_DURATION_MS);
      readyAttentionTimersRef.current.set(key, timer);
    });
  }, []);

  useEffect(() => {
    const timers = readyAttentionTimersRef.current;
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
    setReadyAttentionStartedAtByKey(new Map());
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [currentTenantKey, dashboardSession.browserSessionId]);

  useEffect(() => {
    const preparingIds = new Set(
      (dashboard.orders || [])
        .filter((order) => order.status === 'preparing')
        .map((order) => String(order.orderId || ''))
        .filter(Boolean)
    );
    setSelectedPreparingOrderIds((current) => current.filter((orderId) => preparingIds.has(orderId)));
  }, [dashboard.orders]);

  useEffect(() => {
    const unlockAudio = () => {
      audioUnlockedRef.current = true;
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };

    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!currentTenantKey) {
      setIfoodWidgetStatus({
        state: 'error',
        message: 'Tenant ausente para carregar o widget iFood.',
      });
      return () => {
        active = false;
        releaseIfoodWidgetForTenant(currentTenantKey);
      };
    }

    if (!navigator.onLine) {
      setIfoodWidgetStatus({
        state: 'offline',
        message: 'Sem conexão para carregar o widget iFood.',
      });
      return () => {
        active = false;
        releaseIfoodWidgetForTenant(currentTenantKey);
      };
    }

    setIfoodWidgetStatus({
      state: 'loading',
      message: 'Carregando widget iFood.',
    });

    showIfoodWidget({ autoShow: true, tenantKey: currentTenantKey })
      .then((result) => {
        if (active) {
          setIfoodWidgetStatus({
            state: 'ready',
            message: `Widget iFood ativo para ${result?.merchantCount || 0} loja(s).`,
          });
        }
      })
      .catch((error) => {
        if (active) {
          setIfoodWidgetStatus({
            state: 'error',
            message: error?.message || 'Falha ao carregar widget iFood.',
          });
          console.error('[iFood Widget] Falha ao exibir na tela de pedidos:', error);
        }
      });

    return () => {
      active = false;
      releaseIfoodWidgetForTenant(currentTenantKey);
    };
  }, [currentTenantKey]);

  useEffect(() => {
    const handleOffline = () => {
      setIfoodWidgetStatus({
        state: 'offline',
        message: 'Sem conexão para carregar o widget iFood.',
      });
    };

    const handleOnline = () => {
      setIfoodWidgetStatus({
        state: 'loading',
        message: 'Reconectando widget iFood.',
      });

      showIfoodWidget({ autoShow: false, tenantKey: currentTenantKey })
        .then((result) => {
          setIfoodWidgetStatus({
            state: 'ready',
            message: `Widget iFood ativo para ${result?.merchantCount || 0} loja(s).`,
          });
        })
        .catch((error) => {
          setIfoodWidgetStatus({
            state: 'error',
            message: error?.message || 'Falha ao reconectar widget iFood.',
          });
          console.error('[iFood Widget] Falha ao reconectar após voltar a internet:', error);
        });
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [currentTenantKey]);

  useEffect(() => {
    const handleSessionChanging = () => {
      invalidateIfoodWidgetPresentation();
    };

    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, handleSessionChanging);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, handleSessionChanging);
    };
  }, [currentTenantKey]);

  useEffect(() => {
    const handleWidgetProfileStorageChange = (event) => {
      if (
        event.key !== IFOOD_WIDGET_PROFILE_TENANT_STORAGE_KEY
        || !shouldInvalidateWidgetForProfileStorageChange(
          currentTenantKey,
          event.newValue
        )
      ) {
        return;
      }

      invalidateIfoodWidgetPresentation();
      setIfoodWidgetStatus({
        state: 'error',
        message: IFOOD_WIDGET_PROFILE_CONFLICT_MESSAGE,
      });
    };

    window.addEventListener('storage', handleWidgetProfileStorageChange);
    return () => {
      window.removeEventListener('storage', handleWidgetProfileStorageChange);
    };
  }, [currentTenantKey]);

  const resetFiscalState = useCallback(() => {
    setFiscalPanelOpen(false);
    setFiscalPreview(null);
    setFiscalIssueResult(null);
    setFiscalLoadingPreview(false);
    setFiscalIssuing(false);
    setFiscalError('');
    setFiscalSuccess('');
  }, []);

  useEffect(() => {
    resetFiscalState();
  }, [currentOperationalAlertSessionKey, resetFiscalState]);

  const closeShippingAddressChangeModal = useCallback(() => {
    setShippingAddressChangeModal({
      open: false,
      order: null,
      form: createShippingAddressChangeForm(),
      geocoding: false,
      submitting: false,
      error: '',
      geocodeResult: null,
    });
  }, []);

  const closeOrderAddressEditModal = useCallback(() => {
    setOrderAddressEditModal({
      open: false,
      order: null,
      form: createShippingAddressChangeForm(),
      submitting: false,
      error: '',
      addressSuggestions: [],
      loadingAddressSuggestions: false,
      addressSuggestionError: '',
      selectedStreetName: '',
    });
  }, []);

  const closeOrderModal = useCallback(() => {
    orderDetailsAbortRef.current?.abort();
    orderDetailsAbortRef.current = null;
    beeStatusRequestRef.current += 1;
    setBeeDeliveryStatus({ orderKey: '', loading: false, advancing: false, data: null, error: '' });
    setOrderModal({ open: false, order: null });
    setOrderEditTarget(null);
    setAmendmentPrintNotice('');
    setOrderDetails(null);
    setLoadingOrderDetails(false);
    setOrderDetailsError('');
    setShippingActionState({ loading: false, error: '', message: '' });
    setPickupCodeConfirmation(createPickupCodeConfirmationState());
    closeShippingAddressChangeModal();
    closeOrderAddressEditModal();
    setOrderAddressEditFeedback(null);
    resetFiscalState();
  }, [closeOrderAddressEditModal, closeShippingAddressChangeModal, resetFiscalState]);

  const closeCancellationModal = useCallback(() => {
    setCancellationModal({
      open: false,
      order: null,
      mode: 'ifood',
      reasons: [],
      loading: false,
      submitLoading: false,
      selectedReason: '',
      justification: '',
      error: '',
    });
  }, []);

  const closeShippingModal = useCallback(() => {
    setShippingModal({
      open: false,
      order: null,
      loading: false,
      requesting: false,
      error: '',
      availabilities: [],
      selectedQuoteId: '',
    });
  }, []);

  const closeOutsideShippingModal = useCallback(() => {
    setOutsideShippingModal({
      open: false,
      sourceOrder: null,
      form: createOutsideShippingForm(ifoodShippingMerchantIds[0] || ''),
      loadingQuote: false,
      geocoding: false,
      creating: false,
      error: '',
      availabilities: [],
      selectedQuoteId: '',
      geocodeResult: null,
      addressSuggestions: [],
      loadingAddressSuggestions: false,
    });
  }, [ifoodShippingMerchantIds]);

  const closeCourierQuoteModal = useCallback(() => {
    courierQuoteRequestRef.current += 1;
    setCourierQuoteModal({
      open: false,
      order: null,
      merchantId: '',
      loading: false,
      quotes: [],
      errors: [],
      selectedQuoteId: '',
      error: '',
      capabilities: {},
      hiringBee: false,
      confirmBee: false,
      hireAttempted: false,
    });
  }, []);

  useEffect(() => {
    const invalidateCourierQuotes = () => closeCourierQuoteModal();
    const beeHireAttemptedKeys = beeHireAttemptedKeysRef.current;
    closeCourierQuoteModal();
    beeStatusRequestRef.current += 1;
    beeHireAttemptedKeys.clear();
    setBeeDeliveryStatus({ orderKey: '', loading: false, advancing: false, data: null, error: '' });
    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidateCourierQuotes);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, invalidateCourierQuotes);
      courierQuoteRequestRef.current += 1;
      beeStatusRequestRef.current += 1;
      beeHireAttemptedKeys.clear();
    };
  }, [auth.token, currentTenantKey, closeCourierQuoteModal]);

  const mergeOrderSnapshot = useCallback((nextOrder, { matchCommercialSource = false } = {}) => {
    if (!nextOrder?.orderId) return;
    const canonicalSource = (order) => getCommercialOrderSource(order) || (getOrderSourceKey(order) === 'whatsapp' ? 'site' : getOrderSourceKey(order));
    const matches = (order) => order?.orderId === nextOrder.orderId
      && (!matchCommercialSource || canonicalSource(order) === canonicalSource(nextOrder));
    const cacheKey = String(nextOrder.orderId || '').trim();
    const cachedDetails = orderDetailsCacheRef.current.get(cacheKey);
    if (cachedDetails?.data && matches(cachedDetails.data)) {
      orderDetailsCacheRef.current.set(cacheKey, {
        ...cachedDetails,
        data: { ...cachedDetails.data, ...nextOrder },
        loadedAt: Date.now(),
      });
    }
    updateDashboard((prev) => ({
      ...prev,
      orders: (prev.orders || []).some(matches)
        ? (prev.orders || []).map((order) => (
            matches(order) ? { ...order, ...nextOrder } : order
          ))
        : [nextOrder, ...(prev.orders || [])],
    }));
    setOrderDetails((prev) => (
      matches(prev) ? { ...prev, ...nextOrder } : prev
    ));
    setOrderModal((prev) => (
      matches(prev.order)
        ? { ...prev, order: { ...prev.order, ...nextOrder } }
        : prev
    ));
  }, [updateDashboard]);

  const statusFilterMenuOpen = Boolean(statusFilterAnchorEl);
  const merchantListOpen = Boolean(merchantListAnchorEl);
  const statusFilterSet = useMemo(() => new Set(statusFilters), [statusFilters]);
  const activeStatusFilterLabel = useMemo(() => {
    if (!statusFilters.length) return 'Todos os status';
    if (statusFilters.length === 1) {
      return statusFilterOptions.find((option) => option.key === statusFilters[0])?.label || '1 status';
    }
    return `${statusFilters.length} status`;
  }, [statusFilters]);

  const toggleStatusFilter = useCallback((statusKey) => {
    setStatusFilters((current) => (
      current.includes(statusKey)
        ? current.filter((item) => item !== statusKey)
        : [...current, statusKey]
    ));
  }, []);

  const togglePreparingOrderSelection = useCallback((event, orderId) => {
    event.stopPropagation();
    event.preventDefault();
    const normalizedOrderId = String(orderId || '').trim();
    if (!isDashboardOrderCurrent({ orderId: normalizedOrderId })) return;
    setSelectedPreparingOrderIds((current) => (
      current.includes(normalizedOrderId)
        ? current.filter((item) => item !== normalizedOrderId)
        : [...current, normalizedOrderId]
    ));
  }, [isDashboardOrderCurrent]);

  const buildReadyStatusMetadata = useCallback((newStatus, options = {}) => {
    if (newStatus !== 'ready') return {};
    return Object.fromEntries(
      Object.entries({
        readyAt: options.readyAt || new Date().toISOString(),
        readySource: options.readySource || 'pedidos-board',
        kdsBatchId: options.kdsBatchId,
        kdsSequence: options.kdsSequence,
      }).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
  }, []);

  const buildCancellationStatusMetadata = useCallback((newStatus, options = {}) => {
    if (newStatus !== 'cancelled') return {};
    return Object.fromEntries(
      Object.entries({
        cancelledAt: options.cancelledAt || new Date().toISOString(),
        cancelledSource: options.cancelledSource || 'pedidos-board',
        cancellationReason: options.cancellationReason || options.reason,
        cancellationReasonDescription: options.cancellationReasonDescription || options.reasonDescription,
        cancellationJustification: options.cancellationJustification || options.justification,
      }).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
  }, []);

  const wasMarkedReadyByKds = useCallback((order = {}) => (
    order?.readySource === 'kds-monitor' ||
    order?.additionalInfo?.kds?.readySource === 'kds-monitor' ||
    order?.additionalInfo?.kds?.kitchenReady === true
  ), []);

  const getKdsReadyCardClassName = useCallback((order = {}) => (
    wasMarkedReadyByKds(order) ? 'pedidos-order-card--kds-ready' : ''
  ), [wasMarkedReadyByKds]);

  const getKdsRouteNumber = useCallback((order = {}) => {
    const value = (
      order.routeNumber ??
      order.routeSequence ??
      order.kdsSequence ??
      order.additionalInfo?.kds?.routeNumber ??
      order.additionalInfo?.kds?.routeSequence ??
      order.additionalInfo?.kds?.kdsSequence
    );
    const numeric = Number(value);
    return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
  }, []);

  const getKdsRouteCardBadge = useCallback((order = {}) => {
    const shortLabel = String(order.shortRouteLabel || order.additionalInfo?.kds?.shortRouteLabel || '').trim();
    if (shortLabel) return shortLabel;
    const routeNumber = getKdsRouteNumber(order);
    return routeNumber ? `R${routeNumber}` : '';
  }, [getKdsRouteNumber]);

  const renderKdsRouteBadge = useCallback((order = {}) => {
    if (!shouldShowKdsRoute(order)) return null;
    const label = getKdsRouteCardBadge(order);
    if (!label) return null;
    return <Box className="pedidos-kds-route-badge">{label}</Box>;
  }, [getKdsRouteCardBadge]);

  const getSourceChip = (order) => {
    if (!order) return null;
    const source = getOrderSourceConfig(order);
    return (
      <Chip
        label={source.label}
        size="small"
        variant="outlined"
        className={`pedidos-source-chip pedidos-source-chip--${source.key}`}
        sx={{
          color: source.color,
          backgroundColor: source.backgroundColor,
          borderColor: source.borderColor,
        }}
      />
    );
  };

  const hasVisibleCardBadges = (order) => (
    getOperationalChipConfigs(order).length > 0 ||
    hasActiveNegotiation(order)
  );

const getScheduledDateTime = (order) => {
    const raw =
      order?.delivery?.deliveryDateTime ||
      order?.fullOrderData?.delivery?.deliveryDateTime ||
      order?.delivery?.takeoutDateTime ||
      order?.fullOrderData?.takeout?.takeoutDateTime ||
      '';
    if (!raw) return '';
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString('pt-BR');
  };

  const isPickupOrder = (order) => {
    if (!order) return false;
    const orderType = String(order.orderType || '').trim().toUpperCase();
    const fulfillment = String(order.fulfillment || '').trim().toLowerCase();
    const compactType = normalizeOrderSourceText([
      order.orderType,
      order.fulfillment,
      order.type,
      order.deliveryType,
      order.deliveryMode,
    ].filter(Boolean).join(' '));
    return (
      orderType === 'TAKEOUT' ||
      fulfillment === 'pickup' ||
      compactType.includes('retirada') ||
      compactType.includes('takeout') ||
      compactType.includes('pickup')
    );
  };

  const getReadableOrderTypeLabel = (order) => {
    if (!order) return '-';
    const orderType = String(order.orderType || '').trim().toUpperCase();
    const orderTiming = String(order.orderTiming || '').trim().toUpperCase();
    const fulfillment = String(order.fulfillment || '').trim().toLowerCase();

    if (isSalonOrder(order) || fulfillment === 'dine_in') {
      return 'Salão';
    }

    if (orderType === 'TAKEOUT' || fulfillment === 'pickup') {
      return orderTiming === 'SCHEDULED' ? 'Retirada Agendada' : 'Retirada';
    }

    if (orderType === 'DELIVERY' || fulfillment === 'delivery') {
      return orderTiming === 'SCHEDULED' ? 'Entrega Agendada' : 'Entrega';
    }

    return order.orderType || order.fulfillment || '-';
  };

  const isIfoodOnDemandShipping = useCallback((shipping = null) => (
    canUseIfoodShipping && hasIfoodOnDemandShippingEvidence(shipping)
  ), [canUseIfoodShipping]);

  const getVisibleShipping = useCallback((order) => {
    const shipping = order?.shipping && typeof order.shipping === 'object' ? order.shipping : null;
    return canUseIfoodShipping && hasIfoodOnDemandShippingEvidence(shipping) && !isCancelledShippingForDisplay(shipping)
      ? shipping
      : null;
  }, [canUseIfoodShipping]);

  const formatShortDateTime = (value) => {
    if (!value) return '';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const isConfirmableShippingPickupCodeSource = (source = '') => {
    const normalized = String(source || '').trim().toLowerCase();
    const compact = normalized.replace(/[^a-z0-9]/g, '');
    return Boolean(
      compact.includes('orderdetailsdeliverypickupcode') ||
      compact.includes('deliverypickupcode') ||
      compact.includes('fullorderdatadeliverypickupcode') ||
      compact.includes('deliverypickupcoderequested') ||
      compact.includes('deliverypickupcodevalidationsuccess') ||
      compact.includes('dpcr') ||
      compact.includes('dpcs')
    );
  };

  const getShippingPickupCode = (shipping = {}) => {
    const eventSource = [shipping?.lastShippingEventFullCode, shipping?.lastShippingEventCode, shipping?.lastEventFullCode, shipping?.lastEventCode]
      .map((value) => String(value || '').trim().toUpperCase())
      .find((code) => ['DELIVERY_PICKUP_CODE_REQUESTED', 'DPCR', 'DELIVERY_PICKUP_CODE_VALIDATION_SUCCESS', 'DPCS'].includes(code));
    const candidates = [
      [shipping?.orderDetails?.pickupCode, shipping?.orderDetails?.pickupCodeSource || 'order-details.delivery.pickupCode'],
      [shipping?.pickupCode, shipping?.pickupCodeSource || eventSource],
      [shipping?.tracking?.pickupCode, shipping?.tracking?.pickupCodeSource || 'tracking.pickupCode'],
      [shipping?.tracking?.data?.pickupCode, 'tracking.data.pickupCode'],
      [shipping?.tracking?.data?.collectCode, 'tracking.data.collectCode'],
      [shipping?.tracking?.data?.collectionCode, 'tracking.data.collectionCode'],
    ];
    const match = candidates.find(([value, source]) => String(value || '').trim() && isConfirmableShippingPickupCodeSource(source));
    return match ? String(match[0]).trim() : '';
  };

  const getPickupCodeEventCodes = (shipping = {}) => [
    shipping?.lastShippingEventFullCode,
    shipping?.lastShippingEventCode,
    shipping?.lastEventFullCode,
    shipping?.lastEventCode,
    ...(Array.isArray(shipping?.events)
      ? shipping.events.flatMap((event) => [event?.fullCode, event?.code])
      : []),
  ].map((code) => String(code || '').trim().toUpperCase()).filter(Boolean);

  const hasPickupCodeRequestEvidence = (order = {}) => {
    const shipping = getVisibleShipping(order);
    if (!shipping) return false;
    if (getShippingPickupCode(shipping)) return true;
    if (order?.delivery?.pickupCode || order?.fullOrderData?.delivery?.pickupCode) return true;
    if (getPickupCodeEventCodes(shipping).some((code) => IFOOD_PICKUP_CODE_EVENT_CODES.has(code))) return true;
    return String(shipping.status || '').trim() === 'arrivedAtOrigin';
  };

  const canConfirmPickupCodeForOrder = (order = {}) => {
    if (!canUseIfoodShipping || !isIfoodOrder(order) || isTerminalStatus(order?.status)) return false;

    const shipping = getVisibleShipping(order);
    if (!shipping || !isIfoodOnDemandShipping(shipping) || shipping.active === false) return false;

    return hasPickupCodeRequestEvidence(order);
  };

  const getShippingDeliveryConfirmationCode = (shipping = {}) => (
    shipping?.deliveryConfirmationCode ||
    shipping?.dropCode ||
    shipping?.tracking?.deliveryConfirmationCode ||
    shipping?.tracking?.data?.deliveryConfirmationCode ||
    shipping?.tracking?.data?.dropCode ||
    shipping?.tracking?.data?.deliveryCode ||
    ''
  );

  const getShippingReturnCode = (shipping = {}) => (
    shipping?.returnCode ||
    shipping?.tracking?.returnCode ||
    shipping?.tracking?.data?.returnCode ||
    ''
  );

  const getShippingEtaLabel = (shipping = {}) => {
    if (!shipping || typeof shipping !== 'object') return '';
    const status = String(shipping.status || '').trim();
    if (status && !['assigned', 'goingToOrigin'].includes(status)) return '';
    const etaSource = String(
      shipping.etaToStoreSource ||
      shipping.tracking?.etaToStoreSource ||
      ''
    ).trim().toLowerCase();
    const hasPositiveTrackingPickupEta =
      Number(shipping.tracking?.data?.pickupEtaStart) > 0 ||
      Number(shipping.tracking?.data?.pickupEtaEnd) > 0 ||
      Number(shipping.tracking?.data?.pickupEtaStartSeconds) > 0 ||
      Number(shipping.tracking?.data?.pickupEtaEndSeconds) > 0;
    const hasTrustedEtaSource =
      etaSource.includes('event:going_to_origin') ||
      etaSource.includes('event:gto') ||
      (etaSource.includes('tracking') && hasPositiveTrackingPickupEta) ||
      hasPositiveTrackingPickupEta;
    if (!hasTrustedEtaSource) return '';

    const etaTimestampMs = [
      shipping.etaToStore,
      shipping.tracking?.etaToStore,
    ].map((value) => new Date(value || '').getTime()).find((value) => Number.isFinite(value));
    if (Number.isFinite(etaTimestampMs)) {
      const remainingSeconds = Math.ceil((etaTimestampMs - Date.now()) / 1000);
      if (remainingSeconds <= 0) return '';
      return `Chega em ${Math.max(1, Math.ceil(remainingSeconds / 60))} min`;
    }

    const rawSeconds = [
      shipping.etaToStoreSeconds,
      shipping.tracking?.etaToStoreSeconds,
      shipping.tracking?.data?.pickupEtaStart,
      shipping.tracking?.data?.pickupEtaEnd,
      shipping.tracking?.data?.pickupEtaStartSeconds,
      shipping.tracking?.data?.pickupEtaEndSeconds,
    ].map((value) => Number(value)).find((value) => Number.isFinite(value) && value > 0);
    if (Number.isFinite(rawSeconds) && rawSeconds > 0) {
      const checkedAtMs = [
        shipping.etaToStoreUpdatedAt,
        shipping.tracking?.etaToStoreUpdatedAt,
        shipping.tracking?.checkedAt,
      ].map((value) => new Date(value || '').getTime()).find((value) => Number.isFinite(value));
      if (!Number.isFinite(checkedAtMs)) return '';
      const remainingSeconds = rawSeconds - Math.max(0, Math.floor((Date.now() - checkedAtMs) / 1000));
      if (remainingSeconds <= 0) return '';
      return `Chega em ${Math.max(1, Math.ceil(remainingSeconds / 60))} min`;
    }

    const rawMinutes = [
      shipping.etaToStoreMinutes,
      shipping.tracking?.etaToStoreMinutes,
    ].map((value) => Number(value)).find((value) => Number.isFinite(value) && value > 0);
    if (Number.isFinite(rawMinutes) && rawMinutes > 0) {
      const checkedAtMs = [
        shipping.etaToStoreUpdatedAt,
        shipping.tracking?.etaToStoreUpdatedAt,
        shipping.tracking?.checkedAt,
      ].map((value) => new Date(value || '').getTime()).find((value) => Number.isFinite(value));
      if (!Number.isFinite(checkedAtMs)) return '';
      const remainingMinutes = rawMinutes - Math.max(0, Math.floor((Date.now() - checkedAtMs) / 60000));
      if (remainingMinutes <= 0) return '';
      return `Chega em ${Math.max(1, Math.ceil(remainingMinutes))} min`;
    }

    return '';
  };

  const getShippingStatus = (order) => String(getVisibleShipping(order)?.status || '').trim();
  const getCourierCardLabel = (order) => {
    if (!isDashboardOrderCurrent(order)) return '';
    const key = getCourierOrderKey(order);
    const beeStatus = key ? courierBoardStatuses.get(key) : null;
    if (beeStatus) return courierCardLabel(beeStatus.provider, beeStatus.status);
    const shipping = order?.shipping;
    return hasIfoodOnDemandShippingEvidence(shipping)
      ? courierCardLabel('ifood', String(shipping?.status || '').trim()) : '';
  };
  const renderCourierCardStatus = (order) => {
    const label = getCourierCardLabel(order);
    return label ? <Typography className="pedidos-order-card__courier-status" title={label}>{label}</Typography> : null;
  };
  const isShippingTerminal = (status) => ['concluded', 'cancelled', 'cancellationAccepted'].includes(String(status || '').trim());
  const isShippingCardVisible = (order) => {
    if (!canUseIfoodShipping) return false;
    const shipping = getVisibleShipping(order);
    const status = getShippingStatus(order);
    return Boolean(
      shipping &&
      status &&
      !['available', 'unavailable', 'cancellationAccepted'].includes(status) &&
      !isTerminalStatus(order?.status)
    );
  };

  const isShippingArrivedAtStore = (order) => getShippingStatus(order) === 'arrivedAtOrigin';

  const isIfoodDeliveryOrder = (order) => (
    isIfoodOrder(order) &&
    !isPickupOrder(order) &&
    (
      String(order?.orderType || '').trim().toUpperCase() === 'DELIVERY' ||
      String(order?.fulfillment || '').trim().toLowerCase() === 'delivery' ||
      Boolean(order?.delivery?.deliveryAddress || order?.fullOrderData?.delivery?.deliveryAddress)
    )
  );

  const getIfoodOrderMerchantId = (order = {}) => String(
    order?.merchant?.id ||
    order?.merchant?.merchantId ||
    order?.fullOrderData?.merchant?.id ||
    order?.fullOrderData?.merchantId ||
    order?.lastPayload?.merchantId ||
    order?.merchantId ||
    ''
  ).trim();

  const canRequestShippingForOrder = (order) => {
    if (!canUseIfoodShipping) return false;
    if (!ifoodShippingEnabled || !isIfoodDeliveryOrder(order) || isTerminalStatus(order?.status)) return false;
    if (ifoodShippingMerchantIds.length) {
      const merchantId = getIfoodOrderMerchantId(order);
      if (!merchantId || !ifoodShippingMerchantIds.includes(merchantId)) return false;
    }
    if (!['preparing', 'ready'].includes(order?.status)) return false;
    const shipping = getVisibleShipping(order);
    const status = String(shipping?.status || '').trim();
    return !shipping?.active && !isShippingTerminal(status) && status !== 'requested' && status !== 'searching';
  };

  const getShippingChipConfig = (order) => {
    if (!isShippingCardVisible(order)) return null;
    const shipping = getVisibleShipping(order);
    const status = getShippingStatus(order);
    const labelByStatus = {
      requested: 'Entrega solicitada',
      searching: 'Buscando',
      assigned: 'Entregador',
      goingToOrigin: 'A caminho',
      arrivedAtOrigin: 'Na loja',
      driverDeallocated: 'Sem entregador',
      inTransit: 'Coletado',
      failed: 'Falha entrega',
      cancellationRequested: 'Cancelando',
      cancellationAccepted: 'Cancelada',
      cancellationRejected: 'Cancelamento negado',
      addressConfirmed: 'Endereco confirmado',
      addressChangeRequested: 'Alteracao solicitada',
      addressChangeAccepted: 'Endereco aceito',
      addressChangeDenied: 'Endereco rejeitado',
    };
    return {
      key: 'shipping',
      label: labelByStatus[status] || shipping?.statusLabel || 'Entrega iFood',
      color: ['failed', 'driverDeallocated'].includes(status) ? 'error' : status === 'arrivedAtOrigin' ? 'warning' : 'info',
      variant: status === 'arrivedAtOrigin' ? 'filled' : 'outlined',
    };
  };

  const getShippingCardClassName = (order) => {
    if (isCompactLocalOrder(order)) return '';
    if (!isShippingCardVisible(order)) return '';
    return [
      'pedidos-order-card--shipping-active',
      isShippingArrivedAtStore(order) ? 'pedidos-order-card--shipping-arrived' : '',
    ].filter(Boolean).join(' ');
  };

  const getSalonCardClassName = (order) => (
    getOrderSourceKey(order) === 'salao' ? 'pedidos-order-card--salao' : ''
  );

  const getLocalReadyCardClassName = (order) => {
    if (!isLocalReadyOrder(order)) return '';
    const key = getReadyOrderAttentionKey(getOrderSourceKey(order), order.orderId);
    return [
      'pedidos-order-card--local-ready',
      readyAttentionStartedAtByKey.has(key) ? 'pedidos-order-card--local-ready-alerting' : '',
    ].filter(Boolean).join(' ');
  };

  const renderLocalReadyAttention = (order) => {
    if (!isLocalReadyOrder(order)) return null;
    const key = getReadyOrderAttentionKey(getOrderSourceKey(order), order.orderId);
    const startedAt = readyAttentionStartedAtByKey.get(key);
    if (!Number.isFinite(startedAt)) return null;
    return <ReadyOrderAttentionOverlay key={`${key}:${startedAt}`} startedAt={startedAt} />;
  };

  const outsideShippingMerchantOptions = useMemo(() => {
    const stores = Array.isArray(merchantCard.stores) ? merchantCard.stores : [];
    const allowedIds = new Set(ifoodShippingMerchantIds);
    const filteredStores = stores
      .filter((store) => !allowedIds.size || allowedIds.has(String(store.merchantId || '').trim()))
      .map((store) => ({
        id: String(store.merchantId || '').trim(),
        name: store.name || store.merchantId || 'Loja iFood',
      }))
      .filter((store) => store.id);

    if (filteredStores.length) return filteredStores;

    const fallbackId = ifoodShippingMerchantIds[0] || merchantCard.merchantId || '';
    return fallbackId
      ? [{ id: fallbackId, name: merchantCard.name || fallbackId }]
      : [];
  }, [ifoodShippingMerchantIds, merchantCard.merchantId, merchantCard.name, merchantCard.stores]);

  const canUseOutsideShipping = canUseIfoodShipping && ifoodShippingEnabled && outsideShippingMerchantOptions.length > 0;

  const isOutsideShippingOrder = useCallback((order) => (
    String(order?.salesChannel || '').trim().toUpperCase() === 'POS' ||
    String(order?.shipping?.mode || '').trim().toUpperCase() === 'OUTSIDE_IFOOD'
  ), []);

  const isExternalDeliveryOrder = (order) => {
    if (!order) return false;
    if (!getExternalOrderSource(order)) return false;
    if (isPickupOrder(order)) return false;
    const compactType = normalizeOrderSourceText([
      order?.fulfillment,
      order?.orderType,
      order?.type,
      order?.deliveryType,
      order?.deliveryMode,
    ].filter(Boolean).join(' '));
    const address = getExternalOrderDeliveryAddress(order);
    const hasDeliveryAddress = typeof address === 'string'
      ? Boolean(address.trim())
      : Boolean(
          address &&
          typeof address === 'object' &&
          (
            address.formattedAddress ||
            address.address ||
            address.streetName ||
            address.street ||
            address.logradouro ||
            address.neighborhood ||
            address.city
          )
        );
    return (
      compactType.includes('delivery') ||
      compactType.includes('entrega') ||
      hasDeliveryAddress
    );
  };

  const canEditOrderAddress = (order) => {
    if (!order || isTerminalStatus(order.status) || isSalonOrder(order) || isPickupOrder(order)) return false;
    const supportedSources = new Set(['ifood', 'balcao', 'site', 'whatsapp', 'pedidos10']);
    if (getOrderSourceKey(order) === '99food' && Number(order.deliveryType) === 2 && order.requiresAcceptance !== true) supportedSources.add('99food');
    if (!supportedSources.has(getOrderSourceKey(order))) return false;
    const fulfillment = normalizeOrderSourceText([
      order.fulfillment,
      order.orderType,
      order.type,
      order.deliveryType,
      order.deliveryMode,
    ].filter(Boolean).join(' '));
    const address = getExternalOrderDeliveryAddress(order);
    const hasAddress = typeof address === 'string'
      ? Boolean(address.trim())
      : Boolean(address && typeof address === 'object' && Object.values(address).some(Boolean));
    return fulfillment.includes('delivery') || fulfillment.includes('entrega') || hasAddress;
  };

  const getExternalShippingOrderId = (order = {}) => String(
    order?.shipping?.orderId ||
    order?.shipping?.ifoodOrderId ||
    order?.shipping?.request?.orderId ||
    order?.shipping?.request?.response?.id ||
    order?.shipping?.request?.response?.orderId ||
    ''
  ).trim();

  const getExternalShippingBaseUrl = (order = {}) => {
    const source = getExternalOrderSource(order);
    const orderId = String(order?.orderId || '').trim();
    if (!source || !orderId) return '';
    return `${API_URL}/api/ifood/shipping/external-orders/${encodeURIComponent(source)}/${encodeURIComponent(orderId)}`;
  };

  const canRequestOutsideShippingForOrder = (order) => {
    if (!canUseOutsideShipping || !isExternalDeliveryOrder(order) || isTerminalStatus(order?.status)) return false;
    if(getOrderSourceKey(order)==='99food'&&!isNinetyNineFoodCourierEligible(order)) return false;
    const status = normalizeOrderStatusKey(order?.status);
    if (!['preparing', 'ready'].includes(status)) return false;
    const shipping = getVisibleShipping(order);
    const shippingStatus = String(shipping?.status || '').trim();
    return !shipping?.active && !getExternalShippingOrderId(order) && !isShippingTerminal(shippingStatus);
  };

  const canRequestCourierForOrder = (order) => {
    if(getOrderSourceKey(order)==='99food'&&!isNinetyNineFoodCourierEligible(order)) return false;
    if (!getExternalOrderSource(order)) return false;
    if (!isExternalDeliveryOrder(order) || isTerminalStatus(order?.status)) return false;
    if (!['preparing', 'ready'].includes(normalizeOrderStatusKey(order?.status))) return false;
    const shipping = getVisibleShipping(order);
    const shippingStatus = String(shipping?.status || '').trim();
    return !shipping?.active
      && !getExternalShippingOrderId(order)
      && !isShippingTerminal(shippingStatus)
      && shippingStatus !== 'requested'
      && shippingStatus !== 'searching';
  };

  const canCancelShippingDriverForOrder = useCallback((order) => {
    if (!canUseIfoodShipping || !order || isTerminalStatus(order?.status)) return false;
    const shipping = getVisibleShipping(order);
    if (!shipping?.active) return false;
    const status = String(shipping.status || '').trim();
    return IFOOD_SHIPPING_CANCEL_DRIVER_STATUSES.has(status);
  }, [canUseIfoodShipping, getVisibleShipping]);

  const canConfirmShippingAddressForOrder = useCallback((order) => {
    if (!canUseIfoodShipping || !order || isTerminalStatus(order?.status)) return false;
    if (!isOutsideShippingOrder(order)) return false;
    const shipping = getVisibleShipping(order);
    if (!shipping?.active) return false;
    const addressStatus = String(shipping.addressChange?.status || '').trim();
    return !shipping.addressChange?.pending && !['confirmed', 'accepted', 'denied'].includes(addressStatus);
  }, [canUseIfoodShipping, getVisibleShipping, isOutsideShippingOrder]);

  const canRequestShippingAddressChangeForOrder = useCallback((order) => {
    if (!canUseIfoodShipping || !order || isTerminalStatus(order?.status)) return false;
    if (!isOutsideShippingOrder(order)) return false;
    const shipping = getVisibleShipping(order);
    if (!shipping?.active || !getExternalShippingOrderId(order)) return false;
    if (shipping.addressChange?.pending) return false;
    const addressStatus = String(shipping.addressChange?.status || '').trim();
    if (['confirmed', 'accepted', 'denied', 'requested'].includes(addressStatus)) return false;
    const status = String(shipping.status || '').trim();
    return status && !isShippingTerminal(status) && !['cancellationRequested', 'cancellationAccepted'].includes(status);
  }, [canUseIfoodShipping, getVisibleShipping, isOutsideShippingOrder]);

  const renderShippingCardDetails = (order) => {
    if (isCompactLocalOrder(order)) return null;
    if (!isShippingCardVisible(order)) return null;
    const shipping = getVisibleShipping(order);
    const driverName = shipping?.driver?.name || '';
    const etaLabel = getShippingEtaLabel(shipping);
    const pickupCode = isIfoodOnDemandShipping(shipping) ? getShippingPickupCode(shipping) : '';
    const deliveryConfirmationCode = isIfoodOnDemandShipping(shipping) ? getShippingDeliveryConfirmationCode(shipping) : '';
    const returnCode = isIfoodOnDemandShipping(shipping) ? getShippingReturnCode(shipping) : '';
    if (!driverName && !pickupCode && !deliveryConfirmationCode && !returnCode && !etaLabel) return null;

    return (
      <Box className="pedidos-shipping-card-info">
        {driverName && (
          <Typography className="pedidos-shipping-card-info__line">
            Entregador: {driverName}{etaLabel ? ` - ${etaLabel}` : ''}
          </Typography>
        )}
        {!driverName && etaLabel && <Typography className="pedidos-shipping-card-info__line">{etaLabel}</Typography>}
        {pickupCode && <Typography className="pedidos-shipping-card-info__line">Codigo de coleta: {pickupCode}</Typography>}
        {deliveryConfirmationCode && <Typography className="pedidos-shipping-card-info__line">Codigo de entrega: {deliveryConfirmationCode}</Typography>}
        {returnCode && <Typography className="pedidos-shipping-card-info__line">Codigo de devolucao: {returnCode}</Typography>}
      </Box>
    );
  };

  const renderPickupCodeConfirmationForm = (order) => {
    if (!canConfirmPickupCodeForOrder(order)) return null;
    const code = String(pickupCodeConfirmation.code || '').replace(/\D/g, '').slice(0, 4);
    const disabled = pickupCodeConfirmation.submitting || code.length !== 4;

    return (
      <Box sx={{ display: 'grid', gap: 1, mt: 1.5, maxWidth: 360 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="Confirmar pickupcode"
            size="small"
            value={code}
            onChange={(event) => {
              const nextCode = String(event.target.value || '').replace(/\D/g, '').slice(0, 4);
              setPickupCodeConfirmation((prev) => ({
                ...prev,
                code: nextCode,
                message: '',
              }));
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 4,
            }}
            disabled={pickupCodeConfirmation.submitting}
          />
          <Button
            variant="contained"
            onClick={() => submitPickupCodeConfirmation(order)}
            disabled={disabled}
          >
            {pickupCodeConfirmation.submitting ? 'Confirmando...' : 'Confirmar'}
          </Button>
        </Box>
        {pickupCodeConfirmation.message && (
          <Alert severity={pickupCodeConfirmation.severity}>
            {pickupCodeConfirmation.message}
          </Alert>
        )}
      </Box>
    );
  };

  const getShippingAvailabilityFee = (availability = {}) => (
    availability.deliveryFee ??
    availability.quote?.netValue ??
    availability.quote?.grossValue ??
    availability.price ??
    availability.amount ??
    0
  );

  const formatShippingTimeWindow = (availability = {}) => {
    const minSeconds = Number(availability.deliveryTimeMinSeconds ?? availability.deliveryTime?.min ?? 0);
    const maxSeconds = Number(availability.deliveryTimeMaxSeconds ?? availability.deliveryTime?.max ?? 0);
    if (Number.isFinite(minSeconds) && Number.isFinite(maxSeconds) && (minSeconds > 0 || maxSeconds > 0)) {
      const minMinutes = Math.max(Math.round(minSeconds / 60), 1);
      const maxMinutes = Math.max(Math.round(maxSeconds / 60), minMinutes);
      return minMinutes === maxMinutes ? `${minMinutes} min` : `${minMinutes}-${maxMinutes} min`;
    }
    const pickupAt = formatShortDateTime(availability.estimatedPickupAt);
    const deliveryAt = formatShortDateTime(availability.estimatedDeliveryAt);
    if (pickupAt && deliveryAt) return `${pickupAt} -> ${deliveryAt}`;
    return pickupAt || deliveryAt || 'Previsao nao informada';
  };

  const getSelectedShippingQuote = () => (
    (shippingModal.availabilities || []).find((item) => item.quoteId === shippingModal.selectedQuoteId) || null
  );

  const getSelectedOutsideShippingQuote = () => (
    (outsideShippingModal.availabilities || []).find((item) => item.quoteId === outsideShippingModal.selectedQuoteId) || null
  );

  const getSelectedCourierQuote = () => (
    (courierQuoteModal.quotes || []).find((item) => item.id === courierQuoteModal.selectedQuoteId) || null
  );

  const getOperationalChipConfigs = (order) => {
    if (!order) return [];

    const chips = [];
    const shippingChip = getShippingChipConfig(order);
    if (shippingChip && !getCourierCardLabel(order)) chips.push(shippingChip);
    const orderType = String(order.orderType || '').trim().toUpperCase();
    const orderTiming = String(order.orderTiming || '').trim().toUpperCase();
    const fulfillment = String(order.fulfillment || '').trim().toLowerCase();

    if (getOrderSourceKey(order) === 'salao') {
      chips.push({
        key: 'salon-table',
        label: `Mesa ${order.tableNumber || '-'}`,
        color: 'error',
        variant: 'filled',
      });
    }

    if (orderTiming === 'SCHEDULED' && !(orderType === 'TAKEOUT' || fulfillment === 'pickup')) {
      chips.push({ key: 'scheduled', label: 'Agendado', color: 'info', variant: 'filled' });
    }

    if (orderType === 'TAKEOUT' || fulfillment === 'pickup') {
      chips.push({
        key: 'pickup',
        label: orderTiming === 'SCHEDULED' ? 'Retirada Agendada' : 'Retirada',
        color: 'secondary',
        variant: 'outlined',
      });
    }

    return chips;
  };

  const formatCustomerNameToken = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';
    return text.charAt(0).toLocaleUpperCase('pt-BR') + text.slice(1).toLocaleLowerCase('pt-BR');
  };

  const getCustomerNameCandidate = (customer) => {
    if (!customer) return '';
    if (typeof customer === 'string') return customer;
    const nameParts = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
    return customer.name || customer.fullName || customer.customerName || nameParts || '';
  };

  const getOrderCustomerName = (order) => {
    const candidates = [
      getCustomerNameCandidate(order?.customer),
      getCustomerNameCandidate(order?.fullOrderData?.customer),
      getCustomerNameCandidate(order?.lastPayload?.order?.customer),
      getCustomerNameCandidate(order?.user),
      getCustomerNameCandidate(order?.buyer),
      order?.customerName,
      order?.clientName,
    ];

    return candidates
      .map((candidate) => String(candidate || '').replace(/\s+/g, ' ').trim())
      .find(Boolean) || '';
  };

  const getOrderCardCustomerLabel = (order) => {
    const rawName = getOrderCustomerName(order);
    const normalized = rawName.toLocaleLowerCase('pt-BR');
    const placeholderNames = new Set(['cliente', 'cliente ifood', 'cliente do site', 'cliente pedidos10']);
    if (!rawName || placeholderNames.has(normalized)) return 'Cliente';

    const [firstName = '', secondName = ''] = rawName
      .split(/\s+/)
      .map((part) => part.replace(/[^\p{L}\p{M}'-]/gu, '').trim())
      .filter(Boolean);

    if (!firstName) return 'Cliente';
    const first = formatCustomerNameToken(firstName);
    const secondInitial = secondName ? `${secondName.charAt(0).toLocaleUpperCase('pt-BR')}.` : '';
    return [first, secondInitial].filter(Boolean).join(' ');
  };

  const getOrderCardSubtitle = (order) => {
    if (!order) return '';
    return getOrderCardCustomerLabel(order);
  };

  const getOrderCardDetail = (order) => {
    if (!order) return '';
    if (isNinetyNineFoodTotalUnknown(order)) return NINETY_NINE_FOOD_TOTAL_UNKNOWN;
    if (isCompactLocalOrder(order)) return '';
    if (isSalonOrder(order) || String(order.fulfillment || '').trim().toLowerCase() === 'dine_in') {
      return [
        `Mesa ${order.tableNumber || '-'}`,
        order.waiterName ? `Garçom: ${order.waiterName}` : '',
        order.tableRoundNumber ? `Rodada ${order.tableRoundNumber}` : '',
      ].filter(Boolean).join(' • ');
    }
    return '';
  };

  const renderOrderCardSource = (order) => {
    return getSourceChip(order);
  };

  const renderLegacyDineInReadyNotice = (order) => {
    if (!order || order.status !== 'ready' || isLocalReadyOrder(order)) return null;
    const isDineIn = String(order.fulfillment || '').trim().toLowerCase() === 'dine_in';
    if (!isSalonOrder(order) && !isDineIn) return null;
    return (
      <Box sx={{ bgcolor: '#dcfce7', border: '1px solid #16a34a', color: '#166534', borderRadius: 1.5, px: 1, py: 0.75, mt: 0.75, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900 }}>
          ENTREGAR NA MESA {order.tableNumber || '-'}
        </Typography>
        {(order.waiterName || order.tableRoundNumber) ? (
          <Typography variant="caption" sx={{ display: 'block' }}>
            {[order.waiterName ? `Garçom: ${order.waiterName}` : '', order.tableRoundNumber ? `Rodada ${order.tableRoundNumber}` : ''].filter(Boolean).join(' • ')}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const getCountdownProps = (order) => {
    const isMesa = isSalonOrder(order);
    return {
      durationMinutes: getOrderCountdownMinutes(order),
      expiredMarkerClassName: isMesa ? 'pedidos-salon-timer-expired' : '',
    };
  };

  const loadPrinterSettings = useCallback(() => loadAutomaticPrinterSettings(settingsSession), [settingsSession]);

  const getAudioContext = useCallback(() => {
    if (!audioUnlockedRef.current) return null;
    if (audioContextRef.current) return audioContextRef.current;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioContextRef.current = new Ctx();
    return audioContextRef.current;
  }, []);

  const playTone = useCallback((durationMs, frequency, volume) => {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    gain.gain.value = volume;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + durationMs / 1000);
  }, [getAudioContext]);

  const playChord = useCallback((durationMs, frequencies, volume, delayMs = 0) => {
    const ctx = getAudioContext();
    if (!ctx) return false;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const startAt = ctx.currentTime + delayMs / 1000;
    const stopAt = startAt + durationMs / 1000;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), startAt + 0.018);
    gain.gain.setValueAtTime(Math.max(0.0001, volume), Math.max(startAt + 0.02, stopAt - 0.08));
    gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
    gain.connect(ctx.destination);

    const oscillators = frequencies.map((frequency) => {
      const oscillator = ctx.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, startAt);
      oscillator.connect(gain);
      oscillator.start(startAt);
      oscillator.stop(stopAt);
      return oscillator;
    });

    const disconnect = () => {
      oscillators.forEach((oscillator) => oscillator.disconnect());
      gain.disconnect();
    };
    oscillators[0]?.addEventListener('ended', disconnect, { once: true });
    return true;
  }, [getAudioContext]);

  const stopAlertLoop = useCallback(() => {
    if (!alertIntervalRef.current) return;
    clearInterval(alertIntervalRef.current);
    alertIntervalRef.current = null;
  }, []);

  const startAlertLoop = useCallback(() => {
    if (alertIntervalRef.current) return;
    playTone(350, 880, 0.6);
    alertIntervalRef.current = setInterval(() => {
      playTone(350, 880, 0.6);
    }, 1400);
  }, [playTone]);

  const playNewOrderSound = useCallback(() => {
    if (!isNewOrderSoundEnabled(settingsSession)) return;
    NEW_ORDER_SOUND_PATTERN.forEach((step) => {
      playChord(step.durationMs, step.frequencies, step.volume, step.delayMs);
    });
  }, [playChord, settingsSession]);

  const applyCatalogToItem = useCallback((item, currentCatalog, orderMerchantId = '') => {
    const catalogToUse = currentCatalog || catalogRef.current;
    return projectOrderDisplayItem(item, catalogToUse, orderMerchantId);
  }, []);

  const applyCatalogToOrders = useCallback((orders, currentCatalog) => {
    return (orders || []).map((order) => ({
      ...order,
      items: getOrderSourceKey(order) === '99food' ? order.items
        : order.items?.map((item) => applyCatalogToItem(item, currentCatalog, getIfoodOrderMerchantId(order))) || order.items,
    }));
  }, [applyCatalogToItem]);

  const requestMerchantCard = useCallback(async ({ signal }) => {
    // Ambas as leituras são locais; nenhuma consulta síncrona à API oficial.
    const [settingsResponse, merchantsResponse] = await Promise.all([
      apiFetch(`${API_URL}/api/settings/ifood`, { signal }),
      apiFetch(`${API_URL}/api/ifood/merchants/status-summary`, { signal }),
    ]);
    const settings = await settingsResponse.json();
    if (!settingsResponse.ok) throw new Error('Falha ao carregar configuração das lojas.');
    const enabled = settings?.integrationEnabled !== false;
    const merchantIds = enabled && Array.isArray(settings?.merchantIds)
      ? settings.merchantIds.map((id) => String(id || '').trim()).filter(Boolean) : [];
    const merchantsData = await merchantsResponse.json().catch(() => []);
    if (merchantIds.length && !merchantsResponse.ok) throw new Error('Falha ao carregar status das lojas.');
    const merchantsById = new Map(normalizeMerchantListPayload(merchantsData)
      .map((merchant) => [getMerchantIdentifier(merchant), merchant]));
    const stores = merchantIds.map((merchantId) => {
      const merchant = merchantsById.get(merchantId) || { id: merchantId };
      return buildMerchantStoreSummary({ merchantId, merchant,
        merchantStatus: extractMerchantStatusPayload(merchant),
        interruptions: extractInterruptionsPayload(merchant) });
    });
    return {
      card: { ...(stores[0] || { merchantId: '', name: 'Loja iFood', statusLabel: 'Sem status', statusColor: 'default', interruptions: [] }), stores },
      refreshPending: stores.some((store) => store.stale),
      shippingEnabled: enabled && canUseIfoodShipping && settings?.shippingEnabled !== false,
      shippingMerchantIds: enabled && canUseIfoodShipping && Array.isArray(settings?.shippingMerchantIds)
        ? settings.shippingMerchantIds.map((id) => String(id || '').trim()).filter(Boolean) : [],
    };
  }, [canUseIfoodShipping]);

  const fetchMerchantCard = useCallback((options = {}) => (
    dataLoaderRef.current?.refreshMerchant(options) || Promise.resolve()
  ), []);
  const loadMerchantStores = useCallback(async () => {
    setMerchantListLoading(true);
    try { await fetchMerchantCard({ force: true }); }
    finally { if (isDashboardSessionCurrent()) setMerchantListLoading(false); }
  }, [fetchMerchantCard, isDashboardSessionCurrent]);
  const fetchOrders = useCallback((withLoader = false, syncIfood = false, options = {}) => {
    if (options.forceMerchantCard) fetchMerchantCard({ force: true });
    return dataLoaderRef.current?.refresh({ syncIfood, afterCurrent: true }) || Promise.resolve();
  }, [fetchMerchantCard]);

  useEffect(() => {
    const names = { ifood: 'iFood', balcao: 'Balcão', salao: 'Salão', site: 'Site', pedidos10: 'Pedidos10', '99food': '99Food' };
    let previousChannels = {};
    const hydratedChannels = new Set();
    const readyStatusByChannel = new Map();
    const readyAttentionHydratedChannels = new Set();
    const fetchPayload = async (path, { signal }) => {
      const response = await apiFetch(`${API_URL}${path}`, { signal });
      const payload = await response.json();
      if (!response.ok) {
        const failure = new Error(payload?.error || payload?.message || 'Falha ao consultar pedidos.');
        failure.status = response.status;
        failure.responseBody = payload;
        throw failure;
      }
      return payload;
    };
    const loader = createPedidosDataLoader({
      session: dashboardSession,
      isCurrent: isDashboardSessionCurrent,
      requests: {
        ifood: (options) => fetchPayload(`/api/ifood/orders${options.sync ? '?sync=1' : ''}`, options),
        balcao: (options) => fetchPayload('/api/balcao/orders', options),
        salao: (options) => fetchPayload('/api/salon/orders', options),
        site: (options) => fetchPayload('/api/webhooks/site/orders', options),
        pedidos10: (options) => fetchPayload('/api/integrations/pedidos10/orders', options),
        '99food': (options) => fetchPayload('/api/99food/orders', options),
        catalog: ({ signal }) => fetchOrderDisplayCatalog(API_URL, { signal }),
        merchant: requestMerchantCard,
      },
      onChange: (state) => {
        const readyTransitions = [];
        ['balcao', 'salao'].forEach((channelKey) => {
          const channel = state.channels[channelKey];
          if (!channel?.verified || previousChannels[channelKey] === channel) return;
          const result = detectVerifiedReadyOrderTransitions({
            channelKey,
            channel,
            previousStatusById: readyStatusByChannel.get(channelKey) || new Map(),
            hydrated: readyAttentionHydratedChannels.has(channelKey),
          });
          readyStatusByChannel.set(channelKey, result.nextStatusById);
          readyAttentionHydratedChannels.add(channelKey);
          readyTransitions.push(...result.transitionedOrderKeys);
        });
        startReadyOrderAttention(readyTransitions);
        const existingById = new Map((dashboardRef.current?.orders || []).map((order) => [order.orderId, order]));
        const verifiedIds = new Set();
        const verifiedVersions = new Map();
        const orders = Object.entries(state.channels).flatMap(([key, channel]) => {
          if (channel.verified) {
            channel.orders.forEach((order) => {
              verifiedIds.add(order.orderId);
              verifiedVersions.set(order.orderId, channel.orders);
            });
            if (!hydratedChannels.has(key)) {
              channel.orders.forEach((order) => previousOrderIdsRef.current.add(order.orderId));
              hydratedChannels.add(key);
            }
          }
          return channel.orders.map((order) => (
            previousChannels[key]?.orders === channel.orders ? existingById.get(order.orderId) || order : order
          ));
        });
        // A mutation may return a new order before its channel next responds.
        // Catalog/merchant/other channels must not remove that confirmed insert.
        const returnedIds = new Set(orders.map((order) => order.orderId));
        existingById.forEach((order, id) => {
          const source = getOrderSourceKey(order);
          const channelKey = source === 'whatsapp' ? 'site' : source;
          const channel = state.channels[channelKey];
          const channelUpdated = channel?.verified && previousChannels[channelKey] !== channel;
          if (!returnedIds.has(id) && !channelUpdated) orders.push(order);
        });
        verifiedOrderIdsRef.current = verifiedIds;
        verifiedOrderVersionsRef.current = verifiedVersions;
        const presentIds = new Set(orders.map((order) => order.orderId));
        autoAcceptAttemptedVersionsRef.current.forEach((version, id) => {
          if (!presentIds.has(id)) autoAcceptAttemptedVersionsRef.current.delete(id);
        });
        previousChannels = state.channels;
        catalogRef.current = state.catalog;
        const nextDashboard = buildDashboardState(applyCatalogToOrders(orders, state.catalog), state.channels.ifood?.lastSync);
        dashboardRef.current = nextDashboard;
        setDashboard(nextDashboard);
        setLoading(false);
        setDataFreshness({
          pending: Object.entries(state.channels).filter(([, channel]) => !channel.verified && !channel.error).map(([key]) => names[key]),
          failed: Object.entries(state.channels).filter(([, channel]) => channel.error).map(([key]) => names[key]),
          merchantVerified: state.merchantVerified, merchantReadVerified: state.merchantReadVerified, merchantError: state.merchantError,
        });
        if (state.merchant) {
          setMerchantCard((prev) => {
            const previousById = new Map((prev.stores || []).map((store) => [store.merchantId, store]));
            const stores = state.merchant.card.stores.map((store) => mergeMerchantStoreSummary(previousById.get(store.merchantId), store));
            return { ...state.merchant.card, ...(stores[0] || {}), stores };
          });
          setIfoodShippingEnabled(state.merchantReadVerified && state.merchant.shippingEnabled);
          setIfoodShippingMerchantIds(state.merchantReadVerified ? state.merchant.shippingMerchantIds : []);
        }
        if (state.channels.ifood?.verified) {
          flushOperationalAlertQueue(currentTenantKey, {
            isTenantCurrent: isOperationalAlertTenantCurrent, sessionKey: currentOperationalAlertSessionKey,
          }).catch(() => {});
        }
      },
      onError: (key, failure) => {
        if (key !== 'ifood' || isOperationalAlertAuthFailure(failure)) return;
        reportOperationalAlert(currentTenantKey, {
          type: 'frontend.ifood.orders.fetch_failed', source: 'Pedidos.fetchOrders', severity: 'warning',
          message: 'Falha ao atualizar pedidos iFood no navegador.',
          details: { channel: 'ifood', path: '/api/ifood/orders', reason: failure.message, lastSync: dashboardRef.current?.lastSync || null },
        }, { isTenantCurrent: isOperationalAlertTenantCurrent, sessionKey: currentOperationalAlertSessionKey });
      },
    });
    previousChannels = loader.getState().channels;
    dataLoaderRef.current = loader;
    loader.refresh();
    // A sincronização oficial segue a leitura local do iFood, sem aguardar os demais canais.
    loader.refreshIfood().then(() => {
      if (isDashboardSessionCurrent() && dataLoaderRef.current === loader) loader.refreshIfood({ sync: true });
    });
    const interval = setInterval(() => loader.refresh(), 30000);
    return () => {
      clearInterval(interval);
      loader.dispose();
      if (dataLoaderRef.current === loader) dataLoaderRef.current = null;
    };
  }, [applyCatalogToOrders, currentOperationalAlertSessionKey, currentTenantKey, dashboardSession,
    isDashboardSessionCurrent, isOperationalAlertTenantCurrent, requestMerchantCard, startReadyOrderAttention]);

  const handleOpenMerchantList = useCallback((event) => {
    setMerchantListAnchorEl(event.currentTarget);
    loadMerchantStores();
  }, [loadMerchantStores]);

  const handleCloseMerchantList = useCallback(() => {
    setMerchantListAnchorEl(null);
  }, []);

  const handleReopenMerchant = useCallback(async () => {
    if (!isDashboardSessionCurrent() || !dataFreshness.merchantReadVerified || merchantCard.stale) return;
    const activeInterruptions = (Array.isArray(merchantCard.interruptions) ? merchantCard.interruptions : [])
      .filter((item) => isInterruptionActive(item));

    if (!merchantCard.merchantId || !activeInterruptions.length) {
      return;
    }

    setMerchantActionLoading(true);
    try {
      for (const interruption of activeInterruptions) {
        const response = await apiFetch(`${API_URL}/api/ifood/merchants/${merchantCard.merchantId}/interruptions/${interruption.id}`, {
          method: 'DELETE',
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(data?.message || data?.error || 'Falha ao reabrir a loja no iFood.');
        }
      }

      await fetchMerchantCard({ force: true });
      await fetchOrders(false, true);
    } catch (err) {
      setError(err.message || 'Falha ao reabrir a loja.');
    } finally {
      setMerchantActionLoading(false);
    }
  }, [dataFreshness.merchantReadVerified, fetchMerchantCard, fetchOrders, isDashboardSessionCurrent, merchantCard.interruptions, merchantCard.merchantId, merchantCard.stale]);

  // Função para aceitar pedido
  const acceptOrder = useCallback(async (orderId, options = {}) => {
    const normalizedOrderId = String(orderId || '').trim();
    if (!isDashboardOrderCurrent({ orderId: normalizedOrderId })) return;
    if (!normalizedOrderId || !getTenantStorageKey(AUTO_ACCEPT_KEY, settingsSession)) return;
    const sourceOrder = (dashboardRef.current?.orders || []).find((order) => order.orderId === normalizedOrderId);
    const sourceKey = getOrderSourceKey(sourceOrder || { orderId: normalizedOrderId });
    if (options.automatic && sourceKey === '99food') return;
    if (options.automatic && readTenantLocalJson(AUTO_ACCEPT_KEY, false, settingsSession) !== true) return;
    if (options.automatic && autoAcceptingOrderIdsRef.current.has(normalizedOrderId)) return;

    if (options.automatic) {
      const version = verifiedOrderVersionsRef.current.get(normalizedOrderId);
      if (autoAcceptAttemptedVersionsRef.current.get(normalizedOrderId) === version) return;
      autoAcceptAttemptedVersionsRef.current.set(normalizedOrderId, version);
      autoAcceptingOrderIdsRef.current.add(normalizedOrderId);
    }

    try {
      const confirmPath = sourceKey === '99food'
        ? `/api/99food/orders/${encodeURIComponent(normalizedOrderId)}/confirm`
        : `/api/ifood/orders/${normalizedOrderId}/confirm`;
      const response = await apiFetch(`${API_URL}${confirmPath}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || (sourceKey === '99food' ? 'Falha ao aceitar pedido na 99Food' : 'Falha ao aceitar pedido no iFood'));
      }

      if (!isDashboardOrderCurrent({ orderId: normalizedOrderId })) return;
      if (data?.order) mergeOrderSnapshot(data.order);
      // Após aceitar o pedido, faz refresh dos dados
      await fetchOrders();

      console.log(`Pedido ${normalizedOrderId} aceito com sucesso`);
    } catch (err) {
      console.error('Erro ao aceitar pedido:', err);
      setError(`Erro ao aceitar pedido: ${err.message}`);
    } finally {
      if (options.automatic) {
        autoAcceptingOrderIdsRef.current.delete(normalizedOrderId);
      }
    }
  }, [fetchOrders, isDashboardOrderCurrent, mergeOrderSnapshot, settingsSession]);

  const [ninetyNineDropAction,setNinetyNineDropAction]=useState(null);
  // Função para alterar status manualmente
  const changeOrderStatus = useCallback(async (orderId, newStatus, options = {}) => {
    if (!isDashboardOrderCurrent({ orderId })) return;
    const readyStatusMetadata = buildReadyStatusMetadata(newStatus, options);
    const cancellationStatusMetadata = buildCancellationStatusMetadata(newStatus, options);
    const manualCommandId=['preparing','ready','enRoute'].includes(newStatus) ? (options.manualCommandId||createCourierIntentId()) : null;
    const manualBody=manualCommandId ? {manualCommandId} : {};
    const skipRefresh = options.skipRefresh === true;
    const silentError = options.silentError === true;
    const readManualResult=async response=>{
      const payload=await response.json().catch(()=>({}));
      if(!response.ok) throw new Error(payload.error||payload.message||'Não foi possível alterar o pedido.');
      const command=payload.manualStatus;
      if(command?.pending) setError('A alteração aguarda confirmação do Brutos Entregas. Atualize o pedido para conferir.');
      else if(['rejected','superseded'].includes(command?.state)) setError('O Brutos Entregas não confirmou esta etapa. Confira a entrega antes de continuar.');
      return payload;
    };
    try {
      const currentOrder = (dashboardRef.current?.orders || []).find((order) => order.orderId === orderId);
      if (isSalonOrder(currentOrder || { orderId }) && newStatus === 'enRoute') {
        setError('Pedidos de Mesa não passam pelo status Em rota. Marque como Pronto ou Concluído.');
        return;
      }

      if (getOrderSourceKey(currentOrder || { orderId }) === '99food') {
        if(newStatus==='finished'||newStatus==='cancelled') {
          setOrderDetails(currentOrder);setOrderModal({open:true,order:currentOrder});
          const external=Boolean(currentOrder.ninetyNineFood?.entrega?.operationId||currentOrder.ninetyNineFood?.externalCourier?.operationId);
          setNinetyNineDropAction({id:createCourierIntentId(),orderId,action:newStatus==='cancelled'?'cancel':Number(currentOrder.deliveryType)===0?'finish':external||Number(currentOrder.deliveryType)===1?'refresh':'verifyDeliveryCode'});
          return;
        }
        const response = await apiFetch(`${API_URL}/api/99food/orders/${encodeURIComponent(orderId)}/status`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus,...manualBody }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.order) throw new Error(payload.error || 'Não foi possível alterar o pedido na 99Food.');
        if (!isDashboardOrderCurrent({ orderId })) return;
        mergeOrderSnapshot(payload.order);
        if(payload.manualStatus?.pending) setError('A alteração aguarda confirmação do Brutos Entregas. Atualize o pedido para conferir.');
        else if(['rejected','superseded'].includes(payload.manualStatus?.state)) setError('O Brutos Entregas não confirmou esta etapa. Confira a entrega antes de continuar.');
        if (!skipRefresh) await fetchOrders();
        return;
      }

      updateDashboard((prev) => ({
        ...prev,
        orders: (prev.orders || []).map((order) => (
          order.orderId === orderId
            ? {
                ...order,
                status: newStatus === 'cancelled' ? order.status : newStatus,
                ...(newStatus === 'ready' ? readyStatusMetadata : null),
                rawStatus: newStatus === 'enRoute'
                  ? 'DSP'
                  : newStatus === 'ready'
                    ? 'READY'
                    : String(newStatus || '').toUpperCase(),
                updatedAt: new Date().toISOString(),
              }
            : order
        )),
      }));

      const sourceKey = getOrderSourceKey(currentOrder || { orderId });
      const isBalcao = sourceKey === 'balcao' || String(orderId || '').startsWith('BALCAO-');
      const isSalao = sourceKey === 'salao' || String(orderId || '').startsWith('SALAO-');
      const isSite = sourceKey === 'site' || sourceKey === 'whatsapp' || String(orderId || '').startsWith('SITE-') || String(orderId || '').startsWith('BZ-');
      const isPedidos10 = sourceKey === 'pedidos10' || String(orderId || '').startsWith('PEDIDOS10-');
      if (isBalcao) {
        const response = await apiFetch(`${API_URL}/api/balcao/orders/${orderId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus, ...readyStatusMetadata,...manualBody }),
        });
        await readManualResult(response);
        if (!skipRefresh) await fetchOrders();
        return;
      }

      if (isSalao) {
        const response = await apiFetch(`${API_URL}/api/salon/orders/${orderId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus }),
        });
        if (!response.ok) {
          throw new Error('Falha ao alterar status do pedido do salão');
        }
        if (!skipRefresh) await fetchOrders();
        return;
      }

      if (isSite) {
        const response = await apiFetch(`${API_URL}/api/webhooks/site/orders/${orderId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus, ...readyStatusMetadata, ...cancellationStatusMetadata,...manualBody }),
        });
        await readManualResult(response);
        if (!skipRefresh) await fetchOrders();
        return;
      }

      if (isPedidos10) {
        const response = await apiFetch(`${API_URL}/api/integrations/pedidos10/orders/${orderId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus, ...readyStatusMetadata,...manualBody }),
        });
        await readManualResult(response);
        if (!skipRefresh) await fetchOrders();
        return;
      }

      const response = await apiFetch(`${API_URL}/api/ifood/orders/${orderId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newStatus, ...readyStatusMetadata,...manualBody }),
      });

      await readManualResult(response);

      // Após alterar o status no servidor, faz refresh dos dados para garantir sincronização
      if (!skipRefresh) await fetchOrders();
    } catch (err) {
      if (!skipRefresh) await fetchOrders();
      if (!silentError) {
        setError(`Erro ao alterar status: ${err.message}`);
      }
      if (options.throwOnError === true) {
        throw err;
      }
    }
  }, [buildCancellationStatusMetadata, buildReadyStatusMetadata, fetchOrders, isDashboardOrderCurrent, mergeOrderSnapshot, updateDashboard]);

  const openCancellationFlow = useCallback(async (order) => {
    if (!isDashboardOrderCurrent(order)) return;
    if (!order) return;
    if (getOrderSourceKey(order) === '99food') {
      setOrderDetails(order);
      setOrderModal({ open: true, order });
      return;
    }

    closeOrderModal();
    setCancellationModal({
      open: true,
      order,
      mode: 'ifood',
      reasons: [],
      loading: true,
      submitLoading: false,
      selectedReason: '',
      justification: '',
      error: '',
    });

    try {
      const externalBaseUrl = getExternalShippingBaseUrl(order);
      const reasonsEndpoint = isOutsideShippingOrder(order)
        ? externalBaseUrl && getExternalShippingOrderId(order)
          ? `${externalBaseUrl}/cancellationReasons`
          : `${API_URL}/api/ifood/shipping/orders/${order.orderId}/cancellationReasons`
        : `${API_URL}/api/ifood/orders/${order.orderId}/cancellationReasons`;
      const response = await apiFetch(reasonsEndpoint);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data.message || data.error || 'Falha ao buscar motivos de cancelamento';
        if (/already cancelled|ja foi cancelado|já foi cancelado/i.test(message)) {
          await fetchOrders(true, true);
          throw new Error('Este pedido já foi cancelado no iFood.');
        }
        throw new Error(message);
      }

      const reasons = normalizeCancellationReasons(data, {
        requireCancellationCode: isOutsideShippingOrder(order),
      });
      if (!reasons.length) {
        throw new Error('O iFood não retornou motivos de cancelamento para este pedido.');
      }

      setCancellationModal((prev) => ({
        ...prev,
        loading: false,
        reasons,
      }));
    } catch (err) {
      setCancellationModal((prev) => ({
        ...prev,
        loading: false,
        error: err.message,
      }));
    }
  }, [isDashboardOrderCurrent, closeOrderModal, fetchOrders, isOutsideShippingOrder]);

  const openLocalCancellationFlow = useCallback((order) => {
    if (!isDashboardOrderCurrent(order)) return;
    if (!order || !canCancelLocalOrder(order)) return;

    closeOrderModal();
    setCancellationModal({
      open: true,
      order,
      mode: 'local',
      reasons: LOCAL_ORDER_CANCELLATION_REASONS,
      loading: false,
      submitLoading: false,
      selectedReason: '',
      justification: '',
      error: '',
    });
  }, [isDashboardOrderCurrent, closeOrderModal]);

  const submitCancellationRequest = useCallback(async () => {
    if (!isDashboardOrderCurrent(cancellationModal.order)) return;
    const orderId = cancellationModal.order?.orderId;
    const selectedReason = cancellationModal.reasons.find(
      (reason) => reason.code === cancellationModal.selectedReason
    );

    if (!orderId) {
      setCancellationModal((prev) => ({
        ...prev,
        error: 'Pedido invalido para cancelamento.',
      }));
      return;
    }

    if (!selectedReason) {
      setCancellationModal((prev) => ({
        ...prev,
        error: 'Selecione o motivo de cancelamento.',
      }));
      return;
    }

    const justification = cancellationModal.justification.trim();
    const isLocalCancel = cancellationModal.mode === 'local';
    const isShippingCancel = isOutsideShippingOrder(cancellationModal.order);
    const shippingCancellationCode = String(
      selectedReason.cancellationCode ||
      selectedReason.cancelCodeId ||
      selectedReason.code ||
      ''
    ).trim();
    if (selectedReason.requiresJustification && !justification) {
      setCancellationModal((prev) => ({
        ...prev,
        error: 'Este motivo exige uma justificativa.',
      }));
      return;
    }

    if (isShippingCancel && !shippingCancellationCode) {
      setCancellationModal((prev) => ({
        ...prev,
        error: 'O iFood não retornou código válido para este motivo.',
      }));
      return;
    }

    setCancellationModal((prev) => ({
      ...prev,
      submitLoading: true,
      error: '',
    }));

    try {
      if (isLocalCancel) {
        await changeOrderStatus(orderId, 'cancelled', {
          throwOnError: true,
          skipRefresh: true,
          cancellationReason: selectedReason.code,
          cancellationReasonDescription: selectedReason.description,
          cancellationJustification: justification,
        });
        closeCancellationModal();
        setError(null);
        await fetchOrders(true, true);
        return;
      }

      const externalBaseUrl = getExternalShippingBaseUrl(cancellationModal.order);
      const response = await apiFetch(
        isShippingCancel
          ? externalBaseUrl && getExternalShippingOrderId(cancellationModal.order)
            ? `${externalBaseUrl}/cancel`
            : `${API_URL}/api/ifood/shipping/orders/${orderId}/cancel`
          : `${API_URL}/api/ifood/orders/${orderId}/requestCancellation`,
        {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(isShippingCancel
          ? {
              cancellationCode: shippingCancellationCode,
              reason: selectedReason.description || justification,
              justification,
            }
          : {
              reason: selectedReason.code,
              justification,
            }),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Falha ao solicitar cancelamento');
      }

      closeCancellationModal();
      setError(null);
      await fetchOrders(true, true);
    } catch (err) {
      setCancellationModal((prev) => ({
        ...prev,
        submitLoading: false,
        error: err.message,
      }));
    }
  }, [isDashboardOrderCurrent, cancellationModal, changeOrderStatus, closeCancellationModal, fetchOrders, isOutsideShippingOrder]);

  const openIfoodWidgetForNegotiation = useCallback((order) => {
    const negotiation = order?.negotiation;
    if (!order || !negotiation?.active) return;

    showIfoodWidget({ tenantKey: currentTenantKey }).catch((error) => {
      console.error('[iFood Widget] Falha ao abrir widget para negociação:', error);
    });
  }, [currentTenantKey]);

  const handleReconnectIfoodWidget = useCallback(async () => {
    if (ifoodWidgetLoading || ifoodWidgetStatus.state === 'offline') return;
    setIfoodWidgetLoading(true);
    setIfoodWidgetStatus({
      state: 'loading',
      message: 'Reconectando widget iFood.',
    });

    try {
      const result = await showIfoodWidget({
        autoShow: true,
        forceReconnect: true,
        refreshConfig: true,
        tenantKey: currentTenantKey,
      });
      setIfoodWidgetStatus({
        state: 'ready',
        message: `Widget iFood reconectado para ${result?.merchantCount || 0} loja(s).`,
      });
    } catch (err) {
      setIfoodWidgetStatus({
        state: 'error',
        message: err?.message || 'Falha ao reconectar widget iFood.',
      });
      setError(err?.message || 'Falha ao reconectar widget do iFood.');
      console.error('[iFood Widget] Falha ao reconectar widget:', err);
    } finally {
      setIfoodWidgetLoading(false);
    }
  }, [currentTenantKey, ifoodWidgetLoading, ifoodWidgetStatus.state]);

  const recordIfoodPrintEvent = useCallback((order, payload = {}) => {
    if (!order?.orderId || getOrderSourceKey(order) !== 'ifood'
      || !getTenantStorageKey(PRINTER_SETTINGS_KEY, settingsSession)) return;
    recordAutomaticIfoodPrintEvent(order, payload, settingsSession);
  }, [settingsSession]);

  const handlePrintComanda = useCallback(async (order, options = {}) => {
    if (!order) return { ok: false, error: 'Pedido nao informado.' };
    let connection = null;
    let manualToken = null;
    const manualStartedAt = Date.now();
    const canContinue = () => isDashboardOrderCurrent(order)
      && Boolean(getTenantStorageKey(PRINTER_SETTINGS_KEY, settingsSession))
      && (!manualToken || getAutoPrintRegistryEntry(order, String(settingsSession.user?.companyId || ''))?.token === manualToken)
      && (!connection || isCibdStationConnectionCurrent(connection, {
        baseUrl: SYSTEM_PRINT_BASE_URL,
        session: settingsSession,
      }));
    if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
    const printTrigger = options.trigger || 'manual';
    const printAttempt = options.attempt || null;
    try {
      const printerConfig = loadPrinterSettings();
      connection = printerConfig.cibdConnection;
      if (!canContinue()) return { ok: false, cancelled: true, error: 'Conexão do CIBD alterada antes da impressão.' };
      const availablePoints = getActivePrintPoints(printerConfig);
      const requestedPointIds = Array.isArray(options.printPointIds) ? options.printPointIds : null;
      const printPoints = requestedPointIds
        ? availablePoints.filter((point) => requestedPointIds.includes(point.id))
        : availablePoints;
      const detailedOrder = options.orderSnapshot || (orderDetails && orderDetails.orderId === order.orderId ? orderDetails : order);
      const sourceKey = getOrderSourceKey(detailedOrder || order);
      const isBalcao = sourceKey === 'balcao' || String(order.orderId || '').startsWith('BALCAO-');
      const isSalao = isSalonOrder(order) || sourceKey === 'salao';
      const isWhatsapp = sourceKey === 'whatsapp';
      const isSite = sourceKey === 'site' || String(order.orderId || '').startsWith('SITE-');
      const isPedidos10 = sourceKey === 'pedidos10' || String(order.orderId || '').startsWith('PEDIDOS10-');
      const isIfood = sourceKey === 'ifood';

      let itemsToRender = sourceKey === '99food' ? (detailedOrder.items || order.items || [])
        : (detailedOrder.items || order.items || []).map((item) => applyCatalogToItem(item));
      if (isIfood) {
        try {
          const printCatalog = await fetchPrintCatalog();
          if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
          itemsToRender = projectIfoodPrintItems(detailedOrder, printCatalog);
        } catch {
          const message = 'Não foi possível validar o cardápio local. A comanda não foi enviada. Tente novamente.';
          setError(message);
          return { ok: false, catalogUnavailable: true, error: message };
        }
      }

      if (!printPoints.length) {
        setError('Selecione uma impressora em um ponto de impressão ativado antes de imprimir.');
        if (isIfood && canContinue()) {
          await recordIfoodPrintEvent(detailedOrder, {
            status: 'failed',
            trigger: printTrigger,
            attempt: printAttempt,
            printerId: '',
            bridge: SYSTEM_PRINT_BASE_URL,
            error: 'Nenhum ponto de impressão ativado possui impressora selecionada.',
          });
        }
        return { ok: false, error: 'Nenhum ponto de impressão ativado possui impressora selecionada.' };
      }
      const totalInfo = getCommercialTotal(detailedOrder);
      const pagamento = getCommercialPayments(detailedOrder);
      const customer = detailedOrder.customer || order.customer || {};
      const fullCustomer = detailedOrder.fullOrderData?.customer || {};
      const orderNumber = String(detailedOrder.displayId || detailedOrder.orderId || '');
      const salesChannel =
        sourceKey === '99food' ? '99Food' : isSalao ? 'Salão' : isBalcao ? 'Balcão' : isWhatsapp ? 'WhatsApp' : isSite ? 'Site' : isPedidos10 ? 'Pedidos10' : (detailedOrder.salesChannel || 'iFood');

      const resolveCustomerName = () => {
        if (typeof customer === 'object' && customer.name) return customer.name;
        if (typeof customer === 'string') return customer;
        if (fullCustomer.name) return fullCustomer.name;
        return 'Cliente';
      };

      const resolveCustomerDocument = () => {
        return (
          customer.documentNumber ||
          customer.cpf ||
          fullCustomer.documentNumber ||
          fullCustomer.cpf ||
          ''
        );
      };

      const resolveCustomerPhone = () => {
        if (sourceKey === '99food') return customer.providerVirtualPhone || getCustomerPhoneNumber(customer.phone) || '';
        const candidates = isIfood
          ? [
              getCustomerPhoneNumber(customer.phone),
              getCustomerPhoneNumber(fullCustomer.phone),
              customer.phoneNumber,
              fullCustomer.phoneNumber,
              customer.contactPhone,
              fullCustomer.contactPhone,
              customer.whatsapp,
              fullCustomer.whatsapp,
            ]
          : [
              customer.whatsapp,
              getCustomerPhoneNumber(customer.phone),
              customer.phoneNumber,
              getCustomerPhoneNumber(fullCustomer.phone),
              fullCustomer.phoneNumber,
            ];
        return candidates.map((candidate) => String(candidate || '').trim()).find(Boolean) || '';
      };

      const resolveCustomerLocalizer = () => {
        if (sourceKey === '99food') return customer.providerLocator || getCustomerPhoneLocalizer(customer.phone) || '';
        const candidates = [
          getCustomerPhoneLocalizer(customer.phone),
          getCustomerPhoneLocalizer(fullCustomer.phone),
          customer.localizer,
          fullCustomer.localizer,
          customer.localizador,
          fullCustomer.localizador,
        ];
        return candidates.map((candidate) => String(candidate || '').trim()).find(Boolean) || '';
      };

      const resolveAddress = () => {
        if (sourceKey === '99food') return address99(detailedOrder);
        const deliveryAddress =
          detailedOrder.delivery?.deliveryAddress ||
          detailedOrder.fullOrderData?.delivery?.deliveryAddress ||
          detailedOrder.fullOrderData?.deliveryAddress ||
          detailedOrder.deliveryAddress ||
          order.delivery?.deliveryAddress ||
          order.fullOrderData?.delivery?.deliveryAddress ||
          order.fullOrderData?.deliveryAddress ||
          order.deliveryAddress ||
          '';
        const customerAddress = typeof customer === 'object'
          ? (customer.address || customer.addressInfo || customer.deliveryAddress || '')
          : '';
        const fullCustomerAddress = typeof fullCustomer === 'object'
          ? (fullCustomer.address || fullCustomer.addressInfo || fullCustomer.deliveryAddress || '')
          : '';
        const preferredCustomerAddress = customerAddress || fullCustomerAddress;
        let addressCandidate = preferredCustomerAddress || deliveryAddress;

        if (preferredCustomerAddress && deliveryAddress && typeof deliveryAddress === 'object') {
          addressCandidate = typeof preferredCustomerAddress === 'object'
            ? { ...deliveryAddress, ...preferredCustomerAddress }
            : {
                ...deliveryAddress,
                formattedAddress: preferredCustomerAddress,
                address: preferredCustomerAddress,
              };
        }

        return formatAddressForDisplay(addressCandidate) ? addressCandidate : '';
      };

      const orderDeliveryFee = parseAmount(totalInfo.deliveryFee || 0);
      const couponBreakdown = extractCouponBreakdown(detailedOrder);
      const orderDiscounts = couponBreakdown.totalAmount;
      const orderAdditionalFees = parseAmount(totalInfo.additionalFees || 0);
      const orderTotal = parseAmount(totalInfo.orderAmount || totalInfo.total || 0);
      const paymentAmounts = resolvePaymentAmounts(pagamento, orderTotal);
      const paymentMethodLabel = resolveComandaPaymentLabel(pagamento, detailedOrder);
      const resolvedAddress = resolveAddress();
      const formattedAddress = formatAddressForDisplay(resolvedAddress);
      const hasConfirmed = detailedOrder.history?.some(isOrderConfirmedEvent);
      const hasPaid = paymentAmounts.paid > 0;
      if (isIfood && detailedOrder.status === 'preparing' && !hasConfirmed && !hasPaid) {
        setError('Pedido ainda não confirmado ou pago para impressão.');
        return { ok: false, error: 'Pedido ainda não confirmado ou pago para impressão.' };
      }

      let cibdStatus = null;
      try {
        cibdStatus = await checkCibdStatus({ baseUrl: printerConfig.cibdBaseUrl });
      } catch (preflightError) {
        const message = preflightError?.message
          || 'Não foi possível acessar o CIBD. Verifique se o CIBD está aberto e permita o acesso à rede/dispositivos locais no navegador.';
        const pointResults = printPoints.map((point) => ({
          ok: false,
          pointId: point.id,
          pointName: point.name,
          printerId: point.printerId,
          error: message,
        }));
        if (isIfood && canContinue()) {
          await Promise.all(pointResults.map((result) => recordIfoodPrintEvent(detailedOrder, {
            status: 'failed',
            trigger: printTrigger,
            attempt: printAttempt,
            printerId: result.printerId,
            printPointId: result.pointId,
            printPointName: result.pointName,
            bridge: SYSTEM_PRINT_BASE_URL,
            error: result.error,
          })));
        }
        setError(message);
        return { ok: false, error: message, pointResults };
      }

      const createdAtLabel = detailedOrder.createdAt
        ? new Date(detailedOrder.createdAt).toLocaleString('pt-BR')
        : new Date().toLocaleString('pt-BR');
      const deliveryTimeLabel = detailedOrder?.delivery?.deliveryDateTime
        ? new Date(detailedOrder.delivery.deliveryDateTime).toLocaleString('pt-BR')
        : '';

      if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
      const JsPdf = await loadJsPdf();
      if (!canContinue()) return { ok: false, cancelled: true, error: 'Sessão alterada antes da impressão.' };
      const pdfDocument = buildComandaPdfBase64({
        JsPdf,
        printerConfig: { ...printerConfig, paperWidthMm: cibdStatus?.paperWidthMm },
        data: {
          orderNumber,
          officialOrderId: getNinetyNineFoodOfficialId(detailedOrder),
          createdAt: createdAtLabel,
          salesChannel,
          customerName: resolveCustomerName(),
          customerDocument: resolveCustomerDocument(),
          customerPhone: resolveCustomerPhone(),
          merchantName: detailedOrder.merchant?.name || '',
          localizer: resolveCustomerLocalizer(),
          address: resolvedAddress,
          deliveryMode:
            detailedOrder.fulfillment === 'delivery'
              ? 'Entrega'
              : detailedOrder.fulfillment === 'pickup'
                ? 'Retirada'
                : detailedOrder.delivery?.mode || '',
          pickupCode: sourceKey === '99food' ? getNinetyNineFoodPickupInfo(detailedOrder).code : detailedOrder.delivery?.pickupCode || '',
          ...(sourceKey === '99food' ? { pickupCodeLabel: getNinetyNineFoodPickupInfo(detailedOrder).label } : {}),
          deliveryTime: deliveryTimeLabel,
          items: itemsToRender,
          ninetyNineFoodInstructions: operational99Rows(detailedOrder, formatCurrency, ['instructions']),
          ninetyNineFoodPrices: operational99Rows(detailedOrder, formatCurrency, ['price']),
          ninetyNineFoodChange: operational99Rows(detailedOrder, formatCurrency, ['payment']),
          notes: printerConfig.comandaShowNotes
            ? getComandaOrderNotes(detailedOrder)
            : '',
          total: orderTotal,
          totalUnknown: isNinetyNineFoodTotalUnknown(detailedOrder),
          deliveryFee: orderDeliveryFee,
          discounts: orderDiscounts,
          discountIfood: couponBreakdown.ifoodAmount,
          discountMerchant: couponBreakdown.merchantAmount,
          additionalFees: orderAdditionalFees,
          paymentMethod: paymentMethodLabel,
          paymentMethods: amendmentPaymentLines(detailedOrder),
          paymentPrepaid: paymentAmounts.paid,
          paymentPending: paymentAmounts.pending,
          paymentToCollect: hasCommercialAmendment(detailedOrder) ? Number(pagamento.deliveryAmountDue || 0) : undefined,
          paymentMerchantPending: hasCommercialAmendment(detailedOrder) ? Math.max(0, paymentAmounts.pending - Number(pagamento.deliveryAmountDue || 0)) : undefined,
          amendmentRevision: detailedOrder.amendmentRevision, amendmentReason: detailedOrder.amendment?.reason,
          refundPendingAmount: pagamento.refundPendingAmount, totalRefunded: pagamento.totalRefunded,
        },
      });

      if (!pdfDocument?.pdfBase64) {
        setError('PDF vazio. Recarregue a pagina e tente novamente.');
        return { ok: false, error: 'PDF vazio.' };
      }
      const { pdfBase64, pdfPaper } = pdfDocument;

      const orderFallback = isNinetyNineFoodTotalUnknown(detailedOrder) ? undefined : {
        id: orderNumber,
        salesChannel,
        createdAt: Number(detailedOrder.createdAt || Date.now()),
        customer: {
          name: resolveCustomerName(),
          phone: resolveCustomerPhone(),
          localizer: resolveCustomerLocalizer(),
          ...(formattedAddress ? { address: formattedAddress } : {}),
        },
        delivery: {
          fee: orderDeliveryFee,
          pickupCode: sourceKey === '99food' ? getNinetyNineFoodPickupInfo(detailedOrder).code : detailedOrder.delivery?.pickupCode || '',
          ...(formattedAddress ? { address: formattedAddress, deliveryAddress: resolvedAddress } : {}),
        },
        items: sourceKey === '99food' ? fallback99Items(itemsToRender) : itemsToRender.map((item) => ({
          name: item.name || '',
          quantity: Number(item.quantity || 1),
          unitPrice: parseAmount(item.unitPrice || 0),
          totalPrice: parseAmount(item.totalPrice || item.unitPrice || 0),
          observations: item.observations || '',
          options: (item.options || []).map((opt) => ({
            name: opt.name || '',
            quantity: Number(opt.quantity || 1),
            price: parseAmount(opt.price || 0),
          })),
        })),
        observations: [Number(detailedOrder.amendmentRevision) > 0 ? `PEDIDO ALTERADO - Revisão ${detailedOrder.amendmentRevision}. Esta comanda substitui a versão anterior. ${detailedOrder.amendment?.reason || ''}` : '',
          sourceKey === '99food' && printerConfig.comandaShowOrderNumber && getNinetyNineFoodOfficialId(detailedOrder) ? `ID 99Food: ${getNinetyNineFoodOfficialId(detailedOrder)}` : '',
          sourceKey === '99food' ? (printerConfig.comandaShowNotes ? getOrderGeneralNotes(detailedOrder) : '') : detailedOrder.observations || '',
          ...(printerConfig.comandaShowPayment ? amendmentPaymentTexts(detailedOrder) : [])].filter(Boolean).join('\n'),
        amendmentRevision: detailedOrder.amendmentRevision,
        total: orderTotal,
        payment: {
          method: paymentMethodLabel,
          methods: hasCommercialAmendment(detailedOrder) ? amendmentPaymentLines(detailedOrder) : undefined,
          prepaid: paymentAmounts.paid,
          pending: paymentAmounts.pending,
          amountToCollect: pagamento.deliveryAmountDue, merchantPending: hasCommercialAmendment(detailedOrder) ? Math.max(0, paymentAmounts.pending - Number(pagamento.deliveryAmountDue || 0)) : undefined,
          refundPendingAmount: pagamento.refundPendingAmount, totalRefunded: pagamento.totalRefunded,
        },
      };

      if (printTrigger === 'manual') {
        const manualTenantKey = String(settingsSession.user?.companyId || '');
        const currentPrint = getAutoPrintRegistryEntry(order, manualTenantKey);
        const currentIsActive = Date.now() - Number(currentPrint?.updatedAt || 0) < AUTO_PRINT_STALE_QUEUE_MS
          && ((currentPrint?.status === 'queued' && currentPrint.phase === 'dispatching')
            || (currentPrint?.status === 'unknown' && currentPrint.phase === 'manual-in-progress'));
        if (currentIsActive) {
          const message = 'Impressão deste pedido em andamento. Aguarde o resultado antes de reenviar.';
          setError(message);
          return { ok: false, cancelled: true, error: message };
        }
        const needsReview = currentPrint?.status === 'needs-review' || currentPrint?.status === 'unknown'
          || (currentPrint?.status === 'queued' && currentPrint.phase === 'dispatching')
          || Object.values(currentPrint?.pointStates || {}).some((point) => point?.status === 'unknown');
        const reviewConfirmed = !needsReview || window.confirm(
          'Confira todos os pontos: a comanda pode já ter saído no papel. Confirma que deseja reenviar manualmente?'
        );
        if (!reviewConfirmed) return { ok: false, cancelled: true, error: 'Reenvio manual cancelado.' };
        manualToken = await reserveManualPrintExclusively(order, manualTenantKey, manualStartedAt, reviewConfirmed);
        if (!manualToken || !canContinue()) {
          const message = 'Outra impressão deste pedido foi iniciada. Confira o papel antes de reenviar.';
          setError(message);
          return { ok: false, cancelled: true, error: message };
        }
      }

      if (isIfood && canContinue()) {
        await Promise.all(printPoints.map((point) => recordIfoodPrintEvent(detailedOrder, {
          status: 'queued',
          trigger: printTrigger,
          attempt: printAttempt,
          printerId: point.printerId,
          // Campos adicionais são ignorados por versões antigas da telemetria.
          printPointId: point.id,
          printPointName: point.name,
          bridge: SYSTEM_PRINT_BASE_URL,
        })));
      }

      const settledPrintResults = await Promise.allSettled(printPoints.map(async (point) => {
        if (!canContinue()) throw new Error('Sessão alterada antes da impressão.');
        await sendCibdPrint({
          baseUrl: printerConfig.cibdBaseUrl,
          headers: buildCibdHeaders(printerConfig),
          body: JSON.stringify({ printerId: point.printerId, pdfBase64, pdfPaper, order: orderFallback,
            ...(sourceKey === '99food' ? { forceExternalPdf: true } : {}) }),
          fetchImpl: (...args) => {
            if (!canContinue()) throw new Error('Sessão alterada antes da impressão.');
            return fetch(...args);
          },
        });
        return { ok: true, pointId: point.id, pointName: point.name, printerId: point.printerId };
      }));
      const printResults = settledPrintResults.map((settled, index) => {
        if (settled.status === 'fulfilled') return settled.value;
        const point = printPoints[index];
        return {
          ok: false,
          pointId: point.id,
          pointName: point.name,
          printerId: point.printerId,
          error: settled.reason?.message || 'Falha ao enviar para a impressora.',
        };
      });
      if (printTrigger === 'manual' && canContinue()) {
        const tenantKey = String(settingsSession.user?.companyId || '');
        const previous = getAutoPrintRegistryEntry(order, tenantKey);
        const pointStates = mergeAutoPrintPointStates(previous?.pointStates || {}, printResults.map((result) => ({
          ...result, dispatchStarted: true,
        })));
        const expected = getAutoPrintPointsForOrder(detailedOrder, availablePoints, isSalonOrder(detailedOrder));
        const allAccepted = expected.length > 0 && expected.every((point) => pointStates[point.id]?.status === 'sent');
        markAutoPrintOrder(order, allAccepted ? 'sent' : 'unknown', {
          pointStates,
          phase: allAccepted ? 'complete' : 'manual-review',
          lastError: allAccepted ? '' : 'Confira os pontos sem aceite antes de reenviar.',
        }, tenantKey);
      }
      if (isIfood && canContinue()) {
        await Promise.all(printResults.map((result) => recordIfoodPrintEvent(detailedOrder, {
          status: result.ok ? 'sent' : 'failed',
          trigger: printTrigger,
          attempt: printAttempt,
          printerId: result.printerId,
          printPointId: result.pointId,
          printPointName: result.pointName,
          bridge: SYSTEM_PRINT_BASE_URL,
          ...(result.ok ? {} : { error: result.error }),
        })));
      }
      const failedResults = printResults.filter((result) => !result.ok);
      if (failedResults.length) {
        const error = failedResults.map((result) => `${result.pointName}: ${result.error}`).join(' | ');
        setError(error);
        return { ok: false, error, pointResults: printResults };
      }
      setError(null);
      return { ok: true, pointResults: printResults };
    } catch (err) {
      console.error('Erro ao imprimir comanda', err);
      const message = err.message || 'Não foi possível imprimir a comanda. Verifique o CIBD e tente novamente.';
      setError(`Erro ao enviar comanda para o CIBD: ${message}`);
      if (canContinue()) await recordIfoodPrintEvent(order, {
        status: 'failed',
        trigger: printTrigger,
        attempt: printAttempt,
        bridge: SYSTEM_PRINT_BASE_URL,
        error: message,
      });
      return { ok: false, error: message };
    }
  }, [applyCatalogToItem, isDashboardOrderCurrent, loadPrinterSettings, orderDetails, recordIfoodPrintEvent, settingsSession]);

  const amendmentRequest = useCallback(async (path, method = 'GET', body) => {
    if (!isDashboardSessionCurrent()) throw new Error('Sessão alterada.');
    const response = await apiFetch(`${API_URL}${path}`, {
      method, cache: 'no-store', ...(body ? { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } } : {}),
    });
    const data = await response.json();
    if (!isDashboardSessionCurrent()) throw new Error('Sessão alterada.');
    if (!response.ok || data.success === false) {
      const requestError = new Error(data.error || 'Falha ao atualizar a comanda alterada.');
      requestError.code = data.code;
      throw requestError;
    }
    return data;
  }, [isDashboardSessionCurrent]);

  const amendmentPrinter = useMemo(() => createAmendmentPrinter({
    request: amendmentRequest,
    getPoints: (order) => getAutoPrintPointsForOrder(order, getActivePrintPoints(loadPrinterSettings()), isSalonOrder(order)),
    prepareCatalog: (source) => source === 'ifood' ? fetchPrintCatalog() : Promise.resolve(null),
    print: (order, options) => printAutomaticComanda(getAmendmentPrintIdentity(order), { ...options, session: settingsSession }),
    isCurrent: isDashboardSessionCurrent,
    confirmUnknown: () => window.confirm('A comanda pode já ter saído no papel. Confira os pontos antes de confirmar o reenvio manual.'),
  }), [amendmentRequest, isDashboardSessionCurrent, loadPrinterSettings, settingsSession]);

  const printAmendment = useCallback(async (order, options = {}) => {
    const source = options.source || getOrderSourceKey(order);
    const revision = options.revision || order.amendmentRevision;
    const key = `${currentTenantKey}:${source}:${order.orderId}:${revision}`;
    if (amendmentPrintInFlightRef.current.has(key)) return { ok: false, busy: true };
    amendmentPrintInFlightRef.current.add(key);
    try {
      const result = await amendmentPrinter({ source, orderId: order.orderId, revision, order, ...options });
      if (isDashboardSessionCurrent() && !result.ok) setAmendmentPrintNotice(result.error || 'A impressão da comanda alterada permanece pendente.');
      return result;
    } catch (printError) {
      if (isDashboardSessionCurrent()) setAmendmentPrintNotice(printError.message);
      return { ok: false, error: printError.message };
    } finally {
      amendmentPrintInFlightRef.current.delete(key);
    }
  }, [amendmentPrinter, currentTenantKey, isDashboardSessionCurrent]);

  const handleOrderEdited = useCallback(async (data) => {
    if (!isDashboardSessionCurrent()) return;
    const order = data.order;
    const canonicalSource = (known) => getCommercialOrderSource(known) || (getOrderSourceKey(known) === 'whatsapp' ? 'site' : getOrderSourceKey(known));
    const sameOrder = (known) => known?.orderId === order.orderId && canonicalSource(known) === canonicalSource(order);
    const knownOrders = [
      ...(dashboardRef.current.orders || []), orderDetails,
      orderDetailsCacheRef.current.get(String(order.orderId))?.data,
    ].filter(sameOrder);
    const visibleOrder = knownOrders.reduce((newest, known) => (
      Number(known.amendmentRevision || 0) > Number(newest.amendmentRevision || 0) ? known : newest
    ), order);
    orderDetailsAbortRef.current?.abort();
    orderDetailsAbortRef.current = null;
    orderDetailsInFlightRef.current.clear();
    setLoadingOrderDetails(false);
    mergeOrderSnapshot(visibleOrder, { matchCommercialSource: true });
    setOrderDetails((current) => sameOrder(current)
      && Number(current.amendmentRevision || 0) > Number(visibleOrder.amendmentRevision || 0) ? current : visibleOrder);
    setOrderEditTarget(null);
    setOrderModal((current) => sameOrder(current.order)
      ? { ...current, order: Number(current.order.amendmentRevision || 0) > Number(visibleOrder.amendmentRevision || 0) ? current.order : visibleOrder }
      : current);
    orderDetailsCacheRef.current.clear();
    setAmendmentPrintNotice('Pedido alterado. Preparando a nova comanda…');
    const result = await printAmendment(order);
    if (isDashboardSessionCurrent() && result.ok) setAmendmentPrintNotice(result.superseded
      ? 'Pedido alterado. Uma revisão mais recente já substituiu esta comanda.'
      : 'Pedido alterado e nova comanda enviada.');
    if (isDashboardSessionCurrent()) fetchOrders();
  }, [fetchOrders, isDashboardSessionCurrent, mergeOrderSnapshot, orderDetails, printAmendment]);

  // Persisted requests survive a closed browser. This recovery does not depend on ordinary auto-print being enabled.
  useEffect(() => {
    let cancelled = false;
    let running = false;
    const recover = async () => {
      if (cancelled || running || !isDashboardSessionCurrent()) return;
      running = true;
      try {
        const data = await amendmentRequest('/api/orders/amendment-print-requests');
        const requests = data.requests || data.printRequests || [];
        for (const pending of requests.slice(0, 10)) {
          if (cancelled || !isDashboardSessionCurrent()) break;
          if (pending.reviewOnly) {
            setAmendmentPrintNotice('Há uma comanda alterada com resultado incerto. Confira o papel e use Imprimir comanda no pedido para reenviar.');
            continue;
          }
          try {
            const context = await amendmentRequest(`/api/orders/${encodeURIComponent(pending.orderId)}/edit-context?source=${encodeURIComponent(pending.source)}`);
            await printAmendment(context.order, { source: pending.source, revision: pending.revision });
          } catch (requestError) {
            if (!cancelled && isDashboardSessionCurrent()) setAmendmentPrintNotice(requestError.message);
          }
        }
      } catch (recoveryError) {
        if (!cancelled && isDashboardSessionCurrent()) setAmendmentPrintNotice(recoveryError.message);
      } finally { running = false; }
    };
    recover();
    const timer = window.setInterval(recover, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [amendmentRequest, isDashboardSessionCurrent, printAmendment]);

  const handleManualPrintComanda = useCallback(async (order) => {
    if (!isDashboardOrderCurrent(order)) return;
    if (!order) return;
    const activePoints = getActivePrintPoints(loadPrinterSettings());
    if (activePoints.length <= 1) {
      if (manualPrintInFlightRef.current) return;
      manualPrintInFlightRef.current = true;
      setManualPrintLoading(true);
      try {
        await (hasCommercialAmendment(orderDetails?.orderId === order.orderId ? orderDetails : order)
          ? printAmendment(orderDetails?.orderId === order.orderId ? orderDetails : order, { manual: true })
          : handlePrintComanda(order, {
          trigger: 'manual',
          printPointIds: activePoints.map((point) => point.id),
        }));
      } finally {
        manualPrintInFlightRef.current = false;
        setManualPrintLoading(false);
      }
      return;
    }
    setPrintPointDialog({ open: true, order: orderDetails?.orderId === order.orderId ? orderDetails : order, points: activePoints, printing: false });
  }, [isDashboardOrderCurrent, handlePrintComanda, loadPrinterSettings, orderDetails, printAmendment]);

  const handlePrintAtSelectedPoint = useCallback(async (pointId) => {
    const { order } = printPointDialog;
    if (!order) return;
    setPrintPointDialog((prev) => ({ ...prev, printing: true }));
    const result = await (hasCommercialAmendment(order)
      ? printAmendment(order, { manual: true, printPointIds: [pointId] })
      : handlePrintComanda(order, { trigger: 'manual', printPointIds: [pointId] }));
    if (result?.ok) {
      setPrintPointDialog({ open: false, order: null, points: [], printing: false });
    } else {
      setPrintPointDialog((prev) => ({ ...prev, printing: false }));
    }
  }, [handlePrintComanda, printPointDialog, printAmendment]);

  const loadFiscalPreview = useCallback(async (order) => {
    if (!isDashboardSessionCurrent() || !canUseFiscalOrderModal || !order?.orderId || getOrderSourceKey(order) === '99food') return;
    setFiscalPanelOpen(true);
    setFiscalPreview(null);
    setFiscalIssueResult(null);
    setFiscalError('');
    setFiscalSuccess('');
    setFiscalLoadingPreview(true);
    try {
      const response = await apiFetch(`${API_URL}/api/fiscal/orders/${encodeURIComponent(order.orderId)}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: getOrderSourceKey(order), debug: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!isDashboardSessionCurrent()) return;
      if (!response.ok) {
        throw new Error(getFiscalResponseMessage(data, 'Falha ao gerar prévia fiscal.'));
      }
      setFiscalPreview(data);
      setFiscalSuccess('Prévia fiscal gerada em homologação.');
    } catch (err) {
      if (isDashboardSessionCurrent()) setFiscalError(err.message || 'Falha ao gerar prévia fiscal.');
    } finally {
      if (isDashboardSessionCurrent()) setFiscalLoadingPreview(false);
    }
  }, [canUseFiscalOrderModal, isDashboardSessionCurrent]);

  const openFiscalPanel = useCallback((order) => {
    if (!canUseFiscalOrderModal || !order?.orderId || getOrderSourceKey(order) === '99food') return;
    const detailedOrder = orderDetails && orderDetails.orderId === order.orderId ? orderDetails : order;
    loadFiscalPreview(detailedOrder);
  }, [canUseFiscalOrderModal, loadFiscalPreview, orderDetails]);

  const issueFiscalHomologation = useCallback(async () => {
    if (!isDashboardSessionCurrent()) return;
    if (!isDashboardOrderCurrent({ orderId: fiscalPreview?.orderId })) return;
    if (!canUseFiscalOrderModal || !fiscalPreview?.orderId || !fiscalPreview.canIssue) return;
    setFiscalIssuing(true);
    setFiscalError('');
    setFiscalSuccess('');
    try {
      const response = await apiFetch(`${API_URL}/api/fiscal/orders/${encodeURIComponent(fiscalPreview.orderId)}/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: fiscalPreview.source }),
      });
      const data = await response.json().catch(() => ({}));
      if (!isDashboardSessionCurrent()) return;
      if (!response.ok) {
        throw new Error(getFiscalResponseMessage(data, 'Falha ao emitir NFC-e em homologação.'));
      }
      setFiscalIssueResult(data);
      setFiscalSuccess('Emissão NFC-e de homologação processada.');
    } catch (err) {
      if (isDashboardSessionCurrent()) setFiscalError(err.message || 'Falha ao emitir NFC-e em homologação.');
    } finally {
      if (isDashboardSessionCurrent()) setFiscalIssuing(false);
    }
  }, [isDashboardOrderCurrent, isDashboardSessionCurrent, canUseFiscalOrderModal, fiscalPreview]);

  const downloadFiscalArtifact = useCallback(async (kind) => {
    if (!isDashboardSessionCurrent() || !canUseFiscalOrderModal || !fiscalIssueResult?.invoiceId) return;
    setFiscalError('');
    try {
      const response = await apiFetch(`${API_URL}/api/fiscal/invoices/${encodeURIComponent(fiscalIssueResult.invoiceId)}/${kind}`);
      if (!isDashboardSessionCurrent()) return;
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(getFiscalResponseMessage(data, `Falha ao baixar ${kind.toUpperCase()}.`));
      }
      const blob = await response.blob();
      if (!isDashboardSessionCurrent()) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileNameFromContentDisposition(
        response.headers.get('Content-Disposition'),
        kind === 'xml'
          ? `nfce-${fiscalIssueResult.invoiceId}.xml`
          : `danfe-${fiscalIssueResult.invoiceId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (isDashboardSessionCurrent()) setFiscalError(err.message || `Falha ao baixar ${kind.toUpperCase()}.`);
    }
  }, [canUseFiscalOrderModal, isDashboardSessionCurrent, fiscalIssueResult]);

  const getSelectedPreparingOrders = useCallback(() => {
    const ordersById = new Map(
      (dashboardRef.current?.orders || [])
        .map((order) => [String(order.orderId || ''), order])
    );
    return selectedPreparingOrderIds
      .map((orderId) => ordersById.get(orderId))
      .filter((order) => order && isDashboardOrderCurrent(order));
  }, [isDashboardOrderCurrent, selectedPreparingOrderIds]);

  const sendSelectedPreparingOrdersToKds = useCallback(async () => {
    const selectedOrders = getSelectedPreparingOrders();
    if (!selectedOrders.length || sendingSelectedToKds) return;

    setError(null);
    setSendingSelectedToKds(true);

    try {
      const response = await apiFetch(`${API_URL}/api/kds/orders/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orders: selectedOrders.map((order) => ({
            id: order.orderId,
            source: order.source,
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao enviar pedidos ao KDS.');
      }

      setSelectedPreparingOrderIds([]);
      setActiveBatchDrag(null);
      await fetchOrders();

      if (Array.isArray(data.ignored) && data.ignored.length) {
        const ignoredLabels = data.ignored
          .map((item) => item.displayId || item.orderId)
          .slice(0, 6)
          .join(', ');
        setError(`Alguns pedidos não foram enviados ao KDS: ${ignoredLabels}. ${data.ignored[0]?.reason || ''}`.trim());
      }
    } catch (err) {
      setError(`Erro ao enviar pedidos ao KDS: ${err.message}`);
      await fetchOrders();
    } finally {
      setSendingSelectedToKds(false);
    }
  }, [fetchOrders, getSelectedPreparingOrders, sendingSelectedToKds]);

  // Freeze the board geometry before the DnD library measures the destinations.
  const onBeforeCapture = useCallback(() => setIsBoardDragging(true), []);

  const onDragStart = useCallback((start) => {
    const draggableId = String(start?.draggableId || '');
    const sourceDroppableId = start?.source?.droppableId;
    const selectedOrders = getSelectedPreparingOrders();
    const draggedIsSelected = selectedOrders.some((order) => order.orderId === draggableId);

    if (sourceDroppableId === 'preparing' && draggedIsSelected && selectedOrders.length > 1) {
      setActiveBatchDrag({
        count: selectedOrders.length,
        labels: selectedOrders.slice(0, 4).map((order) => order.displayId || order.orderId),
      });
      return;
    }

    setActiveBatchDrag(null);
  }, [getSelectedPreparingOrders]);

  // Função para lidar com o drag and drop
  const onDragEnd = useCallback((result) => {
    const { destination, source, draggableId, reason } = result;
    setIsBoardDragging(false);
    setActiveBatchDrag(null);

    // Se foi cancelado ou não foi solto em lugar nenhum
    if (reason === 'CANCEL' || !destination) {
      return;
    }

    // Se foi solto na mesma posição
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const newStatus = destination.droppableId;
    const draggedOrder = (dashboardRef.current?.orders || []).find((order) => order.orderId === draggableId);
    if (isSalonOrder(draggedOrder || { orderId: draggableId }) && newStatus === 'enRoute') {
      setError('Pedidos de Mesa não passam pelo status Em rota. Use Pronto e depois Concluído.');
      return;
    }

    changeOrderStatus(draggableId, newStatus);
  }, [changeOrderStatus]);

  // Verifica se o pedido precisa ser aceito
  const needsAcceptance = useCallback((order) => {
    if (getOrderSourceKey(order) === '99food') return order.requiresAcceptance === true && order.canAccept !== false;
    // Pedidos que têm evento PLACED mas não têm CONFIRMED no histórico
    const hasPlaced = order.history?.some(isOrderPlacedEvent);
    const hasConfirmed = order.history?.some(isOrderConfirmedEvent);

    // Para testes: mostrar botão em pedidos de teste que começam com TEST- e não foram confirmados
    const orderId = String(order?.orderId || '');
    const isTestOrder = orderId.startsWith('TEST-');

    return Boolean(hasPlaced && !hasConfirmed && (isTestOrder || order.status === 'preparing'));
  }, []);

  const needsManualAcceptance = useCallback((order) => (
    needsAcceptance(order) && (!autoAccept || getOrderSourceKey(order) === '99food')
  ), [autoAccept, needsAcceptance]);

  useEffect(() => {
    const currentIds = new Set(
      (dashboard.orders || [])
        .map((order) => order?.orderId)
        .filter(Boolean)
    );
    const previousIds = previousOrderIdsRef.current;
    const newOrders = (dashboard.orders || []).filter(
      (order) => isDashboardOrderCurrent(order) && !previousIds.has(order.orderId)
    );
    previousOrderIdsRef.current = currentIds;

    if (newOrders.length > 0) {
      playNewOrderSound();
      const hasPendingAcceptance =
        newOrders.some(needsManualAcceptance) ||
        (dashboard.orders || []).some((order) => isDashboardOrderCurrent(order) && needsManualAcceptance(order));
      if (hasPendingAcceptance) startAlertLoop();
      else stopAlertLoop();
    }

    const pendingAcceptance = (dashboard.orders || []).some((order) => isDashboardOrderCurrent(order) && needsManualAcceptance(order));
    if (!pendingAcceptance) {
      stopAlertLoop();
    }
  }, [dashboard.orders, isDashboardOrderCurrent, needsManualAcceptance, playNewOrderSound, startAlertLoop, stopAlertLoop]);
  useEffect(() => {
    const refreshSetting = () => {
      setAutoPrint(readTenantLocalJson(AUTO_PRINT_KEY, false, settingsSession) === true);
      setAutoAccept(readTenantLocalJson(AUTO_ACCEPT_KEY, false, settingsSession) === true);
    };
    refreshSetting();
    window.addEventListener('storage', refreshSetting);
    window.addEventListener(AUTO_PRINT_SETTINGS_CHANGED_EVENT, refreshSetting);
    return () => {
      window.removeEventListener('storage', refreshSetting);
      window.removeEventListener(AUTO_PRINT_SETTINGS_CHANGED_EVENT, refreshSetting);
    };
  }, [settingsSession]);

  const handleAutoAcceptChange = (enabled) => {
    if (!writeTenantLocalJson(AUTO_ACCEPT_KEY, enabled, settingsSession)) return;
    setAutoAccept(enabled);
  };

  const handleAutoPrintChange = (enabled) => {
    if (!writeTenantLocalJson(AUTO_PRINT_KEY, enabled, settingsSession)) return;
    setAutoPrint(enabled);
    window.dispatchEvent(new Event(AUTO_PRINT_SETTINGS_CHANGED_EVENT));
  };

  useEffect(() => () => stopAlertLoop(), [stopAlertLoop]);

  const hasCompleteIfoodOrderDetails = useCallback((order = {}) => {
    const displayId = String(order?.displayId || order?.fullOrderData?.displayId || '').trim();
    const customerName = typeof order?.customer === 'object'
      ? String(order.customer?.name || '').trim()
      : String(order?.customer || '').trim();
    return Boolean(
      order?.fullOrderData &&
      displayId &&
      !displayId.includes('...') &&
      Array.isArray(order?.items) &&
      order.items.length > 0 &&
      customerName &&
      customerName !== 'Cliente iFood'
    );
  }, []);

  const fetchOrderDetails = useCallback(async (orderId) => {
    const cacheKey = String(orderId || '').trim();
    if (!cacheKey || !isDashboardSessionCurrent()) return null;

    const cached = orderDetailsCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.loadedAt < ORDER_DETAILS_CACHE_TTL_MS) {
      setOrderDetails(cached.data);
      setOrderDetailsError(cached.error || '');
      return cached.data;
    }

    if (orderDetailsInFlightRef.current.has(cacheKey)) {
      setLoadingOrderDetails(true);
      return orderDetailsInFlightRef.current.get(cacheKey);
    }

    orderDetailsAbortRef.current?.abort();
    const controller = new AbortController();
    orderDetailsAbortRef.current = controller;
    const assertDetailsCurrent = () => {
      if (controller.signal.aborted || orderDetailsAbortRef.current !== controller || !isDashboardSessionCurrent()) {
        throw Object.assign(new Error('Leitura do pedido substituída.'), { name: 'AbortError' });
      }
    };
    const publishDetails = (details) => { assertDetailsCurrent(); setOrderDetails(details); };
    setLoadingOrderDetails(true);
    setOrderDetailsError('');

    const request = (async () => {
      const sourceOrder = (dashboardRef.current?.orders || []).find((order) => order.orderId === orderId);
      const sourceKey = getOrderSourceKey(sourceOrder || { orderId });
      const isBalcao = sourceKey === 'balcao' || String(orderId || '').startsWith('BALCAO-');
      const isSalao = sourceKey === 'salao' || String(orderId || '').startsWith('SALAO-');
      const isSite = sourceKey === 'site' || sourceKey === 'whatsapp' || String(orderId || '').startsWith('SITE-') || String(orderId || '').startsWith('BZ-');
      const isPedidos10 = sourceKey === 'pedidos10' || String(orderId || '').startsWith('PEDIDOS10-');
      if (sourceKey === '99food') {
        const response = await apiFetch(`${API_URL}/api/99food/orders/${encodeURIComponent(orderId)}`, { signal: controller.signal });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.order) throw new Error(data.error || 'Erro ao buscar o pedido na 99Food.');
        publishDetails(data.order);
        return data.order;
      }
      if (isBalcao) {
        const response = await apiFetch(`${API_URL}/api/balcao/orders/${orderId}`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error('Erro ao buscar detalhes do pedido do balcão');
        }
        const data = await response.json();
        publishDetails(data.order);
        return data.order;
      }

      if (isSalao) {
        const response = await apiFetch(`${API_URL}/api/salon/orders/${orderId}`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error('Erro ao buscar detalhes do pedido do salão');
        }
        const data = await response.json();
        const nextDetails = {
          ...data.order,
          items: data.order?.items?.map((item) => applyCatalogToItem(item)) || data.order?.items,
        };
        publishDetails(nextDetails);
        return nextDetails;
      }

      if (isSite) {
        const response = await apiFetch(`${API_URL}/api/webhooks/site/orders/${orderId}`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error('Erro ao buscar detalhes do pedido do site/WhatsApp');
        }
        const data = await response.json();
        publishDetails(data.order);
        return data.order;
      }

      if (isPedidos10) {
        const response = await apiFetch(`${API_URL}/api/integrations/pedidos10/orders/${orderId}`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error('Erro ao buscar detalhes do pedido do Pedidos10');
        }
        const data = await response.json();
        const nextDetails = {
          ...data.order,
          items: data.order?.items?.map((item) => applyCatalogToItem(item)) || data.order?.items,
        };
        publishDetails(nextDetails);
        return nextDetails;
      }

      const response = await apiFetch(`${API_URL}/api/ifood/orders/${orderId}`, { signal: controller.signal });
      if (!response.ok) {
        throw new Error('Erro ao buscar detalhes do pedido');
      }

      let data = await response.json();
      data = {
        ...data,
        items: data.items?.map((item) => applyCatalogToItem(item)) || data.items,
      };
      publishDetails(data);

      if (hasCompleteIfoodOrderDetails(data)) {
        return data;
      }

      const fullResponse = await apiFetch(`${API_URL}/api/ifood/orders-api/${orderId}`, { signal: controller.signal });
      if (fullResponse.ok) {
        const fullData = await fullResponse.json();
        assertDetailsCurrent();
        data.fullOrderData = fullData.order;
        data.displayId = fullData.order?.displayId || data.displayId;
        data.customer = fullData.order?.customer || data.customer;
        if (!hasCommercialAmendment(data)) {
          data.items = fullData.order?.items || data.items;
          data.payments = fullData.order?.payments || data.payments;
          data.total = fullData.order?.total || data.total;
        }
        data.orderDetailsSync = { success: true };
      } else {
        data.orderDetailsSync = {
          success: false,
          status: fullResponse.status,
          error: 'Dados complementares do iFood indisponíveis.',
        };
        setOrderDetailsError('Dados complementares do iFood indisponíveis. Os dados locais foram mantidos.');
      }

      const nextDetails = {
        ...data,
        items: data.items?.map((item) => applyCatalogToItem(item)) || data.items,
      };
      publishDetails(nextDetails);
      return nextDetails;
    })();

    orderDetailsInFlightRef.current.set(cacheKey, request);

    try {
      const details = await request;
      assertDetailsCurrent();
      orderDetailsCacheRef.current.set(cacheKey, {
        data: details,
        error: details?.orderDetailsSync?.success === false ? 'Dados complementares do iFood indisponíveis. Os dados locais foram mantidos.' : '',
        loadedAt: Date.now(),
      });
      return details;
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Erro ao buscar detalhes do pedido:', err);
        setOrderDetailsError(err.message || 'Falha ao carregar detalhes do pedido.');
      }
      return null;
    } finally {
      if (orderDetailsInFlightRef.current.get(cacheKey) === request) orderDetailsInFlightRef.current.delete(cacheKey);
      if (orderDetailsAbortRef.current === controller) {
        orderDetailsAbortRef.current = null;
        setLoadingOrderDetails(false);
      }
    }
  }, [applyCatalogToItem, hasCompleteIfoodOrderDetails, isDashboardSessionCurrent]);

  const openShippingFlow = useCallback(async (order) => {
    const currentOrder = orderDetails?.orderId === order?.orderId ? { ...order, ...orderDetails } : order;
    if (!canUseIfoodShipping || !currentOrder?.orderId) return;

    setShippingModal({
      open: true,
      order: currentOrder,
      loading: true,
      requesting: false,
      error: '',
      availabilities: [],
      selectedQuoteId: '',
    });

    try {
      const response = await apiFetch(`${API_URL}/api/ifood/shipping/orders/${currentOrder.orderId}/deliveryAvailabilities`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao consultar disponibilidade da Entrega iFood.');
      }

      if (data.order) {
        mergeOrderSnapshot(data.order);
      }

      const availabilities = Array.isArray(data.availabilities) ? data.availabilities : [];
      setShippingModal((prev) => ({
        ...prev,
        order: data.order || currentOrder,
        loading: false,
        availabilities,
        selectedQuoteId: availabilities[0]?.quoteId || '',
        error: availabilities.length ? '' : 'Entrega iFood indisponivel para este pedido.',
      }));
    } catch (err) {
      setShippingModal((prev) => ({
        ...prev,
        loading: false,
        error: err.message || 'Falha ao consultar disponibilidade da Entrega iFood.',
      }));
    }
  }, [canUseIfoodShipping, mergeOrderSnapshot, orderDetails]);

  const submitShippingRequest = useCallback(async () => {
    if (!isDashboardOrderCurrent(shippingModal.order)) return;
    if (!canUseIfoodShipping) return;
    const orderId = shippingModal.order?.orderId;
    const quoteId = shippingModal.selectedQuoteId;
    if (!orderId) {
      setShippingModal((prev) => ({ ...prev, error: 'Pedido invalido para chamar entrega.' }));
      return;
    }
    if (!quoteId) {
      setShippingModal((prev) => ({ ...prev, error: 'Selecione uma cotacao de entrega.' }));
      return;
    }

    setShippingModal((prev) => ({ ...prev, requesting: true, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/shipping/orders/${orderId}/requestDriver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao chamar entregador iFood.');
      }

      if (data.order) {
        mergeOrderSnapshot(data.order);
      }
      closeShippingModal();
      setError(null);
      await fetchOrders(false, true);
      await fetchOrderDetails(orderId);
    } catch (err) {
      setShippingModal((prev) => ({
        ...prev,
        requesting: false,
        error: err.message || 'Falha ao chamar entregador iFood.',
      }));
    }
  }, [isDashboardOrderCurrent, canUseIfoodShipping, closeShippingModal, fetchOrderDetails, fetchOrders, mergeOrderSnapshot, shippingModal]);

  const fetchCourierQuotes = useCallback(async (sourceOrder, merchantId = '') => {
    const source = getExternalOrderSource(sourceOrder);
    const orderId = String(sourceOrder?.orderId || '').trim();
    if (!source || !orderId || !isDashboardOrderCurrent(sourceOrder)) {
      setCourierQuoteModal((previous) => ({ ...previous, loading: false, error: 'Pedido não validado para cotação.' }));
      return;
    }
    const requestId = ++courierQuoteRequestRef.current;
    setCourierQuoteModal((previous) => ({
      ...previous,
      loading: true,
      quotes: [],
      errors: [],
      selectedQuoteId: '',
      error: '',
      capabilities: {},
      confirmBee: false,
    }));
    try {
      const response = await apiFetch(
        `${API_URL}/api/courier/orders/${encodeURIComponent(source)}/${encodeURIComponent(orderId)}/quotes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(merchantId ? { merchantId } : {}),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.message || 'Não foi possível cotar a entrega.');
      if (requestId !== courierQuoteRequestRef.current || !isDashboardOrderCurrent(sourceOrder)) return;
      setCourierQuoteModal((previous) => ({
        ...previous,
        loading: false,
        quotes: Array.isArray(data.quotes) ? data.quotes.filter((quote) => quote?.id && quote?.provider) : [],
        errors: Array.isArray(data.errors) ? data.errors : [],
        capabilities: data.capabilities && typeof data.capabilities === 'object' ? data.capabilities : {},
      }));
    } catch (quoteError) {
      if (requestId !== courierQuoteRequestRef.current || !isDashboardSessionCurrent()) return;
      setCourierQuoteModal((previous) => ({
        ...previous,
        loading: false,
        error: quoteError.message || 'Não foi possível cotar a entrega.',
      }));
    }
  }, [isDashboardOrderCurrent, isDashboardSessionCurrent]);

  const openCourierQuoteFlow = (sourceOrder) => {
    if (!canRequestCourierForOrder(sourceOrder)) return;
    const merchantId = outsideShippingMerchantOptions[0]?.id || '';
    setCourierQuoteModal({
      open: true,
      order: sourceOrder,
      merchantId,
      loading: false,
      quotes: [],
      errors: [],
      selectedQuoteId: '',
      error: '',
      capabilities: {},
      hiringBee: false,
      confirmBee: false,
      hireAttempted: beeHireAttemptedKeysRef.current.has(getCourierOrderKey(sourceOrder)),
    });
    void fetchCourierQuotes(sourceOrder, merchantId);
  };

  const openOutsideShippingFlow = useCallback((sourceOrder = null, preferredMerchantId = '') => {
    if (!canUseOutsideShipping) return;
    const merchantId = outsideShippingMerchantOptions.some((merchant) => merchant.id === preferredMerchantId)
      ? preferredMerchantId
      : outsideShippingMerchantOptions[0]?.id || ifoodShippingMerchantIds[0] || '';
    setOutsideShippingModal({
      open: true,
      sourceOrder,
      form: { ...(sourceOrder
        ? createOutsideShippingFormFromOrder(sourceOrder, merchantId)
        : createOutsideShippingForm(merchantId)), draftId:createCourierIntentId() },
      loadingQuote: false,
      geocoding: false,
      creating: false,
      error: '',
      availabilities: [],
      selectedQuoteId: '',
      geocodeResult: null,
      addressSuggestions: [],
      loadingAddressSuggestions: false,
    });
  }, [canUseOutsideShipping, ifoodShippingMerchantIds, outsideShippingMerchantOptions]);

  const continueWithIfoodCourier = () => {
    const order = courierQuoteModal.order;
    if (!order || courierQuoteModal.capabilities?.canHireIfood === false || !canRequestOutsideShippingForOrder(order) || !isDashboardOrderCurrent(order)) return;
    closeCourierQuoteModal();
    openOutsideShippingFlow(order, courierQuoteModal.merchantId);
  };

  const refreshBeeDeliveryStatus = useCallback(async (sourceOrder) => {
    const orderKey = getCourierOrderKey(sourceOrder);
    if (!orderKey || !isDashboardOrderCurrent(sourceOrder)) return;
    const source = getExternalOrderSource(sourceOrder);
    const orderId = String(sourceOrder.orderId).trim();
    const requestId = ++beeStatusRequestRef.current;
    setBeeDeliveryStatus((previous) => ({
      orderKey,
      loading: true,
      advancing: previous.orderKey === orderKey ? previous.advancing : false,
      data: null,
      error: '',
    }));
    try {
      const response = await apiFetch(`${API_URL}/api/courier/orders/${encodeURIComponent(source)}/${encodeURIComponent(orderId)}/status`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || 'Não foi possível consultar a entrega.');
      if (requestId !== beeStatusRequestRef.current || !isDashboardOrderCurrent(sourceOrder)) return;
      setCourierBoardStatuses((previous) => {
        const next = new Map(previous);
        if (data.available && data.provider === 'bee' && courierCardLabel('bee', data.status)) {
          next.set(orderKey, { source, orderId, provider: 'bee', status: data.status, observedAt: data.observedAt });
        } else {
          next.delete(orderKey);
        }
        return next;
      });
      setBeeDeliveryStatus((previous) => ({
        ...previous,
        orderKey,
        loading: false,
        data,
        error: !data.available && (beeHireAttemptedKeysRef.current.has(orderKey) || previous.data?.available)
          ? 'A contratação ainda não pôde ser confirmada. Consulte novamente antes de qualquer nova tentativa.'
          : '',
      }));
    } catch (statusError) {
      if (requestId !== beeStatusRequestRef.current || !isDashboardSessionCurrent()) return;
      setBeeDeliveryStatus((previous) => ({
        ...previous,
        orderKey,
        loading: false,
        data: null,
        error: statusError.message || 'Não foi possível consultar a entrega.',
      }));
    }
  }, [isDashboardOrderCurrent, isDashboardSessionCurrent]);

  const openBeeHireConfirmation = () => {
    const quote = getSelectedCourierQuote();
    const order = courierQuoteModal.order;
    const orderKey = getCourierOrderKey(order);
    if (!orderKey || !isDashboardOrderCurrent(order) || quote?.provider !== 'bee'
      || quote.id !== 'bee:current'
      || !(courierQuoteModal.capabilities?.canHireBee===true || courierQuoteModal.capabilities?.canHireBeeSandbox===true)
      || !Number.isSafeInteger(quote.feeCents) || quote.feeCents < 0
      || courierQuoteModal.loading || courierQuoteModal.hiringBee
      || courierQuoteModal.hireAttempted || beeHireAttemptedKeysRef.current.has(orderKey)) return;
    setCourierQuoteModal((previous) => ({ ...previous, confirmBee: true, error: '' }));
  };

  const hireBeeSandboxCourier = async () => {
    const quote = getSelectedCourierQuote();
    const order = courierQuoteModal.order;
    const orderKey = getCourierOrderKey(order);
    if (!courierQuoteModal.confirmBee || !orderKey || !isDashboardOrderCurrent(order)
      || quote?.provider !== 'bee' || quote.id !== 'bee:current'
      || !(courierQuoteModal.capabilities?.canHireBee===true || courierQuoteModal.capabilities?.canHireBeeSandbox===true)
      || !Number.isSafeInteger(quote.feeCents) || quote.feeCents < 0
      || courierQuoteModal.hireAttempted || beeHireAttemptedKeysRef.current.has(orderKey)) return;
    beeHireAttemptedKeysRef.current.add(orderKey);
    setCourierQuoteModal((previous) => ({ ...previous, confirmBee: false, hiringBee: true, hireAttempted: true, error: '' }));
    try {
      const source = getExternalOrderSource(order);
      const orderId = String(order.orderId).trim();
      const response = await apiFetch(`${API_URL}/api/courier/orders/${encodeURIComponent(source)}/${encodeURIComponent(orderId)}/hire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: 'bee', expectedQuoteId: quote.id, expectedPriceCents: quote.feeCents }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || 'Não foi possível confirmar a contratação Bee.');
      if (!isDashboardOrderCurrent(order)) return;
      setBeeDeliveryStatus({
        orderKey,
        loading: false,
        advancing: false,
        data: { ...data, available: true },
        error: '',
      });
      closeCourierQuoteModal();
      void refreshBeeDeliveryStatus(order);
      void fetchOrders(false, true);
    } catch (hireError) {
      if (!isDashboardSessionCurrent()) return;
      setCourierQuoteModal((previous) => ({
        ...previous,
        hiringBee: false,
        error: `${hireError.message || 'Falha na contratação.'} Consulte o status do pedido antes de tentar novamente.`,
      }));
      void refreshBeeDeliveryStatus(order);
    }
  };

  const advanceBeeSandboxStatus = async (sourceOrder, event) => {
    const orderKey = getCourierOrderKey(sourceOrder);
    if (!orderKey || beeDeliveryStatus.orderKey !== orderKey
      || beeDeliveryStatus.data?.sandbox !== true
      || beeDeliveryStatus.data?.capabilities?.canAdvanceBeeSandboxStatus !== true
      || !isAdminLike(currentUser)
      || beeDeliveryStatus.advancing || !isDashboardOrderCurrent(sourceOrder)) return;
    setBeeDeliveryStatus((previous) => ({ ...previous, advancing: true, error: '' }));
    try {
      const source = getExternalOrderSource(sourceOrder);
      const orderId = String(sourceOrder.orderId).trim();
      const response = await apiFetch(`${API_URL}/api/courier/orders/${encodeURIComponent(source)}/${encodeURIComponent(orderId)}/bee-test-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || 'Não foi possível simular o status Bee.');
      if (!isDashboardOrderCurrent(sourceOrder)) return;
      setBeeDeliveryStatus((previous) => ({ ...previous, advancing: false }));
      await refreshBeeDeliveryStatus(sourceOrder);
    } catch (advanceError) {
      if (!isDashboardSessionCurrent()) return;
      setBeeDeliveryStatus((previous) => ({
        ...previous,
        advancing: false,
        error: `${advanceError.message || 'Falha na simulação.'} Atualize o status para conferir o estado atual.`,
      }));
    }
  };

  const openCourierOrderKey = orderModal.open ? getCourierOrderKey(orderModal.order) : '';
  const confirmBeeLocalCode = async (sourceOrder,code) => {
    if (!isAdminLike(currentUser) || !isDashboardOrderCurrent(sourceOrder) ||
        (beeDeliveryStatus.data?.capabilities?.canConfirmLocalCode !== true && beeDeliveryStatus.data?.capabilities?.canConfirmNinetyNineFoodCode !== true) || beeDeliveryStatus.advancing) return;
    const source = getExternalOrderSource(sourceOrder);
    const orderId = String(sourceOrder?.orderId || '').trim();
    const orderKey = getCourierOrderKey(sourceOrder);
    if (!source || !orderId || orderKey !== beeDeliveryStatus.orderKey) return;
    setBeeDeliveryStatus(previous => ({...previous,advancing:true,error:''}));
    try {
      const response = await apiFetch(`${API_URL}/api/courier/orders/${encodeURIComponent(source)}/${encodeURIComponent(orderId)}/${source==='99food'?'confirm-99food-code':'confirm-local-code'}`,{
        method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(source==='99food'&&!code ? {checkOnly:true} : {code}),
      });
      const data = await response.json().catch(() => ({}));
      if (!isDashboardOrderCurrent(sourceOrder)) return;
      if (!response.ok || (data.success !== true&&data.pending!==true)) throw new Error(data.error || 'Não foi possível conferir o código.');
      await refreshBeeDeliveryStatus(sourceOrder);
      await fetchOrders(false,true);
    } catch(failure) {
      if (!isDashboardOrderCurrent(sourceOrder)) return;
      if(source==='99food') await refreshBeeDeliveryStatus(sourceOrder);
      if (!isDashboardOrderCurrent(sourceOrder)) return;
      setBeeDeliveryStatus(previous => ({...previous,error:failure.message || 'Não foi possível conferir o código.'}));
    } finally {
      if (isDashboardOrderCurrent(sourceOrder)) setBeeDeliveryStatus(previous => ({...previous,advancing:false}));
    }
  };
  const beeOrderRef = useRef(null);
  beeOrderRef.current = orderModal.order;
  useEffect(() => {
    if (!openCourierOrderKey) return;
    void refreshBeeDeliveryStatus(beeOrderRef.current);
  }, [openCourierOrderKey, refreshBeeDeliveryStatus]);

  const updateOutsideShippingField = useCallback((field, value) => {
    setOutsideShippingModal((prev) => {
      if(getOrderSourceKey(prev.sourceOrder || {})==='99food'&&field!=='merchantId') return prev;
      const affectsQuote = OUTSIDE_SHIPPING_ADDRESS_FIELDS.has(field) || field === 'merchantId';
      return {
        ...prev,
        error: '',
        availabilities: affectsQuote ? [] : prev.availabilities,
        selectedQuoteId: affectsQuote ? '' : prev.selectedQuoteId,
        geocodeResult: OUTSIDE_SHIPPING_ADDRESS_FIELDS.has(field) ? null : prev.geocodeResult,
        addressSuggestions: field === 'streetName' ? prev.addressSuggestions : [],
        form: {
          ...prev.form,
          [field]: value,
          ...(OUTSIDE_SHIPPING_ADDRESS_FIELDS.has(field)
            ? {
                latitude: '',
                longitude: '',
              }
            : null),
        },
      };
    });
  }, []);

  const applyOutsideShippingAddressSuggestion = useCallback((suggestion) => {
    if (!suggestion || typeof suggestion !== 'object') return;
    const address = normalizeMapSuggestionAddress(suggestion);
    setOutsideShippingModal((prev) => ({
      ...prev,
      error: '',
      availabilities: [],
      selectedQuoteId: '',
      addressSuggestions: [],
      geocodeResult: address.label
        ? {
            label: address.label,
            resultType: suggestion.resultType || '',
            scoring: suggestion.scoring || null,
          }
        : null,
      form: {
        ...prev.form,
        postalCode: address.postalCode || prev.form.postalCode,
        streetName: address.streetName || prev.form.streetName,
        streetNumber: address.streetNumber || prev.form.streetNumber,
        neighborhood: address.neighborhood || prev.form.neighborhood,
        city: address.city || prev.form.city,
        state: address.state || prev.form.state,
        country: address.country || prev.form.country,
        latitude: address.latitude || '',
        longitude: address.longitude || '',
      },
    }));
  }, []);

  useEffect(() => {
    if (!outsideShippingModal.open || getOrderSourceKey(outsideShippingModal.sourceOrder || {})==='99food') return undefined;
    const form = outsideShippingModal.form;
    const streetQuery = String(form.streetName || '').trim();
    if (streetQuery.length < 3) {
      setOutsideShippingModal((prev) => ({
        ...prev,
        addressSuggestions: [],
        loadingAddressSuggestions: false,
      }));
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setOutsideShippingModal((prev) => ({
        ...prev,
        loadingAddressSuggestions: true,
      }));

      try {
        const params = new URLSearchParams({
          q: streetQuery,
          neighborhood: String(form.neighborhood || '').trim(),
          city: String(form.city || '').trim(),
          state: String(form.state || '').trim(),
          postalCode: String(form.postalCode || '').trim(),
          country: String(form.country || 'BR').trim(),
        });
        const response = await apiFetch(`${API_URL}/api/ifood/shipping/address-suggestions?${params.toString()}`, {
          signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
          throw new Error(data.message || data.error || 'Falha ao buscar sugestoes de endereco.');
        }
        setOutsideShippingModal((prev) => ({
          ...prev,
          addressSuggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
          loadingAddressSuggestions: false,
        }));
      } catch (err) {
        if (err.name === 'AbortError') return;
        setOutsideShippingModal((prev) => ({
          ...prev,
          addressSuggestions: [],
          loadingAddressSuggestions: false,
        }));
      }
    }, 450);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [outsideShippingModal.open, outsideShippingModal.form, outsideShippingModal.sourceOrder]);

  const updateOutsideShippingItem = useCallback((index, field, value) => {
    setOutsideShippingModal((prev) => ({
      ...prev,
      error: '',
      form: {
        ...prev.form,
        items: prev.form.items.map((item, itemIndex) => (
          itemIndex === index ? { ...item, [field]: value } : item
        )),
      },
    }));
  }, []);

  const addOutsideShippingItem = useCallback(() => {
    setOutsideShippingModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        items: [...prev.form.items, createOutsideShippingItem()],
      },
    }));
  }, []);

  const removeOutsideShippingItem = useCallback((index) => {
    setOutsideShippingModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        items: prev.form.items.length > 1
          ? prev.form.items.filter((_, itemIndex) => itemIndex !== index)
          : prev.form.items,
      },
    }));
  }, []);

  const fetchOutsideShippingAvailability = useCallback(async () => {
    const form = outsideShippingModal.form;
    const merchantId = String(form.merchantId || '').trim();
    let latitude = parseCoordinateInput(form.latitude);
    let longitude = parseCoordinateInput(form.longitude);

    if (!merchantId) {
      setOutsideShippingModal((prev) => ({ ...prev, error: 'Selecione a loja iFood.' }));
      return;
    }

    setOutsideShippingModal((prev) => ({
      ...prev,
      loadingQuote: true,
      geocoding: latitude === null || longitude === null,
      error: '',
      availabilities: [],
      selectedQuoteId: '',
    }));
    try {
      if(getOrderSourceKey(outsideShippingModal.sourceOrder || {})==='99food') {
        const sourceOrder=outsideShippingModal.sourceOrder;
        const response=await apiFetch(`${API_URL}/api/courier/orders/99food/${encodeURIComponent(sourceOrder.orderId)}/quotes`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({merchantId})});
        const data=await response.json().catch(()=>({}));
        if(!response.ok) throw new Error(data.error||'Não foi possível cotar esta entrega.');
        const availabilities=(data.quotes||[]).filter(quote=>quote.provider==='ifood').map(quote=>({quoteId:quote.id.replace(/^ifood:/,''),deliveryFee:quote.fee,deliveryTimeMinSeconds:quote.etaMinutes*60}));
        setOutsideShippingModal(prev=>({...prev,loadingQuote:false,geocoding:false,availabilities,selectedQuoteId:'',error:availabilities.length?'':'iFood sem cotação para este pedido.'}));
        return;
      }
      if (latitude === null || longitude === null) {
        const geocodePayload = buildOutsideShippingGeocodePayload(form);
        const response = await apiFetch(`${API_URL}/api/ifood/shipping/geocode`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(geocodePayload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || !data.position) {
          throw new Error(data.message || data.error || 'Nao foi possivel localizar automaticamente o endereco.');
        }
        latitude = parseCoordinateInput(data.position.lat);
        longitude = parseCoordinateInput(data.position.lng);
        if (latitude === null || longitude === null) {
          throw new Error('O servico de mapas nao retornou coordenadas validas para esse endereco.');
        }
        setOutsideShippingModal((prev) => ({
          ...prev,
          geocoding: false,
          geocodeResult: {
            label: data.label || data.title || '',
            resultType: data.resultType || '',
            scoring: data.scoring || null,
          },
          form: {
            ...prev.form,
            latitude: String(latitude),
            longitude: String(longitude),
          },
        }));
      }

      const params = new URLSearchParams({
        latitude: String(latitude),
        longitude: String(longitude),
      });
      const response = await apiFetch(`${API_URL}/api/ifood/shipping/merchants/${merchantId}/deliveryAvailabilities?${params.toString()}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao consultar cotação sob demanda iFood.');
      }
      const availabilities = Array.isArray(data.availabilities) ? data.availabilities : [];
      setOutsideShippingModal((prev) => ({
        ...prev,
        loadingQuote: false,
        geocoding: false,
        availabilities,
        selectedQuoteId: '',
        error: availabilities.length ? '' : 'Entrega iFood indisponível para esse endereço.',
      }));
    } catch (err) {
      setOutsideShippingModal((prev) => ({
        ...prev,
        loadingQuote: false,
        geocoding: false,
        error: err.message || 'Falha ao consultar cotação sob demanda iFood.',
      }));
    }
  }, [outsideShippingModal.form, outsideShippingModal.sourceOrder]);

  const buildOutsideShippingPayload = useCallback(() => {
    const form = outsideShippingModal.form;
    const selectedQuoteId = outsideShippingModal.selectedQuoteId;
    if(getOrderSourceKey(outsideShippingModal.sourceOrder || {})==='99food') return {delivery:{quoteId:selectedQuoteId}};
    const items = (form.items || []).map((item) => {
      const quantity = Number(item.quantity);
      const unitPrice = normalizeMoneyInput(item.unitPrice);
      const optionsPrice = normalizeMoneyInput(item.optionsPrice) || 0;
      const price = Number.isFinite(quantity) && Number.isFinite(unitPrice)
        ? quantity * unitPrice
        : null;
      return {
        id: String(item.id || '').trim(),
        name: String(item.name || '').trim(),
        quantity,
        unitPrice,
        price,
        optionsPrice,
        totalPrice: Number.isFinite(price) ? price + optionsPrice : null,
      };
    });

    const merchantFee = normalizeMoneyInput(form.merchantFee);
    const paymentValue = normalizeMoneyInput(form.paymentValue);
    const preparationTime = Number(form.preparationTime);
    const payload = {
      customer: {
        name: String(form.customerName || '').trim(),
        phone: {
          countryCode: String(form.countryCode || '').trim(),
          areaCode: String(form.areaCode || '').trim(),
          number: String(form.phoneNumber || '').trim(),
          type: String(form.phoneType || 'CUSTOMER').trim(),
        },
      },
      delivery: {
        merchantFee,
        quoteId: selectedQuoteId,
        ...(Number.isInteger(preparationTime) && preparationTime >= 0 ? { preparationTime } : null),
        deliveryAddress: {
          postalCode: String(form.postalCode || '').replace(/\D/g, ''),
          streetNumber: String(form.streetNumber || '').trim(),
          streetName: String(form.streetName || '').trim(),
          ...(String(form.complement || '').trim() ? { complement: String(form.complement || '').trim() } : null),
          ...(String(form.reference || '').trim() ? { reference: String(form.reference || '').trim() } : null),
          neighborhood: String(form.neighborhood || '').trim(),
          city: String(form.city || '').trim(),
          state: String(form.state || '').trim().toUpperCase(),
          country: String(form.country || '').trim().toUpperCase(),
          coordinates: {
            latitude: parseCoordinateInput(form.latitude),
            longitude: parseCoordinateInput(form.longitude),
          },
        },
      },
      items,
    };

    if (String(form.displayId || '').trim()) {
      payload.displayId = String(form.displayId || '').trim();
    }

    if (form.paymentMode === 'OFFLINE') {
      const method = String(form.paymentMethod || '').trim().toUpperCase();
      payload.payments = {
        methods: [
          {
            method,
            type: 'OFFLINE',
            value: paymentValue,
            ...(method === 'CREDIT' || method === 'DEBIT'
              ? { card: { brand: String(form.cardBrand || '').trim() } }
              : null),
            ...(method === 'CASH' && normalizeMoneyInput(form.cashChangeFor) !== null
              ? { cash: { changeFor: normalizeMoneyInput(form.cashChangeFor) } }
              : null),
          },
        ],
      };
    }

    return payload;
  }, [outsideShippingModal.form, outsideShippingModal.selectedQuoteId, outsideShippingModal.sourceOrder]);

  const validateOutsideShippingPayload = useCallback((payload) => {
    if (!payload.customer.name) return 'Informe o nome real do cliente.';
    if (payload.customer.phone.type !== 'STORE') {
      if (!payload.customer.phone.countryCode || !payload.customer.phone.areaCode || !payload.customer.phone.number) {
        return 'Informe o telefone real do cliente.';
      }
    }
    if (!payload.delivery.quoteId) return 'Consulte e selecione uma cotação real antes de registrar o pedido.';
    if (!Number.isFinite(payload.delivery.merchantFee)) return 'Informe a taxa real cobrada pelo parceiro.';
    const address = payload.delivery.deliveryAddress;
    if (!address.postalCode || !address.streetName || !address.streetNumber || !address.neighborhood || !address.city || !address.state || !address.country) {
      return 'Preencha o endereço real completo.';
    }
    if (address.coordinates.latitude === null || address.coordinates.longitude === null) {
      return 'Informe coordenadas reais do endereço.';
    }
    if (!payload.items.length) return 'Informe ao menos um item real do pedido.';
    const invalidItem = payload.items.find((item) => (
      !item.id ||
      !item.name ||
      !Number.isFinite(item.quantity) ||
      item.quantity <= 0 ||
      !Number.isFinite(item.unitPrice) ||
      !Number.isFinite(item.price) ||
      !Number.isFinite(item.totalPrice)
    ));
    if (invalidItem) return 'Todos os itens precisam de ID, nome, quantidade e preço reais.';
    if (payload.payments) {
      const method = payload.payments.methods?.[0];
      if (!Number.isFinite(method?.value)) return 'Informe o valor real do pagamento offline.';
      if ((method.method === 'CREDIT' || method.method === 'DEBIT') && !method.card?.brand) {
        return 'Informe a bandeira real do cartão.';
      }
    }
    return '';
  }, []);

  const submitOutsideShippingOrder = useCallback(async () => {
    if (!isDashboardSessionCurrent() || (outsideShippingModal.sourceOrder && !isDashboardOrderCurrent(outsideShippingModal.sourceOrder))) return;
    const form = outsideShippingModal.form;
    const sourceOrder = outsideShippingModal.sourceOrder;
    const merchantId = String(form.merchantId || '').trim();
    const payload = buildOutsideShippingPayload();
    const validationMessage = getOrderSourceKey(sourceOrder)==='99food' ? (payload.delivery.quoteId ? '' : 'Cote e selecione uma entrega atual.') : validateOutsideShippingPayload(payload);
    if (!merchantId || validationMessage) {
      setOutsideShippingModal((prev) => ({ ...prev, error: validationMessage || 'Selecione a loja iFood.' }));
      return;
    }

    setOutsideShippingModal((prev) => ({ ...prev, creating: true, error: '' }));
    try {
      const source = getExternalOrderSource(sourceOrder);
      const sourceOrderId = String(sourceOrder?.orderId || '').trim();
      const endpoint = outsideShippingEndpoint({apiUrl:API_URL,merchantId,sourceOrder,source});
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Courier-Intent-Id':form.draftId },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao registrar pedido sob demanda iFood.');
      }
      if (data.order) {
        mergeOrderSnapshot(data.order);
      }
      closeOutsideShippingModal();
      await fetchOrders(false, true);
      if (sourceOrderId && orderModal.open) {
        orderDetailsCacheRef.current.delete(sourceOrderId);
        await fetchOrderDetails(sourceOrderId);
      }
      setError(null);
    } catch (err) {
      setOutsideShippingModal((prev) => ({
        ...prev,
        creating: false,
        error: err.message || 'Falha ao registrar pedido sob demanda iFood.',
      }));
    }
  }, [isDashboardOrderCurrent, isDashboardSessionCurrent, buildOutsideShippingPayload, closeOutsideShippingModal, fetchOrderDetails, fetchOrders, mergeOrderSnapshot, orderModal.open, outsideShippingModal.form, outsideShippingModal.sourceOrder, validateOutsideShippingPayload]);

  const orderLocationConfirmationRef = useRef(null);
  const [orderSuggestionPosition, setOrderSuggestionPosition] = useState(null);
  const [orderLocationPolicyEnabled, setOrderLocationPolicyEnabled] = useState(null);

  const openOrderAddressEdit = (order) => {
    if (!canEditOrderAddress(order)) return;
    const form = createOrderAddressEditFormFromOrder(order);
    setOrderSuggestionPosition(null);
    setOrderLocationPolicyEnabled(null);
    setOrderAddressEditFeedback(null);
    setOrderAddressEditModal({
      open: true,
      order,
      form,
      submitting: false,
      error: '',
      addressSuggestions: [],
      loadingAddressSuggestions: false,
      addressSuggestionError: '',
      selectedStreetName: String(form.streetName || '').trim(),
    });
  };

  const updateOrderAddressEditField = (field, value) => {
    const affectsSuggestions = ['streetName', 'city', 'state'].includes(field);
    setOrderAddressEditModal((prev) => ({
      ...prev,
      error: '',
      ...(affectsSuggestions
        ? {
            addressSuggestions: [],
            loadingAddressSuggestions: false,
            addressSuggestionError: '',
            selectedStreetName: '',
          }
        : null),
      form: {
        ...prev.form,
        [field]: value,
      },
    }));
  };

  const applyOrderAddressEditSuggestion = (suggestion) => {
    if (!suggestion || typeof suggestion !== 'object') return;
    const address = normalizeMapSuggestionAddress(suggestion);
    setOrderSuggestionPosition(suggestion.position || null);
    setOrderAddressEditModal((prev) => ({
      ...prev,
      error: '',
      addressSuggestions: [],
      loadingAddressSuggestions: false,
      addressSuggestionError: '',
      selectedStreetName: address.streetName || prev.form.streetName,
      form: mergeOrderAddressSuggestion(prev.form, suggestion),
    }));
  };

  useEffect(() => {
    if (!orderAddressEditModal.open) return undefined;
    const form = {
      streetName: orderAddressEditModal.form.streetName,
      city: orderAddressEditModal.form.city,
      state: orderAddressEditModal.form.state,
      country: orderAddressEditModal.form.country,
    };
    const streetQuery = String(form.streetName || '').trim();
    const selectedStreetName = String(orderAddressEditModal.selectedStreetName || '').trim();
    if (streetQuery.length < 3 || streetQuery === selectedStreetName) {
      setOrderAddressEditModal((prev) => (
        prev.addressSuggestions.length || prev.loadingAddressSuggestions
          ? { ...prev, addressSuggestions: [], loadingAddressSuggestions: false }
          : prev
      ));
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    const requestPayload = buildOrderAddressSuggestionRequest(form);
    const timer = setTimeout(async () => {
      setOrderAddressEditModal((prev) => ({
        ...prev,
        loadingAddressSuggestions: true,
        addressSuggestionError: '',
      }));

      try {
        const response = await apiFetch(`${API_URL}/api/delivery-pricing/${orderLocationPolicyEnabled === false ? 'address-suggestions' : 'destination-suggestions'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestPayload),
          signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Falha ao consultar endereços.');
        }
        if (!active) return;
        setOrderAddressEditModal((prev) => {
          const currentPayload = buildOrderAddressSuggestionRequest(prev.form);
          const isCurrent = currentPayload.q === requestPayload.q
            && currentPayload.city === requestPayload.city
            && currentPayload.state === requestPayload.state;
          if (!isCurrent) return prev;
          return {
            ...prev,
            addressSuggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
            loadingAddressSuggestions: false,
            addressSuggestionError: '',
          };
        });
      } catch (error) {
        if (!active || error.name === 'AbortError') return;
        setOrderAddressEditModal((prev) => ({
          ...prev,
          addressSuggestions: [],
          loadingAddressSuggestions: false,
          addressSuggestionError: 'Não foi possível consultar endereços agora. Continue preenchendo manualmente.',
        }));
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    orderAddressEditModal.form.city,
    orderAddressEditModal.form.country,
    orderAddressEditModal.form.state,
    orderAddressEditModal.form.streetName,
    orderAddressEditModal.open,
    orderAddressEditModal.selectedStreetName,
    orderLocationPolicyEnabled,
  ]);

  const submitOrderAddressEdit = async () => {
    if (!isDashboardOrderCurrent(orderAddressEditModal.order)) return;
    const order = orderAddressEditModal.order;
    const form = orderAddressEditModal.form;
    const orderId = String(order?.orderId || order?.id || '').trim();
    if (!canEditOrderAddress(order) || !orderId || orderAddressEditModal.submitting) return;

    const requiredFields = [
      ['streetName', 'Rua'],
      ['streetNumber', 'Número'],
      ['neighborhood', 'Bairro'],
      ['city', 'Cidade'],
      ['state', 'UF'],
    ];
    const missing = requiredFields.find(([field]) => !String(form[field] || '').trim());
    if (missing) {
      setOrderAddressEditModal((prev) => ({ ...prev, error: `${missing[1]} é obrigatório.` }));
      return;
    }
    if (String(form.state || '').trim().length !== 2) {
      setOrderAddressEditModal((prev) => ({ ...prev, error: 'UF deve ter duas letras.' }));
      return;
    }
    const postal = normalizeOptionalOrderPostalCode(form.postalCode);
    if (!postal.valid) {
      setOrderAddressEditModal((prev) => ({ ...prev, error: 'Informe o CEP com 8 dígitos ou deixe-o vazio.' }));
      return;
    }

    setOrderAddressEditModal((prev) => ({ ...prev, submitting: true, error: '' }));
    try {
      const location = await orderLocationConfirmationRef.current?.ensureConfirmed();
      if (!location?.confirmationToken && !location?.legacy) throw new Error('Confirme a localização antes de corrigir o endereço.');
      if (!isDashboardOrderCurrent(order)) return;
      const response = await apiFetch(`${API_URL}/api/orders/${encodeURIComponent(orderId)}/address`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: getOrderSourceKey(order),
          ...(getOrderSourceKey(order) === '99food' ? { expectedAddressRevision: Number(order.additionalInfo?.deliveryAddressOverride?.revision || 0) } : {}),
          address: buildOutsideShippingGeocodePayload({ ...form, postalCode: postal.postalCode }),
          locationConfirmation: location?.confirmationToken,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Falha ao corrigir endereço do pedido.');
      }

      if (data.order) mergeOrderSnapshot(data.order);
      closeOrderAddressEditModal();
      setOrderAddressEditFeedback({
        severity: data.warning ? 'warning' : 'success',
        message: data.warning || 'Endereço corrigido no Brutos Delivery e no Brutos Entregas.',
      });
    } catch (err) {
      setOrderAddressEditModal((prev) => ({
        ...prev,
        submitting: false,
        error: err.message || 'Falha ao corrigir endereço do pedido.',
      }));
    }
  };

  const openShippingAddressChangeFlow = useCallback((order) => {
    if (!canRequestShippingAddressChangeForOrder(order)) return;
    setShippingActionState({ loading: false, error: '', message: '' });
    setShippingAddressChangeModal({
      open: true,
      order,
      form: createShippingAddressChangeFormFromOrder(order),
      geocoding: false,
      submitting: false,
      error: '',
      geocodeResult: null,
    });
  }, [canRequestShippingAddressChangeForOrder]);

  const updateShippingAddressChangeField = useCallback((field, value) => {
    setShippingAddressChangeModal((prev) => ({
      ...prev,
      error: '',
      geocodeResult: OUTSIDE_SHIPPING_ADDRESS_FIELDS.has(field) ? null : prev.geocodeResult,
      form: {
        ...prev.form,
        [field]: value,
        ...(OUTSIDE_SHIPPING_ADDRESS_FIELDS.has(field)
          ? {
              latitude: '',
              longitude: '',
            }
          : null),
      },
    }));
  }, []);

  const locateShippingAddressChange = useCallback(async (options = {}) => {
    const form = options.form || shippingAddressChangeModal.form;
    setShippingAddressChangeModal((prev) => ({
      ...prev,
      geocoding: true,
      error: '',
    }));
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/shipping/geocode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildOutsideShippingGeocodePayload(form)),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !data.position) {
        throw new Error(data.message || data.error || 'Nao foi possivel localizar o novo endereco.');
      }
      const latitude = parseCoordinateInput(data.position.lat);
      const longitude = parseCoordinateInput(data.position.lng);
      if (latitude === null || longitude === null) {
        throw new Error('O servico de mapas nao retornou coordenadas validas para esse endereco.');
      }
      setShippingAddressChangeModal((prev) => ({
        ...prev,
        geocoding: false,
        geocodeResult: {
          label: data.label || data.title || '',
          resultType: data.resultType || '',
          scoring: data.scoring || null,
        },
        form: {
          ...prev.form,
          latitude: String(latitude),
          longitude: String(longitude),
        },
      }));
      return { latitude, longitude, label: data.label || data.title || '' };
    } catch (err) {
      setShippingAddressChangeModal((prev) => ({
        ...prev,
        geocoding: false,
        error: err.message || 'Falha ao localizar o novo endereco.',
      }));
      return null;
    }
  }, [shippingAddressChangeModal.form]);

  const submitShippingAddressChangeRequest = useCallback(async () => {
    if (!isDashboardOrderCurrent(shippingAddressChangeModal.order)) return;
    const order = shippingAddressChangeModal.order;
    const form = shippingAddressChangeModal.form;
    const orderId = String(order?.orderId || '').trim();
    if (!canRequestShippingAddressChangeForOrder(order) || !orderId || shippingAddressChangeModal.submitting) return;

    setShippingAddressChangeModal((prev) => ({ ...prev, submitting: true, error: '' }));
    let coordinates = {
      latitude: parseCoordinateInput(form.latitude),
      longitude: parseCoordinateInput(form.longitude),
    };
    if (coordinates.latitude === null || coordinates.longitude === null) {
      const geocode = await locateShippingAddressChange({ form });
      if (!geocode) {
        setShippingAddressChangeModal((prev) => ({ ...prev, submitting: false }));
        return;
      }
      coordinates = geocode;
    }

    if (!isDashboardOrderCurrent(order)) {
      setShippingAddressChangeModal((prev) => ({ ...prev, submitting: false, error: 'Aguarde a atualização do pedido.' }));
      return;
    }
    const payload = buildShippingAddressChangePayload(form, coordinates);
    const validationMessage = validateShippingAddressChangePayload(payload);
    if (validationMessage) {
      setShippingAddressChangeModal((prev) => ({
        ...prev,
        submitting: false,
        error: validationMessage,
      }));
      return;
    }

    try {
      const externalBaseUrl = getExternalShippingBaseUrl(order);
      const endpoint = externalBaseUrl && getExternalShippingOrderId(order)
        ? `${externalBaseUrl}/deliveryAddressChangeRequest`
        : `${API_URL}/api/ifood/shipping/orders/${orderId}/deliveryAddressChangeRequest`;
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao solicitar alteracao de endereco.');
      }

      if (data.order) {
        mergeOrderSnapshot(data.order);
      }
      closeShippingAddressChangeModal();
      orderDetailsCacheRef.current.delete(orderId);
      await fetchOrders(false, true);
      if (orderModal.open) {
        await fetchOrderDetails(orderId);
      }
      setShippingActionState({
        loading: false,
        error: '',
        message: 'Alteração de endereço solicitada. Agora use Aceitar endereço ou Rejeitar endereço no pedido.',
      });
    } catch (err) {
      setShippingAddressChangeModal((prev) => ({
        ...prev,
        submitting: false,
        error: err.message || 'Falha ao solicitar alteracao de endereco.',
      }));
    }
  }, [isDashboardOrderCurrent,
    canRequestShippingAddressChangeForOrder,
    closeShippingAddressChangeModal,
    fetchOrderDetails,
    fetchOrders,
    locateShippingAddressChange,
    mergeOrderSnapshot,
    orderModal.open,
    shippingAddressChangeModal.form,
    shippingAddressChangeModal.order,
    shippingAddressChangeModal.submitting,
  ]);

  const refreshShippingTracking = useCallback(async (order, options = {}) => {
    const orderId = order?.orderId;
    if (!canUseIfoodShipping || !orderId) return;
    const refreshKey = `${getExternalOrderSource(order) || 'ifood'}:${orderId}:${getExternalShippingOrderId(order) || orderId}`;
    if (trackingRefreshInFlightRef.current.has(refreshKey)) return;

    trackingRefreshInFlightRef.current.add(refreshKey);
    try {
      const externalBaseUrl = getExternalShippingBaseUrl(order);
      const response = await apiFetch(
        externalBaseUrl && getExternalShippingOrderId(order)
          ? `${externalBaseUrl}/tracking`
          : `${API_URL}/api/ifood/shipping/orders/${orderId}/tracking`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok && response.status !== 202) {
        throw new Error(data.message || data.error || 'Falha ao atualizar rastreio iFood.');
      }
      if (data.order) {
        mergeOrderSnapshot(data.order);
      }
      if (options.refreshOrders !== false) {
        await fetchOrders(false, false);
      }
      if (options.refreshDetails !== false && orderModal.open) {
        orderDetailsCacheRef.current.delete(String(orderId || '').trim());
        await fetchOrderDetails(orderId);
      }
      if (!options.silent) {
        setError(null);
      }
    } catch (err) {
      if (!options.silent) {
        setError(err.message || 'Falha ao atualizar rastreio iFood.');
      }
    } finally {
      trackingRefreshInFlightRef.current.delete(refreshKey);
    }
  }, [canUseIfoodShipping, fetchOrderDetails, fetchOrders, mergeOrderSnapshot, orderModal.open]);

  const submitPickupCodeConfirmation = async (order) => {
    if (!isDashboardOrderCurrent(order)) return;
    const orderId = String(order?.orderId || '').trim();
    const code = String(pickupCodeConfirmation.code || '').replace(/\D/g, '').slice(0, 4);
    if (!orderId || pickupCodeConfirmation.submitting) return;

    if (!canConfirmPickupCodeForOrder(order)) {
      setPickupCodeConfirmation((prev) => ({
        ...prev,
        submitting: false,
        severity: 'error',
        message: 'Erro no codigo: este pedido nao esta elegivel para confirmar pickupCode.',
      }));
      return;
    }

    if (code.length !== 4) {
      setPickupCodeConfirmation((prev) => ({
        ...prev,
        code,
        submitting: false,
        severity: 'error',
        message: 'Erro no codigo: informe 4 digitos.',
      }));
      return;
    }

    setPickupCodeConfirmation((prev) => ({
      ...prev,
      code,
      submitting: true,
      message: '',
    }));

    try {
      const response = await apiFetch(`${API_URL}/api/ifood/shipping/orders/${encodeURIComponent(orderId)}/validatePickupCode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false || data.valid === false) {
        throw new Error(data.message || data.error || 'Erro no codigo.');
      }

      if (data.order) {
        mergeOrderSnapshot(data.order);
      }
      await fetchOrders(false, false);
      if (orderModal.open) {
        orderDetailsCacheRef.current.delete(orderId);
        await fetchOrderDetails(orderId);
      }
      setPickupCodeConfirmation({
        code: '',
        submitting: false,
        severity: 'success',
        message: data.message || 'Sucesso: codigo confirmado.',
      });
    } catch (err) {
      setPickupCodeConfirmation((prev) => ({
        ...prev,
        submitting: false,
        severity: 'error',
        message: err.message || 'Erro no codigo.',
      }));
    }
  };

  const submitShippingAddressAction = useCallback(async (order, action) => {
    if (!isDashboardOrderCurrent(order)) return;
    const orderId = order?.orderId;
    if (!canUseIfoodShipping || !orderId || shippingActionState.loading) return;

    const endpointByAction = {
      confirm: 'userConfirmAddress',
      accept: 'acceptDeliveryAddressChange',
      deny: 'denyDeliveryAddressChange',
    };
    const endpoint = endpointByAction[action];
    if (!endpoint) return;

    setShippingActionState({ loading: true, error: '', message: '' });
    try {
      const externalBaseUrl = getExternalShippingBaseUrl(order);
      const response = await apiFetch(
        externalBaseUrl && getExternalShippingOrderId(order)
          ? `${externalBaseUrl}/${endpoint}`
          : `${API_URL}/api/ifood/shipping/orders/${orderId}/${endpoint}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao responder endereço da entrega iFood.');
      }
      if (data.order) {
        mergeOrderSnapshot(data.order);
      }
      await fetchOrders(false, true);
      orderDetailsCacheRef.current.delete(String(orderId || '').trim());
      await fetchOrderDetails(orderId);
      setShippingActionState({
        loading: false,
        error: '',
        message: action === 'deny' ? 'Alteração de endereço rejeitada.' : action === 'accept' ? 'Alteração de endereço aceita.' : 'Endereço confirmado.',
      });
    } catch (err) {
      setShippingActionState({
        loading: false,
        error: err.message || 'Falha ao responder endereço da entrega iFood.',
        message: '',
      });
    }
  }, [isDashboardOrderCurrent, canUseIfoodShipping, fetchOrderDetails, fetchOrders, mergeOrderSnapshot, shippingActionState.loading]);

  const submitCancelShippingDriverRequest = useCallback(async (order) => {
    if (!isDashboardOrderCurrent(order)) return;
    const orderId = order?.orderId;
    if (!canCancelShippingDriverForOrder(order) || !orderId || shippingActionState.loading) return;

    if (isOutsideShippingOrder(order)) {
      openCancellationFlow(order);
      return;
    }

    const confirmed = window.confirm('Cancelar a solicitação do entregador iFood deste pedido?');
    if (!confirmed) return;

    setShippingActionState({ loading: true, error: '', message: '' });
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/shipping/orders/${encodeURIComponent(orderId)}/cancelRequestDriver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || 'Falha ao cancelar entregador iFood.');
      }

      if (data.order) {
        mergeOrderSnapshot(data.order);
      }
      await fetchOrders(false, true);
      if (orderModal.open) {
        orderDetailsCacheRef.current.delete(String(orderId || '').trim());
        await fetchOrderDetails(orderId);
      }
      setShippingActionState({
        loading: false,
        error: '',
        message: 'Cancelamento do entregador solicitado.',
      });
    } catch (err) {
      setShippingActionState({
        loading: false,
        error: err.message || 'Falha ao cancelar entregador iFood.',
        message: '',
      });
    }
  }, [isDashboardOrderCurrent,
    canCancelShippingDriverForOrder,
    fetchOrderDetails,
    fetchOrders,
    isOutsideShippingOrder,
    mergeOrderSnapshot,
    openCancellationFlow,
    orderModal.open,
    shippingActionState.loading,
  ]);

  useEffect(() => {
    if (!canUseIfoodShipping) return undefined;

    const refreshActiveShippingTracking = () => {
      const activeOrders = (dashboardRef.current?.orders || [])
        .filter((order) => {
          const shipping = getVisibleShipping(order);
          const status = String(shipping?.status || '').trim();
          return Boolean(
            isDashboardOrderCurrent(order) &&
            shipping?.active &&
            ['requested', 'searching', 'assigned', 'goingToOrigin', 'arrivedAtOrigin', 'inTransit'].includes(status) &&
            !isShippingTerminal(status) &&
            !isTerminalStatus(order?.status)
          );
        })
        .slice(0, 5);

      activeOrders.forEach((order) => {
        refreshShippingTracking(order, {
          silent: true,
          refreshOrders: false,
          refreshDetails: false,
        });
      });
    };

    refreshActiveShippingTracking();
    const interval = setInterval(refreshActiveShippingTracking, 30000);
    return () => clearInterval(interval);
  }, [canUseIfoodShipping, getVisibleShipping, isDashboardOrderCurrent, refreshShippingTracking]);

  useEffect(() => {
    if (autoAccept) {
      dashboard.orders.forEach(order => {
        if (getOrderSourceKey(order) !== '99food' && isDashboardOrderCurrent(order) && needsAcceptance(order)) {
          acceptOrder(order.orderId, { automatic: true });
        }
      });
    }
  }, [dashboard.orders, autoAccept, acceptOrder, isDashboardOrderCurrent, needsAcceptance]);

  useEffect(() => {
    const currentKeys = new Set();
    const previousKeys = seenNegotiationKeysRef.current;
    let pendingAutoOpen = null;

    (dashboard.orders || []).forEach((order) => {
      const disputeId = order?.negotiation?.disputeId;
      if (!order?.negotiation?.active || !disputeId) return;
      const key = `${order.orderId}:${disputeId}:${order.negotiation.status || 'PENDING'}`;
      if (isDashboardOrderCurrent(order) || previousKeys.has(key)) currentKeys.add(key);
      if (isDashboardOrderCurrent(order) && !previousKeys.has(key) && !pendingAutoOpen) {
        pendingAutoOpen = order;
      }
    });

    seenNegotiationKeysRef.current = currentKeys;

    if (pendingAutoOpen) {
      openIfoodWidgetForNegotiation(pendingAutoOpen);
    }
  }, [dashboard.orders, isDashboardOrderCurrent, openIfoodWidgetForNegotiation]);

  const filteredOrders = useMemo(() => {
    const normalizedQuery = normalizeSearchText(orderSearch);
    const compactQuery = normalizeSearchCompact(orderSearch);
    const hasStatusFilter = statusFilterSet.size > 0;

    return (dashboard.orders || []).filter((order) => {
      if (hasStatusFilter && !statusFilterSet.has(order.status)) {
        return false;
      }

      if (!normalizedQuery && !compactQuery) {
        return true;
      }

      const searchableText = getOrderSearchText(order);
      const normalizedText = normalizeSearchText(searchableText);
      const compactText = normalizeSearchCompact(searchableText);
      return (
        (normalizedQuery && normalizedText.includes(normalizedQuery)) ||
        (compactQuery && compactText.includes(compactQuery))
      );
    });
  }, [dashboard.orders, orderSearch, statusFilterSet]);

  const categorizedOrders = useMemo(() => {
    const groups = {
      preparing: [],
      ready: [],
      enRoute: [],
      finished: [],
    };
    filteredOrders.forEach((order) => {
      const orderId = String(order.orderId || '');
      if (order.status === 'ready' && orderId.startsWith('TEST-')) {
        // Ignorar pedidos de teste na coluna "Pronto"
        return;
      }
      if (isTerminalStatus(order.status)) {
        groups.finished.push(order);
        return;
      }
      if (groups[order.status]) {
        groups[order.status].push(order);
      } else {
        groups.preparing.push(order);
      }
    });
    return groups;
  }, [filteredOrders]);

  const visibleCounters = useMemo(() => ({
    preparing: categorizedOrders.preparing.length,
    ready: categorizedOrders.ready.length,
    enRoute: categorizedOrders.enRoute.length,
    finished: categorizedOrders.finished.length,
  }), [categorizedOrders]);

  const selectedPreparingOrderIdSet = useMemo(
    () => new Set(selectedPreparingOrderIds),
    [selectedPreparingOrderIds]
  );

  const selectedPreparingSequenceById = useMemo(
    () => new Map(selectedPreparingOrderIds.map((orderId, index) => [orderId, index + 1])),
    [selectedPreparingOrderIds]
  );

  const getBatchDragPreview = useCallback((orderId, snapshot) => {
    if (!snapshot.isDragging || !activeBatchDrag || !selectedPreparingOrderIdSet.has(orderId)) return null;
    return (
      <Box className="pedidos-batch-drag-overlay" aria-hidden="true">
        <Typography className="pedidos-batch-drag-count">
          {activeBatchDrag.count} pedidos
        </Typography>
        <Typography className="pedidos-batch-drag-labels">
          {activeBatchDrag.labels.join(' • ')}
        </Typography>
      </Box>
    );
  }, [activeBatchDrag, selectedPreparingOrderIdSet]);

  const ifoodWidgetIsOffline = ifoodWidgetStatus.state === 'offline';
  const ifoodWidgetIsBusy = ifoodWidgetLoading || ifoodWidgetStatus.state === 'loading';
  const ifoodWidgetReconnectTooltip = ifoodWidgetIsOffline
    ? 'Reconexão indisponível sem internet'
    : 'Reconectar widget iFood';

  return (
    <Box className="pedidos-root pedidos-root--compact">
      <Paper className="pedidos-topbar" elevation={0}>
        <Box className="pedidos-topbar-main">
          <IconButton
            onClick={onToggleSidebar}
            className="pedidos-menu-button"
            aria-label="Abrir menu"
            size="small"
          >
            <MenuIcon />
          </IconButton>

          <Box className="pedidos-brand" aria-label="BRUTOS GESTOR">
            <Box
              component="img"
              src="/brutos-gestor-mark.png"
              alt=""
              aria-hidden="true"
              className="pedidos-brand-mark"
            />
            <Box className="pedidos-brand-wordmark">
              <Typography component="span" className="pedidos-brand-name">
                BRUTOS
              </Typography>
              <Typography component="span" className="pedidos-brand-product">
                GESTOR
              </Typography>
            </Box>
          </Box>

          <Box className="pedidos-store-block">
            <Box
              component="button"
              type="button"
              className="pedidos-store-meta pedidos-store-meta--button"
              onClick={handleOpenMerchantList}
              aria-label="Ver lojas cadastradas"
              aria-controls={merchantListOpen ? 'pedidos-merchant-list-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={merchantListOpen ? 'true' : undefined}
            >
              <Typography variant="subtitle2" color="text.secondary" className="pedidos-store-name">
                {merchantCard.name || 'Loja iFood'}
              </Typography>
              <Chip
                label={`${merchantCard.statusLabel}${!dataFreshness.merchantReadVerified || merchantCard.stale ? ' • Último status' : ''}`}
                color={merchantCard.statusColor}
                size="small"
                className="pedidos-store-chip"
                icon={<FiberManualRecordIcon className="pedidos-status-icon" />}
              />
            </Box>
            <Menu
              id="pedidos-merchant-list-menu"
              anchorEl={merchantListAnchorEl}
              open={merchantListOpen}
              onClose={handleCloseMerchantList}
              MenuListProps={{ 'aria-label': 'Lojas cadastradas no login atual' }}
              PaperProps={{ className: 'pedidos-store-menu' }}
            >
              <Box className="pedidos-store-menu-header">
                <Typography variant="subtitle2" className="pedidos-store-menu-title">
                  Lojas cadastradas
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Status no iFood para este login/CNPJ
                </Typography>
              </Box>
              <Divider />
              {merchantListLoading && !(merchantCard.stores || []).length ? (
                <MenuItem className="pedidos-store-menu-loading">
                  <CircularProgress size={18} />
                  <ListItemText primary="Atualizando lojas..." />
                </MenuItem>
              ) : (merchantCard.stores || []).length ? (
                (merchantCard.stores || []).map((store) => (
                  <MenuItem
                    key={store.merchantId || store.name}
                    className="pedidos-store-menu-item"
                    disableRipple
                  >
                    <ListItemText
                      primary={store.name || 'Loja iFood'}
                      primaryTypographyProps={{ className: 'pedidos-store-menu-item-name' }}
                    />
                    <Chip
                      label={`${store.statusLabel || 'Sem status'}${!dataFreshness.merchantReadVerified || store.stale ? ' • Último status' : ''}`}
                      color={store.statusColor || 'default'}
                      size="small"
                      className="pedidos-store-chip"
                      icon={<FiberManualRecordIcon className="pedidos-status-icon" />}
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem className="pedidos-store-menu-empty" disableRipple>
                  <ListItemText primary="Nenhuma loja cadastrada neste login/CNPJ." />
                </MenuItem>
              )}
            </Menu>
            {(Array.isArray(merchantCard.interruptions) ? merchantCard.interruptions : []).some((item) => isInterruptionActive(item)) ? (
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={handleReopenMerchant}
                disabled={merchantActionLoading || !dataFreshness.merchantReadVerified || merchantCard.stale}
                className="pedidos-reopen-button"
              >
                {merchantActionLoading ? 'Abrindo loja...' : 'Abrir loja'}
              </Button>
            ) : null}
          </Box>

          <Box className="pedidos-search pedidos-search--compact">
            <SearchIcon className="pedidos-search-icon" />
            <InputBase
              placeholder="Buscar pedido"
              className="pedidos-search-input"
              value={orderSearch}
              onChange={(event) => setOrderSearch(event.target.value)}
              inputProps={{ 'aria-label': 'Buscar pedido' }}
            />
            <Tooltip title={`Filtrar por status: ${activeStatusFilterLabel}`}>
              <IconButton
                size="small"
                color={statusFilters.length ? 'primary' : 'default'}
                aria-label="Filtrar pedidos por status"
                aria-controls={statusFilterMenuOpen ? 'pedidos-status-filter-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={statusFilterMenuOpen ? 'true' : undefined}
                onClick={(event) => setStatusFilterAnchorEl(event.currentTarget)}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="pedidos-status-filter-menu"
              anchorEl={statusFilterAnchorEl}
              open={statusFilterMenuOpen}
              onClose={() => setStatusFilterAnchorEl(null)}
              MenuListProps={{ 'aria-label': 'Filtros de status do pedido' }}
            >
              <MenuItem
                selected={!statusFilters.length}
                onClick={() => setStatusFilters([])}
              >
                <ListItemText primary="Todos os status" />
              </MenuItem>
              {statusFilterOptions.map((option) => (
                <MenuItem key={option.key} onClick={() => toggleStatusFilter(option.key)}>
                  <Checkbox
                    size="small"
                    checked={statusFilters.includes(option.key)}
                  />
                  <ListItemText
                    primary={option.label}
                    secondary={`${dashboard.counters[option.key] || 0} pedido(s)`}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box className="pedidos-toolbar-icons pedidos-toolbar-icons--compact">
            <Button
              size="small"
              variant="outlined"
              startIcon={<PointOfSaleRoundedIcon />}
              onPointerEnter={() => { void prefetchBalcaoCatalog(); }}
              onFocus={() => { void prefetchBalcaoCatalog(); }}
              onPointerDown={() => { void prefetchBalcaoCatalog(); }}
              onClick={() => {
                void prefetchBalcaoCatalog();
                navigate('/balcao');
              }}
              className="pedidos-counter-shortcut-button"
            >
              Balcão
            </Button>
            <Tooltip title={ifoodWidgetReconnectTooltip}>
              <span>
                <IconButton
                  size="small"
                  color={ifoodWidgetStatus.state === 'error' ? 'warning' : 'primary'}
                  aria-label="Reconectar widget iFood"
                  onClick={handleReconnectIfoodWidget}
                  disabled={ifoodWidgetIsBusy || ifoodWidgetIsOffline}
                  className="pedidos-widget-button"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <FormControlLabel
              control={<Switch checked={autoAccept} onChange={(e) => handleAutoAcceptChange(e.target.checked)} size="small" />}
              label="Aceitação automática"
              className="pedidos-toolbar-toggle pedidos-toolbar-toggle--live"
            />
            <FormControlLabel
              control={<Switch checked={autoPrint} onChange={(e) => handleAutoPrintChange(e.target.checked)} size="small" />}
              label="Impressão automática"
              className="pedidos-toolbar-toggle"
            />
          </Box>
        </Box>
      </Paper>
      {error && (
        <Paper className="pedidos-alert" elevation={0}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      <DragDropContext onBeforeCapture={onBeforeCapture} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Box
          ref={boardRef}
          className="pedidos-grid"
        >
          <Box className="pedidos-grid-slot pedidos-grid-slot--preparing">
            <Paper className="pedidos-card pedidos-card--primary">
              <Box className="pedidos-card-header">
                <Typography variant="h6" className="pedidos-card-header-title">{statusBlocks[0].title}</Typography>
                <Chip label={visibleCounters.preparing} size="small" className="pedidos-chip" />
                <Button
                  size="small"
                  variant="contained"
                  className="pedidos-kds-send-button"
                  disabled={!selectedPreparingOrderIds.length || sendingSelectedToKds}
                  onClick={sendSelectedPreparingOrdersToKds}
                >
                  {sendingSelectedToKds
                    ? 'Enviando...'
                    : selectedPreparingOrderIds.length
                      ? `KDS (${selectedPreparingOrderIds.length})`
                      : 'KDS'}
                </Button>
              </Box>
              <Divider className="pedidos-divider" />
              <Droppable droppableId="preparing">
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`pedidos-droppable-area ${snapshot.isDraggingOver ? 'pedidos-drop-zone' : ''}`}
                  >
                    {loading ? (
                      <Box className="pedidos-empty pedidos-empty--primary">
                        <Box className="pedidos-empty-icon" aria-hidden="true">
                          {React.createElement(statusBlocks[0].icon)}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {statusBlocks[0].description}
                        </Typography>
                        {statusBlocks[0].action && (
                          <Button variant="contained" size="small" className="pedidos-cta">
                            {statusBlocks[0].action}
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Box className="pedidos-order-list pedidos-order-list--primary">
                        {categorizedOrders.preparing.length > 0 ? (
                          categorizedOrders.preparing.map((order, index) => (
                            <Draggable key={order.orderId} draggableId={order.orderId} index={index} isDragDisabled={!isDashboardOrderCurrent(order)}>
                              {(provided, snapshot) => (
                                <Paper
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`pedidos-order-card pedidos-order-card--selectable ${selectedPreparingOrderIdSet.has(order.orderId) ? 'pedidos-order-card--selected' : ''} ${getKdsReadyCardClassName(order)} ${getShippingCardClassName(order)} ${getSalonCardClassName(order)} ${getLocalReadyCardClassName(order)} ${needsManualAcceptance(order) ? 'pedidos-order-card--with-actions' : ''} ${snapshot.isDragging ? 'pedidos-dragging' : ''}`}
                                  elevation={snapshot.isDragging ? 3 : 0}
                                  style={{
                                    ...provided.draggableProps.style,
                                    position: 'relative',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isDashboardOrderCurrent(order)) return;
                                    setOrderModal({ open: true, order });
                                    setOrderDetails(null);
                                    setPickupCodeConfirmation(createPickupCodeConfirmationState());
                                    fetchOrderDetails(order.orderId);
                                  }}
                                >
                                  <Box
                                    className="pedidos-selection-control"
                                    onClick={(event) => event.stopPropagation()}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onTouchStart={(event) => event.stopPropagation()}
                                  >
                                    {selectedPreparingSequenceById.has(order.orderId) && (
                                      <Box className="pedidos-selection-badge">
                                        {selectedPreparingSequenceById.get(order.orderId)}#
                                      </Box>
                                    )}
                                    <Checkbox
                                      size="small"
                                      disabled={!isDashboardOrderCurrent(order)}
                                      checked={selectedPreparingOrderIdSet.has(order.orderId)}
                                      onChange={(event) => togglePreparingOrderSelection(event, order.orderId)}
                                      inputProps={{ 'aria-label': `Selecionar pedido ${order.displayId || order.orderId}` }}
                                      className="pedidos-selection-checkbox"
                                    />
                                  </Box>
                                  {renderKdsRouteBadge(order)}
                                  {renderLocalReadyAttention(order)}
                                  {getBatchDragPreview(order.orderId, snapshot)}
                                  <Typography className="pedidos-order-card__number">{order.displayId || order.orderId}</Typography>
                                  <Typography className="pedidos-order-card__subtitle">{getOrderCardSubtitle(order)}</Typography>
                                  {renderOrderCardSource(order)}
                                  {renderCourierCardStatus(order)}
                                  <Typography className="pedidos-order-card__detail">{getOrderCardDetail(order)}</Typography>
                                  {renderLegacyDineInReadyNotice(order)}
                                  {!isCompactLocalOrder(order) && hasVisibleCardBadges(order) && (
                                    <Box className="pedidos-order-card__badges pedidos-order-card__badges--centered">
                                      {getOperationalChipConfigs(order).map((chip) => (
                                        <Chip key={chip.key} label={chip.label} color={chip.color} size="small" variant={chip.variant} className="pedidos-mini-chip" />
                                      ))}
                                      {hasActiveNegotiation(order) && (
                                        <Chip label="Negociação" color="warning" size="small" variant="outlined" className="pedidos-mini-chip" />
                                      )}
                                    </Box>
                                  )}
                                  {renderShippingCardDetails(order)}

                                  {/* Botões para pedidos que precisam ser confirmados */}
                                  {needsManualAcceptance(order) && (
                                    <Box className="pedidos-accept-button-container">
                                      <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          acceptOrder(order.orderId);
                                        }}
                                        disabled={!isDashboardOrderCurrent(order)}
                                        className="pedidos-accept-button"
                                      >
                                        ACEITAR
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openCancellationFlow(order);
                                        }}
                                        disabled={!isDashboardOrderCurrent(order)}
                                        className="pedidos-accept-button"
                                      >
                                        RECUSAR
                                      </Button>
                                    </Box>
                                  )}
                                  <CountdownTimer
                                    order={order}
                                    compact={String(order?.orderTiming || '').trim().toUpperCase() === 'SCHEDULED'}
                                    {...getCountdownProps(order)}
                                  />
                                </Paper>
                              )}
                            </Draggable>
                          ))
                        ) : (
                          <Box className="pedidos-empty pedidos-empty--primary">
                            <Box className="pedidos-empty-icon" aria-hidden="true">
                              {React.createElement(statusBlocks[0].icon)}
                            </Box>
                            <Typography className="pedidos-empty-title">
                              Nenhum pedido em preparo
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {statusBlocks[0].description}
                            </Typography>
                            {statusBlocks[0].action && (
                              <Button variant="contained" size="small" className="pedidos-cta">
                                {statusBlocks[0].action}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          </Box>

          <Box className="pedidos-grid-column pedidos-grid-column--stacked">
            <Box className="pedidos-grid-slot pedidos-grid-slot--ready">
              <Paper className="pedidos-card">
                <Box className="pedidos-card-header">
                  <Typography variant="h6" className="pedidos-card-header-title">{statusBlocks[1].title}</Typography>
                  <Chip label={visibleCounters[statusBlocks[1].key]} size="small" className="pedidos-chip" />
                </Box>
                <Divider className="pedidos-divider" />
                <Droppable droppableId={statusBlocks[1].key}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`pedidos-droppable-area ${snapshot.isDraggingOver ? 'pedidos-drop-zone' : ''}`}
                    >
                      <Box className="pedidos-order-list">
                        {categorizedOrders[statusBlocks[1].key].length > 0 ? (
                          categorizedOrders[statusBlocks[1].key].map((order, index) => (
                            <Draggable key={order.orderId} draggableId={order.orderId} index={index} isDragDisabled={!isDashboardOrderCurrent(order)}>
                              {(provided, snapshot) => (
                                <Paper
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`pedidos-order-card ${getKdsReadyCardClassName(order)} ${getShippingCardClassName(order)} ${getSalonCardClassName(order)} ${getLocalReadyCardClassName(order)} ${needsManualAcceptance(order) ? 'pedidos-order-card--with-actions' : ''} ${snapshot.isDragging ? 'pedidos-dragging' : ''}`}
                                  elevation={snapshot.isDragging ? 3 : 0}
                                  style={{
                                    ...provided.draggableProps.style,
                                    position: 'relative',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isDashboardOrderCurrent(order)) return;
                                    setOrderModal({ open: true, order });
                                    setOrderDetails(null);
                                    setPickupCodeConfirmation(createPickupCodeConfirmationState());
                                    fetchOrderDetails(order.orderId);
                                  }}
                                >
                                  {renderKdsRouteBadge(order)}
                                  {renderLocalReadyAttention(order)}
                                  <Typography className="pedidos-order-card__number">{order.displayId || order.orderId}</Typography>
                                  <Typography className="pedidos-order-card__subtitle">{getOrderCardSubtitle(order)}</Typography>
                                  {renderOrderCardSource(order)}
                                  {renderCourierCardStatus(order)}
                                  <Typography className="pedidos-order-card__detail">{getOrderCardDetail(order)}</Typography>
                                  {renderLegacyDineInReadyNotice(order)}
                                  {!isCompactLocalOrder(order) && hasVisibleCardBadges(order) && (
                                    <Box className="pedidos-order-card__badges pedidos-order-card__badges--centered">
                                      {getOperationalChipConfigs(order).map((chip) => (
                                        <Chip key={chip.key} label={chip.label} color={chip.color} size="small" variant={chip.variant} className="pedidos-mini-chip" />
                                      ))}
                                      {hasActiveNegotiation(order) && (
                                        <Chip label="Negociação" color="warning" size="small" variant="outlined" className="pedidos-mini-chip" />
                                      )}
                                    </Box>
                                  )}
                                  {renderShippingCardDetails(order)}

                                  {/* Botões para pedidos que precisam ser confirmados */}
                                  {needsManualAcceptance(order) && (
                                    <Box className="pedidos-accept-button-container">
                                      <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          acceptOrder(order.orderId);
                                        }}
                                        disabled={!isDashboardOrderCurrent(order)}
                                        className="pedidos-accept-button"
                                      >
                                        ACEITAR
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openCancellationFlow(order);
                                        }}
                                        disabled={!isDashboardOrderCurrent(order)}
                                        className="pedidos-accept-button"
                                      >
                                        RECUSAR
                                      </Button>
                                    </Box>
                                  )}
                                  <CountdownTimer
                                    order={order}
                                    compact={String(order?.orderTiming || '').trim().toUpperCase() === 'SCHEDULED'}
                                    {...getCountdownProps(order)}
                                  />
                                </Paper>
                              )}
                            </Draggable>
                          ))
                        ) : (
                          <Box className={`pedidos-empty ${statusBlocks[1].key === 'preparing' ? '' : 'pedidos-empty-small'}`}>
                            <Box className="pedidos-empty-icon" aria-hidden="true">
                              {React.createElement(statusBlocks[1].icon)}
                            </Box>
                            <Typography className="pedidos-empty-title">
                              Nenhum pedido pronto
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {statusBlocks[1].description}
                            </Typography>
                            {statusBlocks[1].action && (
                              <Button variant="contained" size="small" className="pedidos-cta">
                                {statusBlocks[1].action}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Paper>
            </Box>

          <Box className="pedidos-grid-slot pedidos-grid-slot--route">
            <Paper className="pedidos-card">
              <Box className="pedidos-card-header">
                <Typography variant="h6" className="pedidos-card-header-title">{statusBlocks[2].title}</Typography>
                <Chip label={visibleCounters[statusBlocks[2].key]} size="small" className="pedidos-chip" />
              </Box>
              <Divider className="pedidos-divider" />
              <Droppable droppableId={statusBlocks[2].key}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`pedidos-droppable-area ${snapshot.isDraggingOver ? 'pedidos-drop-zone' : ''}`}
                  >
                    <Box className="pedidos-order-list">
                      {categorizedOrders[statusBlocks[2].key].length > 0 ? (
                        categorizedOrders[statusBlocks[2].key].map((order, index) => (
                          <Draggable key={order.orderId} draggableId={order.orderId} index={index} isDragDisabled={!isDashboardOrderCurrent(order)}>
                            {(provided, snapshot) => (
                              <Paper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`pedidos-order-card ${getKdsReadyCardClassName(order)} ${getShippingCardClassName(order)} ${getSalonCardClassName(order)} ${getLocalReadyCardClassName(order)} ${needsManualAcceptance(order) ? 'pedidos-order-card--with-actions' : ''} ${snapshot.isDragging ? 'pedidos-dragging' : ''}`}
                                elevation={snapshot.isDragging ? 3 : 0}
                                style={{
                                  ...provided.draggableProps.style,
                                  position: 'relative',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isDashboardOrderCurrent(order)) return;
                                    setOrderModal({ open: true, order });
                                  setOrderDetails(null);
                                  setPickupCodeConfirmation(createPickupCodeConfirmationState());
                                  fetchOrderDetails(order.orderId);
                                }}
                              >
                                {renderKdsRouteBadge(order)}
                                {renderLocalReadyAttention(order)}
                                <Typography className="pedidos-order-card__number">{order.displayId || order.orderId}</Typography>
                                <Typography className="pedidos-order-card__subtitle">{getOrderCardSubtitle(order)}</Typography>
                                {renderOrderCardSource(order)}
                                {renderCourierCardStatus(order)}
                                <Typography className="pedidos-order-card__detail">{getOrderCardDetail(order)}</Typography>
                                {renderLegacyDineInReadyNotice(order)}
                                {!isCompactLocalOrder(order) && hasVisibleCardBadges(order) && (
                                  <Box className="pedidos-order-card__badges pedidos-order-card__badges--centered">
                                    {getOperationalChipConfigs(order).map((chip) => (
                                      <Chip key={chip.key} label={chip.label} color={chip.color} size="small" variant={chip.variant} className="pedidos-mini-chip" />
                                    ))}
                                    {hasActiveNegotiation(order) && (
                                      <Chip label="Negociação" color="warning" size="small" variant="outlined" className="pedidos-mini-chip" />
                                    )}
                                  </Box>
                                )}
                                {renderShippingCardDetails(order)}

                                {/* Botões para pedidos que precisam ser confirmados */}
                                {needsManualAcceptance(order) && (
                                  <Box className="pedidos-accept-button-container">
                                    <Button
                                      variant="contained"
                                      color="error"
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        acceptOrder(order.orderId);
                                      }}
                                      disabled={!isDashboardOrderCurrent(order)}
                                        className="pedidos-accept-button"
                                    >
                                      ACEITAR
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openCancellationFlow(order);
                                      }}
                                      disabled={!isDashboardOrderCurrent(order)}
                                        className="pedidos-accept-button"
                                    >
                                      RECUSAR
                                    </Button>
                                  </Box>
                                )}
                                <CountdownTimer
                                  order={order}
                                  compact={String(order?.orderTiming || '').trim().toUpperCase() === 'SCHEDULED'}
                                  {...getCountdownProps(order)}
                                />
                                {order.status === 'enRoute' && (
                                  <RouteElapsedTimer order={order} />
                                )}
                              </Paper>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <Box className={`pedidos-empty ${statusBlocks[2].key === 'preparing' ? '' : 'pedidos-empty-small'}`}>
                          <Box className="pedidos-empty-icon" aria-hidden="true">
                            {React.createElement(statusBlocks[2].icon)}
                          </Box>
                          <Typography className="pedidos-empty-title">
                            Nenhum pedido em rota
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {statusBlocks[2].description}
                          </Typography>
                          {statusBlocks[2].action && (
                            <Button variant="contained" size="small" className="pedidos-cta">
                              {statusBlocks[2].action}
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          </Box>

          <Box className="pedidos-grid-slot pedidos-grid-slot--finished">
            <Paper className="pedidos-card">
              <Box className="pedidos-card-header">
                <Typography variant="h6" className="pedidos-card-header-title">Finalizados</Typography>
                <Chip label={visibleCounters.finished} size="small" className="pedidos-chip" />
              </Box>
              <Divider className="pedidos-divider" />

              <Droppable droppableId="finished">
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`pedidos-droppable-area ${snapshot.isDraggingOver ? 'pedidos-drop-zone' : ''}`}
                  >
                    {categorizedOrders.finished.length ? (
                      <Box className="pedidos-finished-list">
                        {categorizedOrders.finished.map((order, index) => (
                          <Draggable key={order.orderId} draggableId={order.orderId} index={index} isDragDisabled={!isDashboardOrderCurrent(order)}>
                            {(provided, snapshot) => (
                              <Paper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`pedidos-finished-card ${getSalonCardClassName(order)} ${order.status === 'cancelled' ? 'pedidos-finished-card--cancelled' : ''} ${snapshot.isDragging ? 'pedidos-dragging' : ''}`}
                                elevation={snapshot.isDragging ? 3 : 0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isDashboardOrderCurrent(order)) return;
                                    setOrderModal({ open: true, order });
                                  setOrderDetails(null);
                                  setPickupCodeConfirmation(createPickupCodeConfirmationState());
                                  fetchOrderDetails(order.orderId);
                                }}
                              >
                                {renderKdsRouteBadge(order)}
                                <Typography variant="h5" className="pedidos-finished-number pedidos-order-card__number pedidos-order-card__number--finished">
                                  {order.displayId || order.orderId}
                                </Typography>
                                <Typography className="pedidos-order-card__subtitle">
                                  {getOrderCardSubtitle(order)}
                                </Typography>
                                {getSourceChip(order)}
                                {renderCourierCardStatus(order)}
                                {!isCompactLocalOrder(order) && hasVisibleCardBadges(order) && (
                                  <Box className="pedidos-order-card__badges pedidos-order-card__badges--centered">
                                    {getOperationalChipConfigs(order).map((chip) => (
                                      <Chip key={chip.key} label={chip.label} color={chip.color} size="small" variant={chip.variant} className="pedidos-mini-chip" />
                                    ))}
                                    {hasActiveNegotiation(order) && (
                                      <Chip label="Negociação" color="warning" size="small" variant="outlined" className="pedidos-mini-chip" />
                                    )}
                                  </Box>
                                )}
                                <Box className="pedidos-order-card__badges pedidos-order-card__badges--centered">
                                  <Chip
                                    {...getStatusChipProps(order.status)}
                                    size="small"
                                    className="pedidos-finished-chip"
                                  />
                                </Box>
                                {!isCompactLocalOrder(order) && (
                                  <Typography className="pedidos-order-card__detail pedidos-order-card__detail--centered">
                                    {getOrderCardDetail(order)}
                                  </Typography>
                                )}
                              </Paper>
                            )}
                          </Draggable>
                        ))}
                      </Box>
                    ) : (
                      <Box className="pedidos-empty pedidos-empty-small">
                        <Box className="pedidos-empty-icon" aria-hidden="true">
                          <TaskAltOutlinedIcon />
                        </Box>
                        <Typography className="pedidos-empty-title">
                          Nenhum pedido finalizado
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Arraste para cá pedidos do Balcão/Site/WhatsApp para concluir.
                        </Typography>
                      </Box>
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          </Box>
          </Box>
        </Box>
      </DragDropContext>

      {!orderModal.open && amendmentPrintNotice && (
        <Alert severity="info" onClose={() => setAmendmentPrintNotice('')}
          sx={{ position: 'fixed', bottom: 20, right: 20, maxWidth: 480, zIndex: 1400 }}>
          {amendmentPrintNotice}
        </Alert>
      )}

      <Dialog open={orderModal.open} onClose={closeOrderModal} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes do Pedido {orderModal.order?.displayId || orderModal.order?.orderId}</DialogTitle>
        <DialogContent>
          {amendmentPrintNotice && <Alert severity="info" sx={{ mb: 2 }}>{amendmentPrintNotice}</Alert>}
          {(orderDetails || orderModal.order)?.commercialConflict && (
            <Alert severity="error" sx={{ mb: 2 }}>A plataforma alterou o conteúdo original deste pedido. A edição salva precisa ser conciliada antes de uma nova alteração, cobrança ou emissão fiscal.</Alert>
          )}
          {['pending', 'retry', 'processing', 'blocked'].includes((orderDetails || orderModal.order)?.amendment?.syncStatus) && (
            <Alert severity="warning" sx={{ mb: 2 }}>A atualização dos valores no Brutos Entregas ainda está pendente. Confira a sincronização antes da cobrança.</Alert>
          )}
          {hasCommercialAmendment(orderDetails || orderModal.order) && (() => {
            const payments = getCommercialPayments(orderDetails || orderModal.order);
            return <Alert severity={Number(payments.refundPendingAmount) > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
              Revisão {(orderDetails || orderModal.order).amendmentRevision}: recebido líquido {formatCurrency(payments.totalPaid)} · A pagar {formatCurrency(payments.totalDue)}
              {Number(payments.refundPendingAmount) > 0 ? ` · A devolver ${formatCurrency(payments.refundPendingAmount)}` : ''}
              {Number(payments.totalRefunded) > 0 ? ` · Devolvido ${formatCurrency(payments.totalRefunded)}` : ''}
            </Alert>;
          })()}
          {loadingOrderDetails && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Atualizando dados complementares do pedido...
            </Alert>
          )}
          {orderDetailsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {orderDetailsError}
            </Alert>
          )}
          {orderAddressEditFeedback && (
            <Alert severity={orderAddressEditFeedback.severity} sx={{ mb: 2 }}>
              {orderAddressEditFeedback.message}
            </Alert>
          )}
          {orderModal.order && (() => {
            const sourceKey = getOrderSourceKey(orderModal.order);
            const isBalcao = sourceKey === 'balcao' || orderModal.order.source === 'balcao' || String(orderModal.order.orderId || '').startsWith('BALCAO-');
            const isSalao = isSalonOrder(orderModal.order) || sourceKey === 'salao';
            const isWhatsapp = sourceKey === 'whatsapp';
            const isSite = sourceKey === 'site' || orderModal.order.source === 'site' || String(orderModal.order.orderId || '').startsWith('SITE-');
            const isPedidos10 = sourceKey === 'pedidos10' || orderModal.order.source === 'pedidos10' || String(orderModal.order.orderId || '').startsWith('PEDIDOS10-');
            const is99Food = sourceKey === '99food';
            if (isBalcao || isSalao || isWhatsapp || isSite || isPedidos10 || is99Food) {
              const order =
                orderDetails && orderDetails.orderId === orderModal.order.orderId
                  ? orderDetails
                  : orderModal.order;
              const customer = order.customer || {};
              const itemsToRender = is99Food ? order.items || [] : order.items?.map((item) => applyCatalogToItem(item)) || order.items || [];
              const sourceLabel = is99Food ? '99Food' : isSalao ? 'Salão' : isPedidos10 ? 'Pedidos10' : isWhatsapp ? 'WhatsApp' : isSite ? 'Site' : 'Balcão';
              const responsibility = (isSalao || isBalcao)
                ? getLocalOrderResponsibility(order, isSalao ? 'salao' : 'balcao')
                : null;
              const paymentInfo = order.payments || {};
              const modalShipping = canUseIfoodShipping ? getVisibleShipping(order) : null;
              const modalShippingEtaLabel = getShippingEtaLabel(modalShipping);
              const modalShippingPickupCode = isIfoodOnDemandShipping(modalShipping) ? getShippingPickupCode(modalShipping) : '';
              const modalShippingDeliveryCode = isIfoodOnDemandShipping(modalShipping) ? getShippingDeliveryConfirmationCode(modalShipping) : '';
              const isPixOnlinePending = isLocalPixOnlinePending(order, paymentInfo);
              const paymentMethodLabel = hasCommercialAmendment(order) ? resolveComandaPaymentLabel(paymentInfo, order) : isPixOnlinePending ? pixOnlineContract.PIX_ONLINE_CONFIRMATION_LABEL :
                formatPaymentMethodLabel(
                  getAmendmentCollectionMethod(order) ||
                  paymentInfo.paymentMethod ||
                  paymentInfo.method ||
                  paymentInfo.methods?.[0]?.method ||
                  order.paymentMethod ||
                  ''
                );
              const paymentTimingLabel = isPixOnlinePending ? '' : formatPaymentTimingLabel(paymentInfo.paymentTiming || order.paymentTiming || '');
              const isPayOnDelivery =
                !isPixOnlinePending && (isSite || isWhatsapp || isBalcao) &&
                (((paymentInfo.paymentTiming || '').toLowerCase().includes('entrega')) ||
                  (paymentInfo.paymentTiming || '').toLowerCase() === 'on_delivery' ||
                  paymentInfo.methods?.some((m) => m.prepaid === false));
              const changeForValue = Number(paymentInfo.changeForRaw ?? paymentInfo.changeFor ?? 0) || 0;
              const paidAmount = hasCommercialAmendment(order) ? Number(paymentInfo.totalPaid || 0) : isPayOnDelivery ? 0 : Number(paymentInfo.totalPaid || 0);
              const totalDue = Number(paymentInfo.totalDue ?? order.total?.orderAmount ?? 0);
              const paymentOrderTotal = Number(order.total?.orderAmount || 0);
              const totalUnknown = isNinetyNineFoodTotalUnknown(order);
              const pendingAmount = hasCommercialAmendment(order) ? Number(paymentInfo.totalDue || 0) : Math.max(totalDue - paidAmount, 0);
              const receiveAmount = !hasCommercialAmendment(order) && isPayOnDelivery && changeForValue ? changeForValue : pendingAmount;
              const couponBreakdown = extractCouponBreakdownFromBenefits(
                order?.fullOrderData?.benefits,
                extractCouponBreakdown(order)
              );
              const discountValue = couponBreakdown.totalAmount;
              const couponCode = couponBreakdown.code;
              return (
                <Box>
                  <Typography variant="h6" gutterBottom>Informações Gerais</Typography>
                  <Typography variant="body2">Origem: {sourceLabel}</Typography>
                  {is99Food && (
                    <NinetyNineFoodOrderActions
                      apiUrl={API_URL} order={order} sessionToken={settingsSession.token} requestedAction={ninetyNineDropAction}
                      onUpdated={(updated) => { if (isDashboardOrderCurrent(updated)) { mergeOrderSnapshot(updated); setOrderDetails(updated); fetchOrders(); } }}
                    />
                  )}
                  <Typography variant="body2">Número do Pedido: {order.displayId || order.orderId}</Typography>
                  {is99Food && getNinetyNineFoodOfficialId(order) && (
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>ID oficial 99Food: {getNinetyNineFoodOfficialId(order)}</Typography>
                  )}
                  {is99Food && getOrderGeneralNotes(order) && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>Observações: {getOrderGeneralNotes(order)}</Typography>
                  )}
                  {is99Food && operational99Rows(order, formatCurrency, ['instructions', 'stock']).map((line, index) => (
                    <Typography key={`99info-${index}`} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{line}</Typography>
                  ))}
                  <Typography variant="body2">Status: {statusLabels[order.status] || order.status || '-'}</Typography>
                  <Typography variant="body2">Criado em: {order.createdAt ? new Date(order.createdAt).toLocaleString('pt-BR') : '-'}</Typography>
                  <Typography variant="body2">Tipo: {isSalao || order.fulfillment === 'dine_in' ? 'Salão' : order.fulfillment === 'delivery' ? 'Entrega' : 'Retirada'}</Typography>
                  {/^\d{4}$/.test(String(order.confirmationCode || '')) && (
                    <Typography variant="body2">
                      {order.fulfillment === 'delivery' ? 'Código de entrega' : 'Código de retirada'}: {order.confirmationCode}
                    </Typography>
                  )}
                  {isBalcao && order.fulfillment === 'delivery' && order.deliveryQuote && (
                    <>
                      <Typography variant="body2">Distância calculada: {(Number(order.deliveryQuote.distanceMeters || 0) / 1000).toFixed(2)} km</Typography>
                      <Typography variant="body2">Faixa aplicada: até {order.deliveryQuote.radiusKm} km</Typography>
                      <Typography variant="body2">Prazo estimado: {order.deliveryQuote.preparationMinutes} min</Typography>
                    </>
                  )}
                  {isSalao && (
                    <>
                      <Typography variant="body2">Mesa: {order.tableNumber || '-'}</Typography>
                      <Typography variant="body2">Comanda: {order.tableSessionId || '-'}</Typography>
                    </>
                  )}

                  {responsibility && (
                    <Box mt={2} data-testid="local-order-responsibility">
                      <Typography variant="h6" gutterBottom>Responsável pelo pedido</Typography>
                      <Typography variant="body2">Realizado por: {responsibility.actor}</Typography>
                      <Typography variant="body2">
                        {responsibility.personLabel}: {responsibility.personName}
                      </Typography>
                    </Box>
                  )}

                  {is99Food ? <NinetyNineFoodCustomerDelivery order={order} driver={getStoreDeliveryDriver(order)} /> : !isSalao && (
                  <Box mt={2}>
                    <Typography variant="h6" gutterBottom>Cliente</Typography>
                    <Typography variant="body2">Nome: {customer.name}</Typography>
                    <Typography variant="body2">{is99Food ? 'Contato 99Food' : 'WhatsApp'}: {is99Food ? customer.providerVirtualPhone || customer.phoneNumber || getCustomerPhoneNumber(customer.phone) || customer.whatsapp || 'Não informado pela plataforma' : customer.whatsapp}</Typography>
                    {is99Food && customer.providerLocator && <Typography variant="body2">Localizador 99Food: {customer.providerLocator}</Typography>}
                    {customer.cpf && <Typography variant="body2">CPF: {customer.cpf}</Typography>}
                    {order.fulfillment === 'delivery' && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        Endereço: {is99Food ? address99(order) : formatAddressForDisplay(customer.address || order.delivery?.deliveryAddress || '-')}
                      </Typography>
                    )}
                  </Box>
                  )}

                  {is99Food && Number(order.deliveryType) === 2 && <NinetyNineFoodEntregaPanel key={order.orderId}
                    apiUrl={API_URL} order={order} sessionToken={settingsSession.token}
                    canConfigure={String(currentUser?.role || '').toLowerCase() === 'programador'} canManage={isAdminLike(currentUser)}
                    onUpdated={updated => { if (isDashboardOrderCurrent(updated)) { mergeOrderSnapshot(updated); setOrderDetails(updated); fetchOrders(); } }} />}

                  {(isSite || isWhatsapp || isBalcao) && order.fulfillment === 'delivery'
                    && !isTerminalStatus(order.status) && !hasActiveIfoodShippingForCustomerTracking(order) && (
                      <IfoodCustomerTrackingLink key={order.orderId} orderId={order.orderId} mode="local" />
                    )}

                  {order.payments && (
                    <Box mt={2}>
                      <Typography variant="h6" gutterBottom>Pagamento</Typography>
                      {couponCode && (
                        <Typography variant="body2">Cupom: {couponCode}</Typography>
                      )}
                      {paymentMethodLabel && (
                        <Typography variant="body2">
                          Forma de pagamento: {paymentMethodLabel}{paymentTimingLabel ? ` (${paymentTimingLabel})` : ''}
                        </Typography>
                      )}
                      {is99Food && operational99Rows(order, formatCurrency, ['payment', 'price']).map((line, index) => (
                        <Typography key={`99payment-${index}`} variant="body2">{line}</Typography>
                      ))}
                      {totalUnknown ? <Typography variant="body2">{NINETY_NINE_FOOD_TOTAL_UNKNOWN}</Typography> : <Box>
                      <Typography variant="body2">Total: {formatCurrency(paymentOrderTotal)}</Typography>
                      <Typography variant="body2">Pago: {formatCurrency(paidAmount)}</Typography>
                      {hasCommercialAmendment(order) && <Typography variant="body2" fontWeight="bold">Cobrar na entrega: {formatCurrency(paymentInfo.deliveryAmountDue || 0)}</Typography>}
                      {((!hasCommercialAmendment(order) && isPayOnDelivery) || pendingAmount > 0) && (
                        <Typography variant="body2" fontWeight="bold">
                          {isPixOnlinePending ? 'Pendente de confirmação' : isPayOnDelivery ? 'Valor a receber' : 'Pendente'}: {formatCurrency(receiveAmount)}
                        </Typography>
                      )}
                      {!is99Food && Number(order.payments.change || 0) > 0 && (
                        <Typography variant="body2" fontWeight="bold">
                          Troco: {formatCurrency(order.payments.change || 0)}
                        </Typography>
                      )}
                      {!is99Food && changeForValue > 0 && (
                        <Typography variant="body2" fontWeight="bold">
                          Valor do Troco: {formatCurrency(Math.max(changeForValue - pendingAmount, 0))}
                        </Typography>
                      )}
                      {Array.isArray(order.payments.methods) && order.payments.methods.length > 0 && (
                        <Box mt={1}>
                          {hasCommercialAmendment(order) ? amendmentPaymentLines(order).map((method, idx) => (
                            <Box key={idx}>
                              {method.amountPaid > 0 && <Typography variant="body2" color="text.secondary">{method.label} recebido: {formatCurrency(method.amountPaid)}</Typography>}
                              {method.amountDue > 0 && <Typography variant="body2" color="text.secondary">{method.label} - {method.pendingLabel}: {formatCurrency(method.amountDue)}</Typography>}
                            </Box>
                          )) : order.payments.methods.map((m, idx) => (
                            <Typography key={idx} variant="body2" color="text.secondary">
                              {(isPixOnlinePending ? pixOnlineContract.PIX_ONLINE_CONFIRMATION_LABEL : isPayOnDelivery ? 'Valor a receber' : formatPaymentMethodLabel(m.method || m.type || ''))}: {formatCurrency(
                                isPayOnDelivery ? receiveAmount : Number(m.amount ?? m.value ?? 0)
                              )}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      </Box>}
                    </Box>
                  )}

                  {itemsToRender && itemsToRender.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="h6" gutterBottom>Itens</Typography>
                      {itemsToRender.map((item, index) => (
                        <Box key={index} mb={2} p={1} border={1} borderColor="grey.300" borderRadius={1} display="flex" gap={1}>
                          {item.imageUrl && (
                            <img
                              src={resolveCatalogImageUrl(item.imageUrl)}
                              alt={item.name}
                              style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }}
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold">{item.quantity}x {item.name}</Typography>
                            <Typography variant="body2">Preço Unitário: {formatCurrency(item.unitPrice)}</Typography>
                            <Typography variant="body2">Preço Total: {formatCurrency(item.totalPrice)}</Typography>
                            {item.observations && (
                              <Typography variant="body2">Observações: {item.observations}</Typography>
                            )}
                            {item.options && item.options.length > 0 && (
                              <Box mt={1}>
                                <Typography variant="subtitle2">Complementos:</Typography>
                                {(is99Food ? flatten99Options(item.options) : item.options).map((option, optIndex) => (
                                  <Box key={optIndex} ml={is99Food ? 2 + (option.depth || 0) * 2 : 2} display="flex" gap={1} alignItems="flex-start">
                                    {option.imageUrl && (
                                      <img
                                        src={resolveCatalogImageUrl(option.imageUrl)}
                                        alt={option.name}
                                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, marginTop: 4 }}
                                      />
                                    )}
                                    <Box>
                                      <Typography variant="body2">{option.quantity}x {option.name}</Typography>
                                      {is99Food && option.observations && <Typography variant="body2">Observações: {option.observations}</Typography>}
                                      {Number(option.price ?? option.unitPrice ?? option.unit_price ?? option.amount ?? 0) > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                          Preço: {formatCurrency(Number(option.price ?? option.unitPrice ?? option.unit_price ?? option.amount ?? 0))}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {order.total && (
                    <Box mt={2}>
                      <Typography variant="h6" gutterBottom>Total</Typography>
                      <Typography variant="body2">Subtotal: {formatCurrency(order.total.subTotal || 0)}</Typography>
                      <Typography variant="body2">
                        Taxa de Entrega{isBalcao && order.fulfillment === 'delivery' && order.deliveryFeeAdjustment?.mode === 'manual' ? ' (Manual)' : ''}: {is99Food && (order.total.deliveryFee === null || order.total.deliveryFee === undefined) ? 'Não informado pela 99Food' : formatCurrency(order.total.deliveryFee || 0)}
                      </Typography>
                      {discountValue > 0 && (
                        <Typography variant="body2">
                          Desconto: -{formatCurrency(discountValue)}
                        </Typography>
                      )}
                      {couponBreakdown.ifoodAmount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Subsidiado iFood: {formatCurrency(couponBreakdown.ifoodAmount)}
                        </Typography>
                      )}
                      {couponBreakdown.merchantAmount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Subsidiado loja: {formatCurrency(couponBreakdown.merchantAmount)}
                        </Typography>
                      )}
                      <Typography variant="body2" fontWeight="bold">
                        Valor Total: {totalUnknown ? NINETY_NINE_FOOD_TOTAL_UNKNOWN : formatCurrency(order.total.orderAmount || order.total || 0)}
                      </Typography>
                    </Box>
                  )}

                  {modalShipping && (
                    <Box mt={2}>
                      <Typography variant="h6" gutterBottom>Entrega iFood</Typography>
                      <Box sx={{ display: 'grid', gap: 0.5 }}>
                        <Chip
                          label={modalShipping.statusLabel || getShippingChipConfig(order)?.label || 'Entrega iFood'}
                          color={getShippingChipConfig(order)?.color || 'info'}
                          variant={getShippingChipConfig(order)?.variant || 'outlined'}
                          size="small"
                          sx={{ width: 'fit-content' }}
                        />
                        {modalShipping.driver?.name && (
                          <Typography variant="body2">
                            Entregador: {modalShipping.driver.name}{modalShippingEtaLabel ? ` - ${modalShippingEtaLabel}` : ''}
                          </Typography>
                        )}
                        {!modalShipping.driver?.name && modalShippingEtaLabel && (
                          <Typography variant="body2">{modalShippingEtaLabel}</Typography>
                        )}
                        {modalShippingPickupCode && (
                          <Typography variant="body2">Codigo de coleta: {modalShippingPickupCode}</Typography>
                        )}
                        {renderPickupCodeConfirmationForm(order)}
                        {modalShippingDeliveryCode && (
                          <Typography variant="body2">Codigo de entrega: {modalShippingDeliveryCode}</Typography>
                        )}
                        {modalShipping.tracking?.checkedAt && (
                          <Typography variant="body2" color="text.secondary">
                            Rastreio atualizado: {formatShortDateTime(modalShipping.tracking.checkedAt)}
                          </Typography>
                        )}
                        {modalShipping.trackingUrl && (
                          <Typography variant="body2" color="text.secondary">
                            Rastreio do cliente: {modalShipping.trackingUrl}
                          </Typography>
                        )}
                        {modalShipping.addressChange && (
                          <Box sx={{ display: 'grid', gap: 0.5, mt: 1 }}>
                            <Chip
                              label={
                                modalShipping.addressChange.pending
                                  ? 'Alteração de endereço pendente'
                                  : `Endereço: ${modalShipping.addressChange.status || 'atualizado'}`
                              }
                              color={modalShipping.addressChange.pending ? 'warning' : 'default'}
                              size="small"
                              sx={{ width: 'fit-content' }}
                            />
                            {modalShipping.addressChange.address && (
                              <Typography variant="body2" color="text.secondary">
                                Novo endereço: {[
                                  modalShipping.addressChange.address.streetName || modalShipping.addressChange.address.street,
                                  modalShipping.addressChange.address.streetNumber || modalShipping.addressChange.address.number,
                                  modalShipping.addressChange.address.neighborhood,
                                  modalShipping.addressChange.address.city,
                                  modalShipping.addressChange.address.state,
                                ].filter(Boolean).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {shippingActionState.error && (
                          <Alert severity="error" sx={{ mt: 1 }}>{shippingActionState.error}</Alert>
                        )}
                        {shippingActionState.message && (
                          <Alert severity="success" sx={{ mt: 1 }}>{shippingActionState.message}</Alert>
                        )}
                      </Box>
                    </Box>
                  )}
                  {beeDeliveryStatus.orderKey === getCourierOrderKey(order) && (
                    <BeeDeliveryStatusPanel
                      status={beeDeliveryStatus.data}
                      loading={beeDeliveryStatus.loading}
                      error={beeDeliveryStatus.error}
                      advancing={beeDeliveryStatus.advancing}
                      canManageSandbox={isAdminLike(currentUser)}
                      canManageConfirmation={isAdminLike(currentUser)}
                      onConfirmCode={(code) => confirmBeeLocalCode(order,code)}
                      onRefresh={() => refreshBeeDeliveryStatus(order)}
                      onAdvance={(event) => advanceBeeSandboxStatus(order, event)}
                    />
                  )}
                  {beeDeliveryStatus.orderKey===getCourierOrderKey(order) && beeDeliveryStatus.data?.provider==='bee' && isAdminLike(currentUser) && (
                    <BeeDeliveryOperationsPanel apiUrl={API_URL} sessionToken={auth.token} source={getExternalOrderSource(order)} orderId={String(order.orderId||'')} deliveryId={beeDeliveryStatus.data.deliveryId}
                      capabilities={beeDeliveryStatus.data.capabilities} canProgrammer={String(currentUser?.role||'').toLowerCase()==='programador'} onRefresh={()=>refreshBeeDeliveryStatus(order)}/>

                  )}
                </Box>
              );
            }

            const customer = orderDetails?.fullOrderData?.customer ||
                             (typeof orderDetails?.customer === 'object' ? orderDetails?.customer : null) ||
                             (typeof orderModal.order.customer === 'object' ? orderModal.order.customer : { name: orderModal.order.customer || 'Cliente iFood' });
            const deliveryInfo = orderDetails?.delivery || orderModal.order.delivery || null;
            const paymentInfo = getCommercialPayments(orderDetails || orderModal.order);
            const paymentMethods = Array.isArray(paymentInfo?.methods) ? paymentInfo.methods : [];
            const totalInfo = getCommercialTotal(orderDetails || orderModal.order);
            const totalAmountValue = typeof totalInfo === 'number' ? totalInfo : totalInfo?.orderAmount;
            const isScheduledOrder = String(orderModal.order.orderTiming || '').trim().toUpperCase() === 'SCHEDULED';
            const itemsToRender = orderDetails?.items
              ? orderDetails.items
              : orderModal.order.items?.map((item) => applyCatalogToItem(item)) || orderModal.order.items;
            const couponBreakdown = extractCouponBreakdownFromBenefits(
              orderDetails?.fullOrderData?.benefits || orderModal.order?.fullOrderData?.benefits,
              extractCouponBreakdown(orderDetails || orderModal.order)
            );
            const negotiation = orderDetails?.negotiation || orderModal.order.negotiation || null;
            const modalOrder = orderDetails || orderModal.order;
            const modalShipping = canUseIfoodShipping ? getVisibleShipping(modalOrder) : null;
            const modalShippingEtaLabel = getShippingEtaLabel(modalShipping);
            const modalShippingPickupCode = isIfoodOnDemandShipping(modalShipping) ? getShippingPickupCode(modalShipping) : '';
            const modalStoreDriver = modalShipping ? null : getStoreDeliveryDriver(modalOrder);
            return (
            <Box>
              <Typography variant="h6" gutterBottom>Informações Gerais</Typography>
              <Typography variant="body2">Número do Pedido: {orderModal.order.displayId}</Typography>
              <Typography variant="body2">Status: {statusLabels[orderDetails?.status || orderModal.order.status] || orderDetails?.status || orderModal.order.status || '-'}</Typography>
              <Typography variant="body2">Criado em: {new Date(orderModal.order.createdAt).toLocaleString('pt-BR')}</Typography>
              {deliveryInfo?.deliveryDateTime && (
                <Typography variant="body2">Previsão de Entrega: {new Date(deliveryInfo.deliveryDateTime).toLocaleString('pt-BR')}</Typography>
              )}
              <Typography variant="body2">Canal de Vendas: {orderModal.order.salesChannel}</Typography>
              <Typography variant="body2">Tipo do pedido: {getReadableOrderTypeLabel(orderModal.order)}</Typography>
              <Typography variant="body2">Janela: {formatOrderTimingLabel(orderModal.order.orderTiming)}</Typography>
              {isScheduledOrder && getScheduledDateTime(orderModal.order) && (
                <Typography variant="body2">Agendado para: {getScheduledDateTime(orderModal.order)}</Typography>
              )}

              {orderModal.order.merchant && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Restaurante</Typography>
                  <Typography variant="body2">Nome: {orderModal.order.merchant.name}</Typography>
                </Box>
              )}

              {customer && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Cliente</Typography>
                  <Typography variant="body2">Nome: {customer.name || 'Não informado'}</Typography>
                  <Typography variant="body2">CPF: {customer.documentNumber || 'Não informado'}</Typography>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700 }}>Telefone:</Box> {customer.phone?.number || 'Não informado'}
                  </Typography>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700 }}>Localizador:</Box> {customer.phone?.localizer || 'Não informado'}
                  </Typography>
                  <Typography variant="body2">Pedidos na Loja: {customer.ordersCountOnMerchant ?? 'Não informado'}</Typography>
                </Box>
              )}

              {deliveryInfo && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Entrega</Typography>
                  {deliveryInfo.deliveryAddress && (
                    <Box mt={1}>
                      <Typography variant="subtitle2">Endereço de Entrega:</Typography>
                      <Typography variant="body2">{deliveryInfo.deliveryAddress.formattedAddress}</Typography>
                      <Typography variant="body2">Bairro: {deliveryInfo.deliveryAddress.neighborhood}</Typography>
                      <Typography variant="body2">CEP: {deliveryInfo.deliveryAddress.postalCode}</Typography>
                      <Typography variant="body2">Cidade: {deliveryInfo.deliveryAddress.city}</Typography>
                      <Typography variant="body2">Estado: {deliveryInfo.deliveryAddress.state}</Typography>
                      <Typography variant="body2">Referência: {deliveryInfo.deliveryAddress.reference}</Typography>
                      {getOrderSourceKey(modalOrder) === 'ifood' &&
                        isIfoodDeliveryOrder(modalOrder) &&
                        !hasActiveIfoodShippingForCustomerTracking(modalOrder) &&
                        !isTerminalStatus(modalOrder.status) && (
                          <IfoodCustomerTrackingLink
                            key={modalOrder.orderId}
                            orderId={modalOrder.orderId}
                          />
                        )}
                    </Box>
                  )}
                </Box>
              )}

              {modalShipping && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Entrega iFood</Typography>
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    <Chip
                      label={modalShipping.statusLabel || getShippingChipConfig(modalOrder)?.label || 'Entrega iFood'}
                      color={getShippingChipConfig(modalOrder)?.color || 'info'}
                      variant={getShippingChipConfig(modalOrder)?.variant || 'outlined'}
                      size="small"
                      sx={{ width: 'fit-content' }}
                    />
                    {modalShipping.driver?.name && (
                      <Typography variant="body2">
                        Entregador: {modalShipping.driver.name}{modalShippingEtaLabel ? ` - ${modalShippingEtaLabel}` : ''}
                      </Typography>
                    )}
                    {!modalShipping.driver?.name && modalShippingEtaLabel && (
                      <Typography variant="body2">{modalShippingEtaLabel}</Typography>
                    )}
                    {modalShippingPickupCode && (
                      <Typography variant="body2">Codigo de coleta: {modalShippingPickupCode}</Typography>
                    )}
                    {renderPickupCodeConfirmationForm(modalOrder)}
                    {modalShipping.driver?.phone && (
                      <Typography variant="body2">Telefone do entregador: {modalShipping.driver.phone}</Typography>
                    )}
                    {modalShipping.tracking?.checkedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Rastreio atualizado: {formatShortDateTime(modalShipping.tracking.checkedAt)}
                      </Typography>
                    )}
                    {modalShipping.trackingUrl && (
                      <Typography variant="body2" color="text.secondary">
                        Rastreio do cliente: {modalShipping.trackingUrl}
                      </Typography>
                    )}
                    {modalShipping.safeDelivery?.data?.score && (
                      <Typography variant="body2" color="text.secondary">
                        Safe Delivery: {modalShipping.safeDelivery.data.score}
                      </Typography>
                    )}
                    {modalShipping.addressChange && (
                      <Box sx={{ display: 'grid', gap: 0.5, mt: 1 }}>
                        <Chip
                          label={
                            modalShipping.addressChange.pending
                              ? 'Alteração de endereço pendente'
                              : `Endereço: ${modalShipping.addressChange.status || 'atualizado'}`
                          }
                          color={modalShipping.addressChange.pending ? 'warning' : 'default'}
                          size="small"
                          sx={{ width: 'fit-content' }}
                        />
                        {modalShipping.addressChange.address && (
                          <Typography variant="body2" color="text.secondary">
                            Novo endereço: {[
                              modalShipping.addressChange.address.streetName || modalShipping.addressChange.address.street,
                              modalShipping.addressChange.address.streetNumber || modalShipping.addressChange.address.number,
                              modalShipping.addressChange.address.neighborhood,
                              modalShipping.addressChange.address.city,
                              modalShipping.addressChange.address.state,
                            ].filter(Boolean).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {shippingActionState.error && (
                      <Alert severity="error" sx={{ mt: 1 }}>{shippingActionState.error}</Alert>
                    )}
                    {shippingActionState.message && (
                      <Alert severity="success" sx={{ mt: 1 }}>{shippingActionState.message}</Alert>
                    )}
                  </Box>
                </Box>
              )}

              {modalStoreDriver && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Entregador da loja</Typography>
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    {modalStoreDriver.name && (
                      <Typography variant="body2">Nome: {modalStoreDriver.name}</Typography>
                    )}
                    {modalStoreDriver.phone && (
                      <Typography variant="body2">Telefone: {modalStoreDriver.phone}</Typography>
                    )}
                    {modalStoreDriver.vehicleType && (
                      <Typography variant="body2">Veiculo: {modalStoreDriver.vehicleType}</Typography>
                    )}
                  </Box>
                </Box>
              )}

              {itemsToRender && itemsToRender.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Itens</Typography>
                  {itemsToRender.map((item, index) => (
                    <Box key={index} mb={2} p={1} border={1} borderColor="grey.300" borderRadius={1} display="flex" gap={1}>
                      {item.imageUrl && (
                        <img
                          src={resolveCatalogImageUrl(item.imageUrl)}
                          alt={item.name}
                          style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">{item.quantity}x {item.name}</Typography>
                      <Typography variant="body2">Preço Unitário: {formatCurrency(item.unitPrice)}</Typography>
                      <Typography variant="body2">Preço Total: {formatCurrency(item.totalPrice)}</Typography>
                      <Typography variant="body2">Observações: {item.observations}</Typography>
                      {item.options && item.options.length > 0 && (
                        <Box mt={1}>
                          <Typography variant="subtitle2">Opções:</Typography>
                          {item.options.map((option, optIndex) => (
                            <Box key={optIndex} ml={2} display="flex" gap={1} alignItems="flex-start">
                              {option.imageUrl && (
                                <img
                                  src={resolveCatalogImageUrl(option.imageUrl)}
                                  alt={option.name}
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, marginTop: 4 }}
                                />
                              )}
                              <Box>
                                <Typography variant="body2">{option.quantity}x {option.name}</Typography>
                              <Typography variant="body2">Preço: {formatCurrency(option.price)}</Typography>
                              {option.customizations && option.customizations.length > 0 && (
                                <Box ml={2}>
                                  <Typography variant="caption">Customizações:</Typography>
                                  {option.customizations.map((cust, custIndex) => (
                                    <Typography key={custIndex} variant="caption" display="block">
                                      {cust.name} - {formatCurrency(cust.price)}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {totalInfo && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Total</Typography>
                  <Typography variant="body2">Subtotal: {typeof totalInfo === 'object' && totalInfo.subTotal !== undefined ? formatCurrency(totalInfo.subTotal) : 'Não informado'}</Typography>
                  <Typography variant="body2">Taxa de Entrega: {typeof totalInfo === 'object' && totalInfo.deliveryFee !== undefined ? formatCurrency(totalInfo.deliveryFee) : 'Não informado'}</Typography>
                  <Typography variant="body2">
                    Descontos: {formatCurrency(couponBreakdown.totalAmount)}
                  </Typography>
                  {couponBreakdown.ifoodAmount > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Subsidiado iFood: {formatCurrency(couponBreakdown.ifoodAmount)}
                    </Typography>
                  )}
                  {couponBreakdown.merchantAmount > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Subsidiado loja: {formatCurrency(couponBreakdown.merchantAmount)}
                    </Typography>
                  )}
                  <Typography variant="body2">Taxas Adicionais: {typeof totalInfo === 'object' && totalInfo.additionalFees !== undefined ? formatCurrency(totalInfo.additionalFees) : 'Não informado'}</Typography>
                  <Typography variant="body2" fontWeight="bold">Valor Total: {totalAmountValue !== undefined ? formatCurrency(totalAmountValue) : 'Não informado'}</Typography>
                </Box>
              )}

              {paymentInfo && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Pagamentos</Typography>
                  {couponBreakdown.code && (
                    <Typography variant="body2">Cupom: {couponBreakdown.code}</Typography>
                  )}
                  <Typography variant="body2">{hasCommercialAmendment(orderDetails || orderModal.order) ? 'Pago' : 'Pago online'}: {formatCurrency(resolvePaymentAmounts(paymentInfo, parseAmount(totalInfo.orderAmount)).paid)}</Typography>
                  <Typography variant="body2">Pendente: {formatCurrency(resolvePaymentAmounts(paymentInfo, parseAmount(totalInfo.orderAmount)).pending)}</Typography>
                  {paymentMethods.map((method, idx) => (
                    <Box key={idx} mt={1}>
                      {(() => {
                        const changeAmount = getCashChangeAmount(method, paymentInfo.pending);
                        return (
                          <>
                      <Typography variant="body2">Forma de Pagamento: {formatPaymentMethodLabel(method.method || method.type || '')}</Typography>
                      <Typography variant="body2">Valor: {formatCurrency(method.value)}</Typography>
                      {hasCommercialAmendment(orderDetails || orderModal.order) && <>
                        <Typography variant="body2">Recebido: {formatCurrency(method.amountPaid || 0)}</Typography>
                        {Number(method.amountDue || 0) > 0 && <Typography variant="body2">{method.collectionResponsibility === 'merchant' ? 'Conferir na loja' : 'Cobrar na entrega'}: {formatCurrency(method.amountDue)}</Typography>}
                      </>}
                      <Typography variant="body2">Pagamento: {formatPaymentStatusLabel(method)}</Typography>
                      {Number(method?.cash?.changeFor || 0) > 0 && (
                        <Typography variant="body2">Troco para: {formatCurrency(method.cash.changeFor)}</Typography>
                      )}
                      {changeAmount > 0 && (
                        <Typography variant="body2">Valor do Troco: {formatCurrency(changeAmount)}</Typography>
                      )}
                      {method.card && <Typography variant="body2">Cartão: {method.card.brand}</Typography>}
                          </>
                        );
                      })()}
                    </Box>
                  ))}
                </Box>
              )}

              {negotiation && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Negociação iFood</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      label={getNegotiationStatusLabel(negotiation.status)}
                      color={negotiation.active ? 'warning' : negotiation.status === 'ACCEPTED' ? 'success' : 'default'}
                      variant={negotiation.active ? 'filled' : 'outlined'}
                      size="small"
                    />
                    {negotiation.action && <Chip label={String(negotiation.action).replace(/_/g, ' ')} size="small" variant="outlined" />}
                    {negotiation.handshakeType && <Chip label={String(negotiation.handshakeType).replace(/_/g, ' ')} size="small" variant="outlined" />}
                  </Box>
                  <Typography variant="body2">Disputa: {negotiation.disputeId}</Typography>
                  {negotiation.message && <Typography variant="body2">Mensagem: {negotiation.message}</Typography>}
                  {negotiation.expiresAt && (
                    <Typography variant="body2">
                      Expira em: {new Date(negotiation.expiresAt).toLocaleString('pt-BR')}
                    </Typography>
                  )}
                  {negotiation.reason && (
                    <Typography variant="body2">Motivo final: {negotiation.reason}</Typography>
                  )}
                  {negotiation.active && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Responda esta solicitação pelo widget do iFood.
                    </Alert>
                  )}

                  {Array.isArray(negotiation.acceptCancellationReasons) && negotiation.acceptCancellationReasons.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="subtitle2">Motivos aceitos pelo iFood</Typography>
                      {negotiation.acceptCancellationReasons.map((item, index) => (
                        <Typography key={`${item}-${index}`} variant="body2" color="text.secondary">
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {Array.isArray(negotiation.details?.items) && negotiation.details.items.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="subtitle2">Itens afetados</Typography>
                      {negotiation.details.items.map((item, index) => (
                        <Typography key={`${item.uniqueId || item.id || index}`} variant="body2" color="text.secondary">
                          {item.quantity}x {item.reason || item.integrationId || item.id}
                          {item.amount?.value ? ` (${formatNegotiationAmount(item.amount.value)})` : ''}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {Array.isArray(negotiation.alternatives) && negotiation.alternatives.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="subtitle2">Alternativas disponíveis</Typography>
                      {negotiation.alternatives.map((alternative) => (
                        <Typography key={alternative.id} variant="body2" color="text.secondary">
                          {alternative.type}
                          {alternative.metadata?.maxAmount?.value ? ` até ${formatNegotiationAmount(alternative.metadata.maxAmount.value)}` : ''}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {orderModal.order.additionalFees && orderModal.order.additionalFees.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>Taxas Adicionais</Typography>
                  {orderModal.order.additionalFees.map((fee, idx) => (
                    <Box key={idx} mt={1}>
                      <Typography variant="body2">Tipo: {fee.type}</Typography>
                      <Typography variant="body2">Descrição: {fee.description}</Typography>
                      <Typography variant="body2">Valor: {formatCurrency(fee.value)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

            </Box>
          );
          })()
        }
        </DialogContent>
        <DialogActions>
          {orderModal.order?.negotiation?.active && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                closeOrderModal();
                openIfoodWidgetForNegotiation(orderDetails || orderModal.order);
              }}
            >
              Abrir widget iFood
            </Button>
          )}
          {orderModal.order && ['programador', 'administrador', 'gerente', 'colaborador'].includes(String(currentUser?.role || '').toLowerCase()) && (
            <Button variant="outlined" startIcon={<EditOutlinedIcon />}
              disabled={!isDashboardOrderCurrent(orderModal.order) || isTerminalStatus(orderModal.order.status)}
              onClick={() => {
                const order = orderDetails || orderModal.order;
                setOrderEditTarget({ orderId: order.orderId, source: getOrderSourceKey(order) });
              }}>
              Editar pedido
            </Button>
          )}
          {orderModal.order && canRequestShippingForOrder(orderDetails || orderModal.order) && (
            <Button
              variant="contained"
              startIcon={<LocalShippingIcon />}
              onClick={() => openShippingFlow(orderDetails || orderModal.order)}
            >
              Chamar entrega
            </Button>
          )}
          {orderModal.order
            && canRequestCourierForOrder(orderDetails || orderModal.order)
            && !beeHireAttemptedKeysRef.current.has(getCourierOrderKey(orderDetails || orderModal.order))
            && !(beeDeliveryStatus.orderKey === getCourierOrderKey(orderDetails || orderModal.order) && beeDeliveryStatus.data?.available)
            && (
            <Button
              variant="contained"
              startIcon={<LocalShippingIcon />}
              onClick={() => openCourierQuoteFlow(orderDetails || orderModal.order)}
            >
              Entregador
            </Button>
          )}
          {orderModal.order && canUseIfoodShipping && getVisibleShipping(orderDetails || orderModal.order)?.active && !isTerminalStatus((orderDetails || orderModal.order).status) && (
            <Button
              variant="outlined"
              startIcon={<LocalShippingIcon />}
              onClick={() => refreshShippingTracking(orderDetails || orderModal.order)}
            >
              Atualizar entrega
            </Button>
          )}
          {orderModal.order && canConfirmShippingAddressForOrder(orderDetails || orderModal.order) && (
            <Button
              variant="outlined"
              onClick={() => submitShippingAddressAction(orderDetails || orderModal.order, 'confirm')}
              disabled={shippingActionState.loading}
            >
              Confirmar endereço
            </Button>
          )}
          {orderModal.order && canEditOrderAddress(orderDetails || orderModal.order) && (
            <Button
              variant="outlined"
              startIcon={<EditLocationAltOutlinedIcon />}
              onClick={() => openOrderAddressEdit(orderDetails || orderModal.order)}
            >
              Editar Endereço
            </Button>
          )}
          {orderModal.order && canRequestShippingAddressChangeForOrder(orderDetails || orderModal.order) && (
            <Button
              variant="outlined"
              onClick={() => openShippingAddressChangeFlow(orderDetails || orderModal.order)}
              disabled={shippingActionState.loading}
            >
              Solicitar alteração ao iFood
            </Button>
          )}
          {orderModal.order && canUseIfoodShipping && getVisibleShipping(orderDetails || orderModal.order)?.addressChange?.pending && (
            <Button
              variant="contained"
              color="success"
              onClick={() => submitShippingAddressAction(orderDetails || orderModal.order, 'accept')}
              disabled={shippingActionState.loading}
            >
              Aceitar endereço
            </Button>
          )}
          {orderModal.order && canUseIfoodShipping && getVisibleShipping(orderDetails || orderModal.order)?.addressChange?.pending && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => submitShippingAddressAction(orderDetails || orderModal.order, 'deny')}
              disabled={shippingActionState.loading}
            >
              Rejeitar endereço
            </Button>
          )}
          {orderModal.order && canCancelShippingDriverForOrder(orderDetails || orderModal.order) && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => submitCancelShippingDriverRequest(orderDetails || orderModal.order)}
              disabled={shippingActionState.loading}
            >
              {shippingActionState.loading ? 'Cancelando...' : 'Cancelar Entregador'}
            </Button>
          )}
          {orderModal.order && canCancelLocalOrder(orderDetails || orderModal.order) && !getVisibleShipping(orderDetails || orderModal.order)?.active && (
            <Button
              variant="contained"
              color="error"
              onClick={() => openLocalCancellationFlow(orderDetails || orderModal.order)}
            >
              Cancelar pedido
            </Button>
          )}
          {orderModal.order && !(
            orderModal.order.source === 'balcao' ||
            String(orderModal.order.orderId || '').startsWith('BALCAO-') ||
            orderModal.order.source === 'salao' ||
            String(orderModal.order.orderId || '').startsWith('SALAO-') ||
            orderModal.order.source === 'site' ||
            String(orderModal.order.orderId || '').startsWith('SITE-') ||
            getOrderSourceKey(orderModal.order) === 'whatsapp' ||
            orderModal.order.source === 'pedidos10' ||
            String(orderModal.order.orderId || '').startsWith('PEDIDOS10-') ||
            getOrderSourceKey(orderModal.order) === '99food' ||
            isTerminalStatus(orderModal.order.status)
          ) && (
            <Button
              variant="contained"
              color="error"
              onClick={() => openCancellationFlow(orderModal.order)}
            >
              Cancelar pedido
            </Button>
          )}
          {orderModal.order && canUseFiscalOrderModal && getOrderSourceKey(orderModal.order) !== '99food' && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => openFiscalPanel(orderDetails || orderModal.order)}
              disabled={fiscalLoadingPreview || fiscalIssuing}
            >
              NFC-e Homologação
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => handleManualPrintComanda(orderModal.order)}
            disabled={!orderModal.order || manualPrintLoading}
          >
            {manualPrintLoading ? 'Preparando...' : 'Imprimir comanda'}
          </Button>
          <Button onClick={closeOrderModal}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <OrderEditDialog open={Boolean(orderEditTarget)} orderId={orderEditTarget?.orderId}
        source={orderEditTarget?.source} apiBaseUrl={API_URL}
        onClose={() => setOrderEditTarget(null)} onSaved={handleOrderEdited}
        isSessionCurrent={isDashboardSessionCurrent} />

      <Dialog
        open={printPointDialog.open}
        onClose={printPointDialog.printing ? undefined : () => setPrintPointDialog({ open: false, order: null, points: [], printing: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Onde deseja imprimir?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Selecione o ponto de impressão para esta comanda.
          </DialogContentText>
          <Box sx={{ display: 'grid', gap: 1 }}>
            {printPointDialog.points.map((point) => (
              <Button
                key={point.id}
                variant="outlined"
                onClick={() => handlePrintAtSelectedPoint(point.id)}
                disabled={printPointDialog.printing}
                sx={{ justifyContent: 'flex-start', py: 1.25 }}
              >
                {point.name}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={printPointDialog.printing} onClick={() => setPrintPointDialog({ open: false, order: null, points: [], printing: false })}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={canUseFiscalOrderModal && fiscalPanelOpen}
        onClose={fiscalIssuing ? undefined : () => setFiscalPanelOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          NFC-e Homologação {fiscalPreview?.displayId || fiscalPreview?.orderId || orderModal.order?.displayId || ''}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Fiscal experimental visível somente para programador. Não emite em produção, não imprime DANFE automaticamente e não altera a comanda.
          </Alert>

          {fiscalError && <Alert severity="error" sx={{ mb: 2 }}>{fiscalError}</Alert>}
          {fiscalSuccess && <Alert severity="success" sx={{ mb: 2 }}>{fiscalSuccess}</Alert>}

          {fiscalLoadingPreview ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="body2">Gerando prévia fiscal...</Typography>
            </Box>
          ) : fiscalPreview ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Pedido ${fiscalPreview.displayId || fiscalPreview.orderId}`} />
                <Chip label={fiscalPreview.canIssue ? 'Pode emitir' : 'Bloqueado'} color={fiscalPreview.canIssue ? 'success' : 'warning'} />
                <Chip label={`Ambiente ${fiscalPreview.environment || 'homologation'}`} variant="outlined" />
                <Chip label={`Itens ${fiscalPreview.items?.length || 0}`} variant="outlined" />
              </Box>

              {fiscalPreview.warnings?.length ? (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {fiscalPreview.warnings.map((warning, index) => (
                    <Alert key={`${warning}-${index}`} severity="warning">{warning}</Alert>
                  ))}
                </Box>
              ) : null}

              {fiscalPreview.missingFields?.all?.length ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Campos faltantes na prévia</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {fiscalPreview.missingFields.all.map((field) => (
                      <Chip key={field} label={field} color="warning" size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              ) : null}

              <Box>
                <Typography variant="subtitle1" gutterBottom>Itens classificados</Typography>
                {(fiscalPreview.items || []).map((item, index) => (
                  <Box key={`${item.itemId || item.name || index}-${index}`} sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 1.5, mb: 1 }}>
                    <Typography variant="subtitle2">
                      {item.quantity || 1}x {item.name || 'Item'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Classificação: {item.classificationSource || '-'} | Perfil: {item.profile?.code || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      NCM {item.fiscal?.ncm || '-'} | CFOP {item.fiscal?.cfop || '-'} | Origem {item.fiscal?.origin || '-'} | Unidade {item.fiscal?.unit || '-'}
                    </Typography>
                    {item.missingFields?.length ? (
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                        {item.missingFields.map((field) => (
                          <Chip key={field} label={field} color="warning" size="small" variant="outlined" />
                        ))}
                      </Box>
                    ) : (
                      <Chip label="Sem faltas no item" color="success" size="small" sx={{ mt: 1 }} />
                    )}
                    {item.warnings?.length ? (
                      <Box sx={{ mt: 1 }}>
                        {item.warnings.map((warning, warningIndex) => (
                          <Typography key={`${warning}-${warningIndex}`} variant="caption" color="warning.main" display="block">
                            {warning}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Clique em NFC-e Homologação no pedido para gerar a prévia fiscal.
            </Typography>
          )}

          {fiscalIssueResult && (
            <Box sx={{ border: '1px solid #dbeafe', borderRadius: 1, p: 2, mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Resultado da emissão</Typography>
              <Typography variant="body2">Status: {fiscalIssueResult.status || '-'}</Typography>
              <Typography variant="body2">cStat: {fiscalIssueResult.sefazStatusCode || '-'}</Typography>
              <Typography variant="body2">Motivo SEFAZ: {fiscalIssueResult.sefazReason || '-'}</Typography>
              <Typography variant="body2">Número: {fiscalIssueResult.number || '-'} Série: {fiscalIssueResult.series || '-'}</Typography>
              <Typography variant="body2">Chave de acesso: {fiscalIssueResult.accessKey || '-'}</Typography>
              <Typography variant="body2">Protocolo: {fiscalIssueResult.protocol || '-'}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                {fiscalIssueResult.invoiceId ? (
                  <Button variant="outlined" size="small" onClick={() => downloadFiscalArtifact('xml')}>
                    XML
                  </Button>
                ) : null}
                {fiscalIssueResult.invoiceId && fiscalIssueResult.danfeAvailable ? (
                  <Button variant="outlined" size="small" onClick={() => downloadFiscalArtifact('danfe')}>
                    DANFE
                  </Button>
                ) : null}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFiscalPanelOpen(false)} disabled={fiscalIssuing}>Fechar</Button>
          {fiscalPreview?.canIssue && (
            <Button
              variant="contained"
              color="warning"
              onClick={issueFiscalHomologation}
              disabled={fiscalIssuing || fiscalLoadingPreview}
            >
              {fiscalIssuing ? 'Emitindo...' : 'Emitir em Homologação'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={canUseIfoodShipping && shippingModal.open}
        onClose={shippingModal.requesting ? undefined : closeShippingModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Chamar entrega iFood {shippingModal.order?.displayId || shippingModal.order?.orderId}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Confirme a cotacao antes de solicitar o entregador. O pedido continua no mesmo fluxo operacional do Brutos.
          </DialogContentText>

          {shippingModal.loading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
              {shippingModal.error && (
                <Alert severity={shippingModal.availabilities.length ? 'warning' : 'error'}>
                  {shippingModal.error}
                </Alert>
              )}

              {shippingModal.availabilities.length > 0 && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="shipping-quote-label">Opcao de entrega</InputLabel>
                    <Select
                      labelId="shipping-quote-label"
                      label="Opcao de entrega"
                      value={shippingModal.selectedQuoteId}
                      onChange={(event) => {
                        setShippingModal((prev) => ({
                          ...prev,
                          selectedQuoteId: event.target.value,
                          error: '',
                        }));
                      }}
                    >
                      {shippingModal.availabilities.map((availability, index) => (
                        <MenuItem key={availability.quoteId} value={availability.quoteId}>
                          {`Opcao ${index + 1} - ${formatCurrency(getShippingAvailabilityFee(availability))} - ${formatShippingTimeWindow(availability)}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {(() => {
                    const selectedQuote = getSelectedShippingQuote();
                    if (!selectedQuote) return null;
                    return (
                      <Box sx={{ display: 'grid', gap: 0.5 }}>
                        <Typography variant="body2">
                          Custo estimado: {formatCurrency(getShippingAvailabilityFee(selectedQuote))}
                        </Typography>
                        <Typography variant="body2">
                          Previsao: {formatShippingTimeWindow(selectedQuote)}
                        </Typography>
                        {selectedQuote.expiresAt && (
                          <Typography variant="body2" color="text.secondary">
                            Valida ate: {formatShortDateTime(selectedQuote.expiresAt)}
                          </Typography>
                        )}
                        {selectedQuote.distanceInMeters > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Distancia aproximada: {(selectedQuote.distanceInMeters / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShippingModal} disabled={shippingModal.requesting}>
            Fechar
          </Button>
          <Button
            variant="contained"
            startIcon={shippingModal.requesting ? <CircularProgress color="inherit" size={16} /> : <LocalShippingIcon />}
            onClick={submitShippingRequest}
            disabled={shippingModal.loading || shippingModal.requesting || !shippingModal.selectedQuoteId}
          >
            {shippingModal.requesting ? 'Chamando...' : 'Confirmar entrega'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={courierQuoteModal.open}
        onClose={courierQuoteModal.loading || courierQuoteModal.hiringBee ? undefined : closeCourierQuoteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Entregador
          {courierQuoteModal.order?.displayId ? ` - Pedido ${courierQuoteModal.order.displayId}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {outsideShippingMerchantOptions.length > 1 && (
              <TextField
                select
                label="Loja iFood para cotação"
                value={courierQuoteModal.merchantId}
                onChange={(event) => {
                  const merchantId = event.target.value;
                  setCourierQuoteModal((previous) => ({ ...previous, merchantId }));
                  void fetchCourierQuotes(courierQuoteModal.order, merchantId);
                }}
                disabled={courierQuoteModal.loading || courierQuoteModal.hiringBee}
                fullWidth
              >
                {outsideShippingMerchantOptions.map((merchant) => (
                  <MenuItem key={merchant.id} value={merchant.id}>{merchant.name}</MenuItem>
                ))}
              </TextField>
            )}
            {courierQuoteModal.loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Consultando plataformas em tempo real...</Typography>
              </Stack>
            )}
            {courierQuoteModal.error && <Alert severity="error">{courierQuoteModal.error}</Alert>}
            {courierQuoteModal.errors.map((quoteError, index) => (
              <Alert severity="warning" key={`${quoteError.provider || 'provider'}-${index}`}>
                {quoteError.provider ? `${quoteError.provider}: ` : ''}{quoteError.message || 'Cotação indisponível.'}
              </Alert>
            ))}
            {courierQuoteModal.quotes.length > 0 && (
              <CourierQuoteOptions
                quotes={courierQuoteModal.quotes}
                merchantId={courierQuoteModal.merchantId}
                merchantOptions={outsideShippingMerchantOptions}
                selectedQuoteId={courierQuoteModal.selectedQuoteId}
                canHireBee={courierQuoteModal.capabilities?.canHireBee===true}
                canHireBeeSandbox={courierQuoteModal.capabilities?.canHireBeeSandbox === true}
                canHireIfood={courierQuoteModal.capabilities?.canHireIfood !== false}
                onSelect={(quoteId) => setCourierQuoteModal((previous) => ({ ...previous, selectedQuoteId: quoteId }))}
              />
            )}
            {!courierQuoteModal.loading && !courierQuoteModal.quotes.length && !courierQuoteModal.error && (
              <Alert severity="info">Nenhuma cotação disponível para este pedido neste momento.</Alert>
            )}
            {getSelectedCourierQuote()?.provider === 'ifood' && (
              <Alert severity="info">
                Ao continuar, confirme os dados no fluxo iFood e faça uma nova cotação antes de chamar o entregador. O preço poderá mudar.
              </Alert>
            )}
            {courierQuoteModal.hireAttempted && (
              <Alert severity="info">
                Uma solicitação Bee já foi enviada para este pedido nesta sessão. Consulte o status no detalhe do pedido antes de qualquer nova tentativa.
              </Alert>
            )}
            {!courierQuoteModal.loading && courierQuoteModal.capabilities?.canHireIfood !== false && canRequestOutsideShippingForOrder(courierQuoteModal.order) && !courierQuoteModal.quotes.length && (
              <Alert severity="info">
                Você pode continuar no fluxo iFood e localizar o endereço antes de solicitar uma nova cotação.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCourierQuoteModal} disabled={courierQuoteModal.loading || courierQuoteModal.hiringBee}>Fechar</Button>
          <Button
            variant="outlined"
            onClick={() => fetchCourierQuotes(courierQuoteModal.order, courierQuoteModal.merchantId)}
            disabled={courierQuoteModal.loading || courierQuoteModal.hiringBee}
          >
            Cotar novamente
          </Button>
          {courierQuoteModal.capabilities?.canHireIfood !== false && canRequestOutsideShippingForOrder(courierQuoteModal.order) && (!getSelectedCourierQuote() || getSelectedCourierQuote()?.provider === 'ifood') && (
            <Button
              variant="contained"
              onClick={continueWithIfoodCourier}
              disabled={courierQuoteModal.loading}
            >
              Continuar no iFood
            </Button>
          )}
          {getSelectedCourierQuote()?.provider === 'bee'
            && (courierQuoteModal.capabilities?.canHireBee===true || courierQuoteModal.capabilities?.canHireBeeSandbox===true)
            && !courierQuoteModal.hireAttempted
            && !beeHireAttemptedKeysRef.current.has(getCourierOrderKey(courierQuoteModal.order)) && (
              <Button
                variant="contained"
                color="warning"
                onClick={openBeeHireConfirmation}
                disabled={courierQuoteModal.loading || courierQuoteModal.hiringBee}
              >
                {courierQuoteModal.capabilities?.canHireBeeSandbox?'Contratar Bee no sandbox':'Contratar Bee'}
              </Button>
            )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={courierQuoteModal.confirmBee}
        onClose={courierQuoteModal.hiringBee ? undefined : () => setCourierQuoteModal((previous) => ({ ...previous, confirmBee: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{courierQuoteModal.capabilities?.canHireBeeSandbox?'Confirmar entrega Bee no sandbox':'Confirmar entrega Bee'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pedido {courierQuoteModal.order?.displayId || courierQuoteModal.order?.orderId}
            {Number.isSafeInteger(getSelectedCourierQuote()?.feeCents)
              ? ` · Valor cotado: ${formatCurrency(getSelectedCourierQuote().feeCents / 100)}`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCourierQuoteModal((previous) => ({ ...previous, confirmBee: false }))}>Voltar</Button>
          <Button variant="contained" color="warning" onClick={hireBeeSandboxCourier}>{courierQuoteModal.capabilities?.canHireBeeSandbox?'Confirmar teste Bee':'Confirmar contratação Bee'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={canUseOutsideShipping && outsideShippingModal.open}
        onClose={(outsideShippingModal.creating || outsideShippingModal.geocoding || outsideShippingModal.loadingQuote) ? undefined : closeOutsideShippingModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Entrega iFood sob demanda
          {outsideShippingModal.sourceOrder?.displayId ? ` - Pedido ${outsideShippingModal.sourceOrder.displayId}` : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Confira os dados reais do pedido externo. O entregador só será chamado após a cotação real do iFood.
          </DialogContentText>

          {outsideShippingModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{outsideShippingModal.error}</Alert>
          )}

          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="outside-shipping-merchant-label">Loja iFood</InputLabel>
              <Select
                labelId="outside-shipping-merchant-label"
                label="Loja iFood"
                value={outsideShippingModal.form.merchantId}
                onChange={(event) => updateOutsideShippingField('merchantId', event.target.value)}
              >
                {outsideShippingMerchantOptions.map((merchant) => (
                  <MenuItem key={merchant.id} value={merchant.id}>{merchant.name || merchant.id}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {getOrderSourceKey(outsideShippingModal.sourceOrder || {})==='99food' ? <>
              <Alert severity="info">Cliente, endereço, itens e pagamento vêm do pedido 99Food. Para corrigir o destino, volte ao pedido antes de cotar.</Alert>
              <Typography>{outsideShippingModal.sourceOrder.customer?.name}</Typography>
              <Typography>{[outsideShippingModal.form.streetName,outsideShippingModal.form.streetNumber,outsideShippingModal.form.neighborhood,outsideShippingModal.form.city].filter(Boolean).join(', ')}</Typography>
              {outsideShippingModal.sourceOrder.customer?.providerVirtualPhone && <Typography>Contato 99Food: {outsideShippingModal.sourceOrder.customer.providerVirtualPhone}{outsideShippingModal.sourceOrder.customer.providerLocator ? ` · Localizador ${outsideShippingModal.sourceOrder.customer.providerLocator}` : ''}</Typography>}
              <Button variant="outlined" onClick={fetchOutsideShippingAvailability} disabled={outsideShippingModal.loadingQuote||outsideShippingModal.creating}>Cotar iFood</Button>
              {outsideShippingModal.availabilities.length>0&&<FormControl fullWidth><InputLabel id="99food-ifood-quote">Cotação iFood</InputLabel><Select labelId="99food-ifood-quote" label="Cotação iFood" value={outsideShippingModal.selectedQuoteId} onChange={event=>setOutsideShippingModal(prev=>({...prev,selectedQuoteId:event.target.value}))}>{outsideShippingModal.availabilities.map((quote,index)=><MenuItem key={quote.quoteId} value={quote.quoteId}>{`Opção ${index+1} · ${formatCurrency(getShippingAvailabilityFee(quote))}`}</MenuItem>)}</Select></FormControl>}
              {(outsideShippingModal.sourceOrder.items||[]).map((item,index)=><Typography key={item.providerLineKey||index}>{item.quantity}x {item.name} · {formatCurrency(item.totalPrice)}</Typography>)}
              <Typography>Pagamento: online confirmado pela 99Food</Typography>
            </> : <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 120px' }, gap: 2 }}>
              <TextField
                label="Cliente"
                value={outsideShippingModal.form.customerName}
                onChange={(event) => updateOutsideShippingField('customerName', event.target.value)}
                fullWidth
              />
              <TextField
                label="Senha curta"
                value={outsideShippingModal.form.displayId}
                onChange={(event) => updateOutsideShippingField('displayId', event.target.value.slice(0, 4))}
                inputProps={{ maxLength: 4 }}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 100px 100px 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="outside-phone-type-label">Telefone</InputLabel>
                <Select
                  labelId="outside-phone-type-label"
                  label="Telefone"
                  value={outsideShippingModal.form.phoneType}
                  onChange={(event) => updateOutsideShippingField('phoneType', event.target.value)}
                >
                  <MenuItem value="CUSTOMER">Cliente</MenuItem>
                  <MenuItem value="STORE">Loja</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="País"
                value={outsideShippingModal.form.countryCode}
                onChange={(event) => updateOutsideShippingField('countryCode', event.target.value.replace(/\D/g, '').slice(0, 2))}
                fullWidth
              />
              <TextField
                label="DDD"
                value={outsideShippingModal.form.areaCode}
                onChange={(event) => updateOutsideShippingField('areaCode', event.target.value.replace(/\D/g, '').slice(0, 2))}
                fullWidth
              />
              <TextField
                label="Número"
                value={outsideShippingModal.form.phoneNumber}
                onChange={(event) => updateOutsideShippingField('phoneNumber', event.target.value.replace(/\D/g, '').slice(0, 9))}
                fullWidth
              />
            </Box>

            <Divider />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 1fr 120px' }, gap: 2 }}>
              <TextField
                label="CEP"
                value={outsideShippingModal.form.postalCode}
                onChange={(event) => updateOutsideShippingField('postalCode', event.target.value.replace(/\D/g, '').slice(0, 8))}
                fullWidth
              />
              <Autocomplete
                freeSolo
                options={outsideShippingModal.addressSuggestions}
                loading={outsideShippingModal.loadingAddressSuggestions}
                inputValue={outsideShippingModal.form.streetName}
                onInputChange={(event, value, reason) => {
                  if (reason !== 'reset') {
                    updateOutsideShippingField('streetName', value);
                  }
                }}
                onChange={(event, value) => {
                  if (value && typeof value === 'object') {
                    applyOutsideShippingAddressSuggestion(value);
                  }
                }}
                getOptionLabel={(option) => (
                  typeof option === 'string'
                    ? option
                    : option.label || option.title || ''
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options) => options}
                noOptionsText="Digite ao menos 3 letras"
                loadingText="Buscando endereços..."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rua"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {outsideShippingModal.loadingAddressSuggestions ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                label="Número"
                value={outsideShippingModal.form.streetNumber}
                onChange={(event) => updateOutsideShippingField('streetNumber', event.target.value)}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 90px 90px' }, gap: 2 }}>
              <TextField label="Bairro" value={outsideShippingModal.form.neighborhood} onChange={(event) => updateOutsideShippingField('neighborhood', event.target.value)} fullWidth />
              <TextField label="Cidade" value={outsideShippingModal.form.city} onChange={(event) => updateOutsideShippingField('city', event.target.value)} fullWidth />
              <TextField label="UF" value={outsideShippingModal.form.state} onChange={(event) => updateOutsideShippingField('state', event.target.value.toUpperCase().slice(0, 2))} fullWidth />
              <TextField label="País" value={outsideShippingModal.form.country} onChange={(event) => updateOutsideShippingField('country', event.target.value.toUpperCase().slice(0, 2))} fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Complemento" value={outsideShippingModal.form.complement} onChange={(event) => updateOutsideShippingField('complement', event.target.value)} fullWidth />
              <TextField label="Referência" value={outsideShippingModal.form.reference} onChange={(event) => updateOutsideShippingField('reference', event.target.value)} fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 220px' }, gap: 2, alignItems: 'stretch' }}>
              <Alert
                severity={outsideShippingModal.form.latitude && outsideShippingModal.form.longitude ? 'success' : 'info'}
                sx={{ alignItems: 'center' }}
              >
                {outsideShippingModal.geocodeResult?.label
                  ? `Endereço localizado: ${outsideShippingModal.geocodeResult.label}`
                  : 'O endereço será localizado automaticamente antes da cotação.'}
              </Alert>
              <Button
                variant="outlined"
                startIcon={(outsideShippingModal.loadingQuote || outsideShippingModal.geocoding) ? <CircularProgress size={16} /> : <LocalShippingIcon />}
                onClick={fetchOutsideShippingAvailability}
                disabled={outsideShippingModal.loadingQuote || outsideShippingModal.geocoding || outsideShippingModal.creating}
              >
                {outsideShippingModal.geocoding ? 'Localizando...' : outsideShippingModal.loadingQuote ? 'Cotando...' : 'Cotar'}
              </Button>
            </Box>

            {outsideShippingModal.availabilities.length > 0 && (
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Alert severity="info">Confira o preço atualizado e escolha a cotação iFood antes de contratar.</Alert>
                <FormControl fullWidth>
                  <InputLabel id="outside-shipping-quote-label">Cotação iFood</InputLabel>
                  <Select
                    labelId="outside-shipping-quote-label"
                    label="Cotação iFood"
                    value={outsideShippingModal.selectedQuoteId}
                    onChange={(event) => setOutsideShippingModal((prev) => ({ ...prev, selectedQuoteId: event.target.value, error: '' }))}
                  >
                    {outsideShippingModal.availabilities.map((availability, index) => (
                      <MenuItem key={availability.quoteId} value={availability.quoteId}>
                        {`Opção ${index + 1} - ${formatCurrency(getShippingAvailabilityFee(availability))} - ${formatShippingTimeWindow(availability)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(() => {
                  const selectedQuote = getSelectedOutsideShippingQuote();
                  if (!selectedQuote) return null;
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Custo: {formatCurrency(getShippingAvailabilityFee(selectedQuote))} · Previsão: {formatShippingTimeWindow(selectedQuote)}
                    </Typography>
                  );
                })()}
              </Box>
            )}

            <Divider />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Taxa cobrada do cliente" value={outsideShippingModal.form.merchantFee} onChange={(event) => updateOutsideShippingField('merchantFee', event.target.value)} fullWidth />
              <TextField label="Tempo de preparo em segundos" value={outsideShippingModal.form.preparationTime} onChange={(event) => updateOutsideShippingField('preparationTime', event.target.value.replace(/\D/g, ''))} fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="subtitle2">Itens</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addOutsideShippingItem}>
                  Item
                </Button>
              </Box>
              {outsideShippingModal.form.items.map((item, index) => (
                <Box
                  key={`outside-item-${index}`}
                  sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.5fr 90px 120px 120px 44px' }, gap: 1 }}
                >
                  <TextField label="ID" value={item.id} onChange={(event) => updateOutsideShippingItem(index, 'id', event.target.value)} size="small" />
                  <TextField label="Nome" value={item.name} onChange={(event) => updateOutsideShippingItem(index, 'name', event.target.value)} size="small" />
                  <TextField label="Qtd" value={item.quantity} onChange={(event) => updateOutsideShippingItem(index, 'quantity', event.target.value.replace(/\D/g, ''))} size="small" />
                  <TextField label="Preço unit." value={item.unitPrice} onChange={(event) => updateOutsideShippingItem(index, 'unitPrice', event.target.value)} size="small" />
                  <TextField label="Complementos" value={item.optionsPrice} onChange={(event) => updateOutsideShippingItem(index, 'optionsPrice', event.target.value)} size="small" />
                  <IconButton aria-label="Remover item" onClick={() => removeOutsideShippingItem(index)} disabled={outsideShippingModal.form.items.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Divider />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 150px 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="outside-payment-mode-label">Pagamento</InputLabel>
                <Select
                  labelId="outside-payment-mode-label"
                  label="Pagamento"
                  value={outsideShippingModal.form.paymentMode}
                  onChange={(event) => updateOutsideShippingField('paymentMode', event.target.value)}
                >
                  <MenuItem value="OFFLINE">Na entrega</MenuItem>
                  <MenuItem value="ONLINE">Online/loja</MenuItem>
                </Select>
              </FormControl>
              {outsideShippingModal.form.paymentMode === 'OFFLINE' && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="outside-payment-method-label">Método</InputLabel>
                    <Select
                      labelId="outside-payment-method-label"
                      label="Método"
                      value={outsideShippingModal.form.paymentMethod}
                      onChange={(event) => updateOutsideShippingField('paymentMethod', event.target.value)}
                    >
                      <MenuItem value="CREDIT">Crédito</MenuItem>
                      <MenuItem value="DEBIT">Débito</MenuItem>
                      <MenuItem value="CASH">Dinheiro</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Valor" value={outsideShippingModal.form.paymentValue} onChange={(event) => updateOutsideShippingField('paymentValue', event.target.value)} fullWidth />
                  {outsideShippingModal.form.paymentMethod === 'CASH' ? (
                    <TextField label="Troco para" value={outsideShippingModal.form.cashChangeFor} onChange={(event) => updateOutsideShippingField('cashChangeFor', event.target.value)} fullWidth />
                  ) : (
                    <TextField label="Bandeira" value={outsideShippingModal.form.cardBrand} onChange={(event) => updateOutsideShippingField('cardBrand', event.target.value)} fullWidth />
                  )}
                </>
              )}
            </Box>
            </>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOutsideShippingModal} disabled={outsideShippingModal.creating || outsideShippingModal.geocoding || outsideShippingModal.loadingQuote}>
            Fechar
          </Button>
          <Button
            variant="contained"
            startIcon={outsideShippingModal.creating ? <CircularProgress color="inherit" size={16} /> : <LocalShippingIcon />}
            onClick={submitOutsideShippingOrder}
            disabled={outsideShippingModal.creating || outsideShippingModal.loadingQuote || outsideShippingModal.geocoding || !outsideShippingModal.selectedQuoteId}
          >
            {outsideShippingModal.creating ? 'Registrando...' : 'Registrar e chamar entregador'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={orderAddressEditModal.open}
        onClose={orderAddressEditModal.submitting ? undefined : closeOrderAddressEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Editar endereço do pedido
          {orderAddressEditModal.order?.displayId ? ` - Pedido ${orderAddressEditModal.order.displayId}` : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Esta correção será usada no Brutos Delivery e no Brutos Entregas.
          </DialogContentText>
          {getOrderSourceKey(orderAddressEditModal.order || {}) === 'ifood' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              O pedido original e o entregador do iFood não serão alterados.
            </Alert>
          )}
          {orderAddressEditModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{orderAddressEditModal.error}</Alert>
          )}
          {orderAddressEditModal.addressSuggestionError && (
            <Alert severity="warning" sx={{ mb: 2 }}>{orderAddressEditModal.addressSuggestionError}</Alert>
          )}

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '130px 1fr 110px' }, gap: 2 }}>
              <TextField
                label="CEP"
                value={orderAddressEditModal.form.postalCode}
                onChange={(event) => updateOrderAddressEditField('postalCode', event.target.value.replace(/\D/g, '').slice(0, 8))}
                fullWidth
              />
              <Autocomplete
                freeSolo
                options={orderAddressEditModal.addressSuggestions}
                loading={orderAddressEditModal.loadingAddressSuggestions}
                inputValue={orderAddressEditModal.form.streetName}
                onInputChange={(event, value, reason) => {
                  if (reason !== 'reset') {
                    updateOrderAddressEditField('streetName', value);
                  }
                }}
                onChange={(event, value) => {
                  if (value && typeof value === 'object') {
                    applyOrderAddressEditSuggestion(value);
                  }
                }}
                getOptionLabel={(option) => (
                  typeof option === 'string'
                    ? option
                    : option.label || option.title || ''
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options) => options}
                noOptionsText={String(orderAddressEditModal.form.streetName || '').trim().length < 3
                  ? 'Digite ao menos 3 letras'
                  : 'Nenhum endereço encontrado'}
                loadingText="Buscando endereços..."
                renderOption={(props, option) => (
                  <li {...props} key={option.id || option.label}>
                    <Box>
                      <Typography>{option.address?.street || option.address?.streetName || option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.label}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rua"
                    required
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {orderAddressEditModal.loadingAddressSuggestions ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                label="Número"
                value={orderAddressEditModal.form.streetNumber}
                onChange={(event) => updateOrderAddressEditField('streetNumber', event.target.value)}
                required
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 80px 80px' }, gap: 2 }}>
              <TextField
                label="Bairro"
                value={orderAddressEditModal.form.neighborhood}
                onChange={(event) => updateOrderAddressEditField('neighborhood', event.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Cidade"
                value={orderAddressEditModal.form.city}
                onChange={(event) => updateOrderAddressEditField('city', event.target.value)}
                required
                fullWidth
              />
              <TextField
                label="UF"
                value={orderAddressEditModal.form.state}
                onChange={(event) => updateOrderAddressEditField('state', event.target.value.toUpperCase().slice(0, 2))}
                required
                fullWidth
              />
              <TextField
                label="País"
                value={orderAddressEditModal.form.country}
                onChange={(event) => updateOrderAddressEditField('country', event.target.value.toUpperCase().slice(0, 2))}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Complemento"
                value={orderAddressEditModal.form.complement}
                onChange={(event) => updateOrderAddressEditField('complement', event.target.value)}
                fullWidth
              />
              <TextField
                label="Referência"
                value={orderAddressEditModal.form.reference}
                onChange={(event) => updateOrderAddressEditField('reference', event.target.value)}
                fullWidth
              />
            </Box>
            {orderAddressEditModal.open && <LocationConfirmation
              ref={orderLocationConfirmationRef} apiUrl={API_URL}
              addressDetails={orderAddressEditModal.form}
              onPolicyChange={setOrderLocationPolicyEnabled}
              initialPosition={orderSuggestionPosition}
              initialPositionQuality="street-estimate"
              onAddressCorrection={(corrected) => setOrderAddressEditModal((current) => ({
                ...current,
                form: {
                  ...current.form,
                  ...(corrected?.neighborhood ? { neighborhood: String(corrected.neighborhood).trim() } : {}),
                  postalCode: String(corrected?.postalCode || corrected?.cep || '').replace(/\D/g, '').slice(0, 8),
                },
              }))}
              disabled={orderAddressEditModal.submitting}
            />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOrderAddressEditModal} disabled={orderAddressEditModal.submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={orderAddressEditModal.submitting ? <CircularProgress color="inherit" size={16} /> : <EditLocationAltOutlinedIcon />}
            onClick={submitOrderAddressEdit}
            disabled={orderAddressEditModal.submitting}
          >
            {orderAddressEditModal.submitting ? 'Salvando...' : 'Salvar endereço'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={shippingAddressChangeModal.open}
        onClose={(shippingAddressChangeModal.submitting || shippingAddressChangeModal.geocoding) ? undefined : closeShippingAddressChangeModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Solicitar alteração ao iFood
          {shippingAddressChangeModal.order?.displayId ? ` - Pedido ${shippingAddressChangeModal.order.displayId}` : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Informe o novo endereço real da entrega. O iFood valida a distância máxima permitida antes de aceitar a solicitação.
          </DialogContentText>

          {shippingAddressChangeModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{shippingAddressChangeModal.error}</Alert>
          )}

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '130px 1fr 110px' }, gap: 2 }}>
              <TextField
                label="CEP"
                value={shippingAddressChangeModal.form.postalCode}
                onChange={(event) => updateShippingAddressChangeField('postalCode', event.target.value.replace(/\D/g, '').slice(0, 8))}
                fullWidth
              />
              <TextField
                label="Rua"
                value={shippingAddressChangeModal.form.streetName}
                onChange={(event) => updateShippingAddressChangeField('streetName', event.target.value)}
                fullWidth
              />
              <TextField
                label="Número"
                value={shippingAddressChangeModal.form.streetNumber}
                onChange={(event) => updateShippingAddressChangeField('streetNumber', event.target.value)}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 80px 80px' }, gap: 2 }}>
              <TextField
                label="Bairro"
                value={shippingAddressChangeModal.form.neighborhood}
                onChange={(event) => updateShippingAddressChangeField('neighborhood', event.target.value)}
                fullWidth
              />
              <TextField
                label="Cidade"
                value={shippingAddressChangeModal.form.city}
                onChange={(event) => updateShippingAddressChangeField('city', event.target.value)}
                fullWidth
              />
              <TextField
                label="UF"
                value={shippingAddressChangeModal.form.state}
                onChange={(event) => updateShippingAddressChangeField('state', event.target.value.toUpperCase().slice(0, 2))}
                fullWidth
              />
              <TextField
                label="País"
                value={shippingAddressChangeModal.form.country}
                onChange={(event) => updateShippingAddressChangeField('country', event.target.value.toUpperCase().slice(0, 2))}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Complemento"
                value={shippingAddressChangeModal.form.complement}
                onChange={(event) => updateShippingAddressChangeField('complement', event.target.value)}
                fullWidth
              />
              <TextField
                label="Referência"
                value={shippingAddressChangeModal.form.reference}
                onChange={(event) => updateShippingAddressChangeField('reference', event.target.value)}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 180px' }, gap: 2, alignItems: 'stretch' }}>
              <Alert
                severity={shippingAddressChangeModal.form.latitude && shippingAddressChangeModal.form.longitude ? 'success' : 'info'}
                sx={{ alignItems: 'center' }}
              >
                {shippingAddressChangeModal.geocodeResult?.label
                  ? `Endereço localizado: ${shippingAddressChangeModal.geocodeResult.label}`
                  : 'Localize o novo endereço antes de solicitar a alteração.'}
              </Alert>
              <Button
                variant="outlined"
                startIcon={shippingAddressChangeModal.geocoding ? <CircularProgress size={16} /> : <SearchIcon />}
                onClick={() => locateShippingAddressChange()}
                disabled={shippingAddressChangeModal.geocoding || shippingAddressChangeModal.submitting}
              >
                {shippingAddressChangeModal.geocoding ? 'Localizando...' : 'Localizar'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShippingAddressChangeModal} disabled={shippingAddressChangeModal.submitting || shippingAddressChangeModal.geocoding}>
            Fechar
          </Button>
          <Button
            variant="contained"
            startIcon={shippingAddressChangeModal.submitting ? <CircularProgress color="inherit" size={16} /> : <LocalShippingIcon />}
            onClick={submitShippingAddressChangeRequest}
            disabled={shippingAddressChangeModal.submitting || shippingAddressChangeModal.geocoding}
          >
            {shippingAddressChangeModal.submitting ? 'Solicitando...' : 'Solicitar alteração'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancellationModal.open}
        onClose={cancellationModal.submitLoading ? undefined : closeCancellationModal}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          const isShippingCancellation = cancellationModal.order && isOutsideShippingOrder(cancellationModal.order);
          const isLocalCancellation = cancellationModal.mode === 'local';
          const targetLabel = cancellationModal.order?.displayId || cancellationModal.order?.orderId;
          return (
            <DialogTitle>
              {isShippingCancellation ? 'Cancelar Entregador' : isLocalCancellation ? 'Cancelar Pedido Local' : 'Cancelar Pedido'} {targetLabel}
            </DialogTitle>
          );
        })()}
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {cancellationModal.mode === 'local'
              ? 'Selecione o motivo interno para cancelar este pedido no Brutos Delivery. Se o pedido estiver integrado ao Brutos Entregas ou Brutos Zap, o status de cancelamento será sincronizado pelo contrato existente.'
              : cancellationModal.order && isOutsideShippingOrder(cancellationModal.order)
                ? 'Selecione o motivo retornado pelo iFood antes de cancelar a entrega sob demanda.'
                : 'Selecione o motivo retornado pelo iFood antes de solicitar o cancelamento.'}
          </DialogContentText>

          {cancellationModal.loading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
              {cancellationModal.error && (
                <Alert severity="error">{cancellationModal.error}</Alert>
              )}

              <FormControl fullWidth>
                <InputLabel id="cancellation-reason-label">Motivo</InputLabel>
                <Select
                  labelId="cancellation-reason-label"
                  label="Motivo"
                  value={cancellationModal.selectedReason}
                  onChange={(event) => {
                    const nextReason = event.target.value;
                    setCancellationModal((prev) => ({
                      ...prev,
                      selectedReason: nextReason,
                      error: '',
                    }));
                  }}
                >
                  {cancellationModal.reasons.map((reason) => (
                    <MenuItem key={reason.code} value={reason.code}>
                      {reason.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {cancellationModal.selectedReason && (() => {
                const selectedReason = cancellationModal.reasons.find(
                  (reason) => reason.code === cancellationModal.selectedReason
                );

                if (!selectedReason) return null;

                return (
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {cancellationModal.mode === 'local' ? 'Motivo interno' : 'Código enviado'}: {selectedReason.code}
                    </Typography>
                    {selectedReason.details && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedReason.details}
                      </Typography>
                    )}
                  </Box>
                );
              })()}

              <TextField
                label="Justificativa"
                placeholder="Descreva se o motivo exigir observação adicional"
                value={cancellationModal.justification}
                onChange={(event) => {
                  const nextJustification = event.target.value;
                  setCancellationModal((prev) => ({
                    ...prev,
                    justification: nextJustification,
                    error: '',
                  }));
                }}
                multiline
                minRows={3}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancellationModal} disabled={cancellationModal.submitLoading}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitCancellationRequest}
            disabled={cancellationModal.loading || cancellationModal.submitLoading || !cancellationModal.reasons.length}
          >
            {cancellationModal.submitLoading
              ? 'Solicitando...'
              : cancellationModal.mode === 'local'
                ? 'Confirmar cancelamento local'
                : cancellationModal.order && isOutsideShippingOrder(cancellationModal.order)
                ? 'Confirmar cancelamento do entregador'
                : 'Confirmar cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

const Pedidos = (props) => {
  const auth = useAuth();
  return <PedidosScreen key={`${auth.token || ''}:${auth.user?.companyId || ''}`} {...props} />;
};

export default Pedidos;
